import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import {
    CheckSquare, LogOut, Zap, Loader2, Users, Briefcase, Clock, Plus, X, Calendar,
    ChevronLeft, ChevronRight, Wallet, AlertTriangle, Star, Edit3, Search,
    UserPlus, Globe, AlertOctagon, TrendingUp, TrendingDown, Trash2, ArrowRight, ArrowUpRight, ArrowDownRight, ArrowDownLeft, Hammer,
    ShieldCheck, Shield, RefreshCw, ChevronDown, FileText, RefreshCcw, BarChart3, Package, Settings, GitMerge, Maximize2, Share2, Maximize, Minimize2, Check,
    Contact, Landmark, Building2, Phone, Link as LinkIcon, Layers, Layout, DollarSign, ArrowLeft, FilePlus, ChevronUp, Settings2, Bell, UserCheck, HelpCircle,
    Send, Sparkles, XCircle, Navigation, MapPin, Copy, PieChart, LayoutGrid, Image, Download, Table, Upload, Tag, MessageSquare, List, CalendarOff, AlignLeft, Target, Filter, FileDown, FileSpreadsheet, Eye
} from 'lucide-react';

const API_URL = 'api.php';
const MASTER_MAGIC_KEY = 'EVENT-MASTER-2025';

// Helper: Ordenar fases numéricamente (ej: "1. Fase" < "10. Fase")
const sortPhasesNumerically = (a, b) => {
    const getNum = (str) => {
        const match = (str || '').match(/^\s*(\d+)/);
        return match ? parseInt(match[1]) : 999;
    };
    const numA = getNum(a);
    const numB = getNum(b);
    if (numA !== numB) return numA - numB;
    return (a || '').localeCompare(b || '');
};

// Helper: Calcular días laborables (Lun-Vie 1, Sab 0.5)
const getWorkDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    let count = 0;
    let curr = new Date(s);
    while (curr <= e) {
        const day = curr.getDay();
        if (day >= 1 && day <= 5) count += 1; // Lun a Vie
        else if (day === 6) count += 0.5;      // Sábado
        curr.setDate(curr.getDate() + 1);
    }
    return count;
};

// Helper: normalizar código de moneda ("usd", "U$S", "US$", "dólar" -> USD; "$", "pesos" -> ARS)
const normCurrency = (c) => {
    const s = String(c || 'ARS').trim().toUpperCase();
    if (['USD', 'U$S', 'US$', 'U$D', 'DOLAR', 'DÓLAR', 'DOLARES', 'DÓLARES'].includes(s)) return 'USD';
    if (['ARS', '$', 'PESOS', 'PESO', 'AR$'].includes(s)) return 'ARS';
    return s;
};

// Helper: convertir un monto de recurso a la moneda del proyecto.
// Usa la cotización del proyecto solo si es realista (> 1); si no, la global. Devuelve { value, converted }.
const convertResourceCost = (amount, fromCur, toCur, projectRate, globalRate) => {
    const from = normCurrency(fromCur);
    const to = normCurrency(toCur);
    if (from === to) return { value: amount, converted: false };
    let rate = parseFloat(projectRate || 0);
    if (!(rate > 1)) rate = parseFloat(globalRate || 0);
    if (!(rate > 1)) rate = 1000;
    if (from === 'USD' && to === 'ARS') return { value: amount * rate, converted: true };
    if (from === 'ARS' && to === 'USD') return { value: amount / rate, converted: true };
    return { value: amount, converted: false }; // par de monedas no soportado: no se marca como convertido
};

const exportResourceUsageToExcel = (resources, assignments, projects) => {
    if (typeof XLSX === 'undefined') {
        alert("La librería de exportación aún está cargando. Reintente en un momento.");
        return;
    }
    // Tab 1: Por Proyecto
    const projectData = projects.map(proj => {
        const asigs = assignments.filter(a => a.event_id === proj.id);
        const totalPct = asigs.reduce((sum, a) => sum + parseFloat(a.percentage || 0), 0);
        return {
            'Proyecto': proj.name,
            'Recursos Asignados': asigs.length,
            'Carga Total (%)': totalPct,
            'Ubicación': proj.location,
            'Estado': proj.status
        };
    });

    // Tab 2: Por Recurso
    const resourceData = resources.map(res => {
        const asigs = assignments.filter(a => a.resource_id === res.id);
        const totalPct = asigs.reduce((sum, a) => sum + parseFloat(a.percentage || 0), 0);
        return {
            'Recurso': res.name,
            'Cargo': res.position,
            'Asignaciones': asigs.length,
            'Carga Actual (%)': totalPct,
            'Horas Diarias': res.daily_hours,
            'Costo Hora': res.hourly_rate
        };
    });

    const wb = XLSX.utils.book_new();
    const wsProj = XLSX.utils.json_to_sheet(projectData);
    const wsRes = XLSX.utils.json_to_sheet(resourceData);

    XLSX.utils.book_append_sheet(wb, wsProj, "Por Proyecto");
    XLSX.utils.book_append_sheet(wb, wsRes, "Por Recurso");

    XLSX.writeFile(wb, `Reporte_Recursos_${new Date().toISOString().split('T')[0]}.xlsx`);
};

// Global progress helper for consistency across screens
const calculateProgressMetrics = (allTasks, eventStartDate = null, eventEndDate = null) => {
    if (!allTasks || allTasks.length === 0) {
        const start = eventStartDate ? new Date(eventStartDate) : null;
        const end = eventEndDate ? new Date(eventEndDate) : start;
        return { physicalProgress: 0, theoreticalProgress: 0, projStart: start, projEnd: end };
    }

    const computeTaskNode = (task, all) => {
        const subs = all.filter(t => t.parent_id === task.id);
        if (subs.length > 0) {
            let totalDur = 0;
            let totalWeightedProg = 0;
            let minS = null;
            let maxE = null;

            const enrichedSubs = subs.map(sub => computeTaskNode(sub, all));

            enrichedSubs.forEach(child => {
                if (child.start_date && child.end_date) {
                    const s = new Date(child.start_date);
                    const e = new Date(child.end_date);
                    if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
                        if (!minS || s < minS) minS = s;
                        if (!maxE || e > maxE) maxE = e;
                        const dur = getWorkDays(s, e) || 1;
                        totalDur += dur;
                        totalWeightedProg += (Number(child.progress || 0) * dur);
                    }
                }
            });

            if (totalDur > 0) {
                task.progress = Math.round(totalWeightedProg / totalDur);
                if (minS) task.start_date = minS.toISOString().split('T')[0];
                if (maxE) task.end_date = maxE.toISOString().split('T')[0];
            } else {
                const sumProg = enrichedSubs.reduce((acc, curr) => acc + Number(curr.progress || 0), 0);
                task.progress = enrichedSubs.length > 0 ? Math.round(sumProg / enrichedSubs.length) : 0;
                if (minS) task.start_date = minS.toISOString().split('T')[0];
                if (maxE) task.end_date = maxE.toISOString().split('T')[0];
            }
        }
        return task;
    };

    const tasksCopy = allTasks.map(t => ({ ...t }));
    const roots = tasksCopy.filter(t => !t.parent_id);
    roots.forEach(root => computeTaskNode(root, tasksCopy));

    let totalDur = 0;
    let weightedProg = 0;
    let projStart = null;
    let projEnd = null;

    roots.forEach(tt => {
        if (tt.start_date && tt.end_date) {
            const s = new Date(tt.start_date);
            const e = new Date(tt.end_date);
            if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
                if (!projStart || s < projStart) projStart = s;
                if (!projEnd || e > projEnd) projEnd = e;
                const dur = getWorkDays(s, e) || 1;
                totalDur += dur;
                weightedProg += (Number(tt.progress || 0) * dur);
            }
        }
    });

    const physicalProgress = totalDur > 0 ? Math.round(weightedProg / totalDur) : 0;
    const finalStart = projStart || (eventStartDate ? new Date(eventStartDate) : null);
    const finalEnd = projEnd || (eventEndDate ? new Date(eventEndDate) : (eventStartDate ? new Date(eventStartDate) : null));

    let theoreticalProgress = 0;
    if (finalStart && finalEnd) {
        const totalWDays = getWorkDays(finalStart, finalEnd);
        const today = new Date();
        const endForElapsed = today < finalEnd ? today : finalEnd;
        const elapsedWDays = finalStart <= endForElapsed ? getWorkDays(finalStart, endForElapsed) : 0;
        theoreticalProgress = totalWDays > 0 ? Math.round((elapsedWDays / totalWDays) * 100) : 0;
    }

    return {
        physicalProgress,
        theoreticalProgress,
        projStart: finalStart,
        projEnd: finalEnd
    };
};



const CashFlowChart = ({ movements, baseCurrency = 'ARS', type = 'PROJECT', fCur, fNum, conversionRate = 1, projects = [], clients = [], embedded = false }) => {
    const [period, setPeriod] = useState('MONTH'); // DAY, WEEK, MONTH, YEAR
    const [viewType, setViewType] = useState('GRID'); // CHART, GRID
    const [isCollapsed, setIsCollapsed] = useState(embedded ? true : false);
    const [selectedDetail, setSelectedDetail] = useState(null); // { periodLabel, type: 'INCOME'|'EXPENSE' }

    // Helper to get period key for a movement
    const getMovementPeriodKey = useCallback((m) => {
        const date = new Date(m.expense_date);
        if (isNaN(date.getTime())) return null;
        if (period === 'DAY') return date.toISOString().split('T')[0];
        if (period === 'WEEK') {
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);
            d.setDate(d.getDate() + 4 - (d.getDay() || 7));
            const yearStart = new Date(d.getFullYear(), 0, 1);
            const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
            return `${d.getFullYear()}-W${weekNo}`;
        }
        if (period === 'MONTH') return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        return `${date.getFullYear()}`;
    }, [period]);

    // Get detail transactions for a period
    const getDetailTransactions = useCallback((periodLabel, movType) => {
        return movements.filter(m => {
            const key = getMovementPeriodKey(m);
            return key === periodLabel && m.movement_type === movType;
        }).map(m => {
            const proj = projects.find(p => p.id === m.event_id);
            const client = proj ? clients.find(c => c.id === proj.client_id) : null;
            return {
                ...m,
                _projectName: proj?.name || 'Sin Proyecto',
                _clientName: client?.name || 'Sin Cliente',
                _concept: m.description || m.category_name || '-',
            };
        });
    }, [movements, projects, clients, getMovementPeriodKey]);

    const exportToExcel = () => {
        if (typeof XLSX === 'undefined') {
            // Fallback to basic CSV if XLSX is not loaded
            let csv = "Periodo,Saldo Inicial,Ingresos,Egresos,Resultado,Saldo Final\n";
            groupedData.forEach(d => {
                csv += `${d.label},${d.openingBalance},${d.income},${d.expense},${d.net},${d.closingBalance}\n`;
            });
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.setAttribute("download", `CashFlow_${type}_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
        }

        // Tab 1: Resumen por Período
        const summaryData = groupedData.map(d => ({
            'Período': d.label,
            'Saldo Inicial': d.openingBalance,
            'Ingresos': d.income,
            'Egresos': d.expense,
            'Resultado': d.net,
            'Saldo Final': d.closingBalance
        }));

        // Tab 2: Detalle por Período
        const detailData = [];
        groupedData.forEach(d => {
            const periodMovements = movements.filter(m => getMovementPeriodKey(m) === d.label);
            periodMovements.forEach(m => {
                const proj = projects.find(p => p.id === m.event_id);
                const client = proj ? clients.find(c => c.id === proj.client_id) : null;
                const mCur = m.currency_original || m.currency || 'ARS';
                detailData.push({
                    'Período': d.label,
                    'Tipo': m.movement_type === 'INCOME' ? 'Ingreso' : 'Egreso',
                    'Cliente': client?.name || 'Sin Cliente',
                    'Proyecto': proj?.name || 'Sin Proyecto',
                    'Concepto': m.description || m.category_name || '-',
                    'Moneda Original': mCur,
                    'Importe Original': Number(m.amount_original || 0),
                    'Importe USD': Number(m.amount_normalized_usd || 0),
                    'Importe ARS': Number(m.amount_normalized_ars || 0),
                });
            });
        });

        const wb = XLSX.utils.book_new();
        const wsSummary = XLSX.utils.json_to_sheet(summaryData);
        const wsDetail = XLSX.utils.json_to_sheet(detailData);
        XLSX.utils.book_append_sheet(wb, wsSummary, "Resumen por Período");
        XLSX.utils.book_append_sheet(wb, wsDetail, "Detalle por Período");
        XLSX.writeFile(wb, `CashFlow_${type}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const groupedData = useMemo(() => {
        const groups = {};
        movements.forEach(m => {
            const date = new Date(m.expense_date);
            if (isNaN(date.getTime())) return;

            let key;
            if (period === 'DAY') key = date.toISOString().split('T')[0];
            else if (period === 'WEEK') {
                const d = new Date(date);
                d.setHours(0, 0, 0, 0);
                d.setDate(d.getDate() + 4 - (d.getDay() || 7));
                const yearStart = new Date(d.getFullYear(), 0, 1);
                const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
                key = `${d.getFullYear()}-W${weekNo}`;
            }
            else if (period === 'MONTH') key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            else key = `${date.getFullYear()}`;

            if (!groups[key]) groups[key] = { income: 0, expense: 0, net: 0, date: date };
            const amount = Number(m.val !== undefined ? m.val : (m.amount_normalized_ars || m.amount_converted || m.amount_original || 0)) * conversionRate;
            if (m.movement_type === 'INCOME') groups[key].income += amount;
            else groups[key].expense += amount;
            groups[key].net = groups[key].income - groups[key].expense;
        });

        const sorted = Object.entries(groups)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([label, data]) => ({ label, ...data }));

        let runningBalance = 0;
        return sorted.map(d => {
            const opening = runningBalance;
            runningBalance += d.net;
            return { ...d, openingBalance: opening, closingBalance: runningBalance };
        });
    }, [movements, period, conversionRate]);

    if (groupedData.length === 0) return (
        <div className="p-12 text-center bg-slate-50 rounded-[3rem] border border-dashed border-slate-200">
            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest italic">No hay datos suficientes para generar el análisis de flujo de caja.</p>
        </div>
    );

    const maxVal = Math.max(...groupedData.map(d => Math.max(d.income, d.expense, Math.abs(d.net)))) || 1;
    const chartHeight = 200;
    const barWidth = Math.max(20, (800 / groupedData.length) - 10);

    // Detail row renderer
    const renderDetailRow = (periodLabel, movType) => {
        if (!selectedDetail || selectedDetail.periodLabel !== periodLabel || selectedDetail.type !== movType) return null;
        const txns = getDetailTransactions(periodLabel, movType);
        if (txns.length === 0) return (
            <tr><td colSpan="6" className="px-6 py-4 text-center text-xs text-slate-400 italic bg-slate-50/50">No hay transacciones para este período</td></tr>
        );
        return (
            <tr><td colSpan="6" className="p-0">
                <div className="bg-slate-50/80 border-t border-b border-slate-200 px-4 py-3">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                            {movType === 'INCOME' ? '📥 Detalle Ingresos' : '📤 Detalle Egresos'} — {periodLabel}
                        </p>
                        <button onClick={() => setSelectedDetail(null)} className="text-slate-400 hover:text-slate-600 p-1">
                            <X size={14} />
                        </button>
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                        <table className="w-full text-left">
                            <thead className="bg-slate-100 text-[8px] font-black uppercase text-slate-400 tracking-widest">
                                <tr>
                                    <th className="px-4 py-2.5">Cliente</th>
                                    <th className="px-4 py-2.5">Proyecto</th>
                                    <th className="px-4 py-2.5">Concepto</th>
                                    <th className="px-4 py-2.5 text-right">Importe USD</th>
                                    <th className="px-4 py-2.5 text-right">Importe ARS</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {txns.map((tx, idx) => (
                                    <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-4 py-2 text-xs font-bold text-slate-700">{tx._clientName}</td>
                                        <td className="px-4 py-2 text-xs font-bold text-slate-600">{tx._projectName}</td>
                                        <td className="px-4 py-2 text-xs text-slate-500">{tx._concept}</td>
                                        <td className="px-4 py-2 text-right text-xs font-black text-indigo-600">{fCur(tx.amount_normalized_usd || 0, 'USD')}</td>
                                        <td className="px-4 py-2 text-right text-xs font-black text-slate-800">{fCur(tx.amount_normalized_ars || 0, 'ARS')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </td></tr>
        );
    };

    const handleCellClick = (periodLabel, movType) => {
        if (selectedDetail && selectedDetail.periodLabel === periodLabel && selectedDetail.type === movType) {
            setSelectedDetail(null);
        } else {
            setSelectedDetail({ periodLabel, type: movType });
        }
    };

    return (
        <div className={`bg-white ${embedded ? 'p-10 rounded-[3rem] border border-slate-100 shadow-sm' : ''} overflow-hidden`}>
            {embedded ? (
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
                    <div className="flex items-center gap-4">
                        <div onClick={() => setIsCollapsed(!isCollapsed)} className="cursor-pointer hover:bg-slate-50 p-2 rounded-xl transition-all flex items-center gap-3">
                            <div className={`transition-transform duration-500 ${isCollapsed ? '' : 'rotate-180'}`}>
                                <ChevronDown size={20} className="text-slate-400" />
                            </div>
                            <div>
                                <h3 className="text-sm font-black uppercase tracking-tighter text-slate-800 flex items-center gap-3">
                                    <TrendingUp size={20} className="text-indigo-600" /> Flujo de Caja {type === 'PROJECT' ? 'del Proyecto' : 'Empresa'}
                                </h3>
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Análisis temporal de ingresos y egresos</p>
                            </div>
                        </div>
                    </div>

                    {!isCollapsed && (
                        <div className="flex items-center gap-4 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
                            <div className="flex bg-slate-100 p-1 rounded-2xl shrink-0">
                                {['CHART', 'GRID'].map(v => (
                                    <button
                                        key={v}
                                        onClick={() => setViewType(v)}
                                        className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all flex items-center gap-2 ${viewType === v ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white text-slate-400 hover:text-slate-600 border border-slate-100'}`}
                                    >
                                        {v === 'CHART' ? <BarChart3 size={15} /> : <Table size={15} />}
                                        {v === 'CHART' ? 'Vista Gráfico' : 'Vista Grilla'}
                                    </button>
                                ))}
                            </div>

                            <div className="flex bg-slate-100 p-1 rounded-2xl shrink-0">
                                {['DAY', 'WEEK', 'MONTH', 'YEAR'].map(p => (
                                    <button
                                        key={p}
                                        onClick={() => setPeriod(p)}
                                        className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${period === p ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                                    >
                                        {p === 'DAY' ? 'Día' : p === 'WEEK' ? 'Sem' : p === 'MONTH' ? 'Mes' : 'Año'}
                                    </button>
                                ))}
                            </div>

                            <button
                                onClick={exportToExcel}
                                className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-100 transition-all shadow-sm flex items-center gap-2 text-[10px] font-black uppercase"
                                title="Exportar a Excel"
                            >
                                <FileSpreadsheet size={16} />
                                <span className="hidden md:inline">Exportar</span>
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
                    <div>
                        <h3 className="text-lg font-black uppercase tracking-tighter text-slate-800 flex items-center gap-3">
                            <TrendingUp size={20} className="text-indigo-600" /> Flujo de Caja {type === 'PROJECT' ? 'del Proyecto' : 'Empresa'}
                        </h3>
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Análisis temporal de ingresos y egresos</p>
                    </div>
                    <div className="flex items-center gap-4 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
                        <div className="flex bg-slate-100 p-1 rounded-2xl shrink-0">
                            {['CHART', 'GRID'].map(v => (
                                <button
                                    key={v}
                                    onClick={() => setViewType(v)}
                                    className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all flex items-center gap-2 ${viewType === v ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white text-slate-400 hover:text-slate-600 border border-slate-100'}`}
                                >
                                    {v === 'CHART' ? <BarChart3 size={15} /> : <Table size={15} />}
                                    {v === 'CHART' ? 'Vista Gráfico' : 'Vista Grilla'}
                                </button>
                            ))}
                        </div>

                        <div className="flex bg-slate-100 p-1 rounded-2xl shrink-0">
                            {['DAY', 'WEEK', 'MONTH', 'YEAR'].map(p => (
                                <button
                                    key={p}
                                    onClick={() => setPeriod(p)}
                                    className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${period === p ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                    {p === 'DAY' ? 'Día' : p === 'WEEK' ? 'Sem' : p === 'MONTH' ? 'Mes' : 'Año'}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={exportToExcel}
                            className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-100 transition-all shadow-sm flex items-center gap-2 text-[10px] font-black uppercase"
                            title="Exportar a Excel"
                        >
                            <FileSpreadsheet size={16} />
                            <span className="hidden md:inline">Exportar</span>
                        </button>
                    </div>
                </div>
            )}

            {(embedded ? !isCollapsed : true) && (
                <div className={embedded ? "mt-10" : ""}>
                    {viewType === 'CHART' ? (
                        <>
                            {(() => {
                                const svgW = Math.max(700, groupedData.length * 80);
                                const svgH = 280;
                                const padL = 70, padR = 20, padT = 20, padB = 60;
                                const plotW = svgW - padL - padR;
                                const plotH = svgH - padT - padB;
                                const allVals = groupedData.flatMap(d => [d.income, d.expense, d.closingBalance, d.openingBalance]);
                                const maxY = Math.max(...allVals.map(Math.abs), 1);
                                const minBalance = Math.min(...groupedData.map(d => Math.min(d.closingBalance, d.openingBalance)), 0);
                                const yMin = minBalance < 0 ? minBalance * 1.15 : -maxY * 0.05;
                                const yMax = maxY * 1.15;
                                const yRange = yMax - yMin || 1;
                                const toY = (v) => padT + plotH - ((v - yMin) / yRange) * plotH;
                                const toX = (i) => padL + (i + 0.5) * (plotW / groupedData.length);
                                const barW = Math.max(8, Math.min(28, (plotW / groupedData.length) * 0.3));
                                const zeroY = toY(0);

                                // Y-axis ticks
                                const nTicks = 5;
                                const yTicks = Array.from({ length: nTicks + 1 }, (_, i) => yMin + (yRange * i / nTicks));

                                // Balance line path
                                const balancePath = groupedData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${toX(i)} ${toY(d.closingBalance)}`).join(' ');
                                const areaPath = `${balancePath} L ${toX(groupedData.length - 1)} ${zeroY} L ${toX(0)} ${zeroY} Z`;

                                // Format for Y axis labels
                                const fmtY = (v) => {
                                    const abs = Math.abs(v);
                                    if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
                                    if (abs >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
                                    return v.toFixed(0);
                                };

                                return (
                                    <div className="overflow-x-auto pb-4 custom-scrollbar">
                                        <svg width={svgW} height={svgH} className="select-none">
                                            <defs>
                                                <linearGradient id="balanceGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#6366f1" stopOpacity="0.2" />
                                                    <stop offset="100%" stopColor="#6366f1" stopOpacity="0.02" />
                                                </linearGradient>
                                            </defs>

                                            {/* Y-axis grid lines & labels */}
                                            {yTicks.map((tick, i) => (
                                                <g key={`yt-${i}`}>
                                                    <line x1={padL} y1={toY(tick)} x2={svgW - padR} y2={toY(tick)} stroke="#f1f5f9" strokeWidth="1" />
                                                    <text x={padL - 8} y={toY(tick) + 3} textAnchor="end" className="fill-slate-400" style={{ fontSize: '9px', fontWeight: 800 }}>{fmtY(tick)}</text>
                                                </g>
                                            ))}

                                            {/* Zero line */}
                                            <line x1={padL} y1={zeroY} x2={svgW - padR} y2={zeroY} stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="4 3" />

                                            {/* Area fill under balance line */}
                                            <path d={areaPath} fill="url(#balanceGrad)" />

                                            {/* Income & Expense bars */}
                                            {groupedData.map((d, i) => {
                                                const cx = toX(i);
                                                const incH = (d.income / yRange) * plotH;
                                                const expH = (d.expense / yRange) * plotH;
                                                return (
                                                    <g key={`bar-${i}`} className="group">
                                                        <rect x={cx - barW - 1} y={zeroY - incH} width={barW} height={Math.max(1, incH)} rx="3" fill="#10b981" opacity="0.7" className="hover:opacity-100 transition-opacity" />
                                                        <rect x={cx + 1} y={zeroY - expH} width={barW} height={Math.max(1, expH)} rx="3" fill="#f43f5e" opacity="0.55" className="hover:opacity-90 transition-opacity" />
                                                        {/* Hover area with tooltip */}
                                                        <rect x={cx - barW - 4} y={padT} width={barW * 2 + 8} height={plotH} fill="transparent" className="cursor-pointer" />
                                                        {/* Tooltip */}
                                                        <g className="opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ transitionDuration: '200ms' }}>
                                                            <rect x={cx - 90} y={Math.max(padT, toY(d.closingBalance) - 80)} width="180" height="72" rx="12" fill="#1e293b" fillOpacity="0.95" />
                                                            <text x={cx} y={Math.max(padT, toY(d.closingBalance) - 80) + 16} textAnchor="middle" className="fill-white" style={{ fontSize: '9px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{d.label}</text>
                                                            <text x={cx - 75} y={Math.max(padT, toY(d.closingBalance) - 80) + 32} className="fill-emerald-400" style={{ fontSize: '9px', fontWeight: 700 }}>Ing: {fCur(d.income, baseCurrency)}</text>
                                                            <text x={cx - 75} y={Math.max(padT, toY(d.closingBalance) - 80) + 46} className="fill-rose-400" style={{ fontSize: '9px', fontWeight: 700 }}>Egr: {fCur(d.expense, baseCurrency)}</text>
                                                            <text x={cx - 75} y={Math.max(padT, toY(d.closingBalance) - 80) + 60} className="fill-indigo-300" style={{ fontSize: '9px', fontWeight: 900 }}>Saldo: {fCur(d.closingBalance, baseCurrency)}</text>
                                                        </g>
                                                    </g>
                                                );
                                            })}

                                            {/* Balance line */}
                                            <path d={balancePath} fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

                                            {/* Balance dots */}
                                            {groupedData.map((d, i) => (
                                                <circle key={`dot-${i}`} cx={toX(i)} cy={toY(d.closingBalance)} r="4" fill="white" stroke="#6366f1" strokeWidth="2" className="hover:r-6 transition-all" />
                                            ))}

                                            {/* X-axis labels */}
                                            {groupedData.map((d, i) => (
                                                <text key={`xl-${i}`} x={toX(i)} y={svgH - 10} textAnchor="middle" className="fill-slate-400" style={{ fontSize: '8px', fontWeight: 800, textTransform: 'uppercase' }}>{d.label}</text>
                                            ))}
                                        </svg>
                                    </div>
                                );
                            })()}

                            <div className="flex gap-8 mt-6 pt-6 border-t border-slate-50">
                                <div className="flex items-center gap-3">
                                    <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                                    <span className="text-[9px] font-black uppercase text-slate-500 tracking-widest">Ingresos</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-3 h-3 bg-rose-500 rounded-full"></div>
                                    <span className="text-[9px] font-black uppercase text-slate-500 tracking-widest">Egresos</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-0.5 bg-indigo-500 rounded-full"></div>
                                    <span className="text-[9px] font-black uppercase text-slate-500 tracking-widest">Saldo Acumulado</span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="mt-6 overflow-x-auto rounded-[2rem] border border-slate-100">
                            <table className="w-full text-left">
                                <thead className="bg-slate-50 text-[9px] font-black uppercase text-slate-400 tracking-widest">
                                    <tr>
                                        <th className="px-6 py-4">Período</th>
                                        <th className="px-6 py-4 text-right">Saldo Inicial</th>
                                        <th className="px-6 py-4 text-right">Ingresos</th>
                                        <th className="px-6 py-4 text-right">Egresos</th>
                                        <th className="px-6 py-4 text-right">Resultado</th>
                                        <th className="px-6 py-4 text-right">Saldo Final</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {groupedData.map((d, i) => (
                                        <React.Fragment key={i}>
                                            <tr className="hover:bg-slate-50 transition-colors">
                                                <td className="px-6 py-4 text-xs font-black uppercase text-slate-700">{d.label}</td>
                                                <td className="px-6 py-4 text-right text-sm font-bold text-slate-400 italic">{fCur(d.openingBalance, baseCurrency)}</td>
                                                <td className="px-6 py-4 text-right text-sm font-black text-emerald-600 cursor-pointer hover:underline hover:text-emerald-700 transition-colors" onClick={() => handleCellClick(d.label, 'INCOME')}>{fCur(d.income, baseCurrency)}</td>
                                                <td className="px-6 py-4 text-right text-sm font-black text-rose-600 cursor-pointer hover:underline hover:text-rose-700 transition-colors" onClick={() => handleCellClick(d.label, 'EXPENSE')}>{fCur(d.expense, baseCurrency)}</td>
                                                <td className={`px-6 py-4 text-right text-base font-black tracking-tighter ${d.net >= 0 ? 'text-indigo-600' : 'text-rose-900'}`}>{fCur(d.net, baseCurrency)}</td>
                                                <td className="px-6 py-4 text-right text-sm font-black text-slate-900">{fCur(d.closingBalance, baseCurrency)}</td>
                                            </tr>
                                            {renderDetailRow(d.label, 'INCOME')}
                                            {renderDetailRow(d.label, 'EXPENSE')}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                                <tfoot className="bg-slate-50/50">
                                    <tr className="font-black">
                                        <td className="px-6 py-4 text-xs font-black uppercase text-slate-800">TOTAL</td>
                                        <td className="px-6 py-4 text-right text-sm text-slate-400">-</td>
                                        <td className="px-6 py-4 text-right text-sm text-emerald-600">{fCur(groupedData.reduce((a, b) => a + b.income, 0), baseCurrency)}</td>
                                        <td className="px-6 py-4 text-right text-sm text-rose-600">{fCur(groupedData.reduce((a, b) => a + b.expense, 0), baseCurrency)}</td>
                                        <td className={`px-6 py-4 text-right text-base tracking-tighter ${groupedData.reduce((a, b) => a + b.net, 0) >= 0 ? 'text-indigo-600' : 'text-rose-900'}`}>{fCur(groupedData.reduce((a, b) => a + b.net, 0), baseCurrency)}</td>
                                        <td className="px-6 py-4 text-right text-sm text-slate-900">{fCur(groupedData.length > 0 ? groupedData[groupedData.length - 1].closingBalance : 0, baseCurrency)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const MATERIAL_MATRIX = {
    // --- CIMIENTOS Y CONTRAPISOS ---
    'CONTRAPISO_CASCOTE_M3': {
        group: 'CIMIENTOS Y CONTRAPISOS',
        name: 'Hormigón de Cascote (1/4:1:4:6) x m3',
        unit: 'm3',
        components: [
            { name: 'Cemento Portland', qty: 45.45, unit: 'kg' },
            { name: 'Cal Hidráulica', qty: 149.35, unit: 'kg' },
            { name: 'Arena Gruesa', qty: 0.52, unit: 'm3' },
            { name: 'Cascote Limpio', qty: 0.78, unit: 'm3' }
        ]
    },
    'CONTRAPISO_CASCOTE_10CM': {
        group: 'CIMIENTOS Y CONTRAPISOS',
        name: 'Contrapiso de Cascote (Espesor 10cm) x m2',
        unit: 'm2',
        components: [
            { name: 'Cemento Portland', qty: 4.54, unit: 'kg' },
            { name: 'Cal Hidráulica', qty: 14.93, unit: 'kg' },
            { name: 'Arena Gruesa', qty: 0.052, unit: 'm3' },
            { name: 'Cascote Limpio', qty: 0.078, unit: 'm3' }
        ]
    },

    // --- BASES, COLUMNAS, VIGAS, LOSAS, ENCADENADOS ---
    'HORMIGON_PIEDRA': {
        group: 'BASES, COLUMNAS, VIGAS, LOSAS, ENCADENADOS',
        name: 'Hormigón de Piedra (1:3:3) x m3',
        unit: 'm3',
        components: [
            { name: 'Cemento Portland', qty: 309.73, unit: 'kg' },
            { name: 'Arena Gruesa', qty: 0.66, unit: 'm3' },
            { name: 'Piedra Partida', qty: 0.66, unit: 'm3' }
        ]
    },

    // --- AISLACIONES Y CARPETAS ---
    'CAPA_AISLADORA_1CM': {
        group: 'AISLACIONES Y CARPETAS',
        name: 'Capa Aisladora con Hidrófugo (1cm) x m2',
        unit: 'm2',
        components: [
            { name: 'Cemento Portland', qty: 5.32, unit: 'kg' }, // 532.32 * 0.01
            { name: 'Arena Media', qty: 0.0114, unit: 'm3' }, // 1.14 * 0.01
            { name: 'Hidrófugo Liquido', qty: 0.5, unit: 'kg' }
        ]
    },

    // --- PAREDES LADRILLO COMUN ---
    'MURO_COMUN_TABIQUE': {
        group: 'PAREDES LADRILLO COMUN',
        name: 'Tabique de Canto x m2',
        unit: 'm2',
        components: [
            { name: 'Ladrillo Común', qty: 30, unit: 'u' },
            { name: 'Cemento Portland', qty: 2.04, unit: 'kg' }, // 234.90 * 0.0087
            { name: 'Cal Hidráulica', qty: 3.36, unit: 'kg' }, // 385.91 * 0.0087
            { name: 'Arena Gruesa', qty: 0.0088, unit: 'm3' } // 1.01 * 0.0087
        ]
    },
    'MURO_COMUN_15': {
        group: 'PAREDES LADRILLO COMUN',
        name: 'Pared 15cm (12cm + mezcla) x m2',
        unit: 'm2',
        components: [
            { name: 'Ladrillo Común', qty: 60, unit: 'u' },
            { name: 'Cemento Portland', qty: 7.52, unit: 'kg' }, // 234.90 * 0.032
            { name: 'Cal Hidráulica', qty: 12.35, unit: 'kg' }, // 385.91 * 0.032
            { name: 'Arena Gruesa', qty: 0.0323, unit: 'm3' } // 1.01 * 0.032
        ]
    },
    'MURO_COMUN_20': {
        group: 'PAREDES LADRILLO COMUN',
        name: 'Pared 20cm (18cm + mezcla) x m2',
        unit: 'm2',
        components: [
            { name: 'Ladrillo Común', qty: 90, unit: 'u' },
            { name: 'Cemento Portland', qty: 6.90, unit: 'kg' }, // 123.24 * 0.056
            { name: 'Cal Hidráulica', qty: 22.67, unit: 'kg' }, // 404.93 * 0.056
            { name: 'Arena Gruesa', qty: 0.0594, unit: 'm3' } // 1.06 * 0.056
        ]
    },
    'MURO_COMUN_30': {
        group: 'PAREDES LADRILLO COMUN',
        name: 'Pared 30cm (25cm + mezcla) x m2',
        unit: 'm2',
        components: [
            { name: 'Ladrillo Común', qty: 120, unit: 'u' },
            { name: 'Cemento Portland', qty: 9.98, unit: 'kg' }, // 123.24 * 0.081
            { name: 'Cal Hidráulica', qty: 32.80, unit: 'kg' }, // 404.93 * 0.081
            { name: 'Arena Gruesa', qty: 0.0859, unit: 'm3' } // 1.06 * 0.081
        ]
    },

    // --- PAREDES LADRILLOS CERAMICOS (Huecos) ---
    'MURO_HUECO_8': {
        group: 'PAREDES LADRILLOS CERAMICOS (HUECO)',
        name: 'Tabique 8cm (8x18x33) x m2',
        unit: 'm2',
        components: [
            { name: 'Ladrillo Hueco 8x18x33', qty: 16, unit: 'u' },
            { name: 'Cemento Portland', qty: 0.69, unit: 'kg' }, // 63.18 * 0.011
            { name: 'Cal Hidráulica', qty: 4.57, unit: 'kg' }, // 415.16 * 0.011
            { name: 'Arena Gruesa', qty: 0.0119, unit: 'm3' } // 1.08 * 0.011
        ]
    },
    'MURO_HUECO_12': {
        group: 'PAREDES LADRILLOS CERAMICOS (HUECO)',
        name: 'Tabique 12cm (12x18x33) x m2',
        unit: 'm2',
        components: [
            { name: 'Ladrillo Hueco 12x18x33', qty: 16, unit: 'u' },
            { name: 'Cemento Portland', qty: 0.66, unit: 'kg' }, // 63.18 * 0.0105
            { name: 'Cal Hidráulica', qty: 4.36, unit: 'kg' }, // 415.16 * 0.0105
            { name: 'Arena Gruesa', qty: 0.0113, unit: 'm3' } // 1.08 * 0.0105
        ]
    },
    'MURO_HUECO_18': {
        group: 'PAREDES LADRILLOS CERAMICOS (HUECO)',
        name: 'Pared 18cm (18x18x33) x m2',
        unit: 'm2',
        components: [
            { name: 'Ladrillo Hueco 18x18x33', qty: 16, unit: 'u' },
            { name: 'Cemento Portland', qty: 0.87, unit: 'kg' }, // 63.18 * 0.0137
            { name: 'Cal Hidráulica', qty: 5.69, unit: 'kg' }, // 415.16 * 0.0137
            { name: 'Arena Gruesa', qty: 0.0148, unit: 'm3' } // 1.08 * 0.0137
        ]
    },

    // --- PAREDES LADRILLOS PORTANTES ---
    'MURO_PORTANTE_12': {
        group: 'PAREDES LADRILLOS PORTANTES',
        name: 'Muro Portante 12cm (12x19x33) x m2',
        unit: 'm2',
        components: [
            { name: 'Ladrillo Portante 12', qty: 16, unit: 'u' },
            { name: 'Cemento Albañilería', qty: 2.75, unit: 'kg' }, // 275 * 0.01
            { name: 'Arena Media', qty: 0.011, unit: 'm3' }  // 1.11 * 0.01
        ]
    },
    'MURO_PORTANTE_18': {
        group: 'PAREDES LADRILLOS PORTANTES',
        name: 'Muro Portante 18cm (18x19x33) x m2',
        unit: 'm2',
        components: [
            { name: 'Ladrillo Portante 18', qty: 16, unit: 'u' },
            { name: 'Cemento Albañilería', qty: 3.30, unit: 'kg' }, // 275 * 0.012
            { name: 'Arena Media', qty: 0.013, unit: 'm3' }  // 1.11 * 0.012
        ]
    },

    // --- REVOQUES ---
    'REVOQUE_GRUESO': {
        group: 'REVOQUES',
        name: 'Revoque Grueso (1.5cm) x m2',
        unit: 'm2',
        components: [
            { name: 'Cemento Portland', qty: 1.91, unit: 'kg' }, // 127.27 * 0.015
            { name: 'Cal Hidráulica', qty: 6.27, unit: 'kg' }, // 418.18 * 0.015
            { name: 'Arena Media', qty: 0.016, unit: 'm3' }  // 1.09 * 0.015
        ]
    },
    'REVOQUE_FINO': {
        group: 'REVOQUES',
        name: 'Revoque Fino (0.5cm) x m2',
        unit: 'm2',
        components: [
            { name: 'Cemento Portland', qty: 0.47, unit: 'kg' }, // 93.58 * 0.005
            { name: 'Cal Aérea', qty: 4.81, unit: 'kg' }, // 962.57 * 0.005
            { name: 'Arena Fina', qty: 0.0054, unit: 'm3' } // 1.07 * 0.005
        ]
    }
};

// CIRSOC 201 - Matriz de Barras de Hierro
const CIRSOC_REBAR_MATRIX = {
    6: { perim_cm: 1.88, kg_m: 0.222, kg_12m: 2.66 },
    8: { perim_cm: 2.51, kg_m: 0.395, kg_12m: 4.74 },
    10: { perim_cm: 3.14, kg_m: 0.617, kg_12m: 7.40 },
    12: { perim_cm: 3.77, kg_m: 0.888, kg_12m: 10.7 },
    16: { perim_cm: 5.03, kg_m: 1.580, kg_12m: 18.9 },
    20: { perim_cm: 6.28, kg_m: 2.470, kg_12m: 29.6 },
    25: { perim_cm: 7.85, kg_m: 3.850, kg_12m: 46.2 },
    32: { perim_cm: 10.10, kg_m: 6.310, kg_12m: 75.7 },
    40: { perim_cm: 12.60, kg_m: 9.860, kg_12m: 118.3 }
};

const ProjectCover = ({ url }) => {
    if (!url) return null;
    return (
        <div className="w-full h-48 rounded-[2.5rem] overflow-hidden mb-8 border border-slate-100 shadow-sm">
            <img src={url} alt="Project Cover" className="w-full h-full object-cover" />
        </div>
    );
};

function calculateRebarBars(diameter, totalKg) {
    const data = CIRSOC_REBAR_MATRIX[diameter];
    if (!data) return null;
    return Math.ceil(totalKg / data.kg_12m);
}

const TRANSLATIONS = {
    ES: {
        dashboard: "Seguimiento de Proyectos",
        events: "Proyectos",
        team: "Equipo",
        master: "Administrador Maestro",
        logout: "Cerrar Sesión",
        welcome: "Portal de Gestión",
        total_projects: "Total Proyectos",
        total_proyectos: "Total Proyectos",
        no_tasks_gantt: "No hay tareas con fechas para mostrar en el Gantt",
        in_progress: "En Curso",
        completed: "Finalizados",
        recent_activity: "Actividad Reciente",
        new_project: "Nuevo Proyecto",
        status: "Estado",
        progress: "Avance",
        members: "Miembros",
        settings: "Ajustes de Perfil",
        save: "Guardar Cambios",
        name: "Nombre completo",
        language: "Idioma del Portal",
        calculated_by_subs: "CALCULADO POR SUB-TAREAS",
        auto_calculated: "CALCULO AUTOMÁTICO",
        milestone: "Hito Crítico",
        parent_task: "Fase / Tarea Principal",
        sub_task_of: "Sub-tarea de:",
        responsibility: "Responsable",
        predecessor: "Predecesora",
        dates: "Fechas",
        delete_confirm: "¿Estás seguro?",
        upload_doc: "Subir Archivo",
        doc_linked: "Documento vinculado",
        add_task: "Nueva Tarea",
        edit_task: "Editar Tarea",
        planning: "Planeamiento",
        work: "En Obra",
        summarize: "Resumen",
        schedule: "Cronograma",
        gantt: "Gantt",
        payments: "Pagos",
        budgets: "Presupuesto",
        rubros: "Rubros",
        computo: "Cómputo",
        estructura: "Estructura",
        calculate_materials: "Asistente de Cálculo",
        add_structure_calc: "Calculadora Estructural",
        profile: "Perfil de Usuario",
        edit_event: "Editar Proyecto",
        create_event: "Nuevo Proyecto",
        manage_members: "Gestión de Miembros",
        add_material: "Nuevo Material",
        edit_material: "Editar Material",
        set_budget: "Asignar Presupuesto",
        add_expense: "Cargar Pago",
        edit_expense: "Editar Pago",
        add_team: "Nuevo Miembro",
        edit_team: "Editar Miembro",
        create_license: "Nueva Licencia",
        edit_license: "Editar Licencia",
        health_optimal: "ESTADO ÓPTIMO: EL AVANCE COINCIDE CON EL CRONOGRAMA.",
        health_caution: "PRECAUCIÓN: LIGERO DESVÍO RESPECTO AL CRONOGRAMA.",
        health_risk: "RIESGO: EL AVANCE REAL ESTÁ MUY POR DEBAJO DEL TEÓRICO.",
        financial: "Finanzas",
        clients: "Clientes",
        help: "Ayuda",
        portada: "Portada",
        documentacion: "Documentación",
        recursos: "Recursos",
        resumen: "Resumen",
        manage_inactivity_title: "Inactividad de Recurso",
        inactivity_subtitle: "Vacaciones, licencias o ausencias programadas",
        new_inactivity_period: "Registrar Nuevo Período",
        from_date: "Desde",
        to_date: "Hasta",
        reason_placeholder: "Motivo (ej: Vacaciones, Licencia Médica)",
        add_inactivity_btn: "Agregar Inactividad",
        registered_periods: "Períodos Registrados",
        no_periods_registered: "No hay períodos registrados",
        delete_inactivity_confirm: "¿Eliminar este período de inactividad?",
        ai_planner: "Asistente de Planificación IA",
        reports_generator: "Generador de Reportes",
        ai_ppt_planner: "Asistente de Reportes PPT",
        close: "Cerrar",
        // NEW keys
        seguimiento_proyectos: "Seguimiento de Proyectos",
        mapa_geografico: "Mapa Geográfico",
        mapa_operaciones: "Mapa de Operaciones",
        control_recursos: "Control de Recursos",
        asignacion_recursos: "Asignación de tiempo y costos de mano de obra",
        centro_ayuda: "Centro de Ayuda",
        guia_funcionalidades: "Guía de funcionalidades y casos prácticos",
        roles_permisos: "Roles y Permisos",
        configuracion: "Configuración",
        dashboard_analitico: "Dashboard Analítico",
        lista_proyectos: "Lista de Proyectos",
        proyectos_por_anio: "Proyectos por Año",
        proyectos_por_zona: "Proyectos por Zona",
        proyectos_por_estatus: "Proyectos por Estatus",
        proyectos_por_avance: "Proyectos por Avance",
        presupuesto_por_proyecto: "Presupuesto por Proyecto (Top 10)",
        avance_fisico_vs_teorico: "Avance Físico vs Teórico",
        sin_datos: "Sin datos suficientes",
        exportar: "Exportar",
        buscar: "Buscar...",
        todos_estados: "Todos los Estados",
        todos_tipos: "Todos los tipos",
        nuevo_recurso: "Nuevo Recurso",
        unirse_proyecto: "Unirse a Proyecto",
        copiar_link_horas: "Copiar Link de Carga de Horas",
        recursos_adicionales: "Recursos Adicionales",
        filtrar_responsable: "Filtrar por responsable",
        todos_responsables: "Todos",
        gestion_seguimiento: "Gestión y Seguimiento de Proyectos Activos",
        click_para_ver: "Clic en un segmento para ver proyectos"
    },
    EN: {
        dashboard: "Project Tracking",
        events: "Projects",
        team: "Team",
        master: "Master Admin",
        logout: "Logout",
        welcome: "Management Portal",
        total_projects: "Total Projects",
        in_progress: "In Progress",
        completed: "Completed",
        recent_activity: "Recent Activity",
        new_project: "New Project",
        status: "Status",
        progress: "Progress",
        members: "Members",
        settings: "Profile Settings",
        save: "Save Changes",
        name: "Full Name",
        language: "Portal Language",
        calculated_by_subs: "CALCULATED BY SUB-TASKS",
        auto_calculated: "AUTO CALCULATED",
        milestone: "Key Milestone",
        parent_task: "Phase / Main Task",
        sub_task_of: "Sub-task of:",
        responsibility: "Assignee",
        predecessor: "Predecessor",
        dates: "Dates",
        delete_confirm: "Are you sure?",
        upload_doc: "Upload File",
        doc_linked: "Document linked",
        add_task: "New Task",
        edit_task: "Edit Task",
        planning: "Planning",
        work: "In Progress",
        summarize: "Summary",
        schedule: "Schedule",
        gantt: "Gantt",
        payments: "Payments",
        budgets: "Budget",
        rubros: "Categories",
        computo: "Computo",
        estructura: "Structure",
        calculate_materials: "Material Assistant",
        add_structure_calc: "Structural Calculator",
        profile: "User Profile",
        edit_event: "Edit Project",
        create_event: "New Project",
        manage_members: "Manage Members",
        add_material: "New Material",
        edit_material: "Edit Material",
        set_budget: "Set Budget",
        add_expense: "Add Payment",
        edit_expense: "Edit Payment",
        add_team: "Add Team Member",
        edit_team: "Edit Team Member",
        create_license: "New License",
        edit_license: "Edit License",
        health_optimal: "OPTIMAL STATUS: PROGRESS MATCHES SCHEDULE.",
        health_caution: "CAUTION: SLIGHT DEVIATION FROM SCHEDULE.",
        health_risk: "RISK: REAL PROGRESS IS WELL BELOW THEORETICAL.",
        financial: "Financial",
        clients: "Clients",
        help: "Help",
        portada: "Brief",
        documentacion: "Documentation",
        recursos: "Resources",
        resumen: "Summary",
        manage_inactivity_title: "Resource Inactivity",
        inactivity_subtitle: "Vacations, leaves or scheduled absences",
        new_inactivity_period: "Register New Period",
        from_date: "From",
        to_date: "To",
        reason_placeholder: "Reason (e.g. Vacations, Medical Leave)",
        add_inactivity_btn: "Add Inactivity",
        registered_periods: "Registered Periods",
        no_periods_registered: "No periods registered",
        delete_inactivity_confirm: "Delete this inactivity period?",
        ai_planner: "AI Planning Assistant",
        reports_generator: "Reports Generator",
        ai_ppt_planner: "AI PPT Reports Assistant",
        close: "Close",
        // NEW keys
        seguimiento_proyectos: "Project Tracking",
        mapa_geografico: "Geographic Map",
        mapa_operaciones: "Operations Map",
        control_recursos: "Resource Control",
        asignacion_recursos: "Time and labor cost assignment",
        centro_ayuda: "Help Center",
        guia_funcionalidades: "Feature guide and practical cases",
        roles_permisos: "Roles & Permissions",
        configuracion: "Settings",
        dashboard_analitico: "Analytics Dashboard",
        lista_proyectos: "Project List",
        proyectos_por_anio: "Projects by Year",
        proyectos_por_zona: "Projects by Zone",
        proyectos_por_estatus: "Projects by Status",
        proyectos_por_avance: "Projects by Progress",
        presupuesto_por_proyecto: "Budget by Project (Top 10)",
        avance_fisico_vs_teorico: "Physical vs Theoretical Progress",
        sin_datos: "Insufficient data",
        exportar: "Export",
        buscar: "Search...",
        todos_estados: "All Statuses",
        todos_tipos: "All types",
        nuevo_recurso: "New Resource",
        unirse_proyecto: "Join Project",
        copiar_link_horas: "Copy Hours Link",
        recursos_adicionales: "Additional Resources",
        filtrar_responsable: "Filter by assignee",
        todos_responsables: "All",
        gestion_seguimiento: "Project Management & Tracking",
        click_para_ver: "Click on a segment to view projects"
    }
};

function StatBox({ label, value, icon, color, active, onClick, t }) {
    const colors = {
        blue: "bg-gradient-to-br from-[#5BA3F5] to-[#4A90E2]",
        emerald: "bg-gradient-to-br from-emerald-400 to-emerald-500",
        amber: "bg-gradient-to-br from-amber-400 to-amber-500",
        rose: "bg-gradient-to-br from-rose-400 to-rose-500",
        slate: "bg-gradient-to-br from-slate-600 to-slate-700"
    };
    return (
        <div
            onClick={onClick}
            className={`p-10 rounded-[3rem] border-2 transition-all cursor-pointer relative overflow-hidden group ${active ? 'border-[#5BA3F5] bg-gradient-to-br from-[#E8F4FF] to-white shadow-lg shadow-[#5BA3F5]/20' : 'bg-white border-slate-200 hover:border-[#6BB6FF] hover:shadow-md shadow-sm'}`}
        >
            <div className={`p-4 rounded-2xl w-fit mb-6 text-white shadow-xl ${colors[color] || colors.blue} group-hover:scale-110 transition-transform duration-300`}>
                {React.cloneElement(icon, { size: 24 })}
            </div>
            <p className="font-black text-sm uppercase tracking-widest text-slate-400 mb-2">{label}</p>
            <p className="font-black text-4xl tracking-tighter text-slate-900">{value}</p>
        </div>
    );
}

const KPI_DEFS = {
    AVANCE_FISICO: { id: 'AVANCE_FISICO', label: 'Avance Físico', color: 'indigo', unit: '%' },
    AVANCE_TEORICO: { id: 'AVANCE_TEORICO', label: 'Avance Teórico', color: 'blue', unit: '%' },
    DESVIO_PLAN: { id: 'DESVIO_PLAN', label: 'Desvío (Real vs Plan)', color: 'amber', unit: '%', type: 'DIFF' },
    EJECUCION_PRESUPUESTO: { id: 'EJECUCION_PRESUPUESTO', label: 'Ejecución Presupuestaria', color: 'emerald', unit: '%' },
    MARGEN_RENTABILIDAD: { id: 'MARGEN_RENTABILIDAD', label: 'Margen de Utilidad', color: 'emerald', unit: '%' },
    TIEMPO_CONSUMIDO: { id: 'TIEMPO_CONSUMIDO', label: 'Tiempo Transcurrido', color: 'blue', unit: '%' },
};

function GaugeChart({ value, label, color = "indigo", min = 0, max = 100, unit = "%" }) {
    const radius = 80;
    const strokeWidth = 12;
    const normalizedValue = Math.min(max, Math.max(min, value));
    const percentage = ((normalizedValue - min) / (max - min)) * 100;

    // SVG Path calculation for the gauge (semi-circle)
    const circumference = Math.PI * radius;

    // For deviation gauges (min < 0), we want to fill from the center (50%)
    let offset = circumference - (percentage / 100) * circumference;
    let strokeDasharray = `${circumference} ${circumference}`;
    let strokeDashoffset = offset;
    let rotation = -180;

    if (min < 0) {
        // If it's a center-zero gauge, we manipulate the offset/array to grow from middle
        const center = 50;
        if (percentage >= center) {
            // Fill right: start at 50%, length is percentage - 50
            const fillPerc = percentage - center;
            strokeDashoffset = circumference * 0.5; // Start at middle
            strokeDasharray = `${(fillPerc / 100) * circumference} ${circumference}`;
            // We need to rotate the whole thing so the "start" of the dash is at the top
            // Actually, easier to just use a second circle or adjust the rotation.
            // Simplified: just use the standard fill but emphasize the center.
        }
    }

    const colors = {
        indigo: "stroke-indigo-600",
        emerald: "stroke-emerald-500",
        rose: "stroke-rose-500",
        amber: "stroke-amber-500",
        blue: "stroke-blue-500",
        slate: "stroke-slate-400"
    };

    return (
        <div className="flex flex-col items-center group">
            <div className="relative w-48 h-24 overflow-hidden mb-2">
                <svg className="w-48 h-48 -rotate-180 transform origin-center" style={{ marginTop: '0px' }}>
                    {/* Background Track */}
                    <circle
                        cx="96" cy="96" r={radius}
                        fill="none"
                        className="stroke-slate-100"
                        strokeWidth={strokeWidth}
                        strokeDasharray={`${circumference} ${circumference}`}
                        style={{ strokeDashoffset: 0 }}
                    />
                    {/* Middle Marker for Deviation */}
                    {min < 0 && (
                        <line x1="96" y1="16" x2="96" y2="4" stroke="#e2e8f0" strokeWidth="2" />
                    )}
                    {/* Value Track */}
                    <circle
                        cx="96" cy="96" r={radius}
                        fill="none"
                        className={`${colors[color] || colors.indigo} transition-all duration-[1500ms] cubic-bezier(0.34, 1.56, 0.64, 1)`}
                        strokeWidth={strokeWidth}
                        strokeDasharray={`${circumference} ${circumference}`}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                    />
                </svg>
                {/* Center Content */}
                <div className="absolute inset-x-0 bottom-0 flex flex-col items-center justify-end pb-1">
                    <span className="text-3xl font-black text-slate-800 tracking-tighter leading-none">
                        {value > -0.5 && value < 0 && Math.round(value) === 0 ? '0' : Math.round(value)}{unit}
                    </span>
                </div>
            </div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 group-hover:text-slate-600 transition-colors text-center max-w-[150px]">{label}</p>
        </div>
    );
}

function ProgressBar({ progress, label, color = "blue" }) {
    const safeProgress = Math.min(100, Math.max(0, parseInt(progress) || 0));
    const colors = {
        blue: "bg-gradient-to-r from-[#6BB6FF] to-[#4A90E2]",
        emerald: "bg-gradient-to-r from-emerald-400 to-emerald-500",
        amber: "bg-gradient-to-r from-amber-400 to-amber-500",
        rose: "bg-gradient-to-r from-rose-400 to-rose-500",
        cyan: "bg-gradient-to-r from-[#00D4FF] to-[#1DE9B6]"
    };
    return (
        <div className="w-full">
            <div className="flex justify-between text-[10px] font-bold uppercase mb-2 tracking-widest opacity-40">
                <span>{label}</span>
                <span>{safeProgress}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden shadow-inner">
                <div className={`h-full ${colors[color] || colors.blue} transition-all duration-1000 cubic-bezier(0.4, 0, 0.2, 1) shadow-lg`} style={{ width: `${safeProgress}%` }}></div>
            </div>
        </div>
    );
}

// ========== MDO BUDGET MODALS ==========

function MDOBudgetModeModal({ onClose, onSelectMode, project }) {
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
            <div className="bg-white rounded-[3rem] p-12 max-w-3xl w-full shadow-2xl border border-slate-100 animate-in fade-in zoom-in duration-300">
                <div className="flex justify-between items-center mb-10">
                    <div>
                        <h2 className="text-3xl font-black uppercase tracking-tighter text-slate-800 leading-none">Configurar Presupuesto</h2>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-3">Selecciona la modalidad de carga para este proyecto</p>
                    </div>
                    <button onClick={onClose} className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-all">
                        <X size={24} />
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <button
                        onClick={() => onSelectMode('GLOBAL')}
                        className="p-10 border-2 border-slate-100 rounded-[2.5rem] hover:border-indigo-500 hover:bg-indigo-50/30 transition-all group text-left hover:shadow-xl hover:shadow-indigo-100 relative overflow-hidden"
                    >
                        <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-all transform group-hover:rotate-6">
                            <DollarSign size={32} className="text-indigo-600" />
                        </div>
                        <h3 className="font-black text-xl text-indigo-900 mb-3">Presupuesto Global</h3>
                        <p className="text-xs text-slate-500 leading-relaxed font-medium">
                            Define montos totales únicos para Materiales y Mano de Obra aplicables a todo el proyecto de forma simplificada.
                        </p>
                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all transform translate-x-4 group-hover:translate-x-0">
                            <ChevronRight size={20} className="text-indigo-600" />
                        </div>
                    </button>

                    <button
                        onClick={() => onSelectMode('POR_FASE')}
                        className="p-10 border-2 border-slate-100 rounded-[2.5rem] hover:border-emerald-500 hover:bg-emerald-50/30 transition-all group text-left hover:shadow-xl hover:shadow-emerald-100 relative overflow-hidden"
                    >
                        <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-emerald-600 group-hover:text-white transition-all transform group-hover:-rotate-6">
                            <Layers size={32} className="text-emerald-600" />
                        </div>
                        <h3 className="font-black text-xl text-emerald-900 mb-3">Por Fases / Rubros</h3>
                        <p className="text-xs text-slate-500 leading-relaxed font-medium">
                            Planificación detallada asignando recursos a cada etapa del cronograma. Permite un control exhaustivo de desvíos.
                        </p>
                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all transform translate-x-4 group-hover:translate-x-0">
                            <ChevronRight size={20} className="text-emerald-600" />
                        </div>
                    </button>
                </div>

                <div className="mt-10 p-6 bg-amber-50/50 border border-amber-100 rounded-[1.5rem] flex items-start gap-4">
                    <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
                        <AlertTriangle size={20} className="text-amber-600" />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest mb-1">Nota Importante</p>
                        <p className="text-xs text-amber-700/80 leading-relaxed font-medium">
                            El modo por fases es la opción recomendada para proyectos complejos ya que permite monitorear la certificación teórica versus la real.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function MDOBudgetGlobalModal({ onClose, onSave, project }) {
    const [amountMdo, setAmountMdo] = useState(project?.budget_mdo_global || 0);
    const [amountMaterials, setAmountMaterials] = useState(project?.budget_materials_global || 0);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({ amount_mdo: amountMdo, amount_materials: amountMaterials });
    };

    const cur = project?.base_currency || 'ARS';

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
            <div className="bg-white rounded-[3rem] p-12 max-w-xl w-full shadow-2xl border border-slate-100 animate-in fade-in zoom-in duration-300">
                <div className="flex justify-between items-center mb-10">
                    <div>
                        <h2 className="text-3xl font-black uppercase tracking-tighter text-slate-800 leading-none">Presupuesto Global</h2>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-3">Configuración de montos totales</p>
                    </div>
                    <button onClick={onClose} className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-all">
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="space-y-6">
                        <div className="p-8 bg-indigo-50/50 rounded-[2rem] border border-indigo-100 group transition-all hover:bg-indigo-50 hover:shadow-lg">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                                    <Hammer size={20} />
                                </div>
                                <label className="text-xs font-black uppercase text-indigo-900 tracking-widest">Mano de Obra (MDO)</label>
                            </div>
                            <div className="relative">
                                <span className="absolute left-6 top-1/2 -translate-y-1/2 text-indigo-400 font-black text-xl">{cur}</span>
                                <input
                                    type="number"
                                    value={amountMdo}
                                    onChange={(e) => setAmountMdo(parseFloat(e.target.value) || 0)}
                                    className="w-full pl-20 pr-8 py-5 bg-white border-2 border-transparent focus:border-indigo-500 rounded-2xl font-black text-2xl text-indigo-900 outline-none transition-all"
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    required
                                />
                            </div>
                        </div>

                        <div className="p-8 bg-emerald-50/50 rounded-[2rem] border border-emerald-100 group transition-all hover:bg-emerald-50 hover:shadow-lg">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                                    <Package size={20} />
                                </div>
                                <label className="text-xs font-black uppercase text-emerald-900 tracking-widest">Materiales / Insumos</label>
                            </div>
                            <div className="relative">
                                <span className="absolute left-6 top-1/2 -translate-y-1/2 text-emerald-400 font-black text-xl">{cur}</span>
                                <input
                                    type="number"
                                    value={amountMaterials}
                                    onChange={(e) => setAmountMaterials(parseFloat(e.target.value) || 0)}
                                    className="w-full pl-20 pr-8 py-5 bg-white border-2 border-transparent focus:border-emerald-500 rounded-2xl font-black text-2xl text-emerald-900 outline-none transition-all"
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-6 items-center">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-8 py-6 bg-slate-100 text-slate-500 rounded-[1.5rem] font-black uppercase text-[10px] tracking-widest hover:bg-slate-200 transition-all"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="flex-[2] px-8 py-6 bg-slate-900 text-white rounded-[1.5rem] font-black uppercase text-[10px] tracking-widest hover:bg-indigo-600 transition-all shadow-xl shadow-slate-200 hover:shadow-indigo-200 transform hover:-translate-y-1"
                        >
                            Guardar Presupuesto Global
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function MDOBudgetPhaseModal({ onClose, onSave, project, tasks, taskBudgets }) {
    const phases = tasks.filter(t => t.event_id === project.id && !t.parent_id).sort((a, b) => {
        const dateA = a.start_date || '9999-99-99';
        const dateB = b.start_date || '9999-99-99';
        if (dateA !== dateB) return new Date(dateA) - new Date(dateB);
        return sortPhasesNumerically(a.title, b.title);
    });
    const [localBudgets, setLocalBudgets] = useState(() => {
        const initial = {};
        phases.forEach(p => {
            const existing = taskBudgets?.find(tb => tb.task_id === p.id);
            initial[p.id] = {
                mdo: existing?.budget_mdo || 0,
                mat: existing?.budget_materials || 0
            };
        });
        return initial;
    });

    // Calcular días laborales de cada fase para la distribución teórica
    const totalDays = phases.reduce((sum, p) => {
        const d1 = new Date(p.start_date);
        const d2 = new Date(p.end_date);
        return sum + Math.max(1, getWorkDays(d1, d2));
    }, 0);

    const handleSubmit = (e) => {
        e.preventDefault();
        const results = Object.entries(localBudgets)
            .filter(([_, vals]) => vals.mdo > 0 || vals.mat > 0)
            .map(([taskId, vals]) => {
                const phase = phases.find(p => p.id === taskId);
                return {
                    task_id: taskId,
                    title: phase?.title,
                    budget_mdo: vals.mdo,
                    budget_materials: vals.mat
                };
            });

        if (results.length === 0) {
            alert('Debes asignar presupuesto a al menos una fase');
            return;
        }

        onSave(results);
    };

    const cur = project?.base_currency || 'ARS';

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4 backdrop-blur-md">
            <div className="bg-[#F8FAFC] rounded-[4rem] p-12 max-w-6xl w-full shadow-[0_35px_60px_-15px_rgba(0,0,0,0.3)] max-h-[95vh] overflow-hidden flex flex-col border border-white">
                <div className="flex justify-between items-center mb-10">
                    <div>
                        <h2 className="text-4xl font-black uppercase tracking-tighter text-slate-800 leading-none">Presupuesto por Fases</h2>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.3em] mt-3">Distribución detallada de recursos por etapa</p>
                    </div>
                    <button onClick={onClose} className="w-14 h-14 bg-white rounded-3xl flex items-center justify-center text-slate-300 hover:text-rose-500 hover:shadow-lg transition-all">
                        <X size={28} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-2 space-y-6 custom-scrollbar pb-8">
                    <div className="grid grid-cols-12 gap-6 px-10 mb-4 text-[10px] font-black uppercase text-slate-400 tracking-widest">
                        <div className="col-span-4">Fase / Cronograma</div>
                        <div className="col-span-3 text-center">Insumos y Materiales</div>
                        <div className="col-span-3 text-center">Mano de Obra (MDO)</div>
                        <div className="col-span-2 text-right">Teórico %</div>
                    </div>

                    {phases.length === 0 ? (
                        <div className="p-20 bg-white rounded-[3rem] text-center border-2 border-dashed border-slate-100">
                            <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6 text-slate-200">
                                <Package size={40} />
                            </div>
                            <p className="text-slate-400 font-bold uppercase text-xs tracking-widest">
                                No se encontraron fases principales.<br />Crea fases en el cronograma primero.
                            </p>
                        </div>
                    ) : (
                        phases.map(phase => {
                            const d1 = new Date(phase.start_date);
                            const d2 = new Date(phase.end_date);
                            const wd = Math.max(1, getWorkDays(d1, d2));
                            const teorico = totalDays > 0 ? ((wd / totalDays) * 100).toFixed(1) : 0;

                            return (
                                <div key={phase.id} className="grid grid-cols-12 gap-6 items-center p-8 bg-white rounded-[2.5rem] border border-slate-100 hover:border-indigo-200 transition-all hover:shadow-xl hover:shadow-slate-100 group">
                                    <div className="col-span-4">
                                        <div className="flex items-center gap-4 mb-2">
                                            <div className="w-8 h-8 rounded-xl bg-slate-900 text-white flex items-center justify-center text-[10px] font-black shadow-lg transform group-hover:scale-110 transition-transform">
                                                {phase.title[0]}
                                            </div>
                                            <h3 className="font-black text-sm uppercase text-slate-700 tracking-tight leading-tight">{phase.title}</h3>
                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <Calendar size={12} className="text-slate-300" />
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                                                {d1.toLocaleDateString('es-AR')} - {d2.toLocaleDateString('es-AR')} ({wd} días)
                                            </p>
                                        </div>
                                    </div>

                                    <div className="col-span-3">
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-black text-xs">{cur}</span>
                                            <input
                                                type="number"
                                                value={localBudgets[phase.id]?.mat || ''}
                                                onChange={(e) => setLocalBudgets({ ...localBudgets, [phase.id]: { ...localBudgets[phase.id], mat: parseFloat(e.target.value) || 0 } })}
                                                className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-transparent focus:border-emerald-500 focus:bg-white rounded-2xl font-black text-emerald-700 outline-none transition-all"
                                                placeholder="0.00"
                                                step="0.01"
                                                min="0"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-span-3">
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-black text-xs">{cur}</span>
                                            <input
                                                type="number"
                                                value={localBudgets[phase.id]?.mdo || ''}
                                                onChange={(e) => setLocalBudgets({ ...localBudgets, [phase.id]: { ...localBudgets[phase.id], mdo: parseFloat(e.target.value) || 0 } })}
                                                className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-2xl font-black text-indigo-700 outline-none transition-all"
                                                placeholder="0.00"
                                                step="0.01"
                                                min="0"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-span-2 text-right">
                                        <div className="inline-flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-xl border border-slate-100">
                                            <span className="text-[11px] font-black text-slate-800">{teorico}%</span>
                                            <Clock size={12} className="text-indigo-400" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {phases.length > 0 && (
                    <div className="mt-10 pt-10 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-8">
                        <div className="flex flex-wrap gap-10 justify-center md:justify-start">
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Materiales</p>
                                <p className="text-2xl font-black text-emerald-600 tracking-tighter">{cur} {Object.values(localBudgets).reduce((sum, v) => sum + Number(v.mat || 0), 0).toLocaleString()}</p>
                            </div>
                            <div className="w-px h-10 bg-slate-200"></div>
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Mano de Obra</p>
                                <p className="text-2xl font-black text-indigo-600 tracking-tighter">{cur} {Object.values(localBudgets).reduce((sum, v) => sum + Number(v.mdo || 0), 0).toLocaleString()}</p>
                            </div>
                        </div>

                        <div className="flex gap-4 shrink-0">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-10 py-5 bg-slate-100 text-slate-500 rounded-3xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-200 transition-all"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-14 py-5 bg-indigo-600 text-white rounded-3xl font-black uppercase text-[10px] tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 transform hover:-translate-y-1 active:scale-95"
                            >
                                Guardar Todo el Presupuesto
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


// ========== SINGLE BUDGET ENTRY MODAL ==========
function BudgetSingleEntryModal({ onClose, onSave, data, cur }) {
    const [amountMat, setAmountMat] = useState(data.amount || 0);
    const [amountMdo, setAmountMdo] = useState(data.budget_mdo || 0);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({
            category_ids: [data.id],
            category_name: data.category_name,
            amount: amountMat,
            amount_mdo: amountMdo
        });
        onClose();
    };

    return (
        <Modal title={`Presupuesto: ${data.category_name}`} onClose={onClose}>
            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-2 gap-6 bg-slate-900 p-10 rounded-[3rem] text-white overflow-hidden relative">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-600/10 rounded-full -mr-16 -mt-16"></div>
                    <div className="space-y-3 relative z-10">
                        <label className="text-[10px] font-black text-emerald-300 uppercase tracking-widest ml-1">Materiales</label>
                        <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500 font-black text-lg">{cur}</span>
                            <input
                                type="number"
                                step="0.01"
                                value={amountMat}
                                onChange={e => setAmountMat(parseFloat(e.target.value) || 0)}
                                className="w-full pl-12 pr-4 py-5 bg-white/10 border-2 border-white/10 focus:border-emerald-500 focus:bg-white focus:text-slate-900 rounded-2xl font-black text-2xl outline-none transition-all"
                                placeholder="0.00"
                            />
                        </div>
                    </div>
                    <div className="space-y-3 relative z-10">
                        <label className="text-[10px] font-black text-indigo-300 uppercase tracking-widest ml-1">Mano de Obra</label>
                        <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400 font-black text-lg">{cur}</span>
                            <input
                                type="number"
                                step="0.01"
                                value={amountMdo}
                                onChange={e => setAmountMdo(parseFloat(e.target.value) || 0)}
                                className="w-full pl-12 pr-4 py-5 bg-white/10 border-2 border-white/10 focus:border-indigo-500 focus:bg-white focus:text-slate-900 rounded-2xl font-black text-2xl outline-none transition-all"
                                placeholder="0.00"
                            />
                        </div>
                    </div>
                </div>
                <button type="submit" className="btn-primary w-full py-6 rounded-3xl text-xs uppercase font-black shadow-xl shadow-indigo-100">Actualizar Presupuesto</button>
            </form>
        </Modal>
    );
}

// ========== SAVE TEMPLATE MODAL ==========
function SaveTemplateModal({ onClose, onConfirm, project }) {
    const [name, setName] = useState((project?.name || '') + ' - Plantilla');
    const [type, setType] = useState(project?.type || 'CONSTRUCCION');
    const [details, setDetails] = useState('');

    return (
        <Modal title="Guardar como Plantilla" onClose={onClose}>
            <form onSubmit={(e) => {
                e.preventDefault();
                onConfirm({ name, type, details });
            }} className="space-y-6">
                <div className="bg-slate-50 p-8 rounded-[2rem] space-y-4">
                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nombre de la Plantilla</label>
                        <input required value={name} onChange={e => setName(e.target.value)} className="input-base" placeholder="Ej: Edificio Tipo A" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Tipo de Proyecto</label>
                        <div className="relative">
                            <input list="projectTypes" required value={type} onChange={e => setType(e.target.value)} className="input-base" placeholder="Seleccionar o escribir..." />
                            <datalist id="projectTypes">
                                <option value="CONSTRUCCION" />
                                <option value="REMODELACION" />
                                <option value="DISENO_INTERIOR" />
                                <option value="EVENTO" />
                            </datalist>
                        </div>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Detalles / Descripción</label>
                        <textarea value={details} onChange={e => setDetails(e.target.value)} className="input-base min-h-[100px]" placeholder="Descripción opcional de la plantilla..." />
                    </div>
                </div>
                <div className="flex justify-end gap-4">
                    <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl text-slate-500 font-bold hover:bg-slate-100 transition-colors uppercase text-[10px] tracking-widest">Cancelar</button>
                    <button type="submit" className="btn-primary py-3 px-8 text-[10px] uppercase font-black">Guardar Plantilla</button>
                </div>
            </form>
        </Modal>
    );
}

// ========== INTERACTIVE TEMPLATE IMPORT MODAL ==========
function TemplateImportModal({ onClose, onConfirm, templates, event, t }) {
    const [selectedType, setSelectedType] = useState(event.type || 'CONSTRUCCION_CASA');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [localTasks, setLocalTasks] = useState([]);
    const [expandedPhases, setExpandedPhases] = useState({});

    useEffect(() => {
        const sel = templates[selectedType] || templates['CONSTRUCCION_CASA'];
        const raw = (sel && sel.tasks) ? sel.tasks : (Array.isArray(sel) ? sel : []);

        const processed = [];
        raw.forEach((phase, pIdx) => {
            const phaseId = `temp_phase_${pIdx}`;
            processed.push({
                id: phaseId,
                title: phase.title,
                days: phase.days,
                isParent: true,
                selected: true,
                is_critical: phase.is_critical || false
            });
            (phase.subs || []).forEach((sub, sIdx) => {
                processed.push({
                    id: `temp_sub_${pIdx}_${sIdx}`,
                    parent_id: phaseId,
                    title: sub.title,
                    days: sub.days,
                    is_milestone: sub.milestone || 0,
                    selected: true,
                    is_critical: sub.is_critical || false
                });
            });
        });
        setLocalTasks(processed);
        const expansions = {};
        raw.forEach((p, i) => expansions[`temp_phase_${i}`] = true);
        setExpandedPhases(expansions);
    }, [selectedType, templates]);

    const handleConfirm = () => {
        const filtered = localTasks.filter(t => t.selected);
        let accumDays = 0;
        const finalTasks = filtered.map(task => {
            const start = new Date(startDate);
            start.setDate(start.getDate() + accumDays);

            const end = new Date(start);
            if (!task.is_milestone) {
                end.setDate(end.getDate() + (parseInt(task.days) || 1));
                if (!task.isParent) {
                    accumDays += (parseInt(task.days) || 1);
                }
            }
            if (task.isParent) {
                const phaseEnd = new Date(start);
                phaseEnd.setDate(phaseEnd.getDate() + (parseInt(task.days) || 5));
                return { ...task, start_date: start.toISOString().split('T')[0], end_date: phaseEnd.toISOString().split('T')[0] };
            }
            return {
                ...task,
                start_date: start.toISOString().split('T')[0],
                end_date: end.toISOString().split('T')[0]
            };
        });
        onConfirm(finalTasks);
    };

    const updateTask = (id, field, value) => {
        setLocalTasks(prev => prev.map(tk => tk.id === id ? { ...tk, [field]: value } : tk));
    };

    const togglePhase = (phaseId, selected) => {
        setLocalTasks(prev => prev.map(tk => (tk.id === phaseId || tk.parent_id === phaseId) ? { ...tk, selected } : tk));
    };

    const [newTask, setNewTask] = useState({ title: '', days: 5 });

    const handleAddTask = () => {
        if (!newTask.title) return;
        const phaseId = `manual_phase_${Date.now()}`;
        const task = {
            id: phaseId,
            title: newTask.title,
            days: newTask.days,
            isParent: true,
            selected: true,
            is_critical: false
        };
        setLocalTasks(prev => [...prev, task]);
        setNewTask({ title: '', days: 5 });
        setExpandedPhases(prev => ({ ...prev, [phaseId]: true }));
    };

    return (
        <Modal title="Importar Plantilla de Proyecto" onClose={onClose}>
            <div className="space-y-8 max-h-[80vh] overflow-y-auto pr-4 scrollbar-thin">
                <div className="flex flex-wrap gap-6 bg-slate-900 p-8 rounded-[3rem] text-white">
                    <div className="flex-1 min-w-[250px]">
                        <label className="block text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-2">Modelo de Proyecto</label>
                        <select
                            value={selectedType}
                            onChange={e => setSelectedType(e.target.value)}
                            className="w-full bg-white/10 border-2 border-white/10 p-4 rounded-2xl font-bold text-white focus:bg-indigo-600 transition-all outline-none"
                        >
                            {Object.keys(templates).map(k => {
                                const t = templates[k];
                                const label = t.name || k.replace(/_/g, ' ');
                                const group = t.type ? ` (${t.type})` : '';
                                return <option key={k} value={k} className="text-slate-900">{label}{group}</option>;
                            })}
                        </select>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-2">Fecha de Inicio</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            className="w-full bg-white/10 border-2 border-white/10 p-4 rounded-2xl font-bold text-white focus:bg-indigo-600 transition-all outline-none"
                        />
                    </div>
                    <div className="flex-1 min-w-[300px]">
                        <div className="bg-white/5 p-4 rounded-2xl border border-white/10 space-y-3">
                            <p className="text-[9px] font-black uppercase text-indigo-300">¿Agregar Tarea Manual?</p>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Nombre de tarea..."
                                    className="flex-1 min-w-0 bg-white/10 border-none p-3 rounded-xl text-[10px] font-bold text-white outline-none focus:bg-white/20"
                                    value={newTask.title}
                                    onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                                />
                                <input
                                    type="number"
                                    className="w-16 bg-white/10 border-none p-3 rounded-xl text-[10px] font-bold text-white outline-none focus:bg-white/20"
                                    value={newTask.days}
                                    onChange={e => setNewTask({ ...newTask, days: parseInt(e.target.value) || 0 })}
                                />
                                <button onClick={handleAddTask} className="bg-indigo-600 p-3 rounded-xl hover:bg-indigo-500 transition-all shrink-0">
                                    <Plus size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest ml-4">Previsualización de Tareas</p>
                    {localTasks.filter(t => t.isParent).map(phase => (
                        <div key={phase.id} className="border-2 border-slate-50 rounded-[2.5rem] overflow-hidden bg-white shadow-sm hover:shadow-md transition-all">
                            <div className="p-6 flex items-center gap-5 bg-slate-50/50">
                                <input
                                    type="checkbox"
                                    checked={phase.selected}
                                    onChange={e => togglePhase(phase.id, e.target.checked)}
                                    className="w-7 h-7 rounded-xl border-2 border-slate-200 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                />
                                <div className="flex-1 cursor-pointer" onClick={() => setExpandedPhases(prev => ({ ...prev, [phase.id]: !prev[phase.id] }))}>
                                    <h4 className="font-black text-slate-800 uppercase text-sm tracking-tight">{phase.title}</h4>
                                    <div className="flex gap-4 mt-1">
                                        <span className="text-[9px] text-slate-400 font-bold uppercase flex items-center gap-1"><Clock size={10} /> {phase.days} días</span>
                                        {phase.is_critical && <span className="text-[9px] text-rose-500 font-bold uppercase flex items-center gap-1"><Zap size={10} fill="currentColor" /> Ruta Crítica</span>}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => updateTask(phase.id, 'is_critical', !phase.is_critical)}
                                        className={`p-3 rounded-2xl transition-all ${phase.is_critical ? 'bg-rose-500 text-white border-rose-600' : 'bg-white text-slate-300 border-slate-100'} border shadow-sm group`}
                                        title="Marcar como Camino Crítico"
                                    >
                                        <Zap size={16} fill={phase.is_critical ? "currentColor" : "none"} className="group-hover:scale-110 transition-transform" />
                                    </button>
                                    <div className="w-10 h-10 rounded-2xl bg-white border border-slate-100 flex items-center justify-center text-slate-300">
                                        <ChevronDown size={20} className={`transition-transform duration-500 ${expandedPhases[phase.id] ? 'rotate-180' : ''}`} />
                                    </div>
                                </div>
                            </div>

                            {expandedPhases[phase.id] && (
                                <div className="p-6 bg-white space-y-4 border-t border-slate-50 animate-in">
                                    {localTasks.filter(t => t.parent_id === phase.id).map(sub => (
                                        <div key={sub.id} className="grid grid-cols-[auto_1fr_auto] items-center gap-5 p-4 bg-slate-50/30 hover:bg-white border border-transparent hover:border-slate-100 rounded-3xl group transition-all">
                                            <input
                                                type="checkbox"
                                                checked={sub.selected}
                                                onChange={e => updateTask(sub.id, 'selected', e.target.checked)}
                                                className="w-6 h-6 rounded-lg border-2 border-slate-200 text-indigo-500 cursor-pointer shrink-0"
                                            />
                                            <div className="min-w-0">
                                                <input
                                                    value={sub.title}
                                                    onChange={e => updateTask(sub.id, 'title', e.target.value)}
                                                    className="bg-transparent border-none p-0 font-black text-slate-600 text-[11px] w-full focus:ring-0 uppercase truncate"
                                                />
                                            </div>
                                            <div className="flex items-center gap-4 shrink-0">
                                                <div className="flex flex-col items-end">
                                                    <span className="text-[8px] font-black text-slate-300 uppercase tracking-widest mr-2">Días</span>
                                                    <input
                                                        type="number"
                                                        value={sub.days}
                                                        onChange={e => updateTask(sub.id, 'days', parseInt(e.target.value) || 0)}
                                                        className="w-16 bg-white border-2 border-slate-100 rounded-xl text-center font-black text-xs py-2 focus:border-indigo-500 outline-none transition-all"
                                                    />
                                                </div>
                                                <button
                                                    onClick={() => updateTask(sub.id, 'is_critical', !sub.is_critical)}
                                                    className={`p-2.5 rounded-xl transition-all border ${sub.is_critical ? 'bg-rose-100 text-rose-600 border-rose-200' : 'bg-white text-slate-200 border-slate-50 group-hover:text-slate-300 group-hover:border-slate-100'}`}
                                                >
                                                    <Zap size={14} fill={sub.is_critical ? "currentColor" : "none"} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="flex gap-4 pt-6 sticky bottom-0 bg-white/90 backdrop-blur-md">
                    <button onClick={onClose} className="flex-1 py-6 rounded-[2rem] font-black uppercase tracking-[0.2em] text-sm text-slate-400 bg-slate-50 hover:bg-slate-100 transition-all">Cancelar</button>
                    <button onClick={handleConfirm} className="flex-[2] py-6 rounded-[2rem] font-black uppercase tracking-[0.2em] text-sm text-white bg-indigo-600 shadow-2xl shadow-indigo-200 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3">
                        <Check size={20} strokeWidth={3} /> Iniciar Importación
                    </button>
                </div>
            </div>
        </Modal>
    );
}

// ========== MDO BUDGET EXECUTION COMPONENT ==========

function MDOBudgetExecution({ event, expenses, tasks, taskBudgets, totalMdoBudget: passedMdoBudget, onEditExpense, onDeleteExpense, onEditBudget, onDeleteBudget }) {
    const { theoreticalProgress: theoreticalProg } = calculateProgressMetrics(tasks, event.event_date, event.end_date);
    const effectiveMode = event.mdo_budget_mode || (taskBudgets?.length > 0 ? 'POR_FASE' : (Number(event.budget_mdo_global) > 0 ? 'GLOBAL' : null));
    const totalMdoBudget = passedMdoBudget || (effectiveMode === 'GLOBAL' ? Number(event.budget_mdo_global) : taskBudgets?.reduce((a, b) => a + Number(b.budget_mdo), 0));
    // Unified calculation for theoretical MDO values to ensure consistency between rows and totals
    const phasesWithBudgetData = React.useMemo(() => {
        if (!taskBudgets) return [];
        return taskBudgets
            .filter(tb => Number(tb.budget_mdo) > 0)
            .map(tb => {
                const task = tasks.find(t => t.id === tb.task_id);
                const s = task?.start_date ? new Date(task.start_date) : null;
                const e = task?.end_date ? new Date(task.end_date) : null;
                let progFraction = 0;
                if (s && e && !isNaN(s.getTime()) && !isNaN(e.getTime())) {
                    const totalWD = getWorkDays(s, e);
                    const today = new Date();
                    const endForElapsed = today < e ? today : e;
                    const elapsedWD = s <= endForElapsed ? getWorkDays(s, endForElapsed) : 0;
                    progFraction = totalWD > 0 ? (elapsedWD / totalWD) : 0;
                }
                return {
                    ...tb,
                    task,
                    theoreticalProg: Math.min(1, progFraction), // Fraction 0-1
                    theoreticalVal: Number(tb.budget_mdo) * Math.min(1, progFraction)
                };
            })
            .sort((a, b) => {
                const dateA = a.task?.start_date || '9999-99-99';
                const dateB = b.task?.start_date || '9999-99-99';
                if (dateA !== dateB) return new Date(dateA) - new Date(dateB);
                return sortPhasesNumerically(a.task?.title, b.task?.title);
            });
    }, [taskBudgets, tasks]);

    const theoreticalMdoVal = React.useMemo(() => {
        if (effectiveMode === 'GLOBAL') {
            return (totalMdoBudget || 0) * (theoreticalProg / 100);
        } else {
            return phasesWithBudgetData.reduce((sum, p) => sum + p.theoreticalVal, 0);
        }
    }, [effectiveMode, totalMdoBudget, theoreticalProg, phasesWithBudgetData]);
    // Filtrar solo gastos de Mano de Obra
    const mdoExpenses = expenses.filter(e => e.expense_type === 'MANO_OBRA' && e.movement_type === 'EXPENSE').sort((a, b) => new Date(b.expense_date) - new Date(a.expense_date));
    const totalPaid = mdoExpenses.reduce((sum, e) => sum + Number(e.amount_converted || e.amount_original || 0), 0);
    const cur = event.base_currency || 'ARS';
    const [expandedPhases, setExpandedPhases] = useState({});
    const [isCollapsed, setIsCollapsed] = useState(false);

    const renderHistory = (filterCategory = null) => {
        const filtered = filterCategory
            ? mdoExpenses.filter(ex => ex.category_name?.toUpperCase() === filterCategory.toUpperCase())
            : mdoExpenses;

        if (filtered.length === 0) return (
            <div className="px-12 py-8 text-center text-[10px] font-bold text-slate-300 uppercase tracking-widest italic bg-slate-50/30 rounded-2xl mb-4">No hay pagos registrados</div>
        );

        return (
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm mb-4 animate-in">
                <table className="w-full text-left">
                    <thead className="bg-slate-50/50 text-[8px] font-black uppercase text-slate-400 tracking-widest">
                        <tr>
                            <th className="px-6 py-3">Fecha</th>
                            <th className="px-6 py-3">Descripción</th>
                            {!filterCategory && <th className="px-6 py-3">Fase</th>}
                            <th className="px-6 py-3 text-right">Monto</th>
                            <th className="px-6 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 text-[10px]">
                        {filtered.map(ex => (
                            <tr key={ex.id} className="hover:bg-slate-50 transition-colors group">
                                <td className="px-6 py-3 font-bold text-slate-500">{ex.expense_date?.split(' ')[0]}</td>
                                <td className="px-6 py-3 font-black text-slate-700 uppercase">{ex.description}</td>
                                {!filterCategory && (
                                    <td className="px-6 py-3">
                                        <span className="bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-black text-[8px] uppercase border border-indigo-100 italic">
                                            {ex.category_name}
                                        </span>
                                    </td>
                                )}
                                <td className="px-6 py-3 text-right font-black text-slate-900">{cur} {Number(ex.amount_original).toLocaleString()}</td>
                                <td className="px-6 py-3 text-right">
                                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => onEditExpense(ex)} className="p-1.5 text-slate-300 hover:text-indigo-600 transition-all"><Edit3 size={12} /></button>
                                        <button onClick={() => { if (confirm("¿Eliminar pago?")) onDeleteExpense(ex.id) }} className="p-1.5 text-slate-300 hover:text-rose-600 transition-all"><Trash2 size={12} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    if (event.mdo_budget_mode === 'GLOBAL') {
        const budgeted = Number(event.budget_mdo_global || 0);
        const balance = budgeted - totalPaid;
        const percentage = budgeted > 0 ? Math.round((totalPaid / budgeted) * 100) : 0;

        return (
            <div className="space-y-12">
                {/* Tabla de Fases (Modo Global tiene una única fila) */}
                <div className="bg-white rounded-[3.5rem] border border-slate-100 overflow-hidden shadow-sm">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">
                            <tr>
                                <th className="px-12 py-8">Concepto</th>
                                <th className="px-12 py-8 text-right">Presupuestado</th>
                                <th className="px-12 py-8 text-right">Ejecutado (Gastos)</th>
                                <th className="px-12 py-8 text-right text-cyan-500">Teórico Esperado</th>
                                <th className="px-12 py-8 text-right">Saldo Real</th>
                                <th className="px-12 py-8 text-center text-indigo-500">Uso %</th>
                                <th className="px-12 py-8"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            <tr className="hover:bg-slate-50/50 transition-all group">
                                <td className="px-12 py-8">
                                    <p className="font-black uppercase text-sm text-slate-800 tracking-tight">Presupuesto Global de Mano de Obra</p>
                                </td>
                                <td className="px-12 py-8 text-right font-black text-slate-900">{cur} {budgeted.toLocaleString()}</td>
                                <td className="px-12 py-8 text-right font-black text-emerald-600">{cur} {totalPaid.toLocaleString()}</td>
                                <td className="px-12 py-8 text-right font-black text-cyan-600">{cur} {theoreticalMdoVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                <td className={`px-12 py-8 text-right font-black ${balance >= 0 ? 'text-slate-400' : 'text-rose-500'}`}>{cur} {balance.toLocaleString()}</td>
                                <td className="px-12 py-8 w-[150px]">
                                    <div className="flex flex-col gap-1">
                                        <ProgressBar progress={percentage} label="" color={percentage > 100 ? "rose" : "emerald"} />
                                        <p className="text-[8px] font-black text-center text-slate-400">REAL: {percentage}% vs TEÓRICO: {theoreticalProg}%</p>
                                    </div>
                                </td>
                                <td className="px-8 text-right">
                                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => onEditBudget('GLOBAL', { amount: budgeted })} className="p-3 bg-white shadow-sm border border-slate-100 rounded-xl text-slate-400 hover:text-indigo-600 transition-all"><Edit3 size={16} /></button>
                                        <button onClick={() => onDeleteBudget('GLOBAL')} className="p-3 bg-white shadow-sm border border-slate-100 rounded-xl text-slate-400 hover:text-rose-600 transition-all"><Trash2 size={16} /></button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot className="bg-slate-900 text-white font-black text-lg">
                            <tr>
                                <td className="px-12 py-10 uppercase tracking-widest text-[11px] text-indigo-400">Total General</td>
                                <td className="px-12 py-10 text-right">{cur} {budgeted.toLocaleString()}</td>
                                <td className="px-12 py-10 text-right text-emerald-400">{cur} {totalPaid.toLocaleString()}</td>
                                <td className="px-12 py-10 text-right text-cyan-400">{cur} {Math.round(theoreticalMdoVal).toLocaleString()}</td>
                                <td className={`px-12 py-10 text-right ${balance >= 0 ? 'text-slate-500' : 'text-rose-400'}`}>{cur} {balance.toLocaleString()}</td>
                                <td className="px-12 py-10 text-center text-indigo-400 font-extrabold">{percentage}%</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div className="pt-4">
                    <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-4 ml-6">Historial de Pagos</h4>
                    {renderHistory()}
                </div>
            </div>
        );
    } else if (event.mdo_budget_mode === 'POR_FASE') {
        const phasesWithBudget = (taskBudgets || []).filter(tb => Number(tb.budget_mdo) > 0);
        const totalBudgeted = phasesWithBudget.reduce((a, v) => a + Number(v.budget_mdo), 0);
        const balanceGlobal = totalBudgeted - totalPaid;
        const percGlobal = totalBudgeted > 0 ? Math.round((totalPaid / totalBudgeted) * 100) : 0;

        return (
            <div className="space-y-12">
                {/* Tabla de Fases */}
                <div className="bg-white rounded-[3.5rem] border border-slate-100 overflow-hidden shadow-sm">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">
                            <tr>
                                <th className="px-12 py-8">
                                    <div className="flex items-center justify-between">
                                        <span>Fase de Trabajo</span>
                                        <button
                                            onClick={() => setIsCollapsed(!isCollapsed)}
                                            className="p-2 hover:bg-slate-200 rounded-lg transition-colors text-slate-400"
                                            title={isCollapsed ? "Expandir" : "Colapsar"}
                                        >
                                            {isCollapsed ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
                                        </button>
                                    </div>
                                </th>
                                <th className="px-12 py-8 text-right">Presupuestado</th>
                                <th className="px-12 py-8 text-right">Ejecutado (Gastos)</th>
                                <th className="px-12 py-8 text-right text-cyan-500">Certificación Teórica</th>
                                <th className="px-12 py-8 text-right">Saldo Real</th>
                                <th className="px-12 py-8 text-center text-indigo-500">Estado Certif.</th>
                                <th className="px-12 py-8"></th>
                            </tr>
                        </thead>
                        {!isCollapsed && (
                            <tbody className="divide-y divide-slate-50">
                                {phasesWithBudgetData.map(tb => {
                                    const phaseName = tb.task?.title || 'Fase Desconocida';
                                    const paidInPhase = mdoExpenses
                                        .filter(ex => ex.category_name?.toUpperCase() === phaseName.toUpperCase())
                                        .reduce((a, v) => a + Number(v.amount_converted || v.amount_original || 0), 0);
                                    const balancePhase = Number(tb.budget_mdo) - paidInPhase;

                                    const taskTheoreticalProg = Math.round(tb.theoreticalProg * 100);
                                    const theoreticalValPhase = tb.theoreticalVal;

                                    const diffMdo = theoreticalValPhase - paidInPhase;
                                    const percPhase = Number(tb.budget_mdo) > 0 ? Math.round((paidInPhase / Number(tb.budget_mdo)) * 100) : 0;
                                    const isExpanded = expandedPhases[tb.id];

                                    return (
                                        <React.Fragment key={tb.id}>
                                            <tr className="hover:bg-slate-50/50 transition-all group cursor-pointer" onClick={() => setExpandedPhases(prev => ({ ...prev, [tb.id]: !prev[tb.id] }))}>
                                                <td className="px-12 py-8 flex items-center gap-4">
                                                    <div className={`transition-transform duration-500 ${isExpanded ? 'rotate-180' : ''}`}><ChevronDown size={14} className="text-slate-300" /></div>
                                                    <div>
                                                        <p className="font-black uppercase text-sm text-slate-800 tracking-tight">{phaseName}</p>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest italic">{tb.task?.start_date} - {tb.task?.end_date}</p>
                                                            <span className="text-[8px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500 font-bold">T: {taskTheoreticalProg}%</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-12 py-8 text-right font-black text-slate-900">{cur} {Number(tb.budget_mdo).toLocaleString()}</td>
                                                <td className="px-12 py-8 text-right font-black text-emerald-600">{cur} {paidInPhase.toLocaleString()}</td>
                                                <td className="px-12 py-8 text-right font-black text-cyan-600">
                                                    <div className="flex flex-col items-end">
                                                        <span>{cur} {Math.round(theoreticalValPhase).toLocaleString()}</span>
                                                        <span className="text-[8px] text-cyan-400 font-bold uppercase">PROG: {taskTheoreticalProg}%</span>
                                                    </div>
                                                </td>
                                                <td className={`px-12 py-8 text-right font-black ${balancePhase >= 0 ? 'text-slate-400' : 'text-rose-500'}`}>{cur} {balancePhase.toLocaleString()}</td>
                                                <td className="px-12 py-8 w-[150px]">
                                                    <div className="flex flex-col gap-2">
                                                        <div className="flex justify-between text-[8px] font-black uppercase mb-1">
                                                            <span className="text-emerald-500">Real: {percPhase}%</span>
                                                            <span className="text-cyan-500">Teo: {taskTheoreticalProg}%</span>
                                                        </div>
                                                        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden relative">
                                                            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, percPhase)}%` }}></div>
                                                            <div className="absolute top-0 h-full w-1 bg-rose-500 shadow-[0_0_5px_rgba(244,63,94,0.5)]" style={{ left: `${Math.min(100, taskTheoreticalProg)}%` }}></div>
                                                        </div>
                                                        <p className={`text-[8px] font-black text-center uppercase tracking-tighter ${diffMdo >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                                            {diffMdo >= 0 ? `Sobrante: +${Math.round(diffMdo).toLocaleString()}` : `Exceso: ${Math.round(diffMdo).toLocaleString()}`}
                                                        </p>
                                                    </div>
                                                </td>
                                                <td className="px-8 text-right">
                                                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button onClick={(e) => { e.stopPropagation(); onEditBudget('POR_FASE', tb); }} className="p-3 bg-white shadow-sm border border-slate-100 rounded-xl text-slate-400 hover:text-indigo-600 transition-all"><Edit3 size={16} /></button>
                                                        <button onClick={(e) => { e.stopPropagation(); onDeleteBudget('POR_FASE', tb.task_id); }} className="p-3 bg-white shadow-sm border border-slate-100 rounded-xl text-slate-400 hover:text-rose-600 transition-all"><Trash2 size={16} /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan="6" className="bg-slate-50/30 px-20 py-6">
                                                        <h5 className="text-[9px] font-black uppercase text-indigo-400 tracking-widest mb-4">Detalle de Imputaciones en {phaseName}</h5>
                                                        {renderHistory(phaseName)}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        )}
                        <tfoot className="bg-slate-900 text-white font-black text-lg">
                            <tr>
                                <td className="px-12 py-10 uppercase tracking-widest text-[11px] text-indigo-400">Total General</td>
                                <td className="px-12 py-10 text-right">{cur} {totalBudgeted.toLocaleString()}</td>
                                <td className="px-12 py-10 text-right text-emerald-400">{cur} {totalPaid.toLocaleString()}</td>
                                <td className="px-12 py-10 text-right text-cyan-400">{cur} {Math.round(theoreticalMdoVal).toLocaleString()}</td>
                                <td className={`px-12 py-10 text-right ${balanceGlobal >= 0 ? 'text-slate-500' : 'text-rose-400'}`}>{cur} {balanceGlobal.toLocaleString()}</td>
                                <td className="px-12 py-10 text-center text-indigo-400 font-extrabold">{percGlobal}%</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div className="bg-indigo-900/5 p-8 rounded-[2.5rem] border border-indigo-100">
                    <h4 className="text-[10px] font-black uppercase text-indigo-400 tracking-[0.2em] mb-4 ml-6">Libro Completo de MDO</h4>
                    {renderHistory()}
                </div>
            </div>
        );
    }

    return null;
}


function HelpPanel({ currentUser, t, aiHelpChatHistory, setAiHelpChatHistory, isAiHelpLoading, setIsAiHelpLoading, view }) {
    const helpItems = [
        {
            id: 'DASHBOARD',
            title: 'Tablero Principal',
            description: 'Monitor central de operaciones. Proporciona una visión panorámica de toda la cartera de proyectos en tiempo real.',
            features: [
                'Indicadores de Salud: Seguimiento de avance físico vs. tiempo transcurrido.',
                'Filtros Temporales: Selección por año y mes para reportes específicos.',
                'Acceso Directo: Navegación instantánea a la gestión de cualquier proyecto activo.'
            ],
            example: {
                scenario: 'Necesitas saber qué proyectos están atrasados este mes.',
                action: 'Selecciona el mes actual en el filtro superior; los proyectos con barra de progreso roja indican un avance real menor al teórico.'
            },
            roles: ['SUPER_ADMIN', 'COMPANY_ADMIN', 'COMPANY_COLABORATOR']
        },
        {
            id: 'PROJECTS',
            title: 'Gestión de Proyectos',
            description: 'El núcleo operativo del sistema. Control total sobre el ciclo de vida del proyecto, desde la planificación hasta la entrega.',
            features: [
                'Diagrama de Gantt: Visualización interactiva de fases, tareas y dependencias.',
                'Asistente de IA: Generación automática de cronogramas basados en el tipo de obra.',
                'Hitos Críticos: Marcación de fechas inamovibles (ej: entrega de llaves o inspecciones).',
                'Documentación: Vinculación de archivos y planos directamente a cada tarea.'
            ],
            example: {
                scenario: 'Vas a iniciar una obra de vivienda desde cero y no tienes el cronograma.',
                action: 'Usa el botón "Asistente IA", describe el proyecto (ej: "Casa 120m2") y el sistema generará todas las fases desde cimientos hasta acabados.'
            },
            roles: ['SUPER_ADMIN', 'COMPANY_ADMIN', 'COMPANY_COLABORATOR']
        },
        {
            id: 'FINANCIAL',
            title: 'Análisis Financiero',
            description: 'Gestión económica integral. Permite el control de costos, ingresos y saldos pendientes con precisión matemática.',
            features: [
                'Flujo de Caja: Registro de cobros y pagos en múltiples monedas (ARS/USD).',
                'MDO (Mano de Obra): Carga detallada por personal y fase de obra.',
                'Ejecución Presupuestaria: Comparativa instantánea entre lo planeado y lo gastado.',
                'Conciliación: Identificación de deudas por cliente y por proyecto.'
            ],
            example: {
                scenario: 'Deseas verificar cuánto se ha pagado en mano de obra en la fase de cimientos.',
                action: 'En el panel financiero, filtra por el proyecto y verás el desglose de MDO comparado con el presupuesto asignado a esa fase.'
            },
            roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
        },
        {
            id: 'CLIENTS',
            title: 'Módulo de Clientes',
            description: 'CRM especializado para la industria. Centraliza la información de contacto y el historial de proyectos por cliente.',
            features: [
                'Contactos Múltiples: Gestión de dueños, arquitectos y representantes legales.',
                'Historial de Deuda: Vista unificada de saldos pendientes de todos los proyectos del cliente.',
                'Vinculación Directa: Creación de proyectos asociados automáticamente a un cliente.'
            ],
            example: {
                scenario: 'Un cliente llama para consultar su saldo total por dos obras diferentes.',
                action: 'Busca al cliente en el módulo; el sistema te mostrará el saldo acumulado de ARS y USD de todos sus proyectos activos.'
            },
            roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
        },
        {
            id: 'TEAM',
            title: 'Gestión de Equipo',
            description: 'Administración de recursos humanos y niveles de seguridad. Controla quién ve qué dentro de la organización.',
            features: [
                'Roles Globales: Definición de Administradores o Colaboradores de empresa.',
                'Permisos por Proyecto: Asignación específica (Gestor, Editor o Lector) para cada obra.',
                'Códigos de Invitación: Método rápido para sumar personal externo a proyectos específicos.'
            ],
            example: {
                scenario: 'Quieres que un capataz solo vea las tareas del Gantt pero no los precios de los materiales.',
                action: 'Regístralo como "Colaborador" y asígnalo al proyecto con el rol de "Lector" o "Editor" según necesite reportar avances.'
            },
            roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
        },
        {
            id: 'MASTER',
            title: 'Panel Maestro',
            description: 'Control de infraestructura y configuración global exclusiva para los dueños de la plataforma.',
            features: [
                'Gestión de Licencias: Control de vencimientos y límites de usuarios por empresa.',
                'Configuración IA: Gestión de llaves de API para el asistente de cronogramas.',
                'Branding: Personalización de logos y parámetros generales del portal.'
            ],
            example: {
                scenario: 'Una empresa cliente necesita aumentar su límite de 5 a 10 usuarios.',
                action: 'Desde Gestión Maestro, edita la licencia de la empresa y actualiza el campo "Máx Usuarios" al valor deseado.'
            },
            roles: ['SUPER_ADMIN']
        },
        {
            id: 'RESOURCES',
            title: 'Recursos y Personal',
            description: 'Gestión centralizada de recursos humanos, maquinaria y subcontratistas. Permite el control de asignaciones y costos operativos.',
            features: [
                'Control de Recursos: Alta y edición de personal propio o externo.',
                'Asignación Directa: Vinculación de recursos a proyectos específicos con roles definidos.',
                'Dashboard de Ocupación: Vista global de qué recurso está en qué obra.',
                'Gestión de Especialidades: Clasificación por roles (Ingeniero, Capataz, Oficial, etc.).'
            ],
            example: {
                scenario: 'Quieres asignar un nuevo ingeniero a un proyecto.',
                action: 'En el módulo "Control de Recursos", pulsa "Nuevo Recurso", completa los datos y luego usa el botón de "Asignación" para vincularlo a tu proyecto.'
            },
            roles: ['SUPER_ADMIN', 'COMPANY_ADMIN']
        }
    ];

    const filteredHelp = helpItems.filter(item => item.roles.includes(currentUser.role));

    return (
        <div className="space-y-12 animate-in pb-20">
            <div className="px-4">
                <div className="flex items-center gap-4 mb-2">
                    <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
                        <HelpCircle size={24} />
                    </div>
                    <div>
                        <h3 className="text-4xl font-black uppercase tracking-tighter">Centro de Ayuda</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Guía de funcionalidades y casos prácticos</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* AI Assistant Card - TOP */}
                <div className="lg:col-span-12">
                    <div className="bg-gradient-to-br from-indigo-600 to-violet-700 p-8 rounded-[3rem] text-white shadow-2xl relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32 group-hover:bg-white/20 transition-all duration-1000"></div>
                        <div className="relative flex flex-col md:flex-row gap-10 items-center">
                            <div className="md:w-1/3 space-y-4">
                                <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-3xl flex items-center justify-center shadow-xl border border-white/30 rotate-3">
                                    <Sparkles size={40} className="animate-pulse" />
                                </div>
                                <h4 className="text-3xl font-black uppercase tracking-tighter leading-tight">Asistente Virtual con IA</h4>
                                <p className="text-sm font-medium text-indigo-100/80 leading-relaxed">
                                    Realiza consultas sobre el uso del sistema, particularidades de cada módulo, o solicita ayuda específica.
                                </p>
                            </div>

                            <div className="md:w-2/3 w-full bg-white/10 backdrop-blur-md border border-white/20 rounded-[2.5rem] p-6 space-y-4">
                                <div className="h-[300px] overflow-y-auto custom-scrollbar flex flex-col gap-3 pr-2" id="ai-help-chat-container">
                                    {aiHelpChatHistory.length === 0 ? (
                                        <div className="h-full flex flex-col items-center justify-center text-center opacity-50 space-y-4">
                                            <MessageSquare size={48} />
                                            <p className="text-xs font-black uppercase tracking-widest">Inicia una conversación con la IA</p>
                                        </div>
                                    ) : (
                                        aiHelpChatHistory.map((msg, i) => (
                                            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2`}>
                                                <div className={`max-w-[90%] px-5 py-3 rounded-2xl text-sm ${msg.role === 'user'
                                                    ? 'bg-white text-indigo-700 rounded-tr-none font-bold'
                                                    : 'bg-indigo-950/40 border border-white/10 text-white rounded-tl-none'
                                                    }`}>
                                                    {msg.content}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                    {isAiHelpLoading && <div className="animate-pulse text-[10px] uppercase font-black tracking-widest text-indigo-200">La IA está pensando...</div>}
                                </div>

                                <form className="flex gap-2" onSubmit={async e => {
                                    e.preventDefault();
                                    const val = e.target.msg.value;
                                    if (!val.trim() || isAiHelpLoading) return;
                                    const newHistory = [...aiHelpChatHistory, { role: 'user', content: val }];
                                    setAiHelpChatHistory(newHistory);
                                    setIsAiHelpLoading(true);
                                    e.target.reset();

                                    try {
                                        const token = localStorage.getItem('em_token');
                                        const headers = { 'Content-Type': 'application/json' };
                                        if (token) headers['Authorization'] = `Bearer ${token}`;

                                        const res = await fetch(`${API_URL}?action=ai_assistant_chat`, {
                                            method: 'POST',
                                            headers: headers,
                                            body: JSON.stringify({
                                                messages: newHistory,
                                                system_context: `Eres un asistente experto para 'GestionEventos'. Tu objetivo es guiar al usuario sobre CÓMO usar las funciones del sistema.

CONOCIMIENTO DEL SISTEMA (Módulos y sus funciones):
${JSON.stringify(helpItems, null, 2)}

INSTRUCCIONES CRÍTICAS:
1. Responde de forma DIRECTA, CONCISA y en ESPAÑOL DE ARGENTINA.
2. Usa el CONOCIMIENTO DEL SISTEMA para dar pasos exactos (ej: "Ve al módulo Recursos, crea el recurso y asígnalo").
3. Módulo actual que el usuario está viendo: ${view}.
4. No saludes en cada mensaje, ve al grano.
5. NO hables de software externo como Excel, Microsoft Project o Trello, a menos que el usuario lo pida explícitamente. Enfócate en las herramientas propias del sistema descritas arriba.`
                                            })
                                        });
                                        const data = await res.json();
                                        if (data.status === 'success') {
                                            setAiHelpChatHistory(prev => [...prev, { role: 'assistant', content: data.reply }]);
                                        } else {
                                            alert("Error del asistente: " + data.message);
                                        }
                                    } catch (err) {
                                        console.error("AI Help Error:", err);
                                        alert("Error de conexión con el Asistente IA (Help). Revisa la consola para más detalles.");
                                    } finally { setIsAiHelpLoading(false); }
                                }}>
                                    <input name="msg" className="flex-1 bg-white/10 border border-white/20 rounded-2xl px-6 py-4 text-sm text-white placeholder-white/40 focus:outline-none focus:bg-white/20 transition-all hover:border-white/40" placeholder="Escribe tu duda aquí..." />
                                    <button type="submit" disabled={isAiHelpLoading} className="p-4 bg-white text-indigo-600 rounded-2xl hover:scale-105 active:scale-95 transition-all shadow-lg disabled:opacity-50">
                                        <Send size={20} />
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-12 border-t border-slate-100 pt-12">
                    <h4 className="font-black text-sm uppercase tracking-widest text-slate-400 mb-8 flex items-center gap-3">
                        <List size={18} className="text-indigo-600" /> Guía Paso a Paso
                    </h4>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {filteredHelp.map(item => (
                    <div key={item.id} className="bg-white p-12 rounded-[4rem] border border-slate-200 shadow-sm hover:border-indigo-400 transition-all group relative overflow-hidden flex flex-col">
                        <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-50/50 rounded-full -mr-24 -mt-24 group-hover:bg-indigo-100 transition-colors"></div>

                        <div className="relative z-10 flex-1">
                            <h4 className="font-black text-2xl uppercase tracking-tight mb-4 text-slate-800 flex items-center gap-4">
                                <span className="w-3 h-3 rounded-full bg-indigo-500 shadow-lg shadow-indigo-200"></span>
                                {item.title}
                            </h4>

                            <p className="text-slate-500 leading-relaxed font-semibold text-sm mb-8">
                                {item.description}
                            </p>

                            <div className="space-y-6">
                                <div>
                                    <p className="text-[9px] font-black uppercase text-indigo-400 tracking-widest mb-3">Características Clave</p>
                                    <ul className="space-y-3">
                                        {item.features.map((f, idx) => (
                                            <li key={idx} className="flex gap-3 text-xs font-bold text-slate-600 italic">
                                                <Check size={14} className="text-emerald-500 shrink-0" />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 mt-6 relative">
                                    <div className="absolute -top-3 left-6 flex items-center gap-2 bg-white px-3 py-1 rounded-full border border-slate-100 shadow-sm">
                                        <Zap size={12} className="text-amber-500" />
                                        <span className="text-[8px] font-black uppercase text-slate-400 tracking-tighter">Ejemplo Práctico</span>
                                    </div>
                                    <p className="text-[10px] font-black text-slate-900 uppercase mb-2 leading-tight">{item.example.scenario}</p>
                                    <p className="text-[11px] text-slate-500 font-medium leading-relaxed italic border-l-2 border-indigo-200 pl-3">
                                        <strong className="text-indigo-600 uppercase text-[9px]">Acción:</strong> {item.example.action}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="bg-indigo-900 rounded-[4rem] p-16 text-white relative overflow-hidden shadow-2xl shadow-indigo-200">
                <div className="absolute right-0 top-0 bottom-0 w-1/2 bg-white/5 skew-x-12 translate-x-24"></div>
                <div className="relative z-10 flex flex-col lg:flex-row justify-between items-center gap-12">
                    <div className="max-w-2xl text-center lg:text-left">
                        <h4 className="text-4xl font-black uppercase tracking-tighter mb-4 leading-none">¿Aún tienes dudas técnicas?</h4>
                        <p className="text-indigo-200 font-bold uppercase text-xs tracking-[0.2em]">Nuestro equipo está listo para ayudarte con capacitaciones personalizadas.</p>
                    </div>
                    <button onClick={() => window.open('https://wa.me/5491159595871?text=Hola!%20Necesito%20soporte%20t%C3%A9cnico%20con%20el%20sistema%20de%20Gesti%C3%B3n%20de%20Proyectos', '_blank')} className="bg-white text-indigo-900 px-12 py-6 rounded-2xl font-black uppercase text-xs tracking-widest hover:scale-105 active:scale-95 transition-all shadow-xl shadow-indigo-950/40 shrink-0 flex items-center gap-3">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                        Contactar Soporte Técnico
                    </button>
                </div>
            </div>
        </div >
    );
}

// Helper to search coordinates via Nominatim (OSM) with fallbacks
const fetchCoordinates = async (address, city, province, country) => {
    const tryQuery = async (query) => {
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`, {
                headers: { 'Accept-Language': 'es', 'User-Agent': 'FRS-Gestion-Proyectos' }
            });
            const data = await res.json();
            return (data && data.length > 0) ? { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) } : null;
        } catch (e) { return null; }
    };

    // fallback chain
    const queries = [
        [country, province, city, address].filter(Boolean).join(', '),
        [country, province, city].filter(Boolean).join(', '),
        [country, province].filter(Boolean).join(', ')
    ];

    for (const q of queries) {
        if (!q) continue;
        const coords = await tryQuery(q);
        if (coords) return coords;
    }
    return null;
};

function App() {
    const [uiPrefs, setUiPrefs] = useState(() => {
        const saved = localStorage.getItem('em_ui_prefs');
        return saved ? JSON.parse(saved) : {
            decimals: 2,
            ganttFontSize: 11,
            financialMode: 'ARS',
            dateFormat: 'DD-MM-YYYY'
        };
    });

    const updateUiPrefs = (newPrefs) => {
        const updated = { ...uiPrefs, ...newPrefs };
        setUiPrefs(updated);
        localStorage.setItem('em_ui_prefs', JSON.stringify(updated));
    };

    const fNum = useCallback((val, decimalsOverride = null) => {
        if (val === undefined || val === null || isNaN(val)) return "0";
        const dec = decimalsOverride !== null ? decimalsOverride : uiPrefs.decimals;
        return Number(val).toLocaleString('es-AR', {
            minimumFractionDigits: dec,
            maximumFractionDigits: dec
        });
    }, [uiPrefs.decimals]);

    const fCur = useCallback((val, currency = 'ARS') => {
        const symbol = currency === 'USD' ? 'USD ' : '$ ';
        return `${symbol}${fNum(val)}`;
    }, [fNum]);

    const fDate = useCallback((dateStr) => {
        if (!dateStr) return '---';
        const iso = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
        const [year, month, day] = iso.split('-');
        if (!year || !month || !day) return dateStr;
        if (uiPrefs.dateFormat === 'YYYY-MM-DD') return `${year}-${month}-${day}`;
        return `${day}-${month}-${year}`;
    }, [uiPrefs.dateFormat]);



    const [currentUser, setCurrentUser] = useState(() => {
        const saved = localStorage.getItem('em_session');
        return saved ? JSON.parse(saved) : null;
    });

    const [view, setView] = useState(currentUser?.role === 'SUPER_ADMIN' ? 'MASTER' : 'DASHBOARD');
    const [pendingRoleChanges, setPendingRoleChanges] = useState({}); // { [userId]: role }
    const [dashFilter, setDashFilter] = useState('PROGRESS'); // ALL, PROGRESS, COMPLETED
    const [dashView, setDashView] = useState('LIST'); // LIST, ANALYTICS
    const [isLoading, setIsLoading] = useState(false);
    const [appData, setAppData] = useState({
        projects: [], tasks: [], expenses: [], budgets: [], team: [],
        categories: [], companies: [], clients: [], project_members: [],
        materials: [], structures: [], task_budgets: [],
        roles: [], resources: [], resource_assignments: [],
        resource_inactivity: [],
        user_preferences: null, alert_settings: null, alert_subscriptions: [],
        purchase_orders: [],
        task_resources: [],
        templates: {},
        connector_config: null
    });
    const [selectedEventId, setSelectedEventId] = useState(null);

    const selectedEvent = useMemo(() =>
        appData.projects.find(p => p.id === selectedEventId),
        [appData.projects, selectedEventId]
    );

    const [activeModal, setActiveModal] = useState(null);
    const [modalData, setModalData] = useState(null);
    const [aiChatHistory, setAiChatHistory] = useState([]);
    const [aiHelpChatHistory, setAiHelpChatHistory] = useState([{ role: 'assistant', content: "¡Hola! Soy tu asistente de soporte. ¿Con qué módulo tienes problema?" }]);
    const [aiPptChatHistory, setAiPptChatHistory] = useState([]);
    const [aiTasks, setAiTasks] = useState([]);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [isAiHelpLoading, setIsAiHelpLoading] = useState(false);
    const [isAiPptLoading, setIsAiPptLoading] = useState(false);
    const [aiChatMode, setAiChatMode] = useState(true);
    const [appSettings, setAppSettings] = useState({ app_logo: '' });
    const [globalFxRate, setGlobalFxRate] = useState(() => localStorage.getItem('em_global_fx') || 1000);
    const [tab, setTab] = useState('RESUMEN');
    const [lang, setLang] = useState('ES'); // Default forced to Spanish
    const [expenseType, setExpenseType] = useState('MATERIALES'); // MATERIALES or MANO_OBRA
    const [movementType, setMovementType] = useState('EXPENSE'); // EXPENSE or INCOME
    const [linkPayment, setLinkPayment] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [movementCurrency, setMovementCurrency] = useState('ARS');
    const [budgetLoadType, setBudgetLoadType] = useState('MATERIALES'); // MATERIALES, MDO, AMBOS
    const [budgetAllocation, setBudgetAllocation] = useState('RUBRO'); // GLOBAL, RUBRO
    const [expenseSplits, setExpenseSplits] = useState([]); // [{id, cat, amount, material, mat_qty, desc}]
    const [movementDesc, setMovementDesc] = useState('');
    const [movementDate, setMovementDate] = useState('');
    const [movementAmount, setMovementAmount] = useState('');
    const [movementFx, setMovementFx] = useState('');
    const [movementSupplier, setMovementSupplier] = useState('');
    const [movementInvoice, setMovementInvoice] = useState('');
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        return localStorage.getItem('em_sidebar_collapsed') === 'true';
    });

    const openAddExpenseModal = () => {
        setExpenseType('MATERIALES');
        setMovementType('EXPENSE');
        const pCur = selectedEvent?.base_currency || 'ARS';
        setMovementCurrency(pCur);
        setExpenseSplits([{ id: Date.now(), cat: '', amount: '', material: '', mat_qty: '', desc: '' }]);
        setMovementDesc('');
        setMovementDate(new Date().toISOString().split('T')[0]);
        setMovementAmount('');
        setMovementSupplier('');
        setMovementInvoice('');
        setMovementFx(pCur === 'USD' ? (localStorage.getItem('em_global_fx') || '1000') : '1');
        setModalData(null);
        setActiveModal('ADD_EXPENSE');
    };

    const openEditExpenseModal = (ex) => {
        setModalData(ex);
        setExpenseType(ex.expense_type || 'MATERIALES');
        setMovementType(ex.movement_type || 'EXPENSE');
        setMovementCurrency(ex.currency_original || ex.currency || 'ARS');
        setMovementDesc(ex.description || '');
        setMovementDate(ex.expense_date?.split(' ')[0] || '');
        setMovementAmount(ex.amount_original || '');
        setMovementSupplier(ex.supplier || '');
        setMovementInvoice(ex.invoice_number || '');

        const rateRaw = Number(ex.exchange_rate || ex.fx || 0);
        const baseCur = selectedEvent?.base_currency || 'ARS';
        const movCur = ex.currency_original || ex.currency || 'ARS';

        if (movCur === 'ARS' && baseCur === 'USD') {
            if (rateRaw > 0 && rateRaw < 1) {
                setMovementFx((1 / rateRaw).toFixed(2));
            } else if (rateRaw >= 1) {
                setMovementFx(rateRaw.toFixed(2));
            } else {
                setMovementFx(localStorage.getItem('em_global_fx') || '1000');
            }
        } else if (movCur === 'USD' && baseCur === 'ARS') {
            setMovementFx(rateRaw > 0 ? rateRaw.toFixed(2) : (localStorage.getItem('em_global_fx') || '1000'));
        } else {
            setMovementFx(rateRaw > 0 ? rateRaw.toFixed(2) : '1');
        }

        if ((ex.movement_type === 'INCOME') && !ex.expense_type) {
            setExpenseType('GENERAL');
        }

        setExpenseSplits([{
            id: ex.id,
            cat: ex.category_name,
            amount: ex.amount_original,
            material: ex.linked_material_name,
            material_id: ex.linked_material_id,
            mat_qty: ex.linked_material_qty,
            desc: ex.description
        }]);
        setActiveModal('EDIT_EXPENSE');
    };

    const t = (key) => TRANSLATIONS[lang]?.[key] || TRANSLATIONS.ES[key] || key;

    const memoOnSelectProject = useCallback((id) => {
        setSelectedEventId(id);
        setView('DASHBOARD');
        setTab('RESUMEN');
    }, []);

    useEffect(() => {
        // Carga inicial de settings públicos
        fetch(`${API_URL}?action=get_public_config`)
            .then(r => r.json())
            .then(data => {
                if (data.status === 'success') {
                    setAppSettings(data.settings);
                    if (data.settings.app_logo) {
                        const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
                        link.rel = 'icon';
                        link.href = data.settings.app_logo;
                        document.getElementsByTagName('head')[0].appendChild(link);
                    }
                }
            })
            .catch(e => console.error("Error loading public config:", e));
    }, []);

    useEffect(() => {
        // MAGIC LINK LOGIN
        const params = new URLSearchParams(window.location.search);
        const tokenRaw = params.get('token');
        const projId = params.get('project_id');

        if (tokenRaw) {
            const token = tokenRaw.replace(/ /g, '+');
            setIsLoading(true);

            // Limpiar URL para no re-intentar si se recarga (especialmente en errores)
            window.history.replaceState({}, document.title, window.location.pathname);

            fetch(`${API_URL}?action=login_with_token&token=${encodeURIComponent(token)}`)
                .then(r => r.json())
                .then(data => {
                    if (data.status === 'success') {
                        const u = data.user;
                        u.company_id = u.company_id || u.companyId || 'SYSTEM';
                        if (data.token) {
                            localStorage.setItem('em_token', data.token);
                        }
                        setCurrentUser(u);
                        localStorage.setItem('em_session', JSON.stringify(u));
                        if (projId) {
                            setView('DASHBOARD');
                            setSelectedEventId(projId);
                            setTab('CRONOGRAMA');
                        }
                    } else {
                        console.error("Magic login failed:", data.message);
                        alert("El enlace de acceso rápido ha fallado: " + data.message);
                    }
                })
                .catch(e => {
                    console.error("Magic login error:", e);
                    alert("Error al procesar el acceso rápido.");
                })
                .finally(() => setIsLoading(false));
        }
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [selectedEventId, view]);

    useEffect(() => {
        // Load initial data
        sync();
    }, []);


    useEffect(() => {
        // Actualizar título de pestaña
        document.title = "Gestión Proyectos" + (selectedEvent ? ` | ${selectedEvent.name}` : "");
    }, [selectedEvent]);

    useEffect(() => {
        if (activeModal === 'ADD_EXPENSE' || activeModal === 'EDIT_EXPENSE') {
            // No automatic hydration here anymore. Initialization handled by open*Modal helpers.
        } else if (activeModal === 'ADD_TASK' || activeModal === 'EDIT_TASK') {
            // Keep task logic if it was here, but for expenses it's now explicit.
        }
    }, [activeModal, modalData]);

    useEffect(() => {
        if (activeModal === 'AI_PLANNER') {
            setAiChatHistory([
                { role: 'assistant', content: '¡Hola! Soy tu asistente de planificación inteligente. Para diseñar el cronograma perfecto para **' + selectedEvent?.name + '**, necesito conocer algunos detalles: **¿Qué tipo de proyecto deseas realizar exactamente?** (ej: Construcción de casa, Remodelación, Evento corporativo...)' }
            ]);
            setAiChatMode(true);
            setAiTasks([]);
        }
    }, [activeModal, selectedEvent?.name]);

    useEffect(() => {
        if (activeModal === 'AI_PPT_PLANNER') {
            setAiPptChatHistory([
                { role: 'assistant', content: '¡Hola! Soy tu asistente de reportes. ¿Qué información te gustaría incluir en tu presentación PowerPoint?' }
            ]);
        }
    }, [activeModal]);

    useEffect(() => {
        if (view === 'HELP' && aiHelpChatHistory.length === 0) {
            setAiHelpChatHistory([
                { role: 'assistant', content: "¡Hola! Soy tu asistente de soporte. ¿Con qué módulo tienes problema?" }
            ]);
        }
    }, [view, aiHelpChatHistory.length]);

    useEffect(() => {
        if (currentUser && view === 'MASTER') {
            if (currentUser.role !== 'SUPER_ADMIN') {
                console.warn("Unauthorized access to MASTER view. Redirecting...");
                setView('DASHBOARD');
            }
        }
    }, [currentUser, view]);

    const userProjectRole = useMemo(() => {
        if (!currentUser) return null;
        const role = currentUser.role?.toUpperCase();
        if (role === 'SUPER_ADMIN') return 'MANAGER';
        if (role === 'COMPANY_ADMIN' && selectedEvent && String(currentUser.company_id) === String(selectedEvent.company_id)) return 'MANAGER';
        const pm = (appData.project_members || []).find(pm => String(pm.event_id) === String(selectedEventId) && String(pm.user_id) === String(currentUser.id));
        return pm ? pm.role : 'VIEWER';
    }, [currentUser, appData.project_members, selectedEventId, appData.team, selectedEvent]);

    const canEdit = useMemo(() => {
        return userProjectRole === 'EDITOR' || userProjectRole === 'MANAGER';
    }, [userProjectRole]);

    const [dashYear, setDashYear] = useState(new Date().getFullYear());
    const [dashMonth, setDashMonth] = useState(null);

    const projectsWithMetrics = useMemo(() => {
        return (appData.projects || []).map(p => {
            const projTasks = (appData.tasks || []).filter(t => t.event_id === p.id);
            const { physicalProgress: avgProg, projStart, projEnd } = calculateProgressMetrics(projTasks, p.event_date, p.end_date);
            const isFinished = avgProg >= 100 || p.status === 'COMPLETED';
            const taskScope = {
                planned: projTasks.filter(t => !t.parent_id && (t.progress === 0)).length,
                active: projTasks.filter(t => !t.parent_id && (t.progress > 0 && t.progress < 100)).length,
                done: projTasks.filter(t => !t.parent_id && (t.progress >= 100)).length
            };
            return { ...p, avgProg, isFinished, projStart, taskScope };
        }).sort((a, b) => {
            const dateA = a.projStart ? a.projStart.getTime() : (a.event_date ? new Date(a.event_date).getTime() : 0);
            const dateB = b.projStart ? b.projStart.getTime() : (b.event_date ? new Date(b.event_date).getTime() : 0);
            if (dateA !== dateB) return dateA - dateB;
            return (a.name || '').localeCompare(b.name || '');
        });
    }, [appData.projects, appData.tasks]);

    const sync = useCallback(async (userOverride = null) => {
        const user = userOverride || currentUser;
        const cid = user?.company_id || user?.companyId;
        if (!cid) return;
        setIsLoading(true);
        try {
            const token = localStorage.getItem('em_token');
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const res = await fetch(`${API_URL}?action=sync_full&company_id=${cid}&user_id=${user.id}&role=${user.role}&_t=${Date.now()}`, {
                headers: headers
            });
            const data = await res.json();
            if (data.status === 'success') {
                setAppData({
                    projects: (data.projects || []).map(p => ({ ...p, base_currency: p.base_currency || p.display_currency || 'ARS' })),
                    tasks: (data.tasks || []).map(task => {
                        const u = (data.team || []).find(tu => tu.id === task.assigned_to);
                        return { ...task, assignee_name: u ? u.name : null, isParent: !task.parent_id };
                    }),
                    expenses: data.expenses || [],
                    budgets: data.budgets || [],
                    team: data.team || [],
                    categories: data.categories || [],
                    companies: data.companies || [],
                    clients: data.clients || [],
                    client_contacts: data.client_contacts || [],
                    project_members: data.project_members || [],
                    roles: data.roles || [],
                    materials: data.materials || [],
                    structures: data.structures || [],
                    task_budgets: data.task_budgets || [],
                    project_documents: data.project_documents || [],
                    user_preferences: data.user_preferences || null,
                    alert_settings: data.alert_settings || null,
                    alert_subscriptions: data.alert_subscriptions || [],
                    resources: data.resources || [],
                    resource_assignments: data.resource_assignments || [],
                    resource_inactivity: data.resource_inactivity || [],
                    resource_hours_log: data.resource_hours_log || [],
                    purchase_orders: data.purchase_orders || [],
                    task_resources: data.task_resources || [],
                    templates: data.templates || appData.templates,
                    connector_config: data.connector_config || null
                });
            } else {
                console.error("Sync API Error:", data.message || "Unknown error", data);
            }

            if (Object.keys(appData.templates).length === 0) {
                try {
                    const tRes = await fetch('tasks_templates.json');
                    const tData = await tRes.json();
                    setAppData(prev => ({ ...prev, templates: tData }));
                } catch (te) { console.warn("Templates fetch failed", te); }
            }
            return data;
        } catch (e) {
            console.error("Sync Critical Error:", e);
        } finally {
            setIsLoading(false);
            const loader = document.getElementById('loading');
            if (loader) loader.style.display = 'none';
        }
    }, [currentUser, appData.templates]);

    useEffect(() => {
        if (currentUser) sync();
        else {
            const loader = document.getElementById('loading');
            if (loader) loader.style.display = 'none';
        }
    }, [currentUser, sync]);

    useEffect(() => {
        if (!activeModal || (activeModal !== 'ADD_EXPENSE' && activeModal !== 'EDIT_EXPENSE')) return;

        const updateBalanceInfo = () => {
            const catSelect = document.getElementById('expense_cat_select');
            const infoDiv = document.getElementById('expense_balance_info');
            if (!catSelect || !infoDiv) return;

            const selectedCat = catSelect.value;
            if (!selectedCat) {
                infoDiv.classList.add('hidden');
                return;
            }

            let limit = 0;
            let spent = 0;
            let collected = 0;
            let isGlobal = false;

            if (expenseType === 'MANO_OBRA') {
                if (Number(selectedEvent?.budget_mdo_global || 0) > 0) {
                    isGlobal = true;
                    limit = Number(selectedEvent.budget_mdo_global);
                    const movs = appData.expenses.filter(ex => ex.event_id === selectedEventId && ex.expense_type === 'MANO_OBRA');
                    spent = movs.filter(ex => ex.movement_type === 'EXPENSE').reduce((a, v) => a + Number(v.amount_converted || v.amount_original || 0), 0);
                    collected = movs.filter(ex => ex.movement_type === 'INCOME').reduce((a, v) => a + Number(v.amount_converted || v.amount_original || 0), 0);
                } else if (selectedCat === 'GLOBAL') {
                    limit = selectedEvent?.budget_mdo_global || 0;
                    spent = appData.expenses
                        .filter(ex => ex.event_id === selectedEventId && ex.expense_type === 'MANO_OBRA' && ex.movement_type === 'EXPENSE')
                        .reduce((a, v) => a + Number(v.amount_converted || v.amount_original || 0), 0);
                } else {
                    const tb = appData.task_budgets?.find(b => {
                        const t = appData.tasks.find(tk => tk.id === b.task_id);
                        return t?.title === selectedCat && b.event_id === selectedEventId;
                    });
                    limit = tb?.budget_mdo || 0;
                    spent = appData.expenses
                        .filter(ex => ex.event_id === selectedEventId && ex.expense_type === 'MANO_OBRA' && ex.movement_type === 'EXPENSE' && (ex.description.includes(selectedCat) || ex.linked_material_name?.includes(selectedCat)))
                        .reduce((a, v) => a + Number(v.amount_converted || v.amount_original || 0), 0);
                }
            } else {
                if (Number(selectedEvent?.budget_materials_global || 0) > 0) {
                    isGlobal = true;
                    limit = Number(selectedEvent.budget_materials_global);
                    const movs = appData.expenses.filter(ex => ex.event_id === selectedEventId && ex.expense_type === 'MATERIALES');
                    spent = movs.filter(ex => ex.movement_type === 'EXPENSE').reduce((a, v) => a + Number(v.amount_converted || v.amount_original || 0), 0);
                    collected = movs.filter(ex => ex.movement_type === 'INCOME').reduce((a, v) => a + Number(v.amount_converted || v.amount_original || 0), 0);
                } else {
                    const cat = appData.categories.find(c => c.name === selectedCat);
                    const b = appData.budgets.find(pb => pb.category_id === cat?.id && pb.event_id === selectedEventId);
                    limit = b ? (Number(b.budget_amount || 0) + Number(b.budget_mdo || 0)) : 0;

                    const catMovs = appData.expenses.filter(ex => ex.category_id === cat?.id && ex.event_id === selectedEventId);
                    spent = catMovs.filter(ex => ex.movement_type === 'EXPENSE').reduce((a, v) => a + Number(v.amount_converted || v.amount_original || 0), 0);
                    collected = catMovs.filter(ex => ex.movement_type === 'INCOME').reduce((a, v) => a + Number(v.amount_converted || v.amount_original || 0), 0);
                }
            }

            const saldo = limit - (movementType === 'INCOME' ? collected : spent);
            const cur = selectedEvent?.base_currency || 'ARS';

            infoDiv.innerHTML = `
                <div class="flex justify-between items-center mb-1">
                    <span class="font-black text-slate-400 text-[8px] uppercase tracking-tighter">${isGlobal ? 'PRESUPUESTO GLOBAL' : `RUBRO: ${selectedCat}`}</span>
                    <span class="font-bold ${saldo < 0 ? 'text-rose-500' : 'text-indigo-600'}">${cur} ${saldo.toLocaleString()} DISP.</span>
                </div>
                <div class="w-full h-1 bg-slate-200 rounded-full overflow-hidden">
                    <div class="h-full ${saldo < 0 ? 'bg-rose-500' : 'bg-indigo-500'} transition-all duration-500" style="width: ${Math.min(100, (limit > 0 ? ((movementType === 'INCOME' ? collected : spent) / limit) * 100 : 0))}%"></div>
                </div>
            `;
            infoDiv.classList.remove('hidden');
        };

        const timer = setInterval(updateBalanceInfo, 500);
        return () => clearInterval(timer);
    }, [activeModal, expenseType, movementType, appData, selectedEventId, selectedEvent]);

    const syncMDOFromResources = async (projectId, dataOverride = null) => {
        const sourceData = dataOverride || appData;
        const proj = sourceData.projects.find(p => p.id === projectId);
        if (!proj || proj.sync_mdo_resources != 1) return;

        const projAssignments = sourceData.resource_assignments.filter(a => a.event_id === projectId);
        let totalMdo = 0;
        projAssignments.forEach(asig => {
            const res = sourceData.resources.find(r => r.id === asig.resource_id);
            if (!res) return;
            const start = new Date(asig.start_date);
            const end = new Date(asig.end_date);
            const wDays = getWorkDays(start, end);
            const hours = wDays * parseFloat(res.daily_hours) * (parseFloat(asig.percentage) / 100);
            totalMdo += convertResourceCost(
                hours * parseFloat(res.hourly_rate || 0),
                res.currency, proj.base_currency || proj.display_currency,
                proj.exchange_rate, globalFxRate
            ).value;
        });

        console.log(`Auto-sync MDO for project ${projectId}: ${totalMdo}`);
        await fetch(`${API_URL}?action=set_budget_mdo_global`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: projectId, budget_mdo_global: totalMdo.toFixed(2) })
        });
    };

    const handleAction = useCallback(async (action, payload) => {
        setIsLoading(true);

        // Capturar proyectos afectados antes de borrar el recurso (para sincronizar MDO)
        let affectedPids = [];
        if (action === 'delete_resource') {
            affectedPids = [...new Set(appData.resource_assignments.filter(a => a.resource_id === payload.id).map(a => a.event_id))];
        }

        try {
            const token = localStorage.getItem('em_token');
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const res = await fetch(`${API_URL}?action=${action}`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                    event_id: selectedEventId,
                    company_id: currentUser?.company_id || currentUser?.companyId,
                    actor_id: currentUser?.id,
                    user_id: currentUser?.id, // Default
                    ...payload // Allow payload to override user_id or event_id
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                if (data.warning) alert(data.warning);
                if (action === 'update_event' && data.updated_event) {
                    setAppData(prev => ({
                        ...prev,
                        projects: prev.projects.map(p => p.id === data.updated_event.id ? data.updated_event : p)
                    }));
                }
                if (action === 'update_company' && data.updated_company) {
                    setAppData(prev => ({
                        ...prev,
                        companies: prev.companies.map(c => c.id === data.updated_company.id ? data.updated_company : c)
                    }));
                }
                if (action === 'update_public_config') {
                    fetch(`${API_URL}?action=get_public_config`)
                        .then(r => r.json())
                        .then(d => { if (d.status === 'success') setAppSettings(d.settings); });
                }
                if (action === 'regenerate_access_key') {
                    alert("¡Llave de acceso regenerada con éxito!");
                }
                if (action !== 'assign_project_member' && action !== 'remove_project_member') {
                    setActiveModal(null);
                    setModalData(null);
                }
                const newData = await sync();

                // Recalcular MDO si el proyecto tiene la sincronización activa (Usando newData)
                if (['assign_resource', 'update_resource_assignment', 'delete_resource_assignment'].includes(action)) {
                    const pid = payload.event_id || (action === 'delete_resource_assignment' ? appData.resource_assignments.find(a => a.id === payload.id)?.event_id : null);
                    if (pid) await syncMDOFromResources(pid, newData);
                }

                // Si se borró un recurso, actualizar todos los proyectos afectados
                if (action === 'delete_resource') {
                    for (const pid of affectedPids) {
                        await syncMDOFromResources(pid, newData);
                    }
                }

                if (action === 'update_event' && payload.sync_mdo_resources == 1) {
                    await syncMDOFromResources(payload.id, newData);
                }
                await sync();
            } else {
                console.error("ACTION ERROR:", data);
                alert("ERROR: " + data.message + (data.debug ? "\n\nDetalle: " + data.debug : ""));
            }

            if (action === 'bulk_add_tasks') {
                setActiveModal(null);
            }

            if (action === 'assign_project_member') {
                // Role assigned, re-syncing...
                // Optimistic Update
                setAppData(prev => ({
                    ...prev,
                    project_members: [
                        ...(prev.project_members || []).filter(pm => !(pm.event_id === selectedEventId && pm.user_id === payload.user_id)),
                        { event_id: selectedEventId, user_id: payload.user_id, role: payload.role }
                    ]
                }));
                await sync(); // Force re-sync
            }

            if (action.includes('purchase_order')) {
                await sync();
            }
            return data;
        } catch (e) {
            alert("Error de conexiÃ³n");
        } finally {
            setIsLoading(false);
        }
    }, [appData.resource_assignments, appData.tasks, appData.templates, currentUser, selectedEventId, sync]);


    const handleLogin = async (email, pass, magic) => {
        if (magic?.trim().toUpperCase() === MASTER_MAGIC_KEY) {
            const admin = { id: 'admin-master', name: 'Master Admin', role: 'SUPER_ADMIN', email: 'admin@system.com', company_id: 'SYSTEM' };
            setCurrentUser(admin);
            localStorage.setItem('em_session', JSON.stringify(admin));
            await sync(admin);
            return;
        }

        if (!email || !pass) {
            alert("Ingresa email y contraseña");
            return;
        }

        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}?action=login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim(), password: pass.trim() })
            });

            const data = await res.json();
            if (data.status === 'success') {
                const u = data.user;
                u.company_id = u.company_id || u.companyId || 'SYSTEM';
                if (data.token) {
                    localStorage.setItem('em_token', data.token);
                }
                setCurrentUser(u);
                localStorage.setItem('em_session', JSON.stringify(u));
                // LOGIN SUCCESS
                if (u.role === 'SUPER_ADMIN') setView('MASTER');
                else setView('DASHBOARD');
                await sync(u);
            } else {
                alert(data.message || "Error al iniciar sesión");
            }
        } catch (e) {
            console.error("Login fail:", e);
            alert("Error crítico de conexión");
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (name, email, pass, key) => {
        if (!name || !email || !pass || !key) {
            alert("Todos los campos son obligatorios");
            return;
        }

        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}?action=register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email: email.trim(), password: pass.trim(), company_id: key.trim() })
            });

            const data = await res.json();
            if (data.status === 'success') {
                alert("¡Registro exitoso! Ya puedes iniciar sesión");
                return true; // Ã‰xito para el componente AuthView
            } else {
                alert(data.message || "Error al registrarse");
                return false;
            }
        } catch (e) {
            alert("Error de conexiÃ³n");
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    // ========== MDO BUDGET HANDLERS ==========

    const handleCreateMDOBudget = async (mode) => {
        if (mode === 'GLOBAL') {
            setActiveModal('MDO_BUDGET_GLOBAL');
        } else if (mode === 'POR_FASE') {
            setActiveModal('MDO_BUDGET_PHASE');
        }
    };

    const handleSaveMDOGlobal = async (data) => {
        await handleAction('save_global_budgets', {
            event_id: selectedEventId,
            amount_mdo: data.amount_mdo,
            amount_materials: data.amount_materials
        });
    };

    const handleSaveMDOPhase = async (phaseBudgets) => {
        for (const budget of phaseBudgets) {
            // Guardar en task_budgets para control de certificación (MDO)
            await handleAction('create_mdo_budget_phase', {
                event_id: selectedEventId,
                task_id: budget.task_id,
                budget_mdo: budget.budget_mdo,
                budget_materials: budget.budget_materials // Added to API
            });

            // Sincronizar con Rubros (event_category_budgets) para vista consolidada
            // Buscamos o creamos el rubro con el nombre de la fase
            await handleAction('save_budget', {
                event_id: selectedEventId,
                company_id: currentUser.company_id,
                category_name: budget.title,
                amount: budget.budget_materials,
                amount_mdo: budget.budget_mdo
            });
        }
        setActiveModal(null);
    };

    const handleSaveSingleBudget = async (data) => {
        await handleAction('save_budget', {
            event_id: selectedEventId,
            company_id: currentUser.company_id,
            category_name: data.category_name,
            amount: data.amount,
            amount_mdo: data.amount_mdo
        });
        // Sincronizar con fase si existe
        const phase = appData.tasks?.find(t => t.title === data.category_name && t.event_id === selectedEventId);
        if (phase) {
            await handleAction('create_mdo_budget_phase', {
                event_id: selectedEventId,
                task_id: phase.id,
                budget_mdo: data.amount_mdo,
                budget_materials: data.amount
            });
        }
        setActiveModal(null);
    };

    const generatePPTFromJSON = (data, projectTitle) => {
        try {
            const pptx = new PptxGenJS();
            pptx.layout = 'LAYOUT_16x9';

            // Theme settings
            pptx.theme = { headFontFace: 'Arial', bodyFontFace: 'Helvetica' };

            data.slides.forEach(slide => {
                const s = pptx.addSlide();

                // Background
                s.background = { color: 'F8FAFC' };

                // Add Title
                s.addText(slide.title, {
                    x: 0.5, y: 0.3, w: '90%', h: 0.8,
                    fontSize: 24, bold: true, color: '1E293B', fontFace: 'Arial'
                });

                if (slide.layout === 'TABLE' && slide.tableData) {
                    s.addTable(slide.tableData, {
                        x: 0.5, y: 1.2, w: '90%',
                        fontSize: 10,
                        border: { type: 'solid', color: 'E2E8F0', pt: 1 },
                        fill: { color: 'FFFFFF' },
                        color: '334155',
                        header: { fill: { color: '4F46E5' }, color: 'FFFFFF', bold: true }
                    });
                } else if (slide.layout === 'CHART' && slide.chartData) {
                    s.addChart(slide.chartType || pptx.ChartType.bar, slide.chartData, {
                        x: 0.5, y: 1.2, w: '90%', h: 4,
                        showLegend: true,
                        legendPos: 'b',
                        chartColors: ['4F46E5', '10B981', 'F59E0B', 'EF4444', '8B5CF6', 'EC4899']
                    });
                } else if (slide.content && Array.isArray(slide.content)) {
                    let curY = 1.3;
                    slide.content.forEach((point) => {
                        s.addText('• ' + point, {
                            x: 0.8, y: curY, w: '85%', h: 0.5,
                            fontSize: 16, color: '334155', align: 'left'
                        });
                        curY += 0.6;
                    });
                }

                // Footer
                s.addText(`© FRS Gestión - ${new Date().toLocaleDateString()}`, {
                    x: 0.5, y: 5.2, w: '90%', h: 0.3,
                    fontSize: 9, color: '94A3B8', align: 'center'
                });
            });

            const safeTitle = (projectTitle || 'Reporte').toString();
            pptx.writeFile({ fileName: `Reporte_${safeTitle.replace(/[^a-z0-9]/gi, '_')}.pptx` });

        } catch (error) {
            console.error("PPT Gen Error", error);
            alert("Error al generar el archivo PPT: " + error.message);
        }
    };

    const exportToExcelFiltered = (projects) => {
        try {
            const wb = XLSX.utils.book_new();

            // 1. Total Executive Summary
            const summaryData = [];
            const clientsByProject = [...new Set(projects.map(p => p.client_name || p.client))];

            summaryData.push({
                "Cliente": "RESUMEN CONSOLIDADO POR CLIENTE",
                "Cant. Proyectos": "",
                "Presupuesto Total": "",
                "Gastos Ejecutados": "",
                "Pagos Recibidos": "",
                "Saldo Pendiente": "",
                "Avance Real (%)": "",
                "Avance Teórico (%)": "",
                "Estado": ""
            });
            summaryData.push({}); // Empty row

            clientsByProject.forEach(clientName => {
                const clientProjects = projects.filter(p => (p.client_name || p.client) === clientName);
                const totalBudget = clientProjects.reduce((sum, p) => sum + parseFloat(p.budget_total_contract || 0), 0);
                const totalExpenses = clientProjects.reduce((sum, p) => sum + parseFloat(p.expenses || 0), 0);
                const totalIncomes = clientProjects.reduce((sum, p) => sum + parseFloat(p.incomes || 0), 0);
                const totalBalance = clientProjects.reduce((sum, p) => sum + parseFloat(p.balance || 0), 0);
                const avgProgress = clientProjects.reduce((sum, p) => sum + (parseFloat(p.progress) || 0), 0) / clientProjects.length;
                const avgTheoProgress = clientProjects.reduce((sum, p) => sum + (parseFloat(p.theoretical_progress) || 0), 0) / clientProjects.length;

                summaryData.push({
                    "Cliente": clientName,
                    "Cant. Proyectos": clientProjects.length,
                    "Presupuesto Total": totalBudget,
                    "Gastos Ejecutados": totalExpenses,
                    "Pagos Recibidos": totalIncomes,
                    "Saldo Pendiente": totalBalance,
                    "Avance Real (%)": Math.round(avgProgress),
                    "Avance Teórico (%)": Math.round(avgTheoProgress),
                    "Estado": avgProgress >= 100 ? "COMPLETADO" : (avgProgress < avgTheoProgress ? "RETRASADO" : "EN FECHA")
                });
            });

            const wsSummary = XLSX.utils.json_to_sheet(summaryData);

            // Basic formatting via col widths
            wsSummary['!cols'] = [
                { wch: 30 }, // Cliente
                { wch: 15 }, // Cant Proyectos
                { wch: 20 }, // Presupuesto
                { wch: 20 }, // Gastos
                { wch: 20 }, // Cobros
                { wch: 20 }, // Saldo
                { wch: 15 }, // Avance Real
                { wch: 15 }, // Avance Teorico
                { wch: 15 }  // Estado
            ];

            XLSX.utils.book_append_sheet(wb, wsSummary, "Resumen General");

            // 2. Project Tabs with Details
            projects.forEach(p => {
                const projectDetail = [];

                // Executive Rows
                projectDetail.push({ "Campo": "REPORTE DETALLADO DE PROYECTO", "Valor": "" });
                projectDetail.push({});
                projectDetail.push({ "Campo": "PROYECTO", "Valor": p.name });
                projectDetail.push({ "Campo": "CLIENTE", "Valor": p.client || p.client_name });
                projectDetail.push({ "Campo": "ESTADO", "Valor": p.status });
                projectDetail.push({ "Campo": "FECHAS", "Valor": `${p.start_date} a ${p.end_date}` });
                projectDetail.push({ "Campo": "AVANCE REAL", "Valor": `${p.progress || 0}%` });
                projectDetail.push({ "Campo": "AVANCE TEÓRICO", "Valor": `${p.theoretical_progress || 0}%` });
                projectDetail.push({ "Campo": "PRESUPUESTO TOTAL", "Valor": `${p.currency || 'ARS'} ${parseFloat(p.budget_total_contract || 0).toLocaleString()}` });
                projectDetail.push({ "Campo": "GASTOS EJECUTADOS", "Valor": `${p.currency || 'ARS'} ${parseFloat(p.expenses || 0).toLocaleString()}` });
                projectDetail.push({ "Campo": "COBROS REALIZADOS", "Valor": `${p.currency || 'ARS'} ${parseFloat(p.incomes || 0).toLocaleString()}` });
                projectDetail.push({ "Campo": "SALDO PENDIENTE", "Valor": `${p.currency || 'ARS'} ${parseFloat(p.balance || 0).toLocaleString()}` });
                projectDetail.push({ "Campo": "TAREAS ATRASADAS", "Valor": p.delayed_tasks_count || 0 });
                projectDetail.push({}); // Empty row

                // Phases Table Header
                projectDetail.push({ "Campo": "DETALLE POR FASES (WBS)", "Valor": "" });
                projectDetail.push({ "Campo": "Fase", "Valor": "Avance Real (%)", "Extra 1": "Avance Teorico (%)", "Extra 2": "Inicio", "Extra 3": "Fin", "Extra 4": "Estado" });

                const today = new Date();
                (p.phases || []).forEach(phase => {
                    const isDelayed = phase.progress < 100 && new Date(phase.end_date) < today;
                    projectDetail.push({
                        "Campo": phase.title,
                        "Valor": (phase.progress || 0) + "%",
                        "Extra 1": (phase.theoretical_progress || 0) + "%",
                        "Extra 2": phase.start_date,
                        "Extra 3": phase.end_date,
                        "Extra 4": isDelayed ? "RETRASADA" : "OK"
                    });
                });

                const wsProject = XLSX.utils.json_to_sheet(projectDetail, { skipHeader: true });
                wsProject['!cols'] = [{ wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];

                // Solve sheet naming conflict: "Client - Project" truncating to 31 chars
                let clientClean = (p.client || p.client_name || 'S_D').toString().substring(0, 15).trim();
                let projectClean = p.name.toString().substring(0, 15).trim();
                let baseName = `${clientClean} - ${projectClean}`.replace(/[\\/?*[\]]/g, '_').substring(0, 28);

                let sheetName = baseName;
                let counter = 1;
                while (wb.SheetNames.includes(sheetName)) {
                    sheetName = `${baseName.substring(0, 25)} (${counter++})`;
                }

                XLSX.utils.book_append_sheet(wb, wsProject, sheetName);
            });

            const fileName = `Reporte_Consolidado_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(wb, fileName);

        } catch (error) {
            console.error("Excel Export Error", error);
            alert("Error al generar el archivo Excel: " + error.message);
        }
    };


    if (isLoading && !currentUser) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-8">
                <div className="max-w-md w-full bg-white rounded-[3rem] shadow-xl p-12 text-center border border-slate-100 flex flex-col gap-8 animate-in">
                    <div className="inline-flex w-16 h-16 bg-indigo-600 rounded-2xl items-center justify-center text-white mx-auto shadow-xl shadow-indigo-100">
                        <Loader2 size={28} className="animate-spin" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black uppercase tracking-tighter text-slate-800 leading-none">Verificando Acceso</h1>
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.3em] mt-3">Iniciando sesión segura...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!currentUser) return <AuthView onLogin={handleLogin} onRegister={handleRegister} isLoading={isLoading} appSettings={appSettings} />;


    return (
        <div className="flex h-screen bg-gradient-to-br from-[#F0F7FF] to-white overflow-hidden text-slate-900 font-sans">
            <aside className={`${isSidebarCollapsed ? 'w-20' : 'w-72'} bg-gradient-to-b from-[#0A1929] to-[#1A2332] flex flex-col shrink-0 z-50 shadow-2xl transition-all duration-300 relative`}>
                <button
                    onClick={() => {
                        const newState = !isSidebarCollapsed;
                        setIsSidebarCollapsed(newState);
                        localStorage.setItem('em_sidebar_collapsed', newState);
                    }}
                    className="absolute -right-4 top-12 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white shadow-xl hover:bg-indigo-500 transition-all z-[60]"
                >
                    {isSidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                </button>

                <div className={`p-10 flex items-center gap-3 ${isSidebarCollapsed ? 'p-6 justify-center' : ''}`}>
                    {appSettings.app_logo ? (
                        <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg shrink-0 bg-white flex items-center justify-center p-1">
                            <img src={appSettings.app_logo} alt="Logo" className="max-w-full max-h-full object-contain" />
                        </div>
                    ) : (
                        <div className="w-10 h-10 bg-gradient-to-br from-[#6BB6FF] to-[#4A90E2] rounded-xl flex items-center justify-center text-white shadow-lg shrink-0">
                            <Building2 size={20} />
                        </div>
                    )}
                    {!isSidebarCollapsed && <h1 className="font-black text-white uppercase tracking-tighter italic text-lg leading-none whitespace-normal break-words w-40">Gestión Proyectos</h1>}
                </div>
                <nav className={`flex-1 overflow-y-auto ${isSidebarCollapsed ? 'px-3' : 'px-6'} space-y-2`}>
                    {currentUser?.role === 'SUPER_ADMIN' ? (
                        <SidebarButton icon={<Zap size={18} />} label={t('master')} active={view === 'MASTER'} onClick={() => { setView('MASTER'); setSelectedEventId(null); }} collapsed={isSidebarCollapsed} />
                    ) : (
                        <>
                            <SidebarButton icon={<Globe size={18} />} label={t('mapa_geografico') || 'Mapa Geográfico'} active={view === 'MANAGER_DASHBOARD'} onClick={() => { setView('MANAGER_DASHBOARD'); setSelectedEventId(null); }} collapsed={isSidebarCollapsed} />
                            <SidebarButton icon={<Layout size={18} />} label={t('seguimiento_proyectos') || 'Seguimiento de Proyectos'} active={view === 'DASHBOARD'} onClick={() => { setView('DASHBOARD'); setSelectedEventId(null); }} collapsed={isSidebarCollapsed} />
                            {currentUser?.role === 'COMPANY_ADMIN' && (
                                <>
                                    <SidebarButton icon={<Contact size={18} />} label={t('clients') || "Clientes"} active={view === 'CLIENTS'} onClick={() => { setView('CLIENTS'); setSelectedEventId(null); }} collapsed={isSidebarCollapsed} />
                                    <SidebarButton icon={<Landmark size={18} />} label={t('financial') || "Finanzas"} active={view === 'FINANCIAL'} onClick={() => { setView('FINANCIAL'); setSelectedEventId(null); }} collapsed={isSidebarCollapsed} />
                                    <SidebarButton icon={<Users size={18} />} label={t('team')} active={view === 'TEAM'} onClick={() => { setView('TEAM'); setSelectedEventId(null); }} collapsed={isSidebarCollapsed} />
                                    {currentUser?.role === 'COMPANY_ADMIN' && (
                                        <>
                                            <SidebarButton icon={<ShieldCheck size={18} />} label={t('roles_permisos') || 'Roles'} active={view === 'ROLES'} onClick={() => { setView('ROLES'); setSelectedEventId(null); }} collapsed={isSidebarCollapsed} />
                                            <SidebarButton icon={<Briefcase size={18} />} label={t('control_recursos') || 'Recursos'} active={view === 'RESOURCES'} onClick={() => { setView('RESOURCES'); setSelectedEventId(null); }} collapsed={isSidebarCollapsed} />
                                        </>
                                    )}
                                    <SidebarButton icon={<Settings2 size={18} />} label="Configuración" active={view === 'COMPANY_SETTINGS'} onClick={() => { setView('COMPANY_SETTINGS'); setSelectedEventId(null); }} collapsed={isSidebarCollapsed} />
                                </>
                            )}
                            <SidebarButton icon={<HelpCircle size={18} />} label={t('help') || "Ayuda"} active={view === 'HELP'} onClick={() => { setView('HELP'); setSelectedEventId(null); }} collapsed={isSidebarCollapsed} />
                        </>
                    )}
                </nav>
                <div className={`${isSidebarCollapsed ? 'p-6' : 'p-10'} border-t border-white/5 space-y-6`}>
                    <button onClick={() => setActiveModal('PROFILE')} className="flex items-center gap-3 text-slate-500 font-black text-[10px] uppercase hover:text-white transition-all">
                        <Edit3 size={18} /> {!isSidebarCollapsed && t('settings')}
                    </button>
                    <button onClick={() => { 
                        localStorage.removeItem('em_session'); 
                        localStorage.removeItem('em_token');
                        window.location.reload(); 
                    }} className="flex items-center gap-3 text-rose-500 font-black text-[10px] uppercase hover:text-rose-400 transition-all">
                        <LogOut size={18} /> {!isSidebarCollapsed && t('logout')}
                    </button>
                </div>
            </aside>

            <main className="flex-1 flex flex-col relative overflow-hidden">
                {/* Header compacto: ruta de navegación (el título grande ya lo muestra cada página) */}
                <header className="h-16 bg-white border-b border-slate-100 flex items-center justify-between px-8 shrink-0 shadow-sm z-10 gap-6">
                    <nav className="flex items-center gap-2 min-w-0 text-sm font-semibold text-slate-500">
                        {(() => {
                            const sectionTitle = {
                                MANAGER_DASHBOARD: t('mapa_operaciones') || 'Mapa de Operaciones',
                                DASHBOARD: t('seguimiento_proyectos') || 'Seguimiento de Proyectos',
                                CLIENTS: t('clients') || 'Clientes',
                                FINANCIAL: t('financial') || 'Finanzas',
                                TEAM: t('team') || 'Equipo',
                                ROLES: t('roles_permisos') || 'Roles y Permisos',
                                RESOURCES: t('control_recursos') || 'Control de Recursos',
                                COMPANY_SETTINGS: t('configuracion') || 'Configuración',
                                HELP: t('help') || 'Ayuda',
                                MASTER: t('master') || 'Panel Maestro',
                            }[view] || view;
                            if (!selectedEvent) return <span className="text-slate-900 font-bold truncate">{sectionTitle}</span>;
                            return (
                                <>
                                    <button onClick={() => setSelectedEventId(null)} title="Volver" className="p-2 -ml-2 text-indigo-600 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-all shrink-0"><ChevronLeft size={18} /></button>
                                    <button onClick={() => setSelectedEventId(null)} className="hover:text-indigo-600 transition-colors truncate shrink-0 max-w-[40%]">{sectionTitle}</button>
                                    <ChevronRight size={14} className="text-slate-300 shrink-0" />
                                    <span className="text-slate-900 font-bold truncate" title={selectedEvent.name}>{selectedEvent.name}</span>
                                </>
                            );
                        })()}
                    </nav>
                    <div className="flex items-center gap-4 shrink-0">
                        {isLoading && <Loader2 className="animate-spin text-indigo-600" size={18} />}
                        <div className="text-right hidden md:block">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t('welcome')}</p>
                            <p className="text-sm font-bold leading-none mt-1">{currentUser.name}</p>
                        </div>
                        <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center font-black text-white shadow-md uppercase">{currentUser.name[0]}</div>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-6 lg:p-10 bg-slate-50/50">
                    {view === 'MASTER' && (
                        <SuperAdminPanel
                            companies={appData.companies}
                            onAdd={() => setActiveModal('CREATE_LICENSE')}
                            onEdit={(c) => { setModalData(c); setActiveModal('EDIT_LICENSE'); }}
                            onDelete={(id) => { if (confirm('¿Seguro? Se borrarán todos los datos de esta empresa')) handleAction('delete_company', { id }); }}
                            onViewUsers={(c) => { setModalData(c); setActiveModal('COMPANY_USERS'); }}
                            appSettings={appSettings}
                            handleAction={handleAction}
                        />
                    )}
                    {view === 'TEAM' && <TeamManager team={appData.team} currentUser={currentUser} onAdd={() => setActiveModal('ADD_TEAM')} onEdit={(u) => { setModalData(u); setActiveModal('EDIT_TEAM'); }} />}
                    {view === 'ROLES' && <RoleManager roles={appData.roles} companyId={currentUser?.company_id} onAdd={() => setActiveModal('ADD_ROLE')} onEdit={(r) => { setModalData(r); setActiveModal('EDIT_ROLE'); }} onDelete={(id) => handleAction('delete_role', { id })} />}
                    {view === 'RESOURCES' && <ResourceManager resources={appData.resources?.filter(r => r.company_id === currentUser.company_id) || []}
                        assignments={appData.resource_assignments} projects={appData.projects}
                        resourceInactivity={appData.resource_inactivity}
                        resourceHoursLog={appData.resource_hours_log || []}
                        onAdd={() => setActiveModal('ADD_RESOURCE')}
                        onEdit={(r) => { setModalData(r); setActiveModal('EDIT_RESOURCE'); }} onDelete={(id) =>
                            handleAction('delete_resource', { id })} onAssign={(r) => { setModalData(r); setActiveModal('ASSIGN_RESOURCE'); }}
                        onEditAssignment={(a) => { setModalData(a); setActiveModal('ASSIGN_RESOURCE'); }}
                        onDeleteAssignment={(id) => handleAction('delete_resource_assignment', { id })}
                        currentUser={currentUser}
                        setActiveModal={setActiveModal}
                        setModalData={setModalData}
                        globalFxRate={globalFxRate}
                    />}
                    {view === 'CLIENTS' && (
                        <ClientsManager
                            clients={appData.clients}
                            projects={appData.projects}
                            contacts={appData.client_contacts}
                            onAdd={() => setActiveModal('ADD_CLIENT')}
                            onEdit={(c) => { setModalData(c); setActiveModal('EDIT_CLIENT'); }}
                            onDelete={(id) => handleAction('delete_client', { id })}
                        />
                    )}
                    {view === 'COMPANY_SETTINGS' && <CompanySettings data={appData} currentUser={currentUser} appSettings={appSettings} handleAction={handleAction} />}
                    {view === 'FINANCIAL' && (
                        <FinancialPanel
                            data={appData}
                            onSelectProject={memoOnSelectProject}
                            setActiveModal={setActiveModal}
                            isViewUsd={uiPrefs.financialMode === 'USD'}
                            currentUser={currentUser}
                            onEditExpense={openEditExpenseModal}
                            t={t}
                            uiPrefs={uiPrefs}
                            setUiPrefs={updateUiPrefs}
                            fNum={fNum}
                            fCur={fCur}
                            globalFxRate={globalFxRate}
                            setGlobalFxRate={setGlobalFxRate}
                        />
                    )}
                    {view === 'HELP' && (
                        <HelpPanel
                            currentUser={currentUser}
                            t={t}
                            aiHelpChatHistory={aiHelpChatHistory}
                            setAiHelpChatHistory={setAiHelpChatHistory}
                            isAiHelpLoading={isAiHelpLoading}
                            setIsAiHelpLoading={setIsAiHelpLoading}
                            view={view}
                        />
                    )}
                    {view === 'MANAGER_DASHBOARD' && (
                        <ManagerDashboard
                            projects={appData.projects}
                            tasks={appData.tasks}
                            expenses={appData.expenses}
                            budgets={appData.budgets}
                            resources={appData.resources || []}
                            assignments={appData.resource_assignments || []}
                            clients={appData.clients || []}
                            onSelectProject={memoOnSelectProject}
                            fDate={fDate}
                            fNum={fNum}
                            fCur={fCur}
                            currentUser={currentUser}
                            handleAction={handleAction}
                            setActiveModal={setActiveModal}
                            setModalData={setModalData}
                            activeModal={activeModal}
                            aiPptChatHistory={aiPptChatHistory}
                            setAiPptChatHistory={setAiPptChatHistory}
                            isAiPptLoading={isAiPptLoading}
                            setIsAiPptLoading={setIsAiPptLoading}
                            generatePPTFromJSON={generatePPTFromJSON}
                            globalFxRate={globalFxRate}
                        />
                    )}

                    {selectedEvent ? (
                        <ProjectDashboard
                            event={selectedEvent}
                            data={appData}
                            tab={tab}
                            setTab={setTab}
                            activeModal={activeModal}
                            setActiveModal={setActiveModal}
                            setModalData={setModalData}
                            modalData={modalData}
                            handleAction={handleAction}
                            userRole={userProjectRole}
                            canEdit={canEdit}
                            t={t}
                            currentUser={currentUser}
                            uiPrefs={uiPrefs}
                            fNum={fNum}
                            fCur={fCur}
                            fDate={fDate}
                            setBudgetLoadType={setBudgetLoadType}
                            setBudgetAllocation={setBudgetAllocation}
                            globalFxRate={globalFxRate}
                            onAddExpense={openAddExpenseModal}
                            onEditExpense={openEditExpenseModal}
                        />
                    ) : (view === 'DASHBOARD' && (
                        <div className="space-y-12 animate-in">
                            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-8 px-4">
                                <div className="space-y-2 pb-2">
                                    <h3 className="text-4xl font-black uppercase tracking-tighter">{t('seguimiento_proyectos') || 'Seguimiento de Proyectos'}</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">{t('gestion_seguimiento') || 'Gestión y Seguimiento de Proyectos Activos'}</p>
                                </div>
                                <div className="flex flex-wrap gap-4 items-center">
                                    {/* Toggle Lista / Dashboard Analítico */}
                                    <div className="flex bg-white p-1.5 rounded-2xl shadow-sm border border-slate-100">
                                        <button onClick={() => setDashView('LIST')} className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all flex items-center gap-2 ${dashView !== 'ANALYTICS' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50'}`}>
                                            <List size={14} /> {t('lista_proyectos') || 'Proyectos'}
                                        </button>
                                        <button onClick={() => setDashView('ANALYTICS')} className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all flex items-center gap-2 ${dashView === 'ANALYTICS' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50'}`}>
                                            <BarChart3 size={14} /> {t('dashboard_analitico') || 'Dashboard'}
                                        </button>
                                    </div>

                                    {dashView !== 'ANALYTICS' && (
                                        <>
                                            {/* Año Filter */}
                                            <div className="flex items-center gap-2 bg-white px-6 py-3 rounded-2xl border border-slate-100 shadow-sm">
                                                <Calendar size={16} className="text-slate-400" />
                                                <select
                                                    value={dashYear}
                                                    onChange={(e) => setDashYear(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value))}
                                                    className="text-[10px] font-black uppercase text-indigo-600 border-none bg-transparent p-0 focus:ring-0"
                                                >
                                                    <option value="ALL">TODOS</option>
                                                    {(() => {
                                                        const currentYear = new Date().getFullYear();
                                                        const projYears = projectsWithMetrics
                                                            .map(p => p.projStart?.getFullYear())
                                                            .filter(y => y && !isNaN(y));
                                                        const years = [...new Set([currentYear, ...projYears])].sort((a, b) => b - a);
                                                        return years.map(y => (
                                                            <option key={y} value={y}>{y}</option>
                                                        ));
                                                    })()}
                                                </select>
                                            </div>
                                        </>
                                    )}

                                    <button
                                        onClick={() => setActiveModal('JOIN_PROJECT')}
                                        className="bg-white px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all text-slate-400 border border-slate-100 hover:border-indigo-400 hover:text-indigo-600 shadow-sm flex items-center gap-3"
                                    >
                                        <GitMerge size={16} /> {t('unirse_proyecto') || 'Unirse a Proyecto'}
                                    </button>
                                    <button
                                        onClick={() => setActiveModal('CREATE_EVENT')}
                                        className="bg-slate-900 px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all text-white shadow-lg hover:bg-slate-800 flex items-center gap-3"
                                    >
                                        <Plus size={16} /> {t('new_project') || 'Nuevo Proyecto'}
                                    </button>
                                </div>
                            </div>

                            {dashView === 'ANALYTICS' ? (
                                <ProjectAnalyticsDashboard
                                    projects={appData.projects || []}
                                    tasks={appData.tasks || []}
                                    t={t}
                                    fCur={fCur}
                                    globalFxRate={globalFxRate}
                                    onSelectProject={(id) => { setSelectedEventId(id); setTab('RESUMEN'); }}
                                />
                            ) : (<>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                    <StatBox
                                        label={dashYear === 'ALL' ? (t('total_proyectos') || "Total Proyectos") : `${t('total_proyectos') || 'Total Proyectos'} ${dashYear}`}
                                        value={projectsWithMetrics.filter(p => {
                                            if (dashYear === 'ALL') return true;
                                            if (!p.projStart || !p.projEnd) return !p.projStart || p.projStart.getFullYear() === dashYear;
                                            return p.projStart.getFullYear() <= dashYear && p.projEnd.getFullYear() >= dashYear;
                                        }).length}
                                        icon={<Briefcase />}
                                        color="indigo"
                                        active={dashFilter === 'ALL'}
                                        onClick={() => setDashFilter('ALL')}
                                    />
                                    <StatBox
                                        label={dashYear === 'ALL' ? (t('in_progress') || "En Curso") : `${t('in_progress') || 'En Curso'} ${dashYear}`}
                                        value={projectsWithMetrics.filter(p => {
                                            if (p.isFinished) return false;
                                            if (dashYear === 'ALL') return true;
                                            if (!p.projStart || !p.projEnd) return !p.projStart || p.projStart.getFullYear() === dashYear;
                                            return p.projStart.getFullYear() <= dashYear && p.projEnd.getFullYear() >= dashYear;
                                        }).length}
                                        icon={<Clock />}
                                        color="emerald"
                                        active={dashFilter === 'PROGRESS'}
                                        onClick={() => setDashFilter('PROGRESS')}
                                    />
                                    <StatBox
                                        label={dashYear === 'ALL' ? (t('completed') || "Finalizados") : `${t('completed') || 'Finalizados'} ${dashYear}`}
                                        value={projectsWithMetrics.filter(p => {
                                            if (!p.isFinished) return false;
                                            if (dashYear === 'ALL') return true;
                                            if (!p.projStart || !p.projEnd) return !p.projStart || p.projStart.getFullYear() === dashYear;
                                            return p.projStart.getFullYear() <= dashYear && p.projEnd.getFullYear() >= dashYear;
                                        }).length}
                                        icon={<CheckSquare />}
                                        color="slate"
                                        active={dashFilter === 'COMPLETED'}
                                        onClick={() => setDashFilter('COMPLETED')}
                                    />
                                </div>

                                {/* Vista por Meses (Resumen) */}
                                <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm">
                                    <h3 className="font-black text-sm uppercase tracking-widest text-slate-400 mb-8 flex items-center gap-2">
                                        <Layers size={18} /> Resumen Mensual {dashYear === 'ALL' ? '' : dashYear}
                                    </h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
                                            const mProjects = projectsWithMetrics.filter(p => {
                                                if (dashYear === 'ALL') return false; // Hide monthly summary in "ALL" mode or handle it
                                                if (!p.projStart || !p.projEnd) return false;
                                                const mStart = new Date(dashYear, m - 1, 1);
                                                const mEnd = new Date(dashYear, m, 0);
                                                const matchesDate = p.projStart <= mEnd && p.projEnd >= mStart;
                                                if (!matchesDate) return false;
                                                if (dashFilter === 'PROGRESS') return !p.isFinished;
                                                if (dashFilter === 'COMPLETED') return p.isFinished;
                                                return true;
                                            });
                                            const mPlanned = mProjects.filter(p => p.avgProg === 0).length;
                                            const mActive = mProjects.filter(p => p.avgProg > 0 && p.avgProg < 100).length;
                                            const mDone = mProjects.filter(p => p.avgProg >= 100).length;

                                            if (mProjects.length === 0) return null;

                                            return (
                                                <div key={m} className={`p-6 rounded-3xl border transition-all cursor-pointer group ${dashMonth === m ? 'bg-indigo-600 border-indigo-400 text-white shadow-xl' : 'bg-slate-50 border-slate-100 hover:border-indigo-300'}`}
                                                    onClick={() => setDashMonth(dashMonth === m ? null : m)}>
                                                    <p className={`text-[10px] font-black uppercase mb-2 ${dashMonth === m ? 'text-white' : 'text-indigo-600'}`}>{new Date(0, m - 1).toLocaleString(lang === 'ES' ? 'es' : 'en', { month: 'long' })}</p>
                                                    <div className="space-y-1">
                                                        <div className="flex justify-between text-[9px] font-bold uppercase"><span>Planeado:</span> <span className={dashMonth === m ? 'text-white' : 'text-slate-600'}>{mPlanned}</span></div>
                                                        <div className="flex justify-between text-[9px] font-bold uppercase"><span>En curso:</span> <span className={dashMonth === m ? 'text-white' : 'text-amber-600'}>{mActive}</span></div>
                                                        <div className="flex justify-between text-[9px] font-bold uppercase"><span>Terminado:</span> <span className={dashMonth === m ? 'text-white' : 'text-emerald-600'}>{mDone}</span></div>
                                                    </div>
                                                    <div className={`mt-4 pt-2 border-t ${dashMonth === m ? 'border-white/20' : 'border-slate-200'}`}>
                                                        <p className={`text-xs font-black ${dashMonth === m ? 'text-white' : 'text-slate-900'}`}>{mProjects.length} Proyectos</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {dashYear !== 'ALL' && projectsWithMetrics.filter(p => {
                                            if (!p.projStart || !p.projEnd) return p.projStart?.getFullYear() === dashYear;
                                            return p.projStart.getFullYear() <= dashYear && p.projEnd.getFullYear() >= dashYear;
                                        }).length === 0 && (
                                            <div className="col-span-full py-12 text-center text-slate-300 font-bold uppercase text-[10px] tracking-widest italic">No hay proyectos registrados para este año. {dashYear}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm">
                                    <h3 className="font-black text-sm uppercase tracking-widest text-slate-400 mb-8 flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                            <TrendingUp size={18} /> Proyectos {dashYear === 'ALL' ? 'Totales' : `del ${dashYear}`} {dashMonth && dashYear !== 'ALL' ? `(${new Date(0, dashMonth - 1).toLocaleString('es', { month: 'long' })})` : ''} {dashFilter !== 'ALL' ? `(${t(dashFilter.toLowerCase() === 'progress' ? 'in_progress' : 'completed')})` : ''}
                                        </div>
                                        {(dashMonth || dashFilter !== 'PROGRESS') && (
                                            <button onClick={() => { setDashMonth(null); setDashFilter('PROGRESS'); }} className="text-[9px] font-black uppercase text-rose-500 hover:underline">Limpiar Filtros</button>
                                        )}
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {projectsWithMetrics
                                            .filter(p => {
                                                const matchesYear = dashYear === 'ALL' || (
                                                    p.projStart && p.projEnd 
                                                    ? (p.projStart.getFullYear() <= dashYear && p.projEnd.getFullYear() >= dashYear)
                                                    : (!p.projStart || p.projStart.getFullYear() === dashYear)
                                                );
                                                if (!matchesYear) return false;
                                                if (dashMonth && dashYear !== 'ALL') {
                                                    const mStart = new Date(dashYear, dashMonth - 1, 1);
                                                    const mEnd = new Date(dashYear, dashMonth, 0);
                                                    if (!(p.projStart <= mEnd && p.projEnd >= mStart)) return false;
                                                }
                                                if (dashFilter === 'PROGRESS') return !p.isFinished;
                                                if (dashFilter === 'COMPLETED') return p.isFinished;
                                                return true;
                                            })
                                            .map(p => {
                                                const avgProg = p.avgProg;

                                                return (
                                                    <div key={p.id} className="p-8 bg-slate-50 rounded-3xl hover:bg-indigo-50 transition-all cursor-pointer group shadow-sm flex flex-col justify-between" onClick={() => { setSelectedEventId(p.id); setTab('RESUMEN'); }}>
                                                        <div className="flex justify-between items-start mb-6">
                                                            <div className="flex items-center gap-4">
                                                                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-600 border border-slate-100 shadow-sm group-hover:scale-110 transition-transform"><Briefcase size={20} /></div>
                                                                <div>
                                                                    <span className="font-black uppercase text-base tracking-tight block leading-tight">{p.name}</span>
                                                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{p.isFinished ? t('completed') : t('planning')} • {fDate(p.projStart?.toISOString())}</span>
                                                                </div>
                                                            </div>
                                                            <ArrowRight size={20} className="text-slate-300 group-hover:text-indigo-600 transition-all" />
                                                        </div>

                                                        {/* task scope summary removed as per request */}

                                                        <div className="space-y-4">
                                                            <div className="flex justify-between items-end mb-1">
                                                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('progress')}</span>
                                                                <span className={`text-xs font-black ${avgProg >= 100 ? 'text-emerald-500' : 'text-indigo-600'}`}>{avgProg}%</span>
                                                            </div>
                                                            <div className="h-2 bg-white rounded-full overflow-hidden shadow-inner">
                                                                <div className={`h-full transition-all duration-1000 ${avgProg >= 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${avgProg}%` }}></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>

                            </>
                            )}
                        </div>
                    ))}

                    {activeModal && !['PPT_CHOICE', 'AI_PPT_PLANNER', 'MDO_BUDGET_MODE', 'MDO_BUDGET_GLOBAL', 'MDO_BUDGET_PHASE', 'BUDGET_SINGLE_ENTRY', 'SAVE_TEMPLATE', 'IMPORT_TEMPLATE', 'PURCHASE_ORDER'].includes(activeModal) && (
                        <Modal title={activeModal === 'AI_PLANNER' ? '' : ((activeModal === 'ADD_EXPENSE' || activeModal === 'EDIT_EXPENSE') ? '' : (t(activeModal.toLowerCase()) !== activeModal.toLowerCase() ? t(activeModal.toLowerCase()) : activeModal.replace(/_/g, ' ')))} onClose={() => { setActiveModal(null); setModalData(null); }} wide={activeModal === 'ADD_EXPENSE' || activeModal === 'EDIT_EXPENSE'}>
                            {activeModal === 'PROFILE' && (
                                <form className="space-y-5" onSubmit={async e => {
                                    e.preventDefault();
                                    const fd = new FormData(e.target);
                                    const newName = fd.get('name');
                                    const newLang = fd.get('lang');
                                    const oldPass = fd.get('old_pass');
                                    const newPass = fd.get('new_pass');

                                    // Si se intenta cambiar la contraseña
                                    if (oldPass || newPass) {
                                        if (!oldPass || !newPass) {
                                            alert("Para cambiar la contraseña debes completar ambos campos (actual y nueva)");
                                            return;
                                        }

                                        try {
                                            const res = await fetch(`${API_URL}?action=change_password`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({
                                                    user_id: currentUser.id,
                                                    old_password: oldPass,
                                                    new_password: newPass
                                                })
                                            });
                                            const data = await res.json();
                                            if (data.status !== 'success') {
                                                alert(data.message);
                                                return;
                                            }
                                        } catch (err) {
                                            alert("Error al conectar con el servidor para cambiar la contraseña");
                                            return;
                                        }
                                    }

                                    // Mock update backend (would need API support)
                                    const updated = { ...currentUser, name: newName, language: newLang };
                                    setCurrentUser(updated);
                                    localStorage.setItem('em_session', JSON.stringify(updated));
                                    setLang(newLang);
                                    setActiveModal(null);
                                    alert("Perfil actualizado correctamente.");
                                }}>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase text-slate-400 ml-2">{t('name')}</label>
                                        <input name="name" required className="input-base" defaultValue={currentUser.name} />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase text-slate-400 ml-2">{t('language')}</label>
                                        <select name="lang" className="input-base font-bold" defaultValue={lang}>
                                            <option value="ES">Español (Latam)</option>
                                            <option value="EN">English (US)</option>
                                        </select>
                                    </div>

                                    <div className="pt-4 border-t border-slate-100 mt-4 space-y-5">
                                        <h5 className="text-[10px] font-black uppercase text-indigo-600 tracking-widest">Seguridad</h5>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-slate-400 ml-2">Contraseña Actual</label>
                                            <input name="old_pass" type="password" className="input-base" placeholder="Dejar en blanco para no cambiar" />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-slate-400 ml-2">Nueva Contraseña</label>
                                            <input name="new_pass" type="password" className="input-base" placeholder="Nueva contraseña" />
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t border-slate-100 mt-4 space-y-5">
                                        <h5 className="text-[10px] font-black uppercase text-indigo-600 tracking-widest">Preferencias de Visualización</h5>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-slate-400 ml-2">Decimales en Números</label>
                                            <input
                                                type="number"
                                                min="0" max="4"
                                                className="input-base font-bold"
                                                defaultValue={uiPrefs.decimals}
                                                onChange={(e) => updateUiPrefs({ decimals: parseInt(e.target.value) || 0 })}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-slate-400 ml-2">Tamaño Letra Gantt (px)</label>
                                            <div className="flex items-center gap-4">
                                                <input
                                                    type="range"
                                                    min="8" max="16"
                                                    className="flex-1 h-2 bg-indigo-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                                    defaultValue={uiPrefs.ganttFontSize}
                                                    onChange={(e) => updateUiPrefs({ ganttFontSize: parseInt(e.target.value) })}
                                                />
                                                <span className="w-8 text-center font-black text-indigo-600">{uiPrefs.ganttFontSize}px</span>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-slate-400 ml-2">Formato de Fechas</label>
                                            <select
                                                className="input-base font-bold"
                                                value={uiPrefs.dateFormat}
                                                onChange={(e) => updateUiPrefs({ dateFormat: e.target.value })}
                                            >
                                                <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                                                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                            </select>
                                        </div>
                                    </div>

                                    <button type="submit" className="btn-primary w-full py-5 uppercase font-black tracking-widest mt-4">{t('save')}</button>
                                </form>
                            )}
                            {activeModal === 'CREATE_EVENT' && (
                                <form className="space-y-5" onSubmit={async e => {
                                    e.preventDefault();
                                    const fd = new FormData(e.target);

                                    let lat = parseFloat(fd.get('latitude'));
                                    let lng = parseFloat(fd.get('longitude'));

                                    // Si no hay lat/lng manual (NaN o 0), intentamos geocodificar
                                    if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
                                        const coords = await fetchCoordinates(fd.get('address'), fd.get('city'), fd.get('province_region'), fd.get('country'));
                                        if (coords) {
                                            lat = coords.lat;
                                            lng = coords.lng;
                                        }
                                    }

                                    handleAction('create_event', {
                                        name: fd.get('name'),
                                        type: fd.get('type'),
                                        description: fd.get('description'),
                                        date: fd.get('date'),
                                        end_date: fd.get('end_date'),
                                        location: fd.get('location'),
                                        address: fd.get('address'),
                                        city: fd.get('city'),
                                        province_region: fd.get('province_region'),
                                        country: fd.get('country'),
                                        latitude: lat,
                                        longitude: lng,
                                        currency: fd.get('currency'),
                                        client_id: fd.get('client_id'),
                                        budget_total_contract: fd.get('budget_total_contract'),
                                        block_overallocation: fd.get('block_overallocation') === 'on' ? 1 : 0,
                                        sync_mdo_resources: fd.get('sync_mdo_resources') === 'on' ? 1 : 0,
                                        exchange_rate: fd.get('exchange_rate') || 1.0
                                    });
                                }}>
                                    <input name="name" required className="input-base" placeholder="Nombre del Proyecto (ej: Obra San Juan)" />
                                    <textarea name="description" className="input-base h-24 pt-4" placeholder="Breve descripción del proyecto..."></textarea>
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Tipo de Proyecto</label>
                                        <select name="type" className="input-base font-bold" required>
                                            <option value="">-- Seleccionar tipo --</option>
                                            <option value="CONSTRUCCION">Construcción</option>
                                            <option value="EVENTO">Evento Social</option>
                                            <option value="CORPORATIVO">Corporativo</option>
                                            <option value="OTRO">Otro</option>
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Inicio</label>
                                            <input name="date" type="date" required className="input-base" />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Fin (Est.)</label>
                                            <input name="end_date" type="date" className="input-base" />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Asociar Cliente</label>
                                        <select name="client_id" className="input-base font-black">
                                            <option value="">Sin cliente asignado...</option>
                                            {appData.clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <input name="location" required className="input-base" placeholder="Nombre Ubicación (ej: Obra Olivos)" />
                                        <input name="address" className="input-base" placeholder="Dirección (Calle y Nro)" />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <input name="city" className="input-base" placeholder="Localidad / Ciudad" />
                                        <input name="province_region" className="input-base" placeholder="Provincia / Región" />
                                        <input name="country" className="input-base" placeholder="País" defaultValue="Argentina" />
                                    </div>
                                    <input type="hidden" name="latitude" />
                                    <input type="hidden" name="longitude" />
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-black">
                                        <div className="space-y-1">
                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Moneda del Proyecto</label>
                                            <select name="currency" className="input-base border-2 border-slate-900 bg-slate-900 text-white rounded-2xl">
                                                <option value="ARS">ARS - Pesos</option>
                                                <option value="USD">USD - Dólares</option>
                                            </select>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Total Contrato</label>
                                            <input name="budget_total_contract" type="number" step="0.01" className="input-base border-emerald-100 bg-emerald-50/20 text-emerald-700" placeholder="0.00" />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[9px] font-black text-rose-500 uppercase ml-2">Cotización de Obra (Ref. Dolar)</label>
                                            <input name="exchange_rate" type="number" step="0.01" required className="input-base border-2 border-rose-100 bg-rose-50/20 text-rose-600" defaultValue={globalFxRate || 1000} placeholder="Ej: 1400" />
                                        </div>
                                    </div>

                                    <div className="bg-slate-50 p-6 rounded-3xl space-y-4">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Configuración de Presupuesto</p>
                                        <div className="flex flex-col gap-3">
                                            <label className="flex items-center gap-3 cursor-pointer group">
                                                <input type="checkbox" name="sync_mdo_resources" className="w-5 h-5 rounded-lg border-2 border-slate-200 text-indigo-600 focus:ring-0 transition-all" />
                                                <div>
                                                    <span className="text-[11px] font-bold text-slate-600 uppercase group-hover:text-indigo-600 transition-colors">Sincronizar Presupuesto MDO con Recursos</span>
                                                    <p className="text-[9px] text-slate-400 mt-0.5">El presupuesto de mano de obra se calculará automáticamente según los recursos asignados</p>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                    <button type="submit" className="btn-primary w-full py-5 uppercase font-black tracking-widest">Lanzar Proyecto</button>
                                </form>
                            )}
                            {(activeModal === 'ADD_CLIENT' || activeModal === 'EDIT_CLIENT') && (
                                <div className="space-y-6">
                                    <form className="space-y-5" onSubmit={e => {
                                        e.preventDefault();
                                        const fd = new FormData(e.target);
                                        const payload = {
                                            name: fd.get('name'),
                                            email: fd.get('email'),
                                            phone: fd.get('phone'),
                                            logo_url: fd.get('logo_url_hidden') || null,
                                            address: fd.get('address'),
                                            city: fd.get('city'),
                                            province: fd.get('province'),
                                            country: fd.get('country'),
                                            oc_mandatory: fd.get('oc_mandatory') === 'on' ? 1 : 0,
                                            cuit: fd.get('cuit')
                                        };
                                        if (activeModal === 'EDIT_CLIENT') {
                                            handleAction('update_client', { ...payload, id: modalData.id });
                                        } else {
                                            handleAction('add_client', payload);
                                        }
                                    }}>
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nombre / Razón Social</label>
                                                    <input name="name" required className="input-base" placeholder="Ej: Eventos S.A." defaultValue={modalData?.name} />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">CUIT</label>
                                                    <input name="cuit" className="input-base" placeholder="Ej: 30-71234567-9" defaultValue={modalData?.cuit} />
                                                </div>
                                            </div>
                                            <div className="bg-amber-50/50 p-6 rounded-[2rem] border border-amber-100/50 space-y-4">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <div className="w-8 h-8 bg-amber-100 text-amber-600 rounded-lg flex items-center justify-center"><ShieldCheck size={18} /></div>
                                                    <h5 className="text-[10px] font-black uppercase text-amber-700 tracking-widest">Parámetros de Cobro y OC</h5>
                                                </div>
                                                <div className="flex flex-col md:flex-row gap-6">
                                                    <div className="flex items-center gap-3 cursor-pointer group" onClick={() => {
                                                        const chk = document.getElementById('chk_can_oc');
                                                        if (chk) chk.checked = !chk.checked;
                                                    }}>
                                                        <input id="chk_can_oc" name="can_use_oc" type="checkbox" className="w-6 h-6 rounded-lg border-amber-300 text-amber-500 focus:ring-amber-500" defaultChecked={modalData?.can_use_oc == 1 || modalData?.requires_oc == 1} />
                                                        <div className="flex flex-col">
                                                            <span className="text-[11px] font-black uppercase text-slate-700 leading-none">Habilitar OC</span>
                                                            <span className="text-[9px] font-bold text-slate-400 uppercase mt-1">Permite vincular OCs a este cliente</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3 cursor-pointer group" onClick={() => {
                                                        const chk = document.getElementById('chk_oc_must');
                                                        if (chk) chk.checked = !chk.checked;
                                                    }}>
                                                        <input id="chk_oc_must" name="oc_mandatory" type="checkbox" className="w-6 h-6 rounded-lg border-amber-300 text-amber-500 focus:ring-amber-500" defaultChecked={modalData?.oc_mandatory == 1} />
                                                        <div className="flex flex-col">
                                                            <span className="text-[11px] font-black uppercase text-slate-700 leading-none">OC Obligatoria</span>
                                                            <span className="text-[9px] font-bold text-slate-400 uppercase mt-1">Bloquea cobros sin OC con saldo</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email de Contacto</label>
                                                    <input name="email" type="email" className="input-base" placeholder="info@cliente.com" defaultValue={modalData?.email} />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Teléfono</label>
                                                    <input name="phone" className="input-base" placeholder="+54 9..." defaultValue={modalData?.phone} />
                                                </div>
                                            </div>

                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Dirección / Ubicación (Para Mapa)</label>
                                                <input name="address" className="input-base" placeholder="Calle y Nro" defaultValue={modalData?.address} />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Ciudad</label>
                                                    <input name="city" className="input-base" placeholder="Ciudad" defaultValue={modalData?.city} />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Provincia</label>
                                                    <input name="province" className="input-base" placeholder="Provincia" defaultValue={modalData?.province_region || modalData?.province} />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">País</label>
                                                    <input name="country" className="input-base" placeholder="País" defaultValue={modalData?.country || 'Argentina'} />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Logo del Cliente</label>
                                                    <div className="flex items-center gap-4">
                                                        {modalData?.logo_url ? (
                                                            <div className="w-16 h-16 bg-white border border-slate-200 rounded-xl p-2 relative group">
                                                                <img src={modalData.logo_url} className="w-full h-full object-contain" alt="Logo" />
                                                                <button type="button" onClick={() => {
                                                                    if (confirm("¿Eliminar logo del cliente?")) {
                                                                        if (activeModal === 'EDIT_CLIENT') {
                                                                            handleAction('update_client_logo', { id: modalData.id, logo_url: '' });
                                                                        }
                                                                        setModalData({ ...modalData, logo_url: '' });
                                                                    }
                                                                }} className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <X size={12} />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="w-16 h-16 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center text-slate-300">
                                                                <Image size={24} />
                                                            </div>
                                                        )}

                                                        <div className="flex-1">
                                                            {/* Hidden input to reliably capture logo_url in FormData, avoiding React closure stale state */}
                                                            <input type="hidden" name="logo_url_hidden" defaultValue={modalData?.logo_url || ''} ref={el => { if (el) el.value = modalData?.logo_url || ''; }} />
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                className="hidden"
                                                                id="logoUpload"
                                                                onChange={(e) => {
                                                                    const file = e.target.files[0];
                                                                    if (!file) return;

                                                                    const formData = new FormData();
                                                                    formData.append('action', 'upload_logo');
                                                                    formData.append('logo', file);

                                                                    // Show loading state if needed
                                                                    const btn = e.target.nextElementSibling;
                                                                    const originalText = btn.innerHTML;
                                                                    btn.innerHTML = '<i class="lucide-loader-2 animate-spin"></i> Subiendo...';
                                                                    btn.disabled = true;

                                                                    const token = localStorage.getItem('em_token');
                                                                    const headers = {};
                                                                    if (token) headers['Authorization'] = `Bearer ${token}`;

                                                                    fetch(API_URL, {
                                                                        method: 'POST',
                                                                        body: formData,
                                                                        headers: headers
                                                                    })
                                                                        .then(res => res.json())
                                                                        .then(data => {
                                                                            if (data.status === 'success') {
                                                                                const url = data.url;
                                                                                // Update both the hidden input AND modalData state
                                                                                const hiddenInput = e.target.closest('form')?.querySelector('input[name="logo_url_hidden"]');
                                                                                if (hiddenInput) hiddenInput.value = url;
                                                                                setModalData(prev => ({ ...prev, logo_url: url }));
                                                                            } else {
                                                                                alert("Error al subir imagen: " + data.message);
                                                                            }
                                                                        })
                                                                        .catch(err => alert("Error de conexión al subir imagen"))
                                                                        .finally(() => {
                                                                            btn.innerHTML = originalText;
                                                                            btn.disabled = false;
                                                                            e.target.value = ''; // Reset file input
                                                                        });
                                                                }}
                                                            />
                                                            <button type="button" onClick={() => document.getElementById('logoUpload').click()} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors">
                                                                Subir Imagen
                                                            </button>
                                                            <p className="text-[9px] text-slate-400 mt-1 ml-1">JPG, PNG o WEBP. Máx 2MB.</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <button type="submit" className="btn-primary w-full py-5 uppercase font-black tracking-widest">
                                            {activeModal === 'EDIT_CLIENT' ? 'Actualizar Datos Principales' : 'Guardar y Continuar'}
                                        </button>
                                        {activeModal === 'ADD_CLIENT' && (
                                            <p className="text-[9px] text-center text-slate-400 font-bold uppercase tracking-widest italic mt-2">Podrás agregar contactos específicos una vez creado el cliente.</p>
                                        )}
                                    </form>

                                    {activeModal === 'EDIT_CLIENT' && (
                                        <div className="pt-8 border-t border-slate-100 space-y-6">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center"><Users size={16} /></div>
                                                <h5 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Gestión de Contactos</h5>
                                            </div>

                                            <div className="space-y-3">
                                                {(appData.client_contacts || [])
                                                    .filter(c => c.client_id === modalData.id)
                                                    .sort((a, b) => (a.priority || 0) - (b.priority || 0))
                                                    .map((contact, idx, filtered) => (
                                                        <div key={contact.id} className="flex justify-between items-center bg-slate-50 p-4 rounded-2xl border border-transparent hover:border-slate-100 transition-all gap-4">
                                                            <div className="flex items-center gap-4 flex-1">
                                                                <div className="flex flex-col gap-1 shrink-0">
                                                                    <button
                                                                        disabled={idx === 0}
                                                                        onClick={() => {
                                                                            const prev = filtered[idx - 1];
                                                                            handleAction('reorder_client_contacts', {
                                                                                client_id: modalData.id,
                                                                                orders: [
                                                                                    { id: contact.id, priority: (prev.priority || 0) },
                                                                                    { id: prev.id, priority: (contact.priority || 0) + 1 }
                                                                                ]
                                                                            });
                                                                        }}
                                                                        className="p-1 hover:text-indigo-600 disabled:opacity-20"
                                                                    >
                                                                        <ChevronUp size={14} />
                                                                    </button>
                                                                    <button
                                                                        disabled={idx === filtered.length - 1}
                                                                        onClick={() => {
                                                                            const next = filtered[idx + 1];
                                                                            handleAction('reorder_client_contacts', {
                                                                                client_id: modalData.id,
                                                                                orders: [
                                                                                    { id: contact.id, priority: (next.priority || 0) + 1 },
                                                                                    { id: next.id, priority: (contact.priority || 0) }
                                                                                ]
                                                                            });
                                                                        }}
                                                                        className="p-1 hover:text-indigo-600 disabled:opacity-20"
                                                                    >
                                                                        <ChevronDown size={14} />
                                                                    </button>
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs font-black uppercase text-slate-700 leading-none">{contact.name}</p>
                                                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter mt-1">{contact.position || 'Representante'} • {contact.phone || contact.email || 'Sin datos'}</p>
                                                                </div>
                                                            </div>
                                                            <button type="button" onClick={() => { if (confirm("¿Eliminar contacto?")) handleAction('delete_client_contact', { id: contact.id }); }} className="text-rose-400 hover:text-rose-600 p-2 transition-colors shrink-0"><Trash2 size={16} /></button>
                                                        </div>
                                                    ))}
                                            </div>

                                            <div className="bg-indigo-50/30 p-6 rounded-[2rem] border border-indigo-100/50 space-y-4">
                                                <p className="text-[9px] font-black uppercase text-indigo-400 tracking-widest">Agregar Nuevo Contacto</p>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <input id="new_con_name" className="input-base text-xs py-3" placeholder="Nombre Completo" />
                                                    <input id="new_con_pos" className="input-base text-xs py-3" placeholder="Cargo / Puesto" />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <input id="new_con_phone" className="input-base text-xs py-3" placeholder="Teléfono" />
                                                    <input id="new_con_email" className="input-base text-xs py-3" placeholder="Correo Electrónico" />
                                                </div>
                                                <button type="button" onClick={() => {
                                                    const name = document.getElementById('new_con_name').value;
                                                    const pos = document.getElementById('new_con_pos').value;
                                                    const ph = document.getElementById('new_con_phone').value;
                                                    const em = document.getElementById('new_con_email').value;
                                                    if (!name) return alert("El nombre es obligatorio");
                                                    handleAction('add_client_contact', { client_id: modalData.id, name, position: pos, phone: ph, email: em });
                                                    // Reset
                                                    ['new_con_name', 'new_con_pos', 'new_con_phone', 'new_con_email'].forEach(id => document.getElementById(id).value = '');
                                                }} className="w-full bg-slate-900 text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all">Vincular Contacto</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {activeModal === 'SET_BUDGET' && (
                                <form className="space-y-5" onSubmit={e => {
                                    e.preventDefault();
                                    const fd = new FormData(e.target);
                                    handleAction('save_budget', { category_name: fd.get('cat'), amount: fd.get('amount') });
                                }}>
                                    <select name="cat" className="input-base font-black uppercase" defaultValue={modalData?.category_id || modalData?.category_name}>
                                        {appData.categories.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                                    </select>
                                    <input name="amount" type="number" step="0.01" required className="input-base" placeholder="Presupuesto Máximo $" defaultValue={modalData?.amount} />
                                    <button type="submit" className="btn-primary w-full py-5 uppercase font-black">Asignar Fondos</button>
                                </form>
                            )}
                            {/* LOAD_BUDGET modal removed - replaced by MDO_BUDGET_MODE workflow */}
                            {(activeModal === 'ADD_EXPENSE' || activeModal === 'EDIT_EXPENSE') && (
                                <form key={activeModal + (modalData?.id || 'new')} className="space-y-5" onSubmit={async e => {
                                    e.preventDefault();
                                    const fd = new FormData(e.target);
                                    const isLinked = fd.get('link_payment') === 'on';
                                    const mainAmount = Number(fd.get('amount'));
                                    const payAmount = isLinked ? Number(fd.get('payment_amount')) : 0;

                                    if (isLinked && payAmount > mainAmount) {
                                        alert("El monto pagado no puede ser superior al cobrado.");
                                        return;
                                    }

                                    if (expenseType === 'MATERIALES') {
                                        // Validar que todos los conceptos tengan un material seleccionado si es tipo MATERIALES
                                        for (const split of expenseSplits) {
                                            if (!split.material) {
                                                alert("Debe seleccionar un material del cómputo para descontar del stock.");
                                                return;
                                            }
                                        }
                                    }

                                    const rawAmount = Number(fd.get('amount'));
                                    let finalFx = Number(fd.get('fx') || 1);
                                    const projCurrency = selectedEvent?.base_currency || selectedEvent?.display_currency || 'ARS';

                                    // Pass the pure FX rate. The backend (api.php) will handle the math
                                    // based on the provided project and movement currencies.

                                    const payload = {
                                        description: fd.get('desc'),
                                        category_name: fd.get('cat'),
                                        amount: fd.get('amount'),
                                        currency: movementCurrency,
                                        exchange_rate: finalFx,
                                        date: fd.get('date'),
                                        supplier: fd.get('supplier'),
                                        invoice: fd.get('invoice'),
                                        linked_material_id: expenseSplits.length === 1 ? expenseSplits[0].material_id : null,
                                        linked_material_name: expenseSplits.length === 1 ? expenseSplits[0].material : null,
                                        linked_material_qty: expenseSplits.length === 1 ? expenseSplits[0].mat_qty : null,
                                        expense_type: expenseType,
                                        movement_type: movementType,
                                        generate_linked_payment: isLinked,
                                        purchase_order_id: movementType === 'INCOME' ? fd.get('purchase_order_id') : null
                                    };

                                    // Validation: Required OC if client demands it
                                    const client = appData.clients?.find(c => c.id === selectedEvent.client_id);
                                    const ocMandatory = client?.oc_mandatory || client?.requires_oc;

                                    if (movementType === 'INCOME' && ocMandatory) {
                                        if (!payload.purchase_order_id) {
                                            alert("ERROR: Este cliente requiere vincular una Órden de Compra para registrar cobros.");
                                            return;
                                        }
                                        const selectedOc = (appData.purchase_orders || []).find(oc => oc.id === payload.purchase_order_id);
                                        if (!selectedOc || Number(selectedOc.balance) <= 0) {
                                            alert("ERROR: La Órden de Compra seleccionada no existe o no tiene saldo disponible.");
                                            return;
                                        }
                                    }

                                    if (activeModal === 'EDIT_EXPENSE') {
                                        await handleAction('update_expense', { ...payload, id: modalData.id });
                                    } else if (expenseSplits.length > 1) {
                                        // Submit multiple expenses
                                        setIsLoading(true);
                                        try {
                                            for (let i = 0; i < expenseSplits.length; i++) {
                                                const split = expenseSplits[i];
                                                if (!split.amount || !split.cat) continue;
                                                const splitPayload = {
                                                    ...payload,
                                                    description: split.desc || payload.description,
                                                    category_name: split.cat,
                                                    amount: split.amount,
                                                    linked_material_id: split.material_id,
                                                    linked_material_name: split.material,
                                                    linked_material_qty: split.mat_qty
                                                };

                                                // Only the absolute last action closes the modal
                                                const isLastAction = (i === expenseSplits.length - 1) && !isLinked;

                                                if (isLastAction) {
                                                    await handleAction('add_expense', splitPayload);
                                                } else {
                                                    // Call API directly to add split without closing modal
                                                    await fetch(`${API_URL}?action=add_expense`, {
                                                        method: 'POST',
                                                        headers: { 'Content-Type': 'application/json' },
                                                        body: JSON.stringify({
                                                            event_id: selectedEventId,
                                                            company_id: currentUser?.company_id || currentUser?.companyId,
                                                            actor_id: currentUser?.id,
                                                            ...splitPayload
                                                        })
                                                    });
                                                }
                                            }

                                            if (isLinked) {
                                                const paymentPayload = {
                                                    ...payload,
                                                    description: `[PAGO VINCULADO] ${payload.description}`,
                                                    amount: payAmount,
                                                    movement_type: 'EXPENSE',
                                                    generate_linked_payment: false
                                                };
                                                // This final action will sync and close the modal
                                                await handleAction('add_expense', paymentPayload);
                                            }
                                        } catch (e) {
                                            alert("Error al procesar split: " + e.message);
                                        } finally {
                                            setIsLoading(false);
                                        }
                                    } else {
                                        await handleAction('add_expense', payload);

                                        if (isLinked) {
                                            const paymentPayload = {
                                                ...payload,
                                                description: `[PAGO VINCULADO] ${payload.description}`,
                                                amount: payAmount,
                                                movement_type: 'EXPENSE',
                                                generate_linked_payment: false
                                            };
                                            await handleAction('add_expense', paymentPayload);
                                        }
                                    }
                                    setLinkPayment(false); // Reset
                                    setPaymentAmount('');
                                }}>
                                    {/* Cabecera Moderna */}
                                    <div className="flex justify-between items-start mb-8">
                                        <div className="flex flex-col">
                                            <h2 className="text-3xl font-black uppercase tracking-tighter text-slate-900 leading-none">
                                                {activeModal === 'EDIT_EXPENSE' ? 'Editar Registro' : (movementType === 'EXPENSE' ? 'Cargar Pago' : 'Cargar Cobro')}
                                            </h2>
                                            <div className="w-24 h-1.5 bg-indigo-600 mt-2 rounded-full"></div>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-3">Detalle del movimiento financiero</p>
                                        </div>
                                        {activeModal === 'EDIT_EXPENSE' && (
                                            <button
                                                type="button"
                                                onClick={() => { if (confirm("¿Eliminar este registro permanentemente?")) handleAction('delete_expense', { id: modalData.id }) }}
                                                className="w-12 h-12 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center hover:bg-rose-100 hover:text-rose-600 transition-all shadow-sm"
                                            >
                                                <Trash2 size={20} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Toggles de Tipo (Estilo Premium) */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 bg-slate-900 p-1.5 rounded-[1.5rem] mb-8 shadow-xl">
                                        <button
                                            type="button"
                                            onClick={() => setMovementType('EXPENSE')}
                                            className={`flex items-center justify-center gap-2 py-4 rounded-xl text-[11px] font-extrabold uppercase transition-all duration-300 ${movementType === 'EXPENSE' ? 'bg-indigo-600 text-white shadow-lg scale-[1.02]' : 'text-slate-500 hover:text-slate-300'}`}
                                        >
                                            <Landmark size={18} /> PAGO (EGRESO)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setMovementType('INCOME')}
                                            className={`flex items-center justify-center gap-2 py-4 rounded-xl text-[11px] font-extrabold uppercase transition-all duration-300 ${movementType === 'INCOME' ? 'bg-emerald-600 text-white shadow-lg scale-[1.02]' : 'text-slate-500 hover:text-slate-300'}`}
                                        >
                                            <Wallet size={18} /> COBRO (INGRESO)
                                        </button>
                                    </div>

                                    <div className="space-y-6">
                                        {/* Categoría de Gasto */}
                                        <div className="grid grid-cols-2 bg-slate-100 p-1.5 rounded-2xl gap-1">
                                            <button
                                                type="button"
                                                onClick={() => setExpenseType('MATERIALES')}
                                                className={`flex items-center justify-center gap-2 py-3 rounded-xl text-[10px] font-black uppercase transition-all ${expenseType === 'MATERIALES' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
                                            >
                                                <Package size={16} /> MATERIALES
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setExpenseType('MANO_OBRA')}
                                                className={`flex items-center justify-center gap-2 py-3 rounded-xl text-[10px] font-black uppercase transition-all ${expenseType === 'MANO_OBRA' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
                                            >
                                                <Users size={16} /> MANO DE OBRA
                                            </button>
                                        </div>

                                        {/* Descripción Principal */}
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">¿Qué se está {movementType === 'EXPENSE' ? 'pagando' : 'cobrando'}?</label>
                                            <input
                                                name="desc"
                                                required
                                                className="input-base text-lg py-6"
                                                placeholder={expenseType === 'MANO_OBRA' ? "Ej: Pago Quincena Nro 4" : "Ej: Compra de cemento y arena"}
                                                value={movementDesc}
                                                onChange={e => setMovementDesc(e.target.value)}
                                            />
                                        </div>

                                        {/* Campos de Pago (Proveedor / Recibo) */}
                                        {movementType === 'EXPENSE' && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Proveedor / Destino</label>
                                                    <input name="supplier" className="input-base" placeholder="Nombre del proveedor" value={movementSupplier} onChange={e => setMovementSupplier(e.target.value)} />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Nro. de Recibo / Factura</label>
                                                    <input name="invoice" className="input-base" placeholder="0001-00001234" value={movementInvoice} onChange={e => setMovementInvoice(e.target.value)} />
                                                </div>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Rubro / Fase Principal</label>
                                                <select
                                                    name="cat"
                                                    className="input-base font-black cursor-pointer appearance-none"
                                                    required={expenseSplits.length <= 1}
                                                    defaultValue={modalData?.category_name}
                                                    id="expense_cat_select"
                                                    onChange={(e) => {
                                                        if (expenseSplits.length === 1) {
                                                            setExpenseSplits(prev => [{ ...prev[0], cat: e.target.value }]);
                                                        }
                                                    }}
                                                >
                                                    <option value="">Seleccionar...</option>
                                                    {expenseType === 'MANO_OBRA' ? (
                                                        <>
                                                            {selectedEvent?.mdo_budget_mode === 'GLOBAL' && (
                                                                <option value="GLOBAL">Presupuesto Global MDO</option>
                                                            )}
                                                            {(appData.tasks || [])
                                                                .filter(t => t.event_id === selectedEventId && !t.isParent)
                                                                .sort((a, b) => sortPhasesNumerically(a.title, b.title))
                                                                .map(t => (
                                                                    <option key={t.id} value={t.title || t.id}>{t.title || 'Fase Desconocida'}</option>
                                                                ))}
                                                        </>
                                                    ) : (
                                                        appData.categories
                                                            .filter(r => r.event_id === selectedEventId || (appData.budgets || []).some(b => b.category_id === r.id && b.event_id === selectedEventId))
                                                            .sort((a, b) => sortPhasesNumerically(a.name, b.name))
                                                            .map(r => <option key={r.id} value={r.name}>{r.name}</option>)
                                                    )}
                                                </select>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Fecha</label>
                                                <input name="date" type="date" required className="input-base font-black" value={movementDate} onChange={e => setMovementDate(e.target.value)} />
                                            </div>
                                            {movementType === 'INCOME' && (
                                                <div className="md:col-span-2 space-y-1 animate-in slide-in-from-top-2">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 flex items-center gap-2">
                                                        <Target size={12} className="text-indigo-500" /> Vincular a Orden de Compra (OC)
                                                    </label>
                                                    <select
                                                        name="purchase_order_id"
                                                        className="input-base font-black text-indigo-600 bg-indigo-50/20 border-indigo-100"
                                                        defaultValue={modalData?.purchase_order_id}
                                                    >
                                                        <option value="">-- Sin Vincular --</option>
                                                        {(appData.purchase_orders || [])
                                                            .filter(oc => oc.event_id === selectedEventId && (oc.status === 'ACTIVE' || oc.id === modalData?.purchase_order_id))
                                                            .map(oc => (
                                                                <option key={oc.id} value={oc.id}>
                                                                    {oc.oc_number} - {oc.description?.substring(0, 30)}... (Saldo: {fCur(oc.balance, oc.currency || 'ARS')})
                                                                </option>
                                                            ))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>

                                        {/* Doble Imputación Toggle Premium */}
                                        {movementType === 'INCOME' && activeModal === 'ADD_EXPENSE' && (
                                            <div className={`p-4 rounded-[2rem] border-2 transition-all flex items-center justify-between ${linkPayment ? 'bg-indigo-600 border-indigo-600 shadow-xl' : 'bg-white border-slate-100 hover:border-indigo-200'}`}>
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${linkPayment ? 'bg-white/20 text-white' : 'bg-indigo-50 text-indigo-600'}`}>
                                                        <RefreshCcw size={24} />
                                                    </div>
                                                    <div>
                                                        <p className={`text-[10px] font-black uppercase tracking-widest ${linkPayment ? 'text-white/80' : 'text-slate-400'}`}>Doble Imputación</p>
                                                        <p className={`text-sm font-black ${linkPayment ? 'text-white' : 'text-slate-900'}`}>¿Registrar pago vinculado?</p>
                                                    </div>
                                                </div>
                                                <div
                                                    onClick={() => {
                                                        const newVal = !linkPayment;
                                                        setLinkPayment(newVal);
                                                        if (newVal) {
                                                            const amt = document.querySelector('input[name="amount"]')?.value;
                                                            setPaymentAmount(amt || '');
                                                        }
                                                    }}
                                                    className={`w-16 h-8 rounded-full p-1 cursor-pointer transition-all ${linkPayment ? 'bg-white' : 'bg-slate-200'}`}
                                                >
                                                    <div className={`w-6 h-6 rounded-full shadow-md transition-all ${linkPayment ? 'bg-indigo-600 translate-x-8' : 'bg-white'}`}></div>
                                                </div>
                                                <input name="link_payment" type="checkbox" className="hidden" checked={linkPayment} readOnly />
                                            </div>
                                        )}

                                        {linkPayment && movementType === 'INCOME' && (
                                            <div className="bg-indigo-50 p-6 rounded-[2rem] border border-indigo-100 animate-in slide-in-from-top-4 flex items-center gap-6">
                                                <div className="flex-1">
                                                    <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest ml-2 mb-2 block">Importe a Pagar</label>
                                                    <div className="relative">
                                                        <span className="absolute left-4 top-4 text-indigo-300 font-bold">$</span>
                                                        <input
                                                            name="payment_amount"
                                                            type="number"
                                                            step="0.01"
                                                            className="input-base pl-10 py-4 font-black text-indigo-600 bg-white border-indigo-100 text-xl"
                                                            value={paymentAmount}
                                                            onChange={(e) => setPaymentAmount(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-[9px] font-black text-indigo-300 uppercase block">Saldo Vinculado</span>
                                                    <span className="text-xs font-bold text-indigo-400 italic">Automático</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Distribución de Conceptos */}
                                        {activeModal === 'ADD_EXPENSE' && (
                                            <div className="bg-white rounded-[2.5rem] border border-slate-100 overflow-hidden shadow-sm">
                                                <div className="p-6 bg-slate-50/50 flex justify-between items-center border-b border-slate-100">
                                                    <div>
                                                        <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Distribución de Conceptos</p>
                                                        <p className="text-[9px] font-semibold text-slate-400 italic">(Haz clic en + para dividir en múltiples rubros)</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setExpenseSplits(prev => [...prev, { id: Date.now(), cat: '', amount: '', material: '', mat_qty: '', desc: '' }])}
                                                        className="bg-indigo-600 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-lg shadow-indigo-100 flex items-center gap-2"
                                                    >
                                                        <Plus size={16} /> Agregar concepto
                                                    </button>
                                                </div>

                                                <div className="p-6 space-y-4">
                                                    {expenseSplits.map((split, idx) => (
                                                        <div key={split.id} className="bg-slate-50 p-6 rounded-[2rem] border border-slate-100 relative group animate-in zoom-in-95 duration-300">
                                                            <div className="grid grid-cols-12 gap-4 items-end">
                                                                <div className="col-span-4 space-y-1">
                                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-3">Rubro</label>
                                                                    <select
                                                                        className="input-base py-3 px-4 text-xs font-black bg-white"
                                                                        value={split.cat}
                                                                        onChange={(e) => {
                                                                            const newSplits = [...expenseSplits];
                                                                            newSplits[idx].cat = e.target.value;
                                                                            setExpenseSplits(newSplits);
                                                                        }}
                                                                    >
                                                                        <option value="">Seleccionar...</option>
                                                                        {expenseType === 'MANO_OBRA' ? (
                                                                            <>
                                                                                {selectedEvent?.mdo_budget_mode === 'GLOBAL' && (
                                                                                    <option value="GLOBAL">Presupuesto Global MDO</option>
                                                                                )}
                                                                                {(appData.tasks || [])
                                                                                    .filter(t => t.event_id === selectedEventId && !t.isParent)
                                                                                    .sort((a, b) => sortPhasesNumerically(a.title, b.title))
                                                                                    .map(t => (
                                                                                        <option key={t.id} value={t.title || t.id}>{t.title || 'Fase Desconocida'}</option>
                                                                                    ))}
                                                                            </>
                                                                        ) : (
                                                                            appData.categories
                                                                                .filter(r => r.event_id === selectedEventId || (appData.budgets || []).some(b => b.category_id === r.id && b.event_id === selectedEventId))
                                                                                .sort((a, b) => sortPhasesNumerically(a.name, b.name))
                                                                                .map(r => <option key={r.id} value={r.name}>{r.name}</option>)
                                                                        )}
                                                                    </select>
                                                                </div>
                                                                <div className="col-span-1 md:col-span-3 space-y-1">
                                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-3">Importe</label>
                                                                    <div className="relative">
                                                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg text-indigo-300 font-bold">$</span>
                                                                        <input
                                                                            type="number"
                                                                            className="input-base py-6 pl-10 pr-4 text-xl font-black text-indigo-600 bg-white border-2 border-slate-100 focus:border-indigo-400"
                                                                            placeholder="0.00"
                                                                            value={split.amount}
                                                                            onChange={(e) => {
                                                                                const newSplits = [...expenseSplits];
                                                                                newSplits[idx].amount = e.target.value;
                                                                                setExpenseSplits(newSplits);
                                                                                const total = newSplits.reduce((acc, s) => acc + Number(s.amount || 0), 0);
                                                                                setMovementAmount(total.toFixed(2));
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-span-4 space-y-1">
                                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-3">Detalle (Opcional)</label>
                                                                    <input
                                                                        className="input-base py-3 px-4 text-xs bg-white"
                                                                        placeholder="Ej: Materiales varios..."
                                                                        value={split.desc}
                                                                        onChange={(e) => {
                                                                            const newSplits = [...expenseSplits];
                                                                            newSplits[idx].desc = e.target.value;
                                                                            setExpenseSplits(newSplits);
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className="col-span-1 flex justify-center pb-2">
                                                                    {expenseSplits.length > 1 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setExpenseSplits(prev => prev.filter((_, i) => i !== idx))}
                                                                            className="w-10 h-10 bg-white border border-rose-100 text-rose-300 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all shadow-sm flex items-center justify-center"
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {expenseType === 'MATERIALES' && (
                                                                <div className="mt-4 pt-4 border-t border-slate-100 flex gap-4 items-center">
                                                                    <div className="flex-1">
                                                                        <select
                                                                            className="w-full bg-white border-none text-[10px] font-bold text-slate-500 rounded-xl px-4 py-2 shadow-sm"
                                                                            value={split.material_id || split.material}
                                                                            onChange={(e) => {
                                                                                const newSplits = [...expenseSplits];
                                                                                newSplits[idx].material_id = e.target.value;
                                                                                newSplits[idx].material = e.target.options[e.target.selectedIndex].text;
                                                                                setExpenseSplits(newSplits);
                                                                            }}
                                                                        >
                                                                            <option value="">{expenseType === 'MATERIALES' ? 'SELECCIONAR MATERIAL (OBLIGATORIO)...' : 'Descontar de Stock (Opcional)...'}</option>
                                                                            {(appData.materials || []).filter(m => m.event_id === selectedEventId).map(m => (
                                                                                <option key={m.id} value={m.id}>{m.name}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    {split.material && (
                                                                        <input
                                                                            type="number"
                                                                            placeholder="Cant."
                                                                            className="w-24 bg-indigo-50 border-none text-[10px] font-black text-indigo-700 rounded-xl px-4 py-2"
                                                                            value={split.mat_qty}
                                                                            onChange={(e) => {
                                                                                const newSplits = [...expenseSplits];
                                                                                newSplits[idx].mat_qty = e.target.value;
                                                                                setExpenseSplits(newSplits);
                                                                            }}
                                                                        />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}

                                                    <div className="flex justify-between items-center px-6 py-4 bg-indigo-600 rounded-3xl text-white shadow-xl shadow-indigo-100">
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] font-black opacity-80 uppercase tracking-widest">Total Distribuido</span>
                                                            <span className="text-2xl font-black">{fCur(expenseSplits.reduce((acc, s) => acc + Number(s.amount || 0), 0), movementCurrency)}</span>
                                                        </div>
                                                        <Zap size={24} className="text-white/30" />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Balance Info Dyn */}
                                        <div id="expense_balance_info" className="px-6 py-4 bg-slate-50 border border-slate-100 rounded-[2rem] text-[11px] hidden animate-in zoom-in-95">
                                        </div>

                                        {/* Importe Final y Moneda */}
                                        <div className="grid grid-cols-4 gap-4 p-8 bg-slate-900 rounded-[3rem] shadow-2xl relative overflow-hidden">
                                            <div className="absolute right-0 top-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16"></div>
                                            <div className="col-span-3 space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Importe Total Original</label>
                                                <div className="relative">
                                                    <span className="absolute left-6 top-5 text-indigo-400 font-black text-xl">$</span>
                                                    <input
                                                        name="amount"
                                                        id="main_amount_input"
                                                        type="number"
                                                        step="0.01"
                                                        required
                                                        className="w-full bg-white/10 border-none rounded-2xl py-6 pl-12 pr-6 text-white text-3xl font-black outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                                        placeholder="0.00"
                                                        value={movementAmount}
                                                        onChange={e => setMovementAmount(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Moneda</label>
                                                <select
                                                    name="mov_currency"
                                                    className="w-full h-[76px] bg-white/10 border-none rounded-2xl text-white text-2xl font-black text-center outline-none focus:ring-2 focus:ring-indigo-500 appearance-none cursor-pointer"
                                                    value={movementCurrency}
                                                    onChange={(e) => setMovementCurrency(e.target.value)}
                                                >
                                                    <option value="ARS" className="text-slate-900">ARS</option>
                                                    <option value="USD" className="text-slate-900">USD</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 flex items-center justify-between group-hover:bg-slate-100 transition-colors">
                                            <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Base</span>
                                            <span className="text-xs font-black text-indigo-600 uppercase">{selectedEvent?.base_currency || selectedEvent?.display_currency || 'ARS'}</span>
                                        </div>

                                        {movementCurrency !== (selectedEvent?.base_currency || selectedEvent?.display_currency || 'ARS') && (
                                            <div className="bg-amber-50 p-8 rounded-[3rem] border border-amber-100 flex items-center gap-6 animate-in slide-in-from-top-4">
                                                <div className="w-16 h-16 bg-amber-500 rounded-3xl flex items-center justify-center text-white shadow-xl shrink-0">
                                                    <TrendingUp size={32} />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-[11px] font-black uppercase text-amber-900 tracking-widest mb-1">Tipo de Cambio</p>
                                                    <p className="text-xs text-amber-700 font-bold leading-tight">
                                                        {(selectedEvent?.base_currency || selectedEvent?.display_currency) === 'USD'
                                                            ? 'Monto en ARS -> Convertido a USD'
                                                            : 'Monto en USD -> Convertido a ARS'}
                                                    </p>
                                                </div>
                                                <div className="w-32">
                                                    <input
                                                        name="fx"
                                                        type="number"
                                                        step="0.01"
                                                        required
                                                        className="input-base bg-white border-amber-100 text-amber-700 font-black text-center text-xl py-4"
                                                        value={movementFx}
                                                        onChange={e => setMovementFx(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-12">
                                        <button
                                            type="submit"
                                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-8 rounded-[2.5rem] font-black uppercase text-sm tracking-[0.3em] shadow-2xl shadow-indigo-200 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-4 group"
                                        >
                                            {activeModal === 'EDIT_EXPENSE' ? 'Guardar Cambios' : 'Confirmar Operación'}
                                            <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
                                        </button>
                                    </div>
                                </form>
                            )
                            }
                            {
                                (activeModal === 'ADD_TASK' || activeModal === 'EDIT_TASK') && (
                                    <form className="space-y-5" onSubmit={e => {
                                        e.preventDefault();
                                        const fd = new FormData(e.target);
                                        const selectedResourceIds = Array.from(e.target.querySelectorAll('input.task-resource-cb:checked')).map(cb => cb.value);
                                        const payload = {
                                            title: fd.get('title'),
                                            assigned_to: fd.get('assignee'),
                                            start_date: fd.get('start'),
                                            end_date: fd.get('end'),
                                            dependency_id: fd.get('dep'),
                                            parent_id: fd.get('parent'),
                                            is_milestone: fd.get('milestone') === 'on' ? 1 : 0,
                                            progress: fd.get('progress') || 0,
                                            document_url: fd.get('doc_url'),
                                            resource_ids: selectedResourceIds
                                        };

                                        // Bloqueo de seguridad: Si es parent, no enviamos fechas/progreso manual (el backend deberÃ­a ignorarlo pero lo limpiamos aquÃ­)
                                        if (modalData?.isParent) {
                                            delete payload.start_date;
                                            delete payload.end_date;
                                            delete payload.progress;
                                        }

                                        if (activeModal === 'EDIT_TASK') {
                                            handleAction('update_task', { ...payload, id: modalData.id });
                                        } else {
                                            handleAction('add_task', payload);
                                        }
                                    }}>
                                        <div className="grid grid-cols-2 gap-4">
                                            <input name="title" required className="input-base" placeholder="Nombre de la tarea" defaultValue={modalData?.title} />
                                            <select name="parent" className="input-base font-bold bg-indigo-50/50" defaultValue={modalData?.parent_id}>
                                                <option value="">Es Tarea Principal...</option>
                                                {appData.tasks.filter(t => t.event_id === selectedEventId && t.id !== modalData?.id).map(t => <option key={t.id} value={t.id}>Sub-tarea de: {t.title}</option>)}
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <select name="assignee" className="input-base font-bold" defaultValue={modalData?.assigned_to}>
                                                <option value="">Responsable del proyecto...</option>
                                                {(appData.resource_assignments || [])
                                                    .filter(ra => ra.event_id === selectedEventId)
                                                    .map(ra => {
                                                        const res = (appData.resources || []).find(r => r.id === ra.resource_id);
                                                        if (!res) return null;
                                                        return <option key={res.id} value={res.id}>{res.name} ({res.position || 'Recurso'})</option>;
                                                    }).filter(Boolean)}
                                            </select>
                                            <select name="dep" className="input-base font-bold" defaultValue={modalData?.dependency_id} onChange={(e) => {
                                                const depId = e.target.value;
                                                if (depId) {
                                                    const depTask = appData.tasks.find(t => t.id === depId);
                                                    if (depTask && depTask.end_date) {
                                                        const startInput = document.getElementById('task_start');
                                                        if (startInput && !startInput.value) {
                                                            startInput.value = depTask.end_date;
                                                            // Trigger end date update if milestone
                                                            const checkMilestone = document.getElementById('task_milestone');
                                                            if (checkMilestone && checkMilestone.checked) {
                                                                const endInput = document.getElementById('task_end');
                                                                if (endInput) endInput.value = depTask.end_date;
                                                            }
                                                        }
                                                    }
                                                }
                                            }}>
                                                <option value="">Predecesora...</option>
                                                {appData.tasks.filter(t => t.event_id === selectedEventId && t.id !== modalData?.id).map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                                            </select>
                                        </div>

                                        {/* Recursos Adicionales */}
                                        {(() => {
                                            const projResources = (appData.resource_assignments || []).filter(ra => ra.event_id === selectedEventId).map(ra => (appData.resources || []).find(r => r.id === ra.resource_id)).filter(Boolean);
                                            if (!projResources.length) return null;
                                            return (
                                                <div className="space-y-2">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">{t('recursos_adicionales') || 'Recursos Adicionales de la tarea'}</label>
                                                    <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-2xl border border-slate-100">
                                                        {projResources.map(res => {
                                                            const assigned = (appData.task_resources || []).some(tr => tr.task_id === modalData?.id && tr.resource_id === res.id);
                                                            return (
                                                                <label key={res.id} className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-xl border border-slate-100 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50 transition-all">
                                                                    <input type="checkbox" className="task-resource-cb accent-indigo-600" value={res.id} defaultChecked={assigned} />
                                                                    <span className="text-[9px] font-black uppercase text-slate-600">{res.name}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        <div className="space-y-1">
                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">
                                                Avance (%) {modalData?.isParent ? '(CALCULADO POR SUB-TAREAS)' : ''}
                                            </label>
                                            <div className="flex items-center gap-4">
                                                <input
                                                    id="prog_slider"
                                                    type="range"
                                                    min="0" max="100"
                                                    className={`flex-1 h-2 rounded-lg appearance-none cursor-pointer accent-indigo-600 ${modalData?.isParent ? 'opacity-50 pointer-events-none' : 'bg-indigo-100'}`}
                                                    defaultValue={modalData?.progress || 0}
                                                    readOnly={modalData?.isParent}
                                                    onInput={(e) => document.getElementById('prog_num').value = e.target.value}
                                                />
                                                <input
                                                    id="prog_num"
                                                    name="progress"
                                                    type="number"
                                                    min="0" max="100"
                                                    className="input-base w-20 text-center font-black"
                                                    defaultValue={modalData?.progress || 0}
                                                    readOnly={modalData?.isParent}
                                                    onInput={(e) => document.getElementById('prog_slider').value = e.target.value}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">DocumentaciÃ³n / Certificado</label>
                                            <div className="flex gap-2">
                                                <input name="doc_url" id="task_doc_url" className="input-base flex-1" placeholder="URL o Link Externo" defaultValue={modalData?.document_url} />
                                                <label className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 cursor-pointer hover:bg-indigo-50 hover:text-indigo-600 transition-all text-slate-400 flex items-center gap-2 font-black text-[10px] uppercase">
                                                    <Plus size={20} /> Subir Archivo
                                                    <input type="file" className="hidden" onChange={async (e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            const formData = new FormData(e.target.form);
                                                            formData.append('file', file);
                                                            try {
                                                                const token = localStorage.getItem('em_token');
                                                                const headers = {};
                                                                if (token) headers['Authorization'] = `Bearer ${token}`;

                                                                const res = await fetch(API_URL + '?action=upload_file', { 
                                                                    method: 'POST', 
                                                                    body: formData,
                                                                    headers: headers
                                                                });
                                                                const data = await res.json();
                                                                if (data.status === 'success') {
                                                                    document.getElementById('task_doc_url').value = data.url;
                                                                    alert("DOCUMENTO VINCULADO: Ya puedes guardar la tarea.");
                                                                } else {
                                                                    alert("Error al subir: " + data.message);
                                                                }
                                                            } catch (err) {
                                                                alert("Error de conexiÃ³n al subir archivo.");
                                                            }
                                                        }
                                                    }} />
                                                </label>
                                                {modalData?.document_url && (
                                                    <button type="button" onClick={() => { document.getElementById('task_doc_url').value = ''; }} className="bg-rose-50 text-rose-500 p-4 rounded-xl hover:bg-rose-100 transition-all"><Trash2 size={20} /></button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-2xl border border-amber-100">
                                            <input id="task_milestone" name="milestone" type="checkbox" className="w-5 h-5 rounded border-amber-300 text-amber-500" defaultChecked={modalData?.is_milestone == 1}
                                                onChange={(e) => {
                                                    const endInput = document.getElementById('task_end');
                                                    if (e.target.checked) {
                                                        endInput.value = document.getElementById('task_start').value;
                                                        endInput.readOnly = true;
                                                        endInput.classList.add('opacity-50');
                                                    } else {
                                                        endInput.readOnly = false;
                                                        endInput.classList.remove('opacity-50');
                                                    }
                                                }}
                                            />
                                            <span className="text-[10px] font-black uppercase text-amber-700">Hito CrÃ­tico (DuraciÃ³n 1 dÃ­a simbÃ³lico)</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Inicio {modalData?.isParent ? '(AUTO)' : ''}</label>
                                                <input
                                                    id="task_start"
                                                    name="start"
                                                    type="date"
                                                    required
                                                    className={`input-base ${modalData?.isParent ? 'bg-slate-50 text-slate-400' : ''}`}
                                                    defaultValue={modalData?.start_date}
                                                    readOnly={modalData?.isParent}
                                                    onChange={(e) => {
                                                        if (document.getElementById('task_milestone').checked) {
                                                            document.getElementById('task_end').value = e.target.value;
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Fin {modalData?.isParent ? '(AUTO)' : ''}</label>
                                                <input
                                                    id="task_end"
                                                    name="end"
                                                    type="date"
                                                    required
                                                    className={`input-base ${modalData?.isParent ? 'bg-slate-50 text-slate-400' : ''}`}
                                                    defaultValue={modalData?.end_date}
                                                    readOnly={modalData?.isParent}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex gap-4 mt-8">
                                            <button type="submit" className="btn-primary flex-1 py-5 uppercase font-black">{activeModal === 'EDIT_TASK' ? t('edit_task') : t('add_task')}</button>
                                            {activeModal === 'EDIT_TASK' && (
                                                <button type="button" onClick={() => { if (confirm(t('delete_confirm'))) handleAction('delete_task', { id: modalData.id }) }} className="bg-rose-50 text-rose-500 p-5 rounded-2xl hover:bg-rose-100 transition-all"><Trash2 size={24} /></button>
                                            )}
                                        </div>
                                    </form>
                                )
                            }


                            {
                                activeModal === 'ADD_TEAM' && (
                                    <form className="space-y-5" onSubmit={e => {
                                        e.preventDefault();
                                        const fd = new FormData(e.target);
                                        handleAction('add_team_member', {
                                            name: fd.get('name'),
                                            email: fd.get('email'),
                                            password: fd.get('pass'),
                                            role: fd.get('role'),
                                            position: fd.get('position')
                                        });
                                    }}>
                                        <h4 className="text-xl font-black uppercase tracking-tighter text-slate-800 mb-6">Nuevo Miembro</h4>
                                        <div className="space-y-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Nombre Completo</label>
                                                <input name="name" required className="input-base font-black" placeholder="Ej: Juan Pérez" />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Email de Acceso</label>
                                                <input name="email" required type="email" className="input-base font-black" placeholder="email@empresa.com" />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Acceso al Sistema</label>
                                                    <select name="role" required className="input-base font-bold">
                                                        <optgroup label="Roles de Sistema">
                                                            <option value="COMPANY_COLABORATOR">Colaborador Estándar</option>
                                                            <option value="COMPANY_ADMIN">Administrador de Empresa</option>
                                                        </optgroup>
                                                        {appData.roles && appData.roles.length > 0 && (
                                                            <optgroup label="Roles Personalizados">
                                                                {appData.roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                                                            </optgroup>
                                                        )}
                                                    </select>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Contraseña Inicial</label>
                                                    <input name="pass" required type="password" className="input-base font-black" placeholder="******" />
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Cargo / Responsabilidad (Opcional)</label>
                                                <input name="position" className="input-base font-black" placeholder="Ej: Jefe de Obra / Arquitecto" />
                                            </div>
                                        </div>
                                        <button type="submit" className="btn-primary w-full py-5 uppercase font-black tracking-widest bg-slate-900 shadow-xl shadow-slate-100">Crear Miembro</button>
                                    </form>
                                )
                            }

                            {
                                (activeModal === 'ADD_ROLE' || activeModal === 'EDIT_ROLE') && (
                                    <form className="space-y-6" onSubmit={e => {
                                        e.preventDefault();
                                        const fd = new FormData(e.target);
                                        const perms = {};
                                        ['PROJECTS', 'TEAM', 'CLIENTS', 'FINANCIAL', 'SCHEDULE', 'BUDGET', 'RESOURCES', 'DOCUMENTATION', 'PURCHASE_ORDERS'].forEach(m => {
                                            perms[m] = fd.get(`perm_${m}`);
                                        });
                                        const payload = {
                                            name: fd.get('name'),
                                            permissions: perms,
                                            company_id: currentUser?.company_id || currentUser?.companyId
                                        };
                                        if (activeModal === 'EDIT_ROLE') {
                                            handleAction('update_role', { ...payload, id: modalData.id });
                                        } else {
                                            handleAction('add_role', payload);
                                        }
                                    }}>
                                        <h4 className="text-xl font-black uppercase tracking-tighter text-slate-800 mb-6">{activeModal === 'EDIT_ROLE' ? 'Editar Rol' : 'Nuevo Rol'}</h4>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Nombre del Rol</label>
                                            <input name="name" required className="input-base font-black" placeholder="Ej: Supervisor de Obra" defaultValue={modalData?.name} />
                                        </div>
                                        <div className="space-y-4">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Permisos por Módulo</p>
                                            <div className="grid grid-cols-1 gap-2 border border-slate-100 rounded-[2rem] p-4 bg-slate-50/30">
                                                {[
                                                    { id: 'PROJECTS', label: 'Proyectos' },
                                                    { id: 'TEAM', label: 'Equipo' },
                                                    { id: 'CLIENTS', label: 'Clientes' },
                                                    { id: 'FINANCIAL', label: 'Finanzas' },
                                                    { id: 'SCHEDULE', label: 'Cronograma' },
                                                    { id: 'BUDGET', label: 'Presupuesto' },
                                                    { id: 'RESOURCES', label: 'Recursos' },
                                                    { id: 'DOCUMENTATION', label: 'Documentación' },
                                                    { id: 'PURCHASE_ORDERS', label: 'OC (Órdenes de Compra)' }
                                                ].map(({ id: m, label }) => {
                                                    const currentPerms = typeof modalData?.permissions === 'string' ? JSON.parse(modalData.permissions) : (modalData?.permissions || {});
                                                    return (
                                                        <div key={m} className="flex justify-between items-center bg-white p-3 rounded-2xl border border-slate-100">
                                                            <span className="text-[11px] font-bold text-slate-600 uppercase tracking-tight">{label}</span>
                                                            <select name={`perm_${m}`} className="text-[10px] font-black uppercase bg-slate-50 border-none rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500" defaultValue={currentPerms[m] || 'NONE'}>
                                                                <option value="NONE">Sin Acceso</option>
                                                                <option value="VIEW">Solo Lectura</option>
                                                                <option value="EDIT">Ver y Editar</option>
                                                                <option value="DELETE">Ver, Edit y Borrar</option>
                                                            </select>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <button type="submit" className="btn-primary w-full py-5 uppercase font-black tracking-widest bg-slate-900 shadow-xl shadow-slate-100">Guardar Rol</button>
                                    </form>
                                )
                            }

                            {
                                (activeModal === 'ADD_RESOURCE' || activeModal === 'EDIT_RESOURCE') && (
                                    <form className="space-y-6" onSubmit={e => {
                                        e.preventDefault();
                                        const fd = new FormData(e.target);
                                        const payload = {
                                            name: fd.get('name'),
                                            email: fd.get('email'),
                                            position: fd.get('position'),
                                            daily_hours: fd.get('daily_hours'),
                                            hourly_rate: fd.get('hourly_rate'),
                                            currency: fd.get('currency'),
                                            resource_type: fd.get('resource_type') || 'OPERATOR',
                                            user_id: fd.get('user_id') || null,
                                            allow_overtime: fd.get('allow_overtime') === 'on' ? 1 : 0,
                                            company_id: currentUser?.company_id || currentUser?.companyId
                                        };
                                        if (activeModal === 'EDIT_RESOURCE') {
                                            handleAction('update_resource', { ...payload, id: modalData.id });
                                        } else {
                                            handleAction('add_resource', payload);
                                        }
                                    }}>
                                        <h4 className="text-xl font-black uppercase tracking-tighter text-slate-800 mb-6">{activeModal === 'EDIT_RESOURCE' ? 'Editar Recurso' : 'Nuevo Recurso'}</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Nombre Completo</label>
                                                <input name="name" required className="input-base font-black" placeholder="Ej: Juan Pérez" defaultValue={modalData?.name} />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Tipo de Recurso</label>
                                                <select name="resource_type" className="input-base font-black" defaultValue={modalData?.resource_type || 'OPERATOR'}>
                                                    <option value="OPERATOR">Mano de Obra / Operario</option>
                                                    <option value="DIRECTOR">Director / Capataz / Arquitecto</option>
                                                    <option value="EQUIPMENT">Equipamiento / Maquinarias</option>
                                                    <option value="OTHER">Otros</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Cargo / Seniority (Etiqueta)</label>
                                            <input
                                                name="position"
                                                required
                                                className="input-base font-black"
                                                placeholder="Ej: Oficial Especializado"
                                                defaultValue={modalData?.position}
                                                list="position-options"
                                            />
                                            <datalist id="position-options">
                                                {[...new Set(appData.resources?.map(r => r.position).filter(Boolean))].sort().map(p => (
                                                    <option key={p} value={p} />
                                                ))}
                                            </datalist>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Email</label>
                                            <input name="email" type="email" className="input-base font-black" placeholder="email@empresa.com" defaultValue={modalData?.email} />
                                        </div>
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Hs Diarias</label>
                                                <input name="daily_hours" type="number" step="0.5" required className="input-base font-black" defaultValue={modalData?.daily_hours || 8} />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Moneda</label>
                                                <select name="currency" className="input-base font-black" defaultValue={modalData?.currency || 'ARS'}>
                                                    <option value="ARS">ARS</option>
                                                    <option value="USD">USD</option>
                                                </select>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Vlr Hora</label>
                                                <input name="hourly_rate" type="number" step="0.01" required className="input-base font-black" defaultValue={modalData?.hourly_rate || 0} />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Vincular a Usuario (Opcional)</label>
                                            <select name="user_id" className="input-base font-black" defaultValue={modalData?.user_id}>
                                                <option value="">-- No vincular --</option>
                                                {appData.team.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                                            </select>
                                        </div>
                                        <div className="bg-amber-50/50 p-5 rounded-2xl border border-amber-100 space-y-2">
                                            <label className="flex items-center gap-3 cursor-pointer group">
                                                <input type="checkbox" name="allow_overtime" defaultChecked={modalData?.allow_overtime == 1} className="w-5 h-5 rounded-lg border-2 border-amber-300 text-amber-500 focus:ring-amber-500 transition-all" />
                                                <div>
                                                    <span className="text-[11px] font-black text-slate-700 uppercase group-hover:text-amber-600 transition-colors">Puede trabajar más del 100% (horas extra)</span>
                                                    <p className="text-[9px] text-slate-400 mt-0.5">Si está desactivado, el sistema bloqueará asignaciones que superen el 100% de capacidad diaria</p>
                                                </div>
                                            </label>
                                        </div>
                                        <button type="submit" className="btn-primary w-full py-5 uppercase font-black tracking-widest bg-slate-900 shadow-xl shadow-slate-100">Guardar Recurso</button>
                                    </form>
                                )
                            }

                            {
                                activeModal === 'ASSIGN_RESOURCE' && (
                                    <ResourceAssignmentModal
                                        modalData={modalData}
                                        appData={appData}
                                        handleAction={handleAction}
                                        fDate={fDate}
                                        onClose={() => setActiveModal(null)}
                                        currentUser={currentUser}
                                    />
                                )
                            }

                            {
                                activeModal === 'MANAGE_INACTIVITY' && (
                                    <div className="space-y-6 animate-in">
                                        <div className="flex justify-between items-center mb-6">
                                            <div>
                                                <h4 className="text-xl font-black uppercase tracking-tighter text-slate-800">{t('manage_inactivity_title')}: {modalData.name}</h4>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('inactivity_subtitle')}</p>
                                            </div>
                                            <div className="w-12 h-12 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-500">
                                                <CalendarOff size={24} />
                                            </div>
                                        </div>

                                        <form className="bg-slate-50 p-6 rounded-3xl space-y-4 border border-slate-100" onSubmit={async e => {
                                            e.preventDefault();
                                            const fd = new FormData(e.target);
                                            await handleAction('add_inactivity', {
                                                company_id: currentUser?.company_id || currentUser?.companyId,
                                                resource_id: modalData.id,
                                                start_date: fd.get('ina_start'),
                                                end_date: fd.get('ina_end'),
                                                reason: fd.get('ina_reason')
                                            });
                                            e.target.reset();
                                        }}>
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">{t('new_inactivity_period')}</p>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">{t('from_date')}</label>
                                                    <input name="ina_start" type="date" required className="input-base" />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">{t('to_date')}</label>
                                                    <input name="ina_end" type="date" required className="input-base" />
                                                </div>
                                            </div>
                                            <input name="ina_reason" className="input-base" placeholder={t('reason_placeholder')} />
                                            <button type="submit" className="w-full py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-rose-600 transition-all shadow-lg shadow-slate-200">
                                                {t('add_inactivity_btn')}
                                            </button>
                                        </form>

                                        <div className="space-y-3">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">{t('registered_periods')}</p>
                                            <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2">
                                                {(appData.resource_inactivity || []).filter(i => i.resource_id === modalData.id).length === 0 ? (
                                                    <p className="text-center py-10 text-[10px] font-bold text-slate-300 uppercase tracking-widest italic border-2 border-dashed border-slate-100 rounded-3xl">{t('no_periods_registered')}</p>
                                                ) : (
                                                    appData.resource_inactivity.filter(i => i.resource_id === modalData.id).map(ina => (
                                                        <div key={ina.id} className="bg-white p-4 rounded-2xl border border-slate-100 flex justify-between items-center group shadow-sm">
                                                            <div>
                                                                <p className="font-bold text-xs text-slate-700">{fDate(ina.start_date)} - {fDate(ina.end_date)}</p>
                                                                <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest">{ina.reason || 'Sin motivo especificado'}</p>
                                                            </div>
                                                            <button
                                                                onClick={() => { if (confirm(t('delete_inactivity_confirm'))) handleAction('delete_inactivity', { id: ina.id }); }}
                                                                className="p-2 text-slate-200 hover:text-rose-500 transition-all opacity-0 group-hover:opacity-100"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>

                                        <div className="pt-4 border-t border-slate-50 flex justify-end">
                                            <button onClick={() => setActiveModal(null)} className="btn-primary py-3 px-8 text-[10px] uppercase font-black">{t('close')}</button>
                                        </div>
                                    </div>
                                )
                            }

                            {
                                activeModal === 'EDIT_TEAM' && (
                                    <form className="space-y-5" onSubmit={e => {
                                        e.preventDefault();
                                        const fd = new FormData(e.target);
                                        handleAction('update_team_member', {
                                            id: modalData.id,
                                            name: fd.get('name'),
                                            email: fd.get('email'),
                                            password: fd.get('pass'),
                                            role: fd.get('role'),
                                            position: fd.get('position')
                                        });
                                    }}>
                                        <div className="flex justify-between items-center mb-2">
                                            <p className="text-[10px] font-black uppercase text-slate-400">Configuración de Miembro</p>
                                            <button type="button" onClick={() => { if (confirm("¿Eliminar acceso de este usuario?")) handleAction('delete_team_member', { id: modalData.id }) }} className="text-rose-500 hover:bg-rose-50 p-2 rounded-lg transition-all"><Trash2 size={16} /></button>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Nombre Completo</label>
                                                <input name="name" required className="input-base font-black" placeholder="Nombre Completo" defaultValue={modalData?.name} />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Email Acceso</label>
                                                <input name="email" required type="email" className="input-base font-black" placeholder="Email Acceso" defaultValue={modalData?.email} />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Acceso al Portal</label>
                                                    <select name="role" className="input-base font-bold" defaultValue={modalData?.role}>
                                                        <option value="COMPANY_COLABORATOR">Colaborador</option>
                                                        <option value="COMPANY_ADMIN">Administrador</option>
                                                    </select>
                                                    <p className="text-[9px] text-slate-400 font-bold ml-4 mt-1">Los roles por proyecto se asignan desde cada proyecto.</p>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Nueva Password</label>
                                                    <input name="pass" type="password" className="input-base font-black" placeholder="Sin cambios" />
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Cargo / Responsabilidad</label>
                                                <input name="position" className="input-base font-black" placeholder="Ej: Jefe de Obra" defaultValue={modalData?.position} />
                                            </div>
                                        </div>
                                        <button type="submit" className="btn-primary w-full py-5 uppercase font-black tracking-widest bg-slate-900 shadow-xl shadow-slate-100">Guardar Cambios</button>
                                    </form>
                                )
                            }
                            {
                                activeModal === 'CREATE_LICENSE' && (
                                    <form className="space-y-5" onSubmit={e => {
                                        e.preventDefault();
                                        const fd = new FormData(e.target);
                                        handleAction('create_company', { name: fd.get('name'), expires: fd.get('expires'), max_users: fd.get('max_users'), status: fd.get('status') });
                                    }}>
                                        <input name="name" required className="input-base" placeholder="Nombre de la Empresa" />
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Vencimiento</label>
                                                <input name="expires" type="date" required className="input-base" />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">MÃ¡x Usuarios</label>
                                                <input name="max_users" type="number" defaultValue="5" required className="input-base" />
                                            </div>
                                        </div>
                                        <select name="status" className="input-base font-bold">
                                            <option value="ACTIVE">Activa</option>
                                            <option value="SUSPENDED">Suspendida</option>
                                            <option value="EXPIRED">Expirada</option>
                                        </select>
                                        <button type="submit" className="btn-primary w-full py-5 uppercase font-black">Crear Licencia</button>
                                    </form>
                                )
                            }
                            {
                                activeModal === 'EDIT_LICENSE' && (
                                    <form className="space-y-5" onSubmit={e => {
                                        e.preventDefault();
                                        const fd = new FormData(e.target);
                                        handleAction('update_company', { id: modalData.id, name: fd.get('name'), expires: fd.get('expires'), max_users: fd.get('max_users'), status: fd.get('status') });
                                    }}>
                                        <input name="name" defaultValue={modalData?.name} required className="input-base" placeholder="Nombre de la Empresa" />
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Vencimiento</label>
                                                <input name="expires" type="date" defaultValue={modalData?.license_expires_at} required className="input-base" />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">MÃ¡x Usuarios</label>
                                                <input name="max_users" type="number" defaultValue={modalData?.max_users} required className="input-base" />
                                            </div>
                                        </div>
                                        <select name="status" defaultValue={modalData?.status} className="input-base font-bold">
                                            <option value="ACTIVE">Activa</option>
                                            <option value="SUSPENDED">Suspendida</option>
                                            <option value="EXPIRED">Expirada</option>
                                        </select>
                                        <div className="flex gap-4">
                                            <button type="submit" className="flex-[2] btn-primary py-5 uppercase font-black">Guardar Cambios</button>
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    if (confirm("¿Estás seguro de regenerar la llave? La anterior dejará de funcionar inmediatamente.")) {
                                                        const res = await fetch(`${API_URL}?action=regenerate_access_key`, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({ id: modalData.id })
                                                        });
                                                        const data = await res.json();
                                                        if (data.status === 'success') {
                                                            alert("Nueva llave generada: " + data.access_key);
                                                            handleAction('sync_full');
                                                            setActiveModal(null);
                                                        }
                                                    }
                                                }}
                                                className="flex-1 bg-amber-50 text-amber-600 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-amber-100 transition-all"
                                            >
                                                Regenerar Llave
                                            </button>
                                        </div>
                                    </form>
                                )
                            }
                            {
                                activeModal === 'COMPANY_USERS' && (
                                    <div className="space-y-6 animate-in">
                                        <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Usuarios de Licencia: {modalData?.name}</h4>
                                        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                                            {appData.team.filter(u => u.company_id == modalData.id).map(u => (
                                                <div key={u.id} className="bg-white p-4 rounded-2xl border border-slate-100 flex justify-between items-center shadow-sm">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center font-black md:text-xs text-[10px]">{u.name[0]}</div>
                                                        <div>
                                                            <p className="text-xs font-black uppercase text-slate-700">{u.name}</p>
                                                            <p className="text-[9px] font-bold text-slate-400 uppercase">{u.email}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <span className="text-[9px] font-black bg-slate-100 px-2 py-1 rounded text-slate-500 uppercase">{u.role}</span>
                                                        <button onClick={() => {
                                                            if (confirm(`¿Eliminar usuario ${u.name}? Se perderá su acceso.`)) {
                                                                handleAction('delete_team_member', { id: u.id });
                                                            }
                                                        }} className="p-2 text-rose-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"><Trash2 size={16} /></button>
                                                    </div>
                                                </div>
                                            ))}
                                            {appData.team.filter(u => u.company_id == modalData.id).length === 0 && <p className="text-center text-xs text-slate-300 font-bold uppercase py-8">No hay usuarios registrados.</p>}
                                        </div>
                                        <div className="flex justify-end pt-4 border-t border-slate-50">
                                            <button onClick={() => setActiveModal(null)} className="btn-primary py-3 px-8 text-[10px] uppercase font-black">Cerrar</button>
                                        </div>
                                    </div>
                                )
                            }
                            {
                                activeModal === 'EDIT_EVENT' && (
                                    <div className="space-y-8 animate-in">
                                        <form className="space-y-5" onSubmit={async e => {
                                            e.preventDefault();
                                            const fd = new FormData(e.target);

                                            let lat = parseFloat(fd.get('latitude'));
                                            let lng = parseFloat(fd.get('longitude'));

                                            // FORCE geocoding on update to ensure map syncs with address text
                                            // This fixes the issue where coordinates are stale (e.g. Parana) but text is correct (Rosario)
                                            // We attempt to geocode whenever address data is present.
                                            const hasAddressData = fd.get('address') || fd.get('city') || fd.get('province_region') || fd.get('country');

                                            if (hasAddressData) {
                                                const coords = await fetchCoordinates(fd.get('address'), fd.get('city'), fd.get('province_region'), fd.get('country'));
                                                if (coords) {
                                                    lat = coords.lat;
                                                    lng = coords.lng;
                                                }
                                            }

                                            handleAction('update_event', {
                                                id: modalData.id,
                                                name: fd.get('name'),
                                                type: fd.get('type'),
                                                description: fd.get('description'),
                                                date: fd.get('date'),
                                                end_date: fd.get('end_date'),
                                                location: fd.get('location'),
                                                address: fd.get('address'),
                                                city: fd.get('city'),
                                                province_region: fd.get('province_region'),
                                                country: fd.get('country'),
                                                latitude: lat,
                                                longitude: lng,
                                                currency: fd.get('currency'),
                                                client_id: fd.get('client_id'),
                                                kpis_enabled: modalData.kpis_enabled,
                                                budget_total_contract: fd.get('budget_total_contract'),
                                                block_overallocation: fd.get('block_overallocation') === 'on' ? 1 : (fd.get('block_overallocation') === '1' ? 1 : 0),
                                                sync_mdo_resources: fd.get('sync_mdo_resources') === 'on' ? 1 : (fd.get('sync_mdo_resources') === '1' ? 1 : 0),
                                                exchange_rate: fd.get('exchange_rate') || 1.0
                                            });
                                        }}>
                                            <input name="name" defaultValue={modalData.name} required className="input-base" placeholder="Nombre del Proyecto" />
                                            <textarea name="description" className="input-base h-24 pt-4" placeholder="Breve descripción del proyecto..." defaultValue={modalData.description}></textarea>
                                            <div className="grid grid-cols-2 gap-4">
                                                <select name="type" defaultValue={modalData.type} className="input-base font-bold" required>
                                                    <option value="CONSTRUCCION">Construcción</option>
                                                    <option value="EVENTO">Evento Social</option>
                                                    <option value="CORPORATIVO">Corporativo</option>
                                                    <option value="OTRO">Otro</option>
                                                </select>
                                                <input name="location" defaultValue={modalData.location} required className="input-base" placeholder="Nombre Ubicación" />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <input name="address" defaultValue={modalData.address} className="input-base" placeholder="Dirección" />
                                                <input name="city" defaultValue={modalData.city} className="input-base" placeholder="Localidad" />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <input name="province_region" defaultValue={modalData.province_region} className="input-base" placeholder="Provincia / Región" />
                                                <input name="country" defaultValue={modalData.country} className="input-base" placeholder="País" />
                                            </div>
                                            <input type="hidden" name="latitude" defaultValue={modalData.latitude} />
                                            <input type="hidden" name="longitude" defaultValue={modalData.longitude} />
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Asociar Cliente</label>
                                                <select name="client_id" className="input-base font-black" defaultValue={modalData.client_id}>
                                                    <option value="">Sin cliente asignado...</option>
                                                    {appData.clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                </select>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-black">
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Moneda del Proyecto</label>
                                                    <select name="currency" defaultValue={modalData.base_currency} className="input-base border-2 border-slate-900 bg-slate-900 text-white rounded-2xl">
                                                        <option value="ARS">ARS - Pesos</option>
                                                        <option value="USD">USD - Dólares</option>
                                                    </select>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-emerald-500 uppercase ml-2">Total Contrato</label>
                                                    <input name="budget_total_contract" type="number" step="0.01" className="input-base border-emerald-100 bg-emerald-50/20 text-emerald-700 font-black" defaultValue={modalData.budget_total_contract || 0} />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-black text-rose-500 uppercase ml-2">Cotización de Obra (Ref. Dolar)</label>
                                                    <input name="exchange_rate" type="number" step="0.01" required className="input-base border-2 border-rose-100 bg-rose-50/20 text-rose-600" defaultValue={modalData.exchange_rate || globalFxRate || 1000} placeholder="Ej: 1400" />
                                                </div>
                                            </div>

                                            <div className="bg-slate-50 p-6 rounded-3xl space-y-4">
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Configuración de Recursos</p>
                                                <div className="flex flex-col gap-3">
                                                    <label className="flex items-center gap-3 cursor-pointer group">
                                                        <input type="checkbox" name="block_overallocation" defaultChecked={modalData.block_overallocation == 1} className="w-5 h-5 rounded-lg border-2 border-slate-200 text-indigo-600 focus:ring-0 transition-all" />
                                                        <span className="text-[11px] font-bold text-slate-600 uppercase group-hover:text-indigo-600 transition-colors">Bloquear sobre-asignación de recursos</span>
                                                    </label>
                                                    <label className="flex items-center gap-3 cursor-pointer group">
                                                        <input type="checkbox" name="sync_mdo_resources" defaultChecked={modalData.sync_mdo_resources == 1} className="w-5 h-5 rounded-lg border-2 border-slate-200 text-indigo-600 focus:ring-0 transition-all" />
                                                        <span className="text-[11px] font-bold text-slate-600 uppercase group-hover:text-indigo-600 transition-colors">Sincronizar Presupuesto MDO con Recursos</span>
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Inicio</label>
                                                    <input name="date" type="date" required className="input-base" defaultValue={modalData.event_date} />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Fin (Est.)</label>
                                                    <input name="end_date" type="date" className="input-base" defaultValue={modalData.end_date} />
                                                </div>
                                            </div>
                                            <button type="submit" className="btn-primary w-full py-5 uppercase font-black">Guardar Cambios</button>
                                            {userProjectRole === 'MANAGER' && (
                                                <button type="button" onClick={() => {
                                                    if (confirm("ATENCION: ¿Seguro que quieres ELIMINAR este proyecto y TODOS sus datos (Tareas, Gastos, etc)? Esta acción no se puede deshacer.")) {
                                                        handleAction('delete_event', { id: modalData.id });
                                                    }
                                                }} className="w-full py-4 uppercase font-black text-rose-500 hover:bg-rose-50 rounded-xl transition-all mt-2">Eliminar Proyecto</button>
                                            )}
                                        </form>

                                        <div className="pt-8 border-t border-slate-100">
                                            <h4 className="text-[10px] font-black uppercase text-slate-400 mb-4 tracking-widest">Imagen de Portada</h4>
                                            <div className="flex items-center gap-4">
                                                {modalData.image_url ? (
                                                    <div className="w-20 h-20 rounded-2xl object-cover border-2 border-slate-100 relative group">
                                                        <img src={modalData.image_url} className="w-full h-full object-cover rounded-xl" />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                if (confirm("¿Eliminar imagen de portada?")) {
                                                                    handleAction('update_event_image', { id: modalData.id, image_url: '' });
                                                                    setModalData(prev => ({ ...prev, image_url: '' }));
                                                                }
                                                            }}
                                                            className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        >
                                                            <X size={12} />
                                                        </button>
                                                    </div>
                                                ) : null}
                                                <label className="flex-1 bg-white p-6 rounded-2xl border-2 border-dashed border-slate-200 cursor-pointer hover:border-indigo-300 hover:text-indigo-500 transition-all flex flex-col items-center justify-center text-slate-400">
                                                    <span className="text-[10px] font-black uppercase">Click para subir foto</span>
                                                    <input type="file" className="hidden" accept="image/*" onChange={async (e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            const formData = new FormData();
                                                            formData.append('file', file);
                                                            try {
                                                                const token = localStorage.getItem('em_token');
                                                                const headers = {};
                                                                if (token) headers['Authorization'] = `Bearer ${token}`;

                                                                const res = await fetch(API_URL + '?action=upload_file', { 
                                                                    method: 'POST', 
                                                                    body: formData,
                                                                    headers: headers
                                                                });
                                                                const data = await res.json();
                                                                if (data.status === 'success') {
                                                                    // Update event image immediately
                                                                    handleAction('update_event_image', { id: modalData.id, image_url: data.url });
                                                                    // Update local modal data preview
                                                                    setModalData(prev => ({ ...prev, image_url: data.url }));
                                                                } else {
                                                                    alert("Error: " + data.message);
                                                                }
                                                            } catch (err) {
                                                                alert("Fallo de conexiÃ³n");
                                                            }
                                                        }
                                                    }} />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )
                            }
                            {
                                activeModal === 'MANAGE_MEMBERS' && (
                                    <div className="space-y-6 animate-in">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-relaxed mb-10 px-4">Gestiona quién tiene acceso a este proyecto y qué nivel de permisos tienen.</p>

                                        {/* Proyect Invitation Section */}
                                        <div className="bg-indigo-50 p-6 rounded-[2rem] border border-indigo-100 flex items-center justify-between">
                                            <div>
                                                <p className="text-[9px] font-black uppercase text-indigo-400 tracking-widest mb-1">Código de Invitación</p>
                                                <p className="text-xl font-black text-indigo-900 tracking-widest">{selectedEvent?.invitation_code || '---'}</p>
                                            </div>
                                            <button
                                                onClick={() => handleAction('generate_invitation_code', { event_id: selectedEventId })}
                                                className="px-6 py-3 rounded-xl bg-white text-indigo-600 font-black text-[10px] uppercase tracking-widest border-2 border-indigo-100 hover:border-indigo-600 transition-all shadow-sm"
                                            >
                                                {selectedEvent?.invitation_code ? 'Regenerar' : 'Generar Código'}
                                            </button>
                                        </div>

                                        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                                            {/* Own company members */}
                                            {(() => {
                                                const ownMembers = appData.team.filter(u => String(u.company_id) === String(currentUser?.company_id));
                                                // Filtrar externos: Solo mostrar los que ya se unieron al proyecto vía código (están en project_members)
                                                const projectMemberIds = (appData.project_members || [])
                                                    .filter(pm => pm.event_id === selectedEventId)
                                                    .map(pm => pm.user_id);
                                                const extMembers = appData.team.filter(u =>
                                                    String(u.company_id) !== String(currentUser?.company_id) &&
                                                    projectMemberIds.includes(u.id)
                                                );

                                                const renderMemberRow = (u, isExternal) => {
                                                    const assignment = (appData.project_members || []).find(pm => pm.event_id === selectedEventId && pm.user_id === u.id);
                                                    const isAssigned = !!assignment;
                                                    const currentRole = assignment?.role || 'VIEWER';

                                                    return (
                                                        <div key={u.id} className={`flex justify-between items-center p-5 rounded-3xl border transition-all ${isExternal ? 'bg-amber-50/40 border-amber-100' : 'bg-slate-50 border-slate-100 hover:shadow-md'}`}>
                                                            <div className="flex items-center gap-4">
                                                                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-[11px] font-black shadow-sm ${isExternal ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>{u.name[0]}</div>
                                                                <div>
                                                                    <div className="flex items-center gap-2">
                                                                        <p className="font-bold text-xs uppercase text-slate-700">{u.name}</p>
                                                                        {isExternal && <span className="text-[8px] font-black bg-amber-200 text-amber-800 px-2 py-0.5 rounded uppercase">Externo</span>}
                                                                    </div>
                                                                    <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">{u.role}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                {isAssigned && (
                                                                    <select
                                                                        value={pendingRoleChanges[u.id] || currentRole}
                                                                        onChange={(e) => {
                                                                            setPendingRoleChanges(prev => ({ ...prev, [u.id]: e.target.value }));
                                                                        }}
                                                                        className="bg-white border-2 border-slate-100 text-[10px] font-black uppercase rounded-xl px-3 py-2 outline-none text-indigo-600 focus:border-indigo-500 shadow-sm"
                                                                    >
                                                                        {appData.roles && appData.roles.filter(r => !r.company_id || String(r.company_id) === String(currentUser?.company_id)).length > 0 ? (
                                                                            appData.roles.filter(r => !r.company_id || String(r.company_id) === String(currentUser?.company_id)).map(r => (
                                                                                <option key={r.id} value={`CUSTOM_${r.id}`}>{r.name}</option>
                                                                            ))
                                                                        ) : (
                                                                            <option value="" disabled>Sin roles personalizados</option>
                                                                        )}
                                                                    </select>
                                                                )}
                                                                {isAssigned && pendingRoleChanges[u.id] && pendingRoleChanges[u.id] !== currentRole && (
                                                                    <button
                                                                        onClick={async () => {
                                                                            await handleAction('assign_project_member', { user_id: u.id, role: pendingRoleChanges[u.id] });
                                                                            setPendingRoleChanges(prev => {
                                                                                const next = { ...prev };
                                                                                delete next[u.id];
                                                                                return next;
                                                                            });
                                                                            alert("Rol actualizado correctamente.");
                                                                        }}
                                                                        className="p-2 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all shadow-lg"
                                                                        title="Guardar Cambio"
                                                                    >
                                                                        <Check size={16} />
                                                                    </button>
                                                                )}
                                                                <div className="flex items-center gap-1 group/btn">
                                                                    {!isAssigned && (
                                                                        <select
                                                                            className="bg-emerald-50 text-emerald-700 text-[9px] font-black uppercase rounded-l-xl px-2 py-2 outline-none border border-emerald-100 hover:border-emerald-300 transition-all h-full"
                                                                            id={`role_sel_${u.id}`}
                                                                            defaultValue={appData.roles?.length > 0 ? `CUSTOM_${appData.roles.filter(r => !r.company_id || String(r.company_id) === String(currentUser?.company_id))[0]?.id}` : ''}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            {appData.roles && appData.roles.filter(r => !r.company_id || String(r.company_id) === String(currentUser?.company_id)).length > 0 ? (
                                                                                appData.roles.filter(r => !r.company_id || String(r.company_id) === String(currentUser?.company_id)).map(r => (
                                                                                    <option key={r.id} value={`CUSTOM_${r.id}`}>{r.name}</option>
                                                                                ))
                                                                            ) : (
                                                                                <option value="" disabled>Sin roles personalizados</option>
                                                                            )}
                                                                        </select>
                                                                    )}
                                                                    <button
                                                                        onClick={() => {
                                                                            if (isAssigned) {
                                                                                handleAction('remove_project_member', { user_id: u.id });
                                                                            } else {
                                                                                const roleInfo = document.getElementById(`role_sel_${u.id}`)?.value || (appData.roles?.length > 0 ? `CUSTOM_${appData.roles[0].id}` : 'VIEWER');
                                                                                handleAction('assign_project_member', { user_id: u.id, role: roleInfo });
                                                                            }
                                                                        }}
                                                                        className={`px-4 py-2 text-[9px] font-black uppercase transition-all shadow-sm ${isAssigned ? 'rounded-xl bg-rose-100 text-rose-600 hover:bg-rose-200' : 'rounded-r-xl bg-emerald-100 text-emerald-600 hover:bg-emerald-200 group-hover/btn:pl-2'}`}
                                                                    >
                                                                        {isAssigned ? 'Quitar' : 'Asignar'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                };

                                                return (
                                                    <>
                                                        {ownMembers.length > 0 && (
                                                            <div className="space-y-3">
                                                                <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest px-1">Equipo Propio ({ownMembers.length})</p>
                                                                {ownMembers.map(u => renderMemberRow(u, false))}
                                                            </div>
                                                        )}
                                                        {extMembers.length > 0 && (
                                                            <div className="space-y-3 mt-6">
                                                                <p className="text-[9px] font-black uppercase text-amber-500 tracking-widest px-1">Colaboradores Externos ({extMembers.length})</p>
                                                                {extMembers.map(u => renderMemberRow(u, true))}
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                            {/* Admins are always managers */}
                                            {appData.team.filter(u => u.role === 'COMPANY_ADMIN' && u.company_id == selectedEvent.company_id).map(u => (
                                                <div key={u.id} className="flex justify-between items-center p-5 bg-indigo-50/40 rounded-3xl border border-indigo-100 opacity-80">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 bg-indigo-600 rounded-2xl flex items-center justify-center text-[11px] font-black text-white shadow-lg">{u.name[0]}</div>
                                                        <div>
                                                            <p className="font-bold text-xs uppercase text-indigo-900">{u.name}</p>
                                                            <p className="text-[9px] text-indigo-400 font-black uppercase tracking-widest">Admin de Empresa (Control Total)</p>
                                                        </div>
                                                    </div>
                                                    <div className="bg-indigo-600/10 text-indigo-600 px-3 py-1 rounded-full"><ShieldCheck size={18} /></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            }
                            {
                                activeModal === 'MANAGE_KPIS' && (
                                    <div className="space-y-8 animate-in">
                                        <div className="flex items-center gap-4 bg-indigo-50 p-6 rounded-[2.5rem] border border-indigo-100">
                                            <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg"><Settings size={24} /></div>
                                            <div>
                                                <p className="text-[10px] font-black uppercase text-indigo-400 tracking-widest leading-none mb-1">Visualización Dinámica</p>
                                                <p className="text-sm font-black text-indigo-900">Configura qué indicadores ver en este proyecto.</p>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            {Object.values(KPI_DEFS).map(kpi => {
                                                let enabledKeys = [];
                                                try {
                                                    if (selectedEvent?.kpis_enabled) enabledKeys = JSON.parse(selectedEvent.kpis_enabled);
                                                } catch (e) { }

                                                // Only apply defaults if explicitly NULL (never set). If '[]' (set to empty), respect it.
                                                if (!selectedEvent?.kpis_enabled || !Array.isArray(enabledKeys)) {
                                                    const type = (selectedEvent?.type || '').toUpperCase();
                                                    const defaultIDs = (type.includes('CONSTRU') || type.includes('OBRA'))
                                                        ? ['AVANCE_FISICO', 'AVANCE_TEORICO', 'DESVIO_PLAN', 'EJECUCION_PRESUPUESTO']
                                                        : ['AVANCE_FISICO', 'AVANCE_TEORICO', 'MARGEN_RENTABILIDAD'];
                                                    enabledKeys = defaultIDs.map(id => ({ id, color: KPI_DEFS[id].color }));
                                                }

                                                // Normalize strings to objects for backward compatibility
                                                if (Array.isArray(enabledKeys)) {
                                                    enabledKeys = enabledKeys.map(k => typeof k === 'string' ? { id: k, color: KPI_DEFS[k].color || 'indigo' } : k);
                                                } else {
                                                    enabledKeys = [];
                                                }

                                                const currentConfig = enabledKeys.find(k => k.id === kpi.id);
                                                const isChecked = !!currentConfig;
                                                const currentColor = currentConfig?.color || kpi.color;

                                                const colors = ['indigo', 'emerald', 'rose', 'amber', 'blue', 'slate', 'violet', 'fuchsia'];

                                                return (
                                                    <div key={kpi.id} className={`p-4 bg-white rounded-3xl border transition-all hover:shadow-md ${isChecked ? 'border-indigo-200 shadow-sm' : 'border-slate-100 opacity-70 hover:opacity-100'}`}>

                                                        <div className="flex items-center justify-between mb-4">
                                                            <div className="flex items-center gap-3">
                                                                <div className={`w-3 h-3 rounded-full shadow-sm bg-${currentColor}-500 transition-colors duration-300`}></div>
                                                                <span className={`text-[10px] font-black uppercase tracking-widest transition-colors ${isChecked ? 'text-slate-900' : 'text-slate-500'}`}>{kpi.label}</span>
                                                            </div>
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={(e) => {
                                                                    let next = [...enabledKeys];
                                                                    if (e.target.checked) {
                                                                        if (!next.some(k => k.id === kpi.id)) next.push({ id: kpi.id, color: kpi.color });
                                                                    } else {
                                                                        next = next.filter(k => k.id !== kpi.id);
                                                                    }
                                                                    handleAction('update_event', {
                                                                        ...selectedEvent,
                                                                        kpis_enabled: JSON.stringify(next)
                                                                    });
                                                                }}
                                                                className="w-5 h-5 rounded-md border-2 border-slate-200 text-indigo-600 focus:ring-indigo-600 cursor-pointer"
                                                            />
                                                        </div>

                                                        {/* Color Palette */}
                                                        <div className={`flex flex-wrap gap-2 transition-all ${isChecked ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
                                                            {colors.map(c => (
                                                                <button
                                                                    key={c}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        let next = [...enabledKeys];
                                                                        const idx = next.findIndex(k => k.id === kpi.id);
                                                                        if (idx >= 0) {
                                                                            next[idx] = { ...next[idx], color: c };
                                                                            handleAction('update_event', {
                                                                                ...selectedEvent,
                                                                                kpis_enabled: JSON.stringify(next)
                                                                            });
                                                                        }
                                                                    }}
                                                                    className={`w-5 h-5 rounded-full border border-white shadow-sm transition-transform hover:scale-110 flex items-center justify-center bg-${c}-500 ${currentColor === c ? 'ring-2 ring-slate-900 scale-110' : ''}`}
                                                                    title={c}
                                                                >
                                                                    {currentColor === c && <Check size={10} className="text-white" />}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="pt-6 border-t border-slate-50 flex justify-end">
                                            <button onClick={() => setActiveModal(null)} className="px-10 py-4 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all">Cerrar Configuración</button>
                                        </div>
                                    </div>
                                )
                            }
                            {
                                activeModal === 'JOIN_PROJECT' && (
                                    <form className="space-y-6 animate-in" onSubmit={e => {
                                        e.preventDefault();
                                        handleAction('join_project', { invitation_code: new FormData(e.target).get('code') });
                                    }}>
                                        <div className="flex justify-between items-center mb-2">
                                            <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Unirse a Proyecto</h4>
                                        </div>
                                        <p className="text-slate-500 text-sm font-bold">Ingresa el código de invitación que recibiste para unirte a un proyecto.</p>
                                        <input name="code" required className="input-base text-center text-2xl font-black uppercase tracking-widest" placeholder="ABC123" maxLength={6} />
                                        <div className="flex gap-4">
                                            <button type="button" onClick={() => setActiveModal(null)} className="flex-1 py-5 rounded-2xl bg-slate-100 text-slate-500 font-black uppercase text-[11px] tracking-widest hover:bg-slate-200 transition-all">Cancelar</button>
                                            <button type="submit" className="flex-1 btn-primary py-5 rounded-2xl text-[11px] uppercase tracking-widest shadow-xl shadow-indigo-100"><Share2 size={18} /> Unirme</button>
                                        </div>
                                    </form>
                                )
                            }
                            {
                                activeModal === 'COMPANY_USERS' && modalData && (
                                    <div className="space-y-6 animate-in">
                                        <div className="flex justify-between items-center mb-4 border-b border-indigo-100 pb-4">
                                            <div>
                                                <h4 className="text-[10px] font-black uppercase text-indigo-400 tracking-widest">Usuarios de la Empresa / Licencia</h4>
                                                <h3 className="text-2xl font-black uppercase text-slate-900">{modalData.name}</h3>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Capacidad</p>
                                                <p className="text-xl font-black text-indigo-600">{modalData.user_count} / {modalData.max_users}</p>
                                            </div>
                                        </div>

                                        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                                            {appData.team.filter(u => u.company_id === modalData.id || u.companyId === modalData.id).length === 0 ? (
                                                <p className="text-center text-slate-400 font-bold uppercase text-xs py-10">No hay usuarios registrados en esta licencia.</p>
                                            ) : (
                                                appData.team.filter(u => u.company_id === modalData.id || u.companyId === modalData.id).map(u => (
                                                    <div key={u.id} className="flex justify-between items-center bg-slate-50 p-4 rounded-2xl border border-slate-100">
                                                        <div className="flex items-center gap-4">
                                                            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-700 font-black">{u.name[0]}</div>
                                                            <div>
                                                                <p className="font-bold text-xs uppercase text-slate-700">{u.name}</p>
                                                                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">{u.email}</p>
                                                            </div>
                                                        </div>
                                                        {(u.role !== 'SUPER_ADMIN' && u.role !== 'COMPANY_ADMIN') && (
                                                            <button
                                                                onClick={() => {
                                                                    if (confirm(`¿Eliminar usuario ${u.name} de esta empresa?`)) handleAction('delete_user', { user_id: u.id });
                                                                }}
                                                                className="p-2 text-rose-300 hover:text-rose-600 transition-colors"
                                                                title="Eliminar Usuario"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )
                            }
                            {
                                (activeModal === 'ADD_MATERIAL' || activeModal === 'EDIT_MATERIAL') && (
                                    <form className="space-y-5" onSubmit={e => {
                                        e.preventDefault();
                                        const fd = new FormData(e.target);
                                        const payload = {
                                            name: fd.get('name'),
                                            unit: fd.get('unit'),
                                            quantity_est: fd.get('qty_est'),
                                            quantity_real: fd.get('qty_real'),
                                            unit_price: fd.get('unit_price'),
                                            notes: fd.get('notes'),
                                            task_id: fd.get('task_id')
                                        };
                                        if (activeModal === 'EDIT_MATERIAL') {
                                            handleAction('update_material', { ...payload, id: modalData.id });
                                        } else {
                                            handleAction('add_material', payload);
                                        }
                                    }}>
                                        <div className="flex justify-between items-center mb-2">
                                            <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{activeModal === 'EDIT_MATERIAL' ? 'Editar Material' : 'Nuevo Material'}</h4>
                                            {activeModal === 'EDIT_MATERIAL' && (
                                                <button type="button" onClick={() => { if (confirm("¿Eliminar material?")) handleAction('delete_material', { id: modalData.id }) }} className="text-rose-500 hover:bg-rose-50 p-2 rounded-lg transition-all"><Trash2 size={16} /></button>
                                            )}
                                        </div>
                                        <input name="name" required className="input-base" placeholder="Nombre (ej: Cemento Portland)" defaultValue={modalData?.name} />
                                        <div className="grid grid-cols-3 gap-4">
                                            <input name="unit" required className="input-base" placeholder="Unidad (kg, m3)" defaultValue={modalData?.unit} />
                                            <input name="qty_est" type="number" step="0.01" required className="input-base" placeholder="Cant. Est." defaultValue={modalData?.quantity_est} onChange={() => {
                                                const p = parseFloat(document.getElementsByName('unit_price')[0].value) || 0;
                                                const q = parseFloat(document.getElementsByName('qty_est')[0].value) || 0;
                                                document.getElementById('mat_total_calc').innerText = (p * q).toLocaleString('es-AR', { minimumFractionDigits: 2 });
                                            }} />
                                            <input name="qty_real" type="number" step="0.01" className="input-base" placeholder="Cant. Real" defaultValue={modalData?.quantity_real || 0} />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-black text-slate-400 uppercase ml-2">Precio Unitario ({selectedEvent?.base_currency || 'ARS'})</label>
                                                <input name="unit_price" type="number" step="0.01" required className="input-base" placeholder="0.00" defaultValue={modalData?.unit_price || 0} onChange={() => {
                                                    const p = parseFloat(document.getElementsByName('unit_price')[0].value) || 0;
                                                    const q = parseFloat(document.getElementsByName('qty_est')[0].value) || 0;
                                                    document.getElementById('mat_total_calc').innerText = (p * q).toLocaleString('es-AR', { minimumFractionDigits: 2 });
                                                }} />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-black text-slate-400 uppercase ml-2">Fase Relacionada</label>
                                                <select name="task_id" className="input-base" defaultValue={modalData?.task_id || ''}>
                                                    <option value="">(Ninguna)</option>
                                                    {(appData.tasks || []).filter(t => t.event_id === selectedEventId && !t.parent_id).map(t => (
                                                        <option key={t.id} value={t.id}>{t.title}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="bg-slate-900 text-white p-6 rounded-[2rem] flex justify-between items-center shadow-lg">
                                            <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">Total Est.:</span>
                                            <span className="text-xl font-black">
                                                {selectedEvent?.base_currency || 'ARS'} <span id="mat_total_calc">{((modalData?.unit_price || 0) * (modalData?.quantity_est || 0)).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                                            </span>
                                        </div>
                                        <textarea name="notes" className="input-base h-24 pt-4" placeholder="Notas adicionales..." defaultValue={modalData?.notes}></textarea>
                                        <button type="submit" className="btn-primary w-full py-5 uppercase font-black">Guardar Material</button>
                                    </form>
                                )
                            }
                            {
                                activeModal === 'CALCULATE_MATERIALS' && (
                                    <div className="space-y-6 animate-in">
                                        <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Asistente de Cálculo de Materiales</h4>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase">Nombre Referencia (ej: Pared Cocina)</label>
                                            <input id="calc_name" className="input-base text-lg" placeholder="Identificador del trabajo" />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-bold text-slate-500 uppercase">1. Rubro / Tipo de Tarea</label>
                                                <select id="calc_group" className="input-base font-bold bg-slate-50" onChange={(e) => {
                                                    const group = e.target.value;
                                                    const typeSelect = document.getElementById('calc_type');
                                                    typeSelect.innerHTML = '<option value="">Seleccionar variante...</option>';
                                                    Object.entries(MATERIAL_MATRIX).filter(([k, v]) => v.group === group).forEach(([k, v]) => {
                                                        const opt = document.createElement('option');
                                                        opt.value = k;
                                                        opt.innerText = v.name;
                                                        typeSelect.appendChild(opt);
                                                    });
                                                }}>
                                                    <option value="">Seleccionar...</option>
                                                    {[...new Set(Object.values(MATERIAL_MATRIX).map(m => m.group))].map(g => (
                                                        <option key={g} value={g}>{g}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-bold text-slate-500 uppercase">2. Elemento Constructivo</label>
                                                <select id="calc_type" className="input-base font-bold border-indigo-200" onChange={(e) => {
                                                    const type = e.target.value;
                                                    const unitLabel = document.getElementById('calc_unit_label');
                                                    if (MATERIAL_MATRIX[type]) {
                                                        unitLabel.innerText = MATERIAL_MATRIX[type].unit;
                                                    }
                                                }}>
                                                    <option value="">Selecciona rubro primero...</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="space-y-4 p-6 bg-slate-50/50 rounded-3xl border border-dashed border-slate-200">
                                            <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">Calculadora Geométrica (Opcional)</p>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Ancho / Lado A (m)</label>
                                                    <input id="calc_side_a" type="number" step="0.01" className="input-base" placeholder="0.00" onChange={() => {
                                                        const a = parseFloat(document.getElementById('calc_side_a').value) || 0;
                                                        const b = parseFloat(document.getElementById('calc_side_b').value) || 0;
                                                        const t = parseFloat(document.getElementById('calc_thick').value) || 1;
                                                        const unitStr = (document.getElementById('calc_unit_label').innerText || '').toUpperCase();
                                                        const qtyInput = document.getElementById('calc_qty');
                                                        if (unitStr === 'M2') qtyInput.value = (a * b).toFixed(2);
                                                        if (unitStr === 'M3') qtyInput.value = (a * b * t).toFixed(2);
                                                    }} />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Largo / Lado B (m)</label>
                                                    <input id="calc_side_b" type="number" step="0.01" className="input-base" placeholder="0.00" onChange={() => {
                                                        const a = parseFloat(document.getElementById('calc_side_a').value) || 0;
                                                        const b = parseFloat(document.getElementById('calc_side_b').value) || 0;
                                                        const t = parseFloat(document.getElementById('calc_thick').value) || 1;
                                                        const unitStr = (document.getElementById('calc_unit_label').innerText || '').toUpperCase();
                                                        const qtyInput = document.getElementById('calc_qty');
                                                        if (unitStr === 'M2') qtyInput.value = (a * b).toFixed(2);
                                                        if (unitStr === 'M3') qtyInput.value = (a * b * t).toFixed(2);
                                                    }} />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Espesor / Alt (m)</label>
                                                    <input id="calc_thick" type="number" step="0.01" className="input-base" placeholder="0.00" onChange={() => {
                                                        const a = parseFloat(document.getElementById('calc_side_a').value) || 0;
                                                        const b = parseFloat(document.getElementById('calc_side_b').value) || 0;
                                                        const t = parseFloat(document.getElementById('calc_thick').value) || 0;
                                                        const unitStr = (document.getElementById('calc_unit_label').innerText || '').toUpperCase();
                                                        const qtyInput = document.getElementById('calc_qty');
                                                        if (unitStr === 'M3') qtyInput.value = (a * b * t).toFixed(2);
                                                    }} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-bold text-slate-500 uppercase">Cantidad / Volumen a Calcular</label>
                                                <div className="relative">
                                                    <input id="calc_qty" type="number" step="0.01" className="input-base text-xl font-black text-indigo-600" placeholder="0.00" />
                                                    <span id="calc_unit_label" className="absolute right-4 top-4 text-xs font-black text-slate-400 uppercase">---</span>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-bold text-slate-500 uppercase">Asignar a Rubro Presupuestario</label>
                                                <select id="calc_cat" className="input-base font-black uppercase text-xs">
                                                    <option value="">-- Sin Asignar --</option>
                                                    {appData.categories.filter(c => c.event_id === selectedEventId).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100" id="calc_preview">
                                            <p className="text-center text-xs text-slate-400">Selecciona un elemento y cantidad para ver el desglose.</p>
                                        </div>
                                        <button onClick={async () => {
                                            const type = document.getElementById('calc_type').value;
                                            const qty = parseFloat(document.getElementById('calc_qty').value);
                                            const elemName = document.getElementById('calc_name').value;
                                            const catId = document.getElementById('calc_cat').value;

                                            if (!type || !qty || !elemName) return alert("Completa todos los campos (Tipo, Cantidad y Nombre del Elemento)");

                                            const mat = MATERIAL_MATRIX[type];
                                            const catName = (appData.categories.find(c => c.id === catId && c.event_id === selectedEventId) || {}).name || "Sin Asignar";

                                            const previewHtml = mat.components.map(c => {
                                                const total = (c.qty * qty).toFixed(2);
                                                return `<div class="flex justify-between text-xs font-bold text-slate-700 border-b border-slate-100 py-2">
                                        <span>${c.name}</span>
                                        <span>${total} ${c.unit}</span>
                                    </div>`;
                                            }).join('');

                                            const container = document.getElementById('calc_preview');
                                            container.innerHTML = `
                                    <div class="bg-indigo-50 p-4 rounded-xl mb-4">
                                        <p class="text-[10px] uppercase font-black text-indigo-400">Elemento a Crear</p>
                                        <p class="text-lg font-black text-indigo-900">${elemName}</p>
                                        <p class="text-xs font-bold text-indigo-500">${mat.name} x ${qty}</p>
                                        <div class="mt-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 border-t border-indigo-100 pt-2">Rubro: ${catName}</div>
                                    </div>
                                    <h5 class="font-black uppercase text-[10px] text-slate-400 mb-3">Materiales Requeridos</h5>
                                    ${previewHtml}
                                    <button id="btn_confirm_calc" class="w-full mt-4 bg-indigo-600 text-white py-2 rounded-lg text-xs font-black uppercase">Confirmar y Generar</button>
                                `;

                                            document.getElementById('btn_confirm_calc').onclick = async () => {
                                                // 1. Create the Element (Tracked Item for Computo)
                                                const details = `Cálculo: ${qty} ${mat.unit} de ${mat.name}\nMateriales: ${mat.components.map(c => c.name).join(', ')}`;

                                                await handleAction('add_structure', {
                                                    name: elemName,
                                                    type: 'ALBAÑILERIA',
                                                    status: 'PLANIFICADO',
                                                    details: details,
                                                    quantity: 1,
                                                    category_id: catId
                                                });

                                                // 2. Crear Insumos
                                                for (const c of mat.components) {
                                                    const totalMat = parseFloat((c.qty * qty).toFixed(2));
                                                    await handleAction('add_material', {
                                                        name: `${c.name} (${mat.name})`,
                                                        unit: c.unit,
                                                        quantity_est: totalMat,
                                                        notes: `${elemName}`,
                                                        category_id: catId
                                                    });
                                                }
                                                setActiveModal(null);
                                                alert("Elemento y materiales creados correctamente.");
                                            };
                                        }} className="btn-primary w-full py-4 uppercase font-black text-xs">Calcular</button>
                                    </div>
                                )
                            }

                            {/* MDO MODALS logic consolidated into bottom-level components for consistency */}
                            {
                                activeModal === 'ADD_STRUCTURE_WIZARD' && (
                                    <form className="space-y-5" onSubmit={e => {
                                        e.preventDefault();
                                        const fd = new FormData(e.target);
                                        const baseName = fd.get('base_name'); // e.g. "Columna C"
                                        const type = fd.get('type');
                                        const levels = fd.get('levels').split('\n').filter(l => l.trim() !== ''); // e.g. ["PB", "Piso 1"]
                                        const qtyPerLevel = parseInt(fd.get('qty_level')); // e.g. 5

                                        if (levels.length === 0 || qtyPerLevel < 1) return alert("Define niveles y cantidad");

                                        // Generate batch
                                        levels.forEach(level => {
                                            for (let i = 1; i <= qtyPerLevel; i++) {
                                                const name = `${baseName}${i}`; // Columna C1
                                                const details = `Nivel: ${level.trim()}\nGenerado automáticamente.`;
                                                handleAction('add_structure', {
                                                    name: name,
                                                    type: type,
                                                    status: 'CALCULADO',
                                                    details: details
                                                });
                                            }
                                        });
                                        setActiveModal(null);
                                    }}>
                                        <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Asistente de Estructuras (Replicar)</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-bold text-slate-500 uppercase">Prefijo Elemento</label>
                                                <input name="base_name" required className="input-base" placeholder="Ej: Columna C" />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-bold text-slate-500 uppercase">Tipo</label>
                                                <select name="type" className="input-base font-bold">
                                                    <option value="COLUMNA">Columna</option>
                                                    <option value="VIGA">Viga</option>
                                                    <option value="LOSA">Losa</option>
                                                    <option value="CIMENTACION">Cimentación</option>
                                                    <option value="MURO">Muro</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase">Niveles (Uno por línea)</label>
                                            <textarea name="levels" required className="input-base h-24 pt-4" placeholder="PB&#10;Piso 1&#10;Piso 2"></textarea>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase">Cantidad por Nivel</label>
                                            <input name="qty_level" type="number" min="1" required className="input-base" placeholder="Ej: 4" />
                                        </div>
                                        <button type="submit" className="btn-primary w-full py-5 uppercase font-black">Generar Estructura</button>
                                    </form>
                                )
                            }
                            {
                                activeModal === 'ADD_STRUCTURE_CALC' && (
                                    <div className="space-y-6 animate-in">
                                        <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Calculadora Estructural (Predimensionado)</h4>

                                        <div className="flex gap-2 p-1 bg-slate-100 rounded-xl mb-4">
                                            {['COLUMNA', 'BASE', 'VIGA', 'LOSA'].map(t => (
                                                <button
                                                    key={t}
                                                    onClick={() => setModalData(prev => ({ ...prev, type: t }))}
                                                    className={`flex-1 py-2 text-[9px] font-black uppercase rounded-lg transition-all ${modalData?.type === t ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
                                                >
                                                    {t === 'BASE' ? 'BASE / ZAPATA' : t}
                                                </button>
                                            ))}
                                        </div>

                                        <form className="space-y-5" onSubmit={async e => {
                                            e.preventDefault();
                                            const fd = new FormData(e.target);
                                            const type = modalData?.type || 'COLUMNA';
                                            const qty = parseInt(fd.get('quantity') || 1);
                                            const name = fd.get('name');

                                            const ironWeights = { 6: 0.222, 8: 0.395, 10: 0.617, 12: 0.888, 16: 1.58, 20: 2.47, 25: 3.85, 32: 6.31 };
                                            const concreteMix = { cement: 309.73, sand: 0.66, stone: 0.66 };

                                            let details = '';
                                            let materialsToAdd = [];

                                            const meshType = fd.get('mesh_type') || 'Q188';
                                            const meshModule = 10;

                                            if (type === 'COLUMNA') {
                                                const p_load = parseFloat(fd.get('p_load')); // kg
                                                const pu = p_load * 1.30;
                                                const lu = parseFloat(fd.get('lu')) / 100;
                                                const areaRequiredCm2 = pu / (0.45 * 210);
                                                const side = Math.ceil(Math.sqrt(areaRequiredCm2) / 5) * 5;
                                                const finalSide = Math.max(20, side);
                                                const sectionAreaM2 = (finalSide * finalSide) / 10000;
                                                const as = (finalSide * finalSide) * 0.01;
                                                const barDiam = 12;
                                                const barArea = (Math.PI * Math.pow(barDiam / 10, 2)) / 4;
                                                const barCount = Math.max(4, Math.ceil(as / barArea));
                                                const volConc = sectionAreaM2 * lu;
                                                const kgMain = barCount * lu * ironWeights[barDiam];
                                                const stirrupLen = (finalSide * 4) / 100;
                                                const stirrupCount = Math.ceil(lu / 0.15);
                                                const kgStirrups = stirrupCount * stirrupLen * ironWeights[6];
                                                details = `P: ${p_load} kg -> PU: ${pu.toFixed(0)} kg\nSección: ${finalSide}x${finalSide} cm | Altura: ${lu} m\nHormigón: ${volConc.toFixed(2)} m³ | Armadura: ${barCount}xØ${barDiam}`;
                                                materialsToAdd.push(
                                                    { name: `Cemento Portland (Hormigón Piedra)`, unit: 'kg', qty: (concreteMix.cement * volConc * qty).toFixed(2), notes: `${name} (${finalSide}x${finalSide})` },
                                                    { name: `Arena Gruesa`, unit: 'm3', qty: (concreteMix.sand * volConc * qty).toFixed(3), notes: `${name} (${finalSide}x${finalSide})` },
                                                    { name: `Piedra Partida`, unit: 'm3', qty: (concreteMix.stone * volConc * qty).toFixed(3), notes: `${name} (${finalSide}x${finalSide})` },
                                                    { name: `Hierro Ø${barDiam}mm`, unit: 'kg', qty: (kgMain * qty).toFixed(2), notes: `Barras Principales ${name}` },
                                                    { name: `Hierro Ø6mm`, unit: 'kg', qty: (kgStirrups * qty).toFixed(2), notes: `Estribos ${name}` }
                                                );
                                            } else if (type === 'BASE') {
                                                const p_load = parseFloat(fd.get('p_load')); // kg
                                                const pu = p_load * 1.30;
                                                const sigmaT = parseFloat(fd.get('sigma_t')); // kg/cm2
                                                const baseB = parseFloat(fd.get('base_b')) || 30;
                                                const areaNeededCm2 = (pu * 1.1) / sigmaT;
                                                const side = Math.ceil(Math.sqrt(areaNeededCm2) / 5) * 5;
                                                const finalSide = Math.max(80, side);
                                                const height = 0.40;
                                                const volConc = (finalSide / 100) * (finalSide / 100) * height;
                                                const kgSteel = (finalSide / 100) * (finalSide / 100) * 12;
                                                details = `P: ${p_load} kg -> PU: ${pu.toFixed(0)} kg\nBase Centrada: ${finalSide}x${finalSide} cm | Altura: ${height}m | σt: ${sigmaT} kg/cm²\nColumna: ${baseB}cm | Hormigón: ${volConc.toFixed(2)} m³`;
                                                materialsToAdd.push(
                                                    { name: `Cemento Portland (Hormigón Piedra)`, unit: 'kg', qty: (concreteMix.cement * volConc * qty).toFixed(2), notes: `${name} ${finalSide}x${finalSide}` },
                                                    { name: `Arena Gruesa`, unit: 'm3', qty: (concreteMix.sand * volConc * qty).toFixed(3), notes: `${name} ${finalSide}x${finalSide}` },
                                                    { name: `Piedra Partida`, unit: 'm3', qty: (concreteMix.stone * volConc * qty).toFixed(3), notes: `${name} ${finalSide}x${finalSide}` },
                                                    { name: `Hierro Ø10mm`, unit: 'kg', qty: (kgSteel * qty).toFixed(2), notes: `Parrilla Base ${name}` }
                                                );
                                            } else if (type === 'VIGA') {
                                                const span = parseFloat(fd.get('span'));
                                                const vigaQ = parseFloat(fd.get('viga_q')) || 0; // kg/m
                                                const vigaB = parseFloat(fd.get('viga_b')) || 20;
                                                const vigaHInput = parseFloat(fd.get('viga_h'));
                                                const vigaH = vigaHInput || Math.ceil((span * 10) / 5) * 5;
                                                const volConc = (vigaB / 100) * (vigaH / 100) * span;

                                                details = `Viga: ${vigaB}x${vigaH} cm | Largo: ${span}m${vigaQ > 0 ? ` | Carga: ${vigaQ} kg/m` : ''}\nHormigón: ${volConc.toFixed(2)} m³`;
                                                materialsToAdd.push(
                                                    { name: `Cemento Portland (Hormigón Piedra)`, unit: 'kg', qty: (concreteMix.cement * volConc * qty).toFixed(2), notes: `Viga ${name}` },
                                                    { name: `Arena Gruesa`, unit: 'm3', qty: (concreteMix.sand * volConc * qty).toFixed(3), notes: `Viga ${name}` },
                                                    { name: `Piedra Partida`, unit: 'm3', qty: (concreteMix.stone * volConc * qty).toFixed(3), notes: `Viga ${name}` },
                                                    { name: `Hierro Ø12mm`, unit: 'kg', qty: (4 * span * ironWeights[12] * qty).toFixed(2), notes: `Viga ${name}` }
                                                );
                                            } else if (type === 'LOSA') {
                                                const lx = parseFloat(fd.get('lx'));
                                                const lyInput = parseFloat(fd.get('ly'));
                                                const ly = lyInput || lx;
                                                const losaQ = parseFloat(fd.get('losa_q')) || 0; // kg/m²
                                                const losaE = parseFloat(fd.get('losa_e')) || 12;
                                                const area = lx * ly;
                                                const losa_tech = fd.get('losa_tech') || (losaQ > 500 ? 'MACIZA' : 'VIGUETAS');
                                                const losaType = lyInput ? `${lx}x${ly}` : `${lx}x${lx}`;

                                                if (losa_tech === 'VIGUETAS') {
                                                    const volConc = area * 0.05;
                                                    const viguetasQty = Math.ceil(lx / 0.5) + 1;
                                                    const viguetasTotalMeters = viguetasQty * lx;
                                                    const ladrillosQty = Math.ceil(area * 8.5);

                                                    details = `Losa Viguetas: ${losaType} m | Carga: ${losaQ} kg/m²\nHormigón (Capa Comp.): ${volConc.toFixed(2)} m³`;
                                                    materialsToAdd.push(
                                                        { name: `Viguetas de Hormigón (${lx}m)`, unit: 'ml', qty: (viguetasTotalMeters * qty).toFixed(1), notes: `Losa ${name}` },
                                                        { name: `Ladrillos Sapo (Copol para Losa)`, unit: 'u', qty: (ladrillosQty * qty), notes: `Losa ${name}` },
                                                        { name: `Cemento Portland (Hormigón Piedra)`, unit: 'kg', qty: (concreteMix.cement * volConc * qty).toFixed(2), notes: `Losa ${name}` },
                                                        { name: `Arena Gruesa`, unit: 'm3', qty: (concreteMix.sand * volConc * qty).toFixed(3), notes: `Losa ${name}` },
                                                        { name: `Piedra Partida`, unit: 'm3', qty: (concreteMix.stone * volConc * qty).toFixed(3), notes: `Losa ${name}` },
                                                        { name: `Malla Sima ${meshType}`, unit: 'u', qty: (Math.ceil(area / meshModule) * qty), notes: `Losa ${name}` }
                                                    );
                                                } else {
                                                    const volConc = area * (losaE / 100);
                                                    details = `Losa Maciza: ${losaType} m | Espesor: ${losaE} cm | Carga: ${losaQ} kg/m²\nHormigón: ${volConc.toFixed(2)} m³`;
                                                    materialsToAdd.push(
                                                        { name: `Cemento Portland (Hormigón Piedra)`, unit: 'kg', qty: (concreteMix.cement * volConc * qty).toFixed(2), notes: `Losa ${name}` },
                                                        { name: `Arena Gruesa`, unit: 'm3', qty: (concreteMix.sand * volConc * qty).toFixed(3), notes: `Losa ${name}` },
                                                        { name: `Piedra Partida`, unit: 'm3', qty: (concreteMix.stone * volConc * qty).toFixed(3), notes: `Losa ${name}` },
                                                        { name: `Malla Sima ${meshType}`, unit: 'u', qty: (Math.ceil(area / meshModule) * qty), notes: `Losa ${name}` }
                                                    );
                                                }
                                            }

                                            // Mostrar Vista Previa
                                            const previewHtml = `
                                    <div class="bg-indigo-50 p-6 rounded-3xl border border-indigo-100 animate-in space-y-4">
                                        <div>
                                           <p class="text-[9px] font-black uppercase text-indigo-400 mb-1">Resultado del Cálculo</p>
                                           <h5 class="text-xl font-black text-indigo-900 leading-none">${name} (x${qty})</h5>
                                        <p class="text-xs font-bold text-indigo-500 mt-1">${details.replace(/\n/g, '<br/>')}</p>
                                    </div>
                                    <div class="space-y-2 border-t border-indigo-100 pt-4">
                                        ${materialsToAdd.map(m => `
                                                <div class="flex justify-between text-xs font-black text-slate-700">
                                                    <span>${m.name}</span>
                                                    <span class="text-indigo-600">${m.qty} ${m.unit}</span>
                                                </div>
                                            `).join('')}
                                    </div>
                                    <button id="btn_confirm_struct" class="btn-primary w-full py-4 text-[10px] uppercase font-black bg-indigo-600 border-none shadow-indigo-100">Confirmar y Guardar Todos los Insumos</button>
                                </div>
                                `;

                                            const container = document.getElementById('struct_calc_preview');
                                            container.innerHTML = previewHtml;
                                            container.scrollIntoView({ behavior: 'smooth' });

                                            // Acción de Guardado Real
                                            document.getElementById('btn_confirm_struct').onclick = async () => {
                                                await handleAction('add_structure', {
                                                    name: name, type: type, quantity: qty, status: 'CALCULADO',
                                                    details: details + "\n\n" + (fd.get('notes') || '')
                                                });
                                                for (const m of materialsToAdd) {
                                                    await handleAction('add_material', {
                                                        name: `${m.name} (${name})`, unit: m.unit,
                                                        quantity_est: parseFloat(m.qty), notes: m.notes
                                                    });
                                                }
                                                setActiveModal(null);
                                                alert("Estructura y materiales guardados correctamente.");
                                            };
                                        }}>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="col-span-2 space-y-1">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Nombre / Identificador</label>
                                                    <input name="name" required className="input-base" placeholder="Ej: Columna C1" />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase ml-2 text-indigo-500">Cantidad</label>
                                                    <input name="quantity" type="number" min="1" required className="input-base font-black text-indigo-600" defaultValue="1" />
                                                </div>
                                            </div>

                                            {(modalData?.type || 'COLUMNA') === 'COLUMNA' && (
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Carga P (kg)</label>
                                                        <input name="p_load" type="number" step="1" required className="input-base" placeholder="ej: 8500" />
                                                        <p className="text-[8px] text-indigo-400 font-bold ml-2">PU se calculará como P * 1.30</p>
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Altura Lib. Lu (cm)</label>
                                                        <input name="lu" type="number" required className="input-base" placeholder="ej: 355" />
                                                    </div>
                                                </div>
                                            )}
                                            {(modalData?.type) === 'BASE' && (
                                                <div className="space-y-4">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Carga P (kg)</label>
                                                            <input name="p_load" type="number" step="1" required className="input-base" placeholder="ej: 8500" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Tensión Suelo σt (kg/cm²)</label>
                                                            <input name="sigma_t" type="number" step="0.01" required className="input-base" placeholder="ej: 1.5" />
                                                        </div>
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Ancho de Columna b (cm)</label>
                                                        <input name="base_b" type="number" step="1" required className="input-base" placeholder="ej: 30" />
                                                    </div>
                                                </div>
                                            )}
                                            {(modalData?.type) === 'VIGA' && (
                                                <div className="space-y-4">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Luz / Largo L (m)</label>
                                                            <input name="span" type="number" step="0.1" required className="input-base" placeholder="ej: 4.5" onChange={(e) => {
                                                                const span = parseFloat(e.target.value);
                                                                const warn = document.getElementById('viga_warning');
                                                                if (warn) {
                                                                    warn.textContent = span > 5 ? "⚠️ Luz > 5m: Requiere evaluación de viga estructural o pretensado." : "";
                                                                }
                                                            }} />
                                                            <p id="viga_warning" className="text-[8px] font-black text-rose-500 uppercase ml-2"></p>
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Carga Distribuida q (kg/m)</label>
                                                            <input name="viga_q" type="number" step="1" className="input-base" placeholder="ej: 1500" />
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Ancho b (cm)</label>
                                                            <input name="viga_b" type="number" step="1" className="input-base" placeholder="ej: 20" defaultValue="20" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Altura h (cm) - opcional</label>
                                                            <input name="viga_h" type="number" step="1" className="input-base" placeholder="Auto-calculado" />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {(modalData?.type) === 'LOSA' && (
                                                <div className="space-y-4">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Luz Menor Lx (m)</label>
                                                            <input name="lx" type="number" step="0.1" required className="input-base" placeholder="ej: 3.0" onChange={() => {
                                                                // Trigger global recom check
                                                                const qInput = document.getElementsByName('losa_q')[0];
                                                                const event = new Event('change', { bubbles: true });
                                                                qInput.dispatchEvent(event);
                                                            }} />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Luz Mayor Ly (m) - opcional</label>
                                                            <input name="ly" type="number" step="0.1" className="input-base" placeholder="Vacío = cuadrada" />
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Naturaleza de Carga</label>
                                                            <select className="input-base font-bold text-[10px]" onChange={(e) => {
                                                                const qInput = e.target.form.elements['losa_q'];
                                                                if (qInput) qInput.value = e.target.value;
                                                                const event = new Event('change', { bubbles: true });
                                                                qInput.dispatchEvent(event);
                                                            }}>
                                                                <option value="">Personalizado...</option>
                                                                <option value="200">Vivienda (200 kg/m²)</option>
                                                                <option value="300">Oficina / Aula (300 kg/m²)</option>
                                                                <option value="500">Comercial / Gym (500 kg/m²)</option>
                                                                <option value="750">Depósito Pesado (750 kg/m²)</option>
                                                            </select>
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Carga Distribuida q (kg/m²)</label>
                                                            <input name="losa_q" type="number" step="1" className="input-base" placeholder="ej: 500" onChange={(e) => {
                                                                const q = parseFloat(e.target.value);
                                                                const lx = parseFloat(document.getElementsByName('lx')[0]?.value || 0);
                                                                const select = document.getElementById('losa_tech_select');
                                                                const warn = document.getElementById('losa_recommendation_text');

                                                                if (select && lx > 0) {
                                                                    let rec = "";
                                                                    let sys = "VIGUETAS";

                                                                    if (lx < 3.0) {
                                                                        rec = "Luz < 3m: Losa maciza recomendada.";
                                                                        sys = "MACIZA";
                                                                    } else if (lx <= 4.5) {
                                                                        rec = "Luz 3-4.5m: Vigueta + Sapo ideal.";
                                                                        sys = "VIGUETAS";
                                                                    } else if (lx <= 5.5) {
                                                                        rec = "Luz 4.5-5.5m: Requiere viguetas reforzadas.";
                                                                        sys = "VIGUETAS";
                                                                    } else {
                                                                        rec = "Luz > 6m: Se recomienda sistema PRETENSADO.";
                                                                        sys = "PRETENSADO";
                                                                    }

                                                                    if (q > 7.2) rec += " (Carga crítica detectada)";

                                                                    warn.textContent = rec;
                                                                    select.value = sys;
                                                                    // Trigger select change to update default thickness/mesh
                                                                    const event = new Event('change', { bubbles: true });
                                                                    select.dispatchEvent(event);
                                                                }
                                                            }} />
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2 text-indigo-500">Sistema Sugerido</label>
                                                            <select name="losa_tech" id="losa_tech_select" className="input-base font-bold text-xs border-indigo-200" defaultValue="VIGUETAS" onChange={(e) => {
                                                                const modal = e.target.form;
                                                                if (e.target.value === 'MACIZA') {
                                                                    modal.elements['losa_e'].value = 15;
                                                                    modal.elements['mesh_type'].value = 'Q188';
                                                                } else if (e.target.value === 'PRETENSADO') {
                                                                    modal.elements['losa_e'].value = 0;
                                                                } else {
                                                                    modal.elements['losa_e'].value = 12;
                                                                    modal.elements['mesh_type'].value = 'Q92';
                                                                }
                                                            }}>
                                                                <option value="VIGUETAS">Viguetas y Ladrillos Sapo</option>
                                                                <option value="MACIZA">Hormigón y Malla Sima</option>
                                                                <option value="PRETENSADO">Vigas/Losas Pretensadas (+6m)</option>
                                                            </select>
                                                            <p id="losa_recommendation_text" className="text-[8px] font-black text-indigo-500 uppercase ml-2 mt-1"></p>
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2 text-indigo-500">Tipo de Malla</label>
                                                            <select name="mesh_type" className="input-base font-black text-xs text-indigo-600 border-indigo-100 bg-indigo-50/20" defaultValue="Q188">
                                                                <option value="Q92">Q92 (ø4.2 c/15)</option>
                                                                <option value="Q188">Q188 (ø6 c/15)</option>
                                                                <option value="Q257">Q257 (ø7 c/15)</option>
                                                                <option value="Q335">Q335 (ø8 c/15)</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Espesor e (cm)</label>
                                                        <input name="losa_e" type="number" step="1" className="input-base" placeholder="ej: 12" defaultValue="12" />
                                                    </div>
                                                </div>
                                            )}

                                            <textarea name="notes" className="input-base h-24 pt-4" placeholder="Notas adicionales..."></textarea>

                                            <button type="submit" className="btn-primary w-full py-5 uppercase font-black">Calcular Predimensionado</button>
                                            <div id="struct_calc_preview" className="mt-6"></div>
                                        </form>
                                    </div>
                                )
                            }
                            {
                                (activeModal === 'ADD_STRUCTURE' || activeModal === 'EDIT_STRUCTURE') && (
                                    <form className="space-y-5" onSubmit={e => {
                                        e.preventDefault();
                                        const fd = new FormData(e.target);
                                        const payload = {
                                            name: fd.get('name'),
                                            type: fd.get('type'),
                                            status: fd.get('status'),
                                            details: fd.get('details'),
                                            quantity: fd.get('quantity') || 1,
                                            category_id: fd.get('category_id')
                                        };
                                        if (activeModal === 'EDIT_STRUCTURE') {
                                            handleAction('update_structure', { ...payload, id: modalData.id });
                                        } else {
                                            handleAction('add_structure', payload);
                                        }
                                    }}>
                                        <div className="flex justify-between items-center mb-2">
                                            <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{activeModal === 'EDIT_STRUCTURE' ? 'Editar Elemento' : 'Nuevo Elemento'}</h4>
                                            {activeModal === 'EDIT_STRUCTURE' && (
                                                <button type="button" onClick={async () => {
                                                    if (confirm("¿Eliminar elemento? Se borrarán también los materiales calculados.")) {
                                                        await handleAction('delete_structure', { id: modalData.id });
                                                    }
                                                }} className="text-rose-500 hover:bg-rose-50 p-2 rounded-lg transition-all"><Trash2 size={16} /></button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <input name="name" required className="input-base" placeholder="Nombre (ej: Losa L1)" defaultValue={modalData?.name} />
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Tipo</label>
                                                <select name="type" className="input-base font-bold" defaultValue={modalData?.type || 'LOSA'}>
                                                    <option value="LOSA">Losa</option>
                                                    <option value="VIGA">Viga</option>
                                                    <option value="COLUMNA">Columna</option>
                                                    <option value="CIMENTACION">Cimentación</option>
                                                    <option value="MURO">Muro</option>
                                                    <option value="ALBAÑILERIA">Albañilería / Cómputo</option>
                                                    <option value="OTRO">Otro</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Cantidad</label>
                                                <input name="quantity" type="number" min="1" required className="input-base" defaultValue={modalData?.quantity || 1} />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Estado</label>
                                                <select name="status" className="input-base font-bold" defaultValue={modalData?.status || 'PENDIENTE'}>
                                                    <option value="PENDIENTE">Pendiente cálculo</option>
                                                    <option value="CALCULADO">Calculado</option>
                                                    <option value="VERIFICADO">Verificado</option>
                                                    <option value="EN EJECUCION">En Ejecución</option>
                                                    <option value="TERMINADO">Terminado</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[9px] font-bold text-slate-400 uppercase ml-2">Asociar a Rubro</label>
                                            <select name="category_id" className="input-base font-black uppercase text-xs" defaultValue={modalData?.category_id}>
                                                <option value="">-- Sin Asignar --</option>
                                                {appData.categories.filter(c => c.event_id === selectedEventId).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                            </select>
                                        </div>
                                        <textarea name="details" className="input-base h-32 pt-4" placeholder="Detalles constructivos, armadura, dimensiones..." defaultValue={modalData?.details}></textarea>
                                        <button type="submit" className="btn-primary w-full py-5 uppercase font-black">Guardar Elemento</button>
                                    </form>
                                )
                            }
                            {
                                activeModal === 'REPORTS_GENERATOR' && (
                                    <div className="space-y-6">
                                        <div className="bg-gradient-to-br from-indigo-600 to-violet-700 p-8 rounded-[3rem] text-white shadow-xl shadow-indigo-200 relative overflow-hidden group">
                                            <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -mr-24 -mt-24 group-hover:bg-white/20 transition-all duration-700"></div>
                                            <div className="relative flex items-center gap-8">
                                                <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-3xl flex items-center justify-center shadow-lg border border-white/30 rotate-3 shrink-0">
                                                    <FileText size={40} className="text-white" />
                                                </div>
                                                <div>
                                                    <h4 className="text-2xl font-black uppercase tracking-tighter leading-none mb-2">Generador de Reportes</h4>
                                                    <p className="text-sm font-medium text-indigo-100/90 leading-tight">Configura los filtros para extraer la información exacta que necesitas.</p>
                                                </div>
                                            </div>
                                        </div>

                                        <form className="space-y-6 bg-slate-50/50 p-8 rounded-[3rem] border border-slate-100" onSubmit={async e => {
                                            e.preventDefault();
                                            const fd = new FormData(e.target);
                                            setIsAiLoading(true);
                                            try {
                                                const res = await fetch(`${API_URL}?action=get_filtered_ppt_data`, {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({
                                                        company_id: currentUser?.company_id || currentUser?.companyId,
                                                        client_id: fd.get('client_id'),
                                                        status: fd.get('status'),
                                                        region: fd.get('region'),
                                                        country: fd.get('country'),
                                                        only_pending: fd.get('only_pending') === 'on'
                                                    })
                                                });
                                                const data = await res.json();
                                                if (data.status === 'success') {
                                                    if (data.projects.length === 0) {
                                                        alert("No se encontraron proyectos con esos filtros.");
                                                        return;
                                                    }
                                                    if (data.projects.length > 50 && !confirm(`Estás intentando generar un reporte con ${data.projects.length} proyectos. ¿Deseas continuar?`)) {
                                                        return;
                                                    }

                                                    const format = e.nativeEvent.submitter?.value || 'PPT';

                                                    if (format === 'EXCEL') {
                                                        exportToExcelFiltered(data.projects);
                                                    } else {
                                                        // Format for PPT generator
                                                        const slides = [
                                                            {
                                                                title: "Resumen Ejecutivo de Reporte",
                                                                content: [
                                                                    `Fecha de Generación: ${new Date().toLocaleDateString()}`,
                                                                    `Total de Proyectos: ${data.projects.length}`,
                                                                    `Empresa: ${currentUser?.company_name || 'FRS Gestión'}`,
                                                                    `Filtros aplicados: ${fd.get('status') || 'TODOS'} / ${fd.get('region') || 'TODAS'}`
                                                                ]
                                                            }
                                                        ];

                                                        // Slide: Projects Grid
                                                        const gridRows = [["Proyecto", "Cliente", "Avance (%)", "Teórico (%)", "Saldo", "Atrasos"]];
                                                        data.projects.forEach(p => {
                                                            gridRows.push([
                                                                p.name,
                                                                p.client,
                                                                `${p.progress}%`,
                                                                `${p.theoretical_progress}%`,
                                                                `${p.currency} ${Math.round(p.balance).toLocaleString()}`,
                                                                p.delayed_tasks_count.toString()
                                                            ]);
                                                        });
                                                        slides.push({
                                                            title: "Consolidado de Proyectos",
                                                            layout: "TABLE",
                                                            tableData: gridRows
                                                        });

                                                        // Slide: Status Chart
                                                        const statusCounts = {};
                                                        data.projects.forEach(p => statusCounts[p.status] = (statusCounts[p.status] || 0) + 1);
                                                        slides.push({
                                                            title: "Distribución por Estado",
                                                            layout: "CHART",
                                                            chartType: "doughnut",
                                                            chartData: [{
                                                                name: "Proyectos por Estado",
                                                                labels: Object.keys(statusCounts),
                                                                values: Object.values(statusCounts)
                                                            }]
                                                        });

                                                        // Slide: Zone Chart
                                                        const zoneCounts = {};
                                                        data.projects.forEach(p => zoneCounts[p.province_region] = (zoneCounts[p.province_region] || 0) + 1);
                                                        slides.push({
                                                            title: "Distribución por Zona / Provincia",
                                                            layout: "CHART",
                                                            chartType: "bar",
                                                            chartData: [{
                                                                name: "Proyectos por Zona",
                                                                labels: Object.keys(zoneCounts),
                                                                values: Object.values(zoneCounts)
                                                            }]
                                                        });

                                                        // Slides per project (Detailed)
                                                        data.projects.forEach(p => {
                                                            slides.push({
                                                                title: `Detalle: ${p.name}`,
                                                                content: [
                                                                    `Cliente: ${p.client}`,
                                                                    `Avance Real: ${p.progress}% (Teórico: ${p.theoretical_progress}%)`,
                                                                    `Presupuesto: ${p.currency} ${parseFloat(p.budget_total_contract).toLocaleString()}`,
                                                                    `Gastos: ${p.currency} ${parseFloat(p.expenses).toLocaleString()}`,
                                                                    `Saldo Pendiente: ${p.currency} ${parseFloat(p.balance).toLocaleString()}`,
                                                                    `Tareas Atrasadas: ${p.delayed_tasks_count}`,
                                                                    `Ubicación: ${p.location}, ${p.province_region}`
                                                                ]
                                                            });
                                                        });

                                                        generatePPTFromJSON({ slides }, data.projects.length === 1 ? data.projects[0].name : "Reporte_Multiproyecto");
                                                    }
                                                    setActiveModal(null);
                                                } else {
                                                    alert(data.message);
                                                }
                                            } catch (err) {
                                                alert("Error al obtener datos para el reporte: " + err.message);
                                            } finally {
                                                setIsAiLoading(false);
                                            }
                                        }}>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Cliente</label>
                                                    <select name="client_id" className="input-base">
                                                        <option value="">TODOS LOS CLIENTES</option>
                                                        {(appData.clients || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                    </select>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Estado del Proyecto</label>
                                                    <select name="status" className="input-base">
                                                        <option value="">TODOS LOS ESTADOS</option>
                                                        <option value="EN_CURSO">EN CURSO</option>
                                                        <option value="RETRASO_LEVE">RETRASO LEVE</option>
                                                        <option value="RETRASO_GRAVE">RETRASO GRAVE</option>
                                                        <option value="FINALIZADO">FINALIZADO</option>
                                                    </select>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Zona / Provincia</label>
                                                    <select name="region" className="input-base">
                                                        <option value="">TODAS LAS PROVINCIAS</option>
                                                        {[...new Set(appData.projects.map(p => p.province_region).filter(Boolean))].sort().map(reg => (
                                                            <option key={reg} value={reg}>{reg}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">País</label>
                                                    <select name="country" className="input-base">
                                                        <option value="">TODOS LOS PAÍSES</option>
                                                        {[...new Set(appData.projects.map(p => p.country).filter(Boolean))].sort().map(c => (
                                                            <option key={c} value={c}>{c}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
                                                <input name="only_pending" type="checkbox" id="chk_pending" className="w-5 h-5 rounded border-slate-300 text-indigo-600" />
                                                <label htmlFor="chk_pending" className="flex flex-col cursor-pointer">
                                                    <span className="text-[11px] font-black uppercase text-slate-700">Solo con Pendientes</span>
                                                    <span className="text-[9px] font-bold text-slate-400 uppercase">Filtra proyectos con tareas inacabadas o saldo pendiente</span>
                                                </label>
                                            </div>

                                            <div className="flex gap-4">
                                                <button
                                                    type="submit"
                                                    name="format"
                                                    value="PPT"
                                                    disabled={isAiLoading}
                                                    className="btn-primary flex-1 py-6 uppercase font-black tracking-widest bg-indigo-600 border-none shadow-indigo-100 flex items-center justify-center gap-3"
                                                >
                                                    {isAiLoading ? (
                                                        <div className="flex gap-1">
                                                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce"></div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <FileDown size={20} /> Generar PPT
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    type="submit"
                                                    name="format"
                                                    value="EXCEL"
                                                    disabled={isAiLoading}
                                                    className="flex-1 py-6 uppercase font-black tracking-widest bg-emerald-600 hover:bg-emerald-700 text-white rounded-[2rem] shadow-lg shadow-emerald-100 flex items-center justify-center gap-3 transition-all border-none"
                                                >
                                                    {isAiLoading ? (
                                                        <div className="flex gap-1">
                                                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                                            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce"></div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <FileSpreadsheet size={20} /> Generar Excel
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                )
                            }
                            {
                                activeModal === 'AI_PLANNER' && (
                                    <div className="space-y-6">
                                        <div className="bg-gradient-to-br from-indigo-600 to-violet-700 p-6 rounded-[2.5rem] text-white shadow-xl shadow-indigo-200 relative overflow-hidden group">
                                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-white/20 transition-all duration-700"></div>
                                            <div className="relative flex items-center gap-6">
                                                <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-lg border border-white/30 rotate-3 shrink-0">
                                                    <Sparkles size={32} className="animate-pulse" />
                                                </div>
                                                <div>
                                                    <h4 className="text-xl font-black uppercase tracking-tighter leading-none mb-2">Asistente de Planificación IA</h4>
                                                    <p className="text-xs font-medium text-indigo-100/80 leading-tight">Configura tu proyecto conversando con nuestra inteligencia artificial.</p>
                                                </div>
                                            </div>
                                        </div>

                                        {aiChatMode && aiTasks.length === 0 && (
                                            <div className="space-y-4">
                                                <div className="bg-slate-50/50 rounded-[2rem] border border-slate-100 p-6 space-y-4 max-h-[350px] overflow-y-auto custom-scrollbar flex flex-col">
                                                    {aiChatHistory.map((msg, i) => (
                                                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}>
                                                            <div className={`max-w-[85%] px-5 py-3 rounded-2xl text-sm leading-relaxed ${msg.role === 'user'
                                                                ? 'bg-indigo-600 text-white rounded-tr-none shadow-md shadow-indigo-100'
                                                                : 'bg-white border border-slate-100 text-slate-700 rounded-tl-none shadow-sm shadow-slate-200'
                                                                }`}>
                                                                {msg.content.split('\n').map((line, j) => {
                                                                    const cleanLine = line.replace('[READY_TO_PLAN]', '');
                                                                    if (cleanLine.trim()) return <p key={j} className={j > 0 ? 'mt-2' : ''}>{cleanLine}</p>;
                                                                    return null;
                                                                })}
                                                                {msg.content.includes('[READY_TO_PLAN]') && (
                                                                    <div className="mt-4 pt-4 border-t border-emerald-50 text-emerald-600 font-black text-[10px] uppercase tracking-widest flex items-center gap-2">
                                                                        <Check size={14} className="bg-emerald-100 rounded-full p-0.5" /> Información Completa
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {isAiLoading && (
                                                        <div className="flex justify-start">
                                                            <div className="bg-white border border-slate-100 shadow-sm px-5 py-3 rounded-2xl rounded-tl-none flex items-center gap-3">
                                                                <div className="flex gap-1">
                                                                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                                                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                                                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {!aiChatHistory[aiChatHistory.length - 1]?.content.includes('[READY_TO_PLAN]') ? (
                                                    <form className="relative flex items-center gap-3" onSubmit={e => {
                                                        e.preventDefault();
                                                        const val = e.target.msg.value;
                                                        if (!val.trim() || isAiLoading) return;
                                                        const handleAiChat = async (userMsg) => {
                                                            const newHistory = [...aiChatHistory, { role: 'user', content: userMsg }];
                                                            setAiChatHistory(newHistory);
                                                            setIsAiLoading(true);
                                                            try {
                                                                const token = localStorage.getItem('em_token');
                                                                const headers = { 'Content-Type': 'application/json' };
                                                                if (token) headers['Authorization'] = `Bearer ${token}`;

                                                                const res = await fetch(`${API_URL}?action=ai_assistant_chat`, {
                                                                    method: 'POST',
                                                                    headers: headers,
                                                                    body: JSON.stringify({ messages: newHistory })
                                                                });
                                                                const data = await res.json();
                                                                if (data.status === 'success') {
                                                                    setAiChatHistory(prev => [...prev, { role: 'assistant', content: data.reply }]);
                                                                } else {
                                                                    alert(data.message);
                                                                }
                                                            } catch (e) {
                                                                alert("Error de conexión con la IA");
                                                            } finally {
                                                                setIsAiLoading(false);
                                                            }
                                                        };
                                                        handleAiChat(val);
                                                        e.target.reset();
                                                    }}>
                                                        <textarea
                                                            name="msg"
                                                            autoComplete="off"
                                                            disabled={isAiLoading}
                                                            className="input-base pr-14 py-4 shadow-inner resize-none overflow-hidden min-h-[60px]"
                                                            rows="1"
                                                            onInput={(e) => {
                                                                e.target.style.height = 'auto';
                                                                e.target.style.height = (e.target.scrollHeight) + 'px';
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                                    e.preventDefault();
                                                                    e.target.form.requestSubmit();
                                                                }
                                                            }}
                                                            placeholder="Escribe tu respuesta aquí..."
                                                        />
                                                        <button type="submit" disabled={isAiLoading} className="absolute right-2 bottom-2 p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all shadow-lg disabled:opacity-50">
                                                            <Send size={18} />
                                                        </button>
                                                    </form>
                                                ) : (
                                                    <button
                                                        onClick={async () => {
                                                            setIsAiLoading(true);
                                                            try {
                                                                const summary = aiChatHistory.map(m => `${m.role}: ${m.content}`).join('\n');
                                                                const token = localStorage.getItem('em_token');
                                                                const headers = { 'Content-Type': 'application/json' };
                                                                if (token) headers['Authorization'] = `Bearer ${token}`;

                                                                const res = await fetch(`${API_URL}?action=ai_generate_schedule`, {
                                                                    method: 'POST',
                                                                    headers: headers,
                                                                    body: JSON.stringify({
                                                                        project_id: selectedEventId,
                                                                        project_name: selectedEvent.name,
                                                                        start_date: selectedEvent.event_date,
                                                                        end_date: selectedEvent.end_date || selectedEvent.event_date,
                                                                        context: summary
                                                                    })
                                                                });
                                                                const data = await res.json();
                                                                if (data.status === 'success') {
                                                                    setAiTasks(data.tasks);
                                                                    setAiChatMode(false);
                                                                } else {
                                                                    alert(data.message);
                                                                }
                                                            } catch (e) {
                                                                alert("Error al generar el cronograma");
                                                            } finally {
                                                                setIsAiLoading(false);
                                                            }
                                                        }}
                                                        className="btn-primary w-full py-6 uppercase font-black tracking-widest bg-emerald-600 border-none shadow-emerald-100 flex items-center justify-center gap-3 animate-in fade-in zoom-in"
                                                    >
                                                        <Zap size={20} /> Generar Cronograma Optimizado
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        {isAiLoading && !aiChatMode && aiTasks.length === 0 && (
                                            <div className="py-16 flex flex-col items-center justify-center gap-8">
                                                <div className="relative">
                                                    <div className="w-24 h-24 border-4 border-indigo-100 rounded-full"></div>
                                                    <div className="absolute top-0 w-24 h-24 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                                    <Zap size={32} className="absolute inset-0 m-auto text-indigo-600 animate-pulse" />
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-xs font-black uppercase text-slate-800 tracking-[0.2em] mb-2 font-mono">Generando Cronograma</p>
                                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Calculando dependencias y fases críticas...</p>
                                                </div>
                                            </div>
                                        )}

                                        {aiTasks.length > 0 && !isAiLoading && (
                                            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                                                <div className="flex justify-between items-center px-2">
                                                    <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Estructura Sugerida:</p>
                                                    <button onClick={() => setAiTasks([])} className="text-[9px] font-black uppercase text-rose-500 hover:underline">Volver a Generar</button>
                                                </div>
                                                <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                                                    {aiTasks.map((t, idx) => {
                                                        const isSubtask = !!t.parent_id;
                                                        return (
                                                            <div key={idx} className={`bg-white p-3 rounded-xl border ${isSubtask ? 'ml-8 border-dashed border-slate-200 bg-slate-50/30' : 'border-slate-100 shadow-sm'} flex flex-col gap-2 group hover:border-indigo-200 transition-all`}>
                                                                <div className="flex justify-between items-center">
                                                                    <input
                                                                        className={`w-full bg-transparent font-black uppercase text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-300 rounded px-1 ${isSubtask ? 'text-[11px]' : 'text-xs'}`}
                                                                        value={t.title}
                                                                        onChange={(e) => {
                                                                            const newTasks = [...aiTasks];
                                                                            newTasks[idx].title = e.target.value;
                                                                            setAiTasks(newTasks);
                                                                        }}
                                                                    />
                                                                    <div className={`text-[9px] font-black px-2 py-1 rounded shrink-0 ${isSubtask ? 'text-slate-400 bg-slate-100' : 'text-indigo-600 bg-indigo-50'}`}>
                                                                        {isSubtask ? 'SUB-TAREA' : `FASE`}
                                                                    </div>
                                                                </div>
                                                                <div className="flex gap-4 items-center">
                                                                    <div className="flex items-center gap-1 group/date">
                                                                        <Calendar size={10} className="text-slate-300" />
                                                                        <input
                                                                            type="date"
                                                                            className="text-[9px] font-bold text-slate-400 uppercase bg-transparent focus:text-indigo-600 outline-none cursor-pointer"
                                                                            value={t.start_date || ''}
                                                                            onChange={(e) => {
                                                                                const newTasks = [...aiTasks];
                                                                                newTasks[idx].start_date = e.target.value;
                                                                                setAiTasks(newTasks);
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <span className="text-[9px] font-black text-slate-200">AL</span>
                                                                    <div className="flex items-center gap-1 group/date">
                                                                        <Calendar size={10} className="text-slate-300" />
                                                                        <input
                                                                            type="date"
                                                                            className="text-[9px] font-bold text-slate-400 uppercase bg-transparent focus:text-indigo-600 outline-none cursor-pointer"
                                                                            value={t.end_date || ''}
                                                                            onChange={(e) => {
                                                                                const newTasks = [...aiTasks];
                                                                                newTasks[idx].end_date = e.target.value;
                                                                                setAiTasks(newTasks);
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <button
                                                    onClick={async () => {
                                                        if (!confirm(`¿Importar estas ${aiTasks.length} fases al proyecto? Escala este proceso puede tardar unos segundos.`)) return;
                                                        setIsAiLoading(true);
                                                        try {
                                                            await handleAction('bulk_add_tasks', {
                                                                event_id: selectedEventId,
                                                                tasks: aiTasks
                                                            });
                                                            alert("¡Cronograma importado con éxito!");
                                                            setActiveModal(null);
                                                            setAiTasks([]);
                                                        } catch (e) {
                                                            alert("Error al importar el cronograma");
                                                        } finally {
                                                            setIsAiLoading(false);
                                                        }
                                                    }}
                                                    className="bg-slate-900 text-white w-full py-5 rounded-[2rem] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl flex items-center justify-center gap-3"
                                                >
                                                    <ArrowRight size={18} /> Confirmar e Importar al Proyecto
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )
                            }
                        </Modal >
                    )
                    }

                    {/* ========== MDO BUDGET MODALS ========== */}
                    {
                        activeModal === 'MDO_BUDGET_MODE' && (
                            <MDOBudgetModeModal
                                onClose={() => setActiveModal(null)}
                                onSelectMode={handleCreateMDOBudget}
                                project={selectedEvent}
                            />
                        )
                    }

                    {
                        activeModal === 'MDO_BUDGET_GLOBAL' && (
                            <MDOBudgetGlobalModal
                                onClose={() => setActiveModal(null)}
                                onSave={handleSaveMDOGlobal}
                                project={selectedEvent}
                            />
                        )
                    }
                    {
                        activeModal === 'MDO_BUDGET_PHASE' && (
                            <MDOBudgetPhaseModal
                                onClose={() => setActiveModal(null)}
                                onSave={handleSaveMDOPhase}
                                project={selectedEvent}
                                tasks={appData.tasks}
                                taskBudgets={appData.task_budgets}
                            />
                        )
                    }

                    {
                        activeModal === 'BUDGET_SINGLE_ENTRY' && (
                            <BudgetSingleEntryModal
                                onClose={() => setActiveModal(null)}
                                onSave={handleSaveSingleBudget}
                                data={modalData}
                                cur={selectedEvent?.base_currency || 'ARS'}
                            />
                        )
                    }

                    {
                        activeModal === 'SAVE_TEMPLATE' && (
                            <SaveTemplateModal
                                project={selectedEvent}
                                onClose={() => setActiveModal(null)}
                                onConfirm={(data) => {
                                    // Export current tasks as a template JSON
                                    const phases = tasks.filter(t => t.isParent || !t.parent_id);
                                    const templateTasks = phases.map(phase => {
                                        const startP = new Date(phase.start_date);
                                        const endP = new Date(phase.end_date);
                                        const days = Math.max(1, Math.round((endP - startP) / (1000 * 60 * 60 * 24)));
                                        const subs = tasks.filter(s => s.parent_id === phase.id).map(sub => {
                                            const startS = new Date(sub.start_date);
                                            const endS = new Date(sub.end_date);
                                            const subDays = Math.max(1, Math.round((endS - startS) / (1000 * 60 * 60 * 24)));
                                            return { title: sub.title, days: subDays, milestone: sub.is_milestone == 1 };
                                        });
                                        return { title: phase.title, days, subs };
                                    });

                                    handleAction('save_template', {
                                        company_id: currentUser?.company_id,
                                        name: data.name,
                                        project_type: data.type,
                                        details: data.details,
                                        tasks: templateTasks
                                    });
                                    setActiveModal(null);
                                }}
                            />
                        )
                    }

                    {
                        activeModal === 'IMPORT_TEMPLATE' && (
                            <TemplateImportModal
                                templates={appData.templates}
                                event={selectedEvent}
                                onClose={() => setActiveModal(null)}
                                onConfirm={async (tasks) => {
                                    await handleAction('bulk_add_tasks', { tasks });
                                    setActiveModal(null);
                                }}
                                t={t}
                            />
                        )
                    }
                    {
                        activeModal === 'PURCHASE_ORDER' && (
                            <PurchaseOrderModal
                                project={selectedEvent}
                                data={modalData}
                                onClose={() => setActiveModal(null)}
                                onSave={(oc) => handleAction('save_purchase_order', oc)}
                                onDelete={(id) => handleAction('delete_purchase_order', { id })}
                                purchase_orders={appData.purchase_orders || []}
                                fCur={fCur}
                            />
                        )
                    }
                </div>
            </main>
        </div>
    );
}

function GanttSettingsModal({ isOpen, onClose, prefs, setPrefs, eventId, handleAction }) {
    if (!isOpen) return null;

    const [localPrefs, setLocalPrefs] = useState(prefs);

    const handleSaveLocal = () => {
        setPrefs(localPrefs);
        // Save to DB
        handleAction('save_user_prefs', { preferences: { gantt: localPrefs } });
        // Compatibilidad: seguimos guardando en local por ahora para transición fluida
        localStorage.setItem(`em_gantt_prefs_${eventId}`, JSON.stringify(localPrefs));
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} width="max-w-2xl">
            <div className="p-12 space-y-10 animate-in fade-in zoom-in duration-300">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-4xl font-black text-slate-900 uppercase tracking-tighter">Preferencia<span className="text-indigo-600">s</span> del Gantt</h3>
                </div>

                <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Modo de Colores</label>
                    <div className="grid grid-cols-3 gap-6">
                        {[
                            { id: 'INDIGO', label: 'Tema Azul', color: 'bg-indigo-500' },
                            { id: 'CATEGORY', label: 'Color por Rubro', color: 'bg-emerald-500' },
                            { id: 'STATUS', label: 'Color por Estado', color: 'bg-amber-500' }
                        ].map(m => (
                            <button
                                key={m.id}
                                onClick={() => setLocalPrefs(prev => ({ ...prev, colorMode: m.id }))}
                                className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${localPrefs.colorMode === m.id ? 'border-indigo-600 bg-indigo-50 shadow-sm' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                            >
                                <div className={`w-8 h-8 rounded-full ${m.color} flex items-center justify-center shadow-inner`}>
                                    {localPrefs.colorMode === m.id && <div className="w-3 h-3 bg-white rounded-full"></div>}
                                </div>
                                <span className={`text-[10px] font-black uppercase text-center leading-tight ${localPrefs.colorMode === m.id ? 'text-indigo-900' : 'text-slate-400'}`}>{m.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Personalización de Colores</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-6 rounded-[2.5rem] border border-slate-100">
                        <div className="space-y-2">
                            <p className="text-[9px] font-black uppercase text-slate-500 ml-2">Color de Fases (Padre)</p>
                            <div className="flex items-center gap-3 bg-white p-3 rounded-2xl border border-slate-100">
                                <input
                                    type="color"
                                    value={localPrefs.phaseColor || '#4f46e5'}
                                    onChange={(e) => setLocalPrefs(prev => ({ ...prev, colorMode: 'CUSTOM', phaseColor: e.target.value }))}
                                    className="w-10 h-10 rounded-xl cursor-pointer border-none bg-transparent"
                                />
                                <span className="text-[10px] font-bold text-slate-700 uppercase tracking-tighter">{localPrefs.phaseColor || '#4f46e5'}</span>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <p className="text-[9px] font-black uppercase text-slate-500 ml-2">Color de Sub-fases (Hijo)</p>
                            <div className="flex items-center gap-3 bg-white p-3 rounded-2xl border border-slate-100">
                                <input
                                    type="color"
                                    value={localPrefs.subTaskColor || '#10b981'}
                                    onChange={(e) => setLocalPrefs(prev => ({ ...prev, colorMode: 'CUSTOM', subTaskColor: e.target.value }))}
                                    className="w-10 h-10 rounded-xl cursor-pointer border-none bg-transparent"
                                />
                                <span className="text-[10px] font-bold text-slate-700 uppercase tracking-tighter">{localPrefs.subTaskColor || '#10b981'}</span>
                            </div>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <p className="text-[9px] font-black uppercase text-slate-500 ml-2">Color de Valores y Etiquetas</p>
                            <div className="flex items-center gap-3 bg-white p-3 rounded-2xl border border-slate-100">
                                <input
                                    type="color"
                                    value={localPrefs.labelColor || '#64748b'}
                                    onChange={(e) => setLocalPrefs(prev => ({ ...prev, colorMode: 'CUSTOM', labelColor: e.target.value }))}
                                    className="w-10 h-10 rounded-xl cursor-pointer border-none bg-transparent"
                                />
                                <span className="text-[10px] font-bold text-slate-700 uppercase tracking-tighter">{localPrefs.labelColor || '#64748b'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Visualización</label>
                    <div className="space-y-3">
                        <button
                            onClick={() => setLocalPrefs(prev => ({ ...prev, showDependencies: !prev.showDependencies }))}
                            className={`w-full p-6 rounded-[2rem] border-2 transition-all flex items-center justify-between ${localPrefs.showDependencies ? 'border-indigo-600 bg-indigo-50/50' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                        >
                            <div className="flex items-center gap-5">
                                <div className={`p-4 rounded-2xl ${localPrefs.showDependencies ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                    <Share2 size={24} />
                                </div>
                                <div className="text-left">
                                    <p className="text-sm font-black text-slate-700 uppercase leading-none mb-1">Mostrar Dependencias</p>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase">Flechas entre tareas vinculadas</p>
                                </div>
                            </div>
                            <div className={`w-12 h-6 rounded-full p-1 transition-colors ${localPrefs.showDependencies ? 'bg-indigo-500' : 'bg-slate-200'}`}>
                                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${localPrefs.showDependencies ? 'translate-x-6' : ''}`}></div>
                            </div>
                        </button>

                        <button
                            onClick={() => setLocalPrefs(prev => ({ ...prev, compactMode: !prev.compactMode }))}
                            className={`w-full p-6 rounded-[2rem] border-2 transition-all flex items-center justify-between ${localPrefs.compactMode ? 'border-indigo-600 bg-indigo-50/50' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                        >
                            <div className="flex items-center gap-5">
                                <div className={`p-4 rounded-2xl ${localPrefs.compactMode ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                    <Maximize size={24} />
                                </div>
                                <div className="text-left">
                                    <p className="text-sm font-black text-slate-700 uppercase leading-none mb-1">Modo Compacto</p>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase">Menos espacio entre filas</p>
                                </div>
                            </div>
                            <div className={`w-12 h-6 rounded-full p-1 transition-colors ${localPrefs.compactMode ? 'bg-indigo-500' : 'bg-slate-200'}`}>
                                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${localPrefs.compactMode ? 'translate-x-6' : ''}`}></div>
                            </div>
                        </button>
                    </div>
                </div>

                <div className="pt-6 border-t border-slate-100 flex gap-4">
                    <button onClick={onClose} className="flex-1 py-5 rounded-3xl text-[11px] font-black uppercase text-slate-400 hover:text-slate-600 tracking-widest transition-all">Cancelar</button>
                    <button onClick={handleSaveLocal} className="flex-[2] btn-primary py-5 rounded-3xl text-[11px] font-black uppercase tracking-widest">Guardar Preferencias</button>
                </div>
            </div>
        </Modal>
    );
}

function PortfolioTab({ event, healthColor, client }) {
    return (
        <div className="bg-white p-12 rounded-[3.5rem] border border-slate-100 shadow-xl relative overflow-hidden">
            <div className={`absolute top-0 right-0 w-32 h-32 bg-${healthColor}-500 opacity-5 -mr-8 -mt-8 rounded-full`}></div>
            <h4 className="text-sm font-black uppercase text-slate-400 tracking-widest mb-6">Información General</h4>
            <div className="space-y-6">
                {event.description && (
                    <div className="p-6 bg-indigo-50/50 rounded-3xl border border-indigo-100/50 mb-6">
                        <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2 flex items-center gap-2"><AlignLeft size={14} /> Descripción del Proyecto</p>
                        <p className="text-sm font-bold text-slate-600 leading-relaxed italic">"{event.description}"</p>
                    </div>
                )}
                <div className="flex items-center gap-6">
                    <div className="w-16 h-16 bg-slate-100 rounded-3xl flex items-center justify-center text-slate-400">
                        <Globe size={32} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Ubicación</p>
                        <p className="text-xl font-black text-slate-800 uppercase tracking-tighter">{event.location || 'No especificada'}</p>
                    </div>
                </div>
                <div className="flex items-center gap-6">
                    <div className="w-16 h-16 bg-slate-100 rounded-3xl flex items-center justify-center text-slate-400">
                        <Tag size={32} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tipo de Proyecto</p>
                        <p className="text-xl font-black text-slate-800 uppercase tracking-tighter">{event.type || 'OTRO'}</p>
                    </div>
                </div>
                <div className="flex items-center gap-6 p-4 bg-slate-50/50 rounded-3xl border border-slate-100/50">
                    <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center shrink-0">
                        <UserCheck size={32} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Cliente / Solicitante</p>
                        <p className="text-xl font-black text-slate-800 uppercase tracking-tighter leading-none mt-1">{event.client_name || client?.name || 'Sin asignar'}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function DocumentationTab({ event, data, handleAction, currentUser, canEdit }) {
    const [isUploading, setIsUploading] = useState(false);
    const docs = data.project_documents?.filter(d => d.event_id === event.id) || [];

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('event_id', event.id);
        formData.append('company_id', currentUser?.company_id);
        formData.append('user_id', currentUser?.id);
        formData.append('action', 'upload_file');

        try {
            const token = localStorage.getItem('em_token');
            const headers = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const resp = await fetch('api.php', { 
                method: 'POST', 
                body: formData,
                headers: headers
            });
            const result = await resp.json();
            if (result.status === 'success') {
                await handleAction('sync_full');
            } else {
                alert("Error al subir: " + result.message);
            }
        } catch (err) {
            alert("Error de conexión");
        } finally {
            setIsUploading(false);
        }
    };

    const getFileIcon = (type) => {
        if (type?.includes('pdf')) return <FileText className="text-rose-500" />;
        if (type?.includes('sheet') || type?.includes('excel')) return <Table className="text-emerald-500" />;
        if (type?.includes('word')) return <FileText className="text-blue-500" />;
        if (type?.includes('image')) return <Image className="text-indigo-500" />;
        return <FileText className="text-slate-400" />;
    };

    return (
        <div className="space-y-8 animate-in">
            <div className="flex justify-between items-center group">
                <div>
                    <h3 className="text-3xl font-black uppercase tracking-tighter">Documentación</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Planos, Contratos y Documentos Técnicos</p>
                </div>
                {canEdit && (
                    <label className={`cursor-pointer btn-primary py-4 px-10 transition-all ${isUploading ? 'opacity-50 cursor-wait' : 'hover:scale-105 active:scale-95'}`}>
                        <Plus size={20} /> {isUploading ? 'Subiendo...' : 'Subir Documento'}
                        <input type="file" className="hidden" onChange={handleUpload} disabled={isUploading} />
                    </label>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {docs.length === 0 ? (
                    <div className="col-span-full p-20 bg-white border-2 border-dashed border-slate-100 rounded-[3rem] text-center">
                        <Upload size={48} className="mx-auto text-slate-100 mb-6" />
                        <p className="text-slate-300 font-black uppercase text-xs tracking-widest italic">No hay documentos cargados en este proyecto</p>
                    </div>
                ) : (
                    docs.map(doc => (
                        <div key={doc.id} className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl hover:border-indigo-200 transition-all group flex flex-col gap-4">
                            <div className="flex justify-between items-start">
                                <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-2xl">
                                    {getFileIcon(doc.file_type)}
                                </div>
                                {canEdit && (
                                    <button
                                        onClick={() => { if (confirm("¿Eliminar documento?")) handleAction('delete_project_document', { id: doc.id }); }}
                                        className="p-2 text-slate-200 hover:text-rose-500 transition-all opacity-0 group-hover:opacity-100"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                )}
                            </div>
                            <div className="flex-1">
                                <h5 className="text-[11px] font-black uppercase text-slate-800 line-clamp-2 leading-tight h-8">{doc.name}</h5>
                                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-2">Subido: {new Date(doc.created_at).toLocaleDateString('es-AR')}</p>
                            </div>
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="w-full py-3 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-indigo-600 transition-all">
                                <Download size={14} /> Descargar / Ver
                            </a>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

const getPermissions = (role) => {
    switch (role) {
        case 'MANAGER': return { canEditTasks: true, canEditExpenses: true, canManageBudget: true, canManageTeam: true, canManageStructure: true, canDelete: true, canExport: true };
        case 'EDITOR': return { canEditTasks: true, canEditExpenses: true, canManageBudget: true, canManageTeam: false, canManageStructure: true, canDelete: false, canExport: true };
        default: return { canEditTasks: false, canEditExpenses: false, canManageBudget: false, canManageTeam: false, canManageStructure: false, canDelete: false, canExport: false };
    }
};

function ProjectDashboard({ event, data, tab, setTab, activeModal, setActiveModal, setModalData, modalData, handleAction, userRole, canEdit, t, currentUser, uiPrefs, fNum, fCur, fDate, setBudgetLoadType, setBudgetAllocation, globalFxRate, onAddExpense, onEditExpense }) {
    const isExternalProject = currentUser && currentUser.role !== 'SUPER_ADMIN' && event.company_id !== currentUser.company_id;
    const perms = useMemo(() => getPermissions(userRole), [userRole]);
    const client = data.clients?.find(c => c.id === event.client_id);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [summaryDetail, setSummaryDetail] = useState(null);
    const [payFilter, setPayFilter] = useState({ group: true, start: '', end: '' });
    const [expandedSections, setExpandedSections] = useState({ mat: true, mdo: true, tot: true });
    const [isMaterialsCollapsed, setIsMaterialsCollapsed] = useState(false);
    const [budgetSort, setBudgetSort] = useState(localStorage.getItem('em_budget_sort') || 'PHASE_ASC');
    const [ganttPrefs, setGanttPrefs] = useState(() => {
        // PRIORIDAD: 1. DB del usuario, 2. LocalStorage (migración), 3. Default
        if (data.user_preferences?.gantt) return data.user_preferences.gantt;
        const saved = localStorage.getItem(`em_gantt_prefs_${event.id}`);
        return saved ? JSON.parse(saved) : {
            colorMode: 'INDIGO', // INDIGO, CATEGORY, STATUS
            showDependencies: true,
            compactMode: false
        };
    });
    const [isGanttPrefsOpen, setIsGanttPrefsOpen] = useState(false);
    const [assigneeFilter, setAssigneeFilter] = useState('ALL');

    const BARRAS_12M_KG = { 6: 2.66, 8: 4.74, 10: 7.40, 12: 10.7, 16: 18.9, 20: 29.6, 25: 46.2, 32: 75.7, 40: 118.3 };
    const getBarCount = (matName, totalKg) => {
        const match = matName.match(/hierro\s+Ø(\d+)mm/i);
        if (match) {
            const diam = parseInt(match[1]);
            const weightPerBar = BARRAS_12M_KG[diam];
            if (weightPerBar) return Math.ceil(totalKg / weightPerBar);
        }
        return null;
    };

    // Calcular tareas con jerarquÃ­a, PONDERACIÃ“N y FECHAS AUTOMÃTICAS
    const rawTasks = data.tasks.filter(t => t.event_id === event.id);
    const tasks = rawTasks.map(t => {
        const subs = rawTasks.filter(s => s.parent_id === t.id);
        if (subs.length > 0) {
            let totalDur = 0;
            let weightedProg = 0;
            let minS = null;
            let maxE = null;

            subs.forEach(s => {
                const sD = new Date(s.start_date);
                const eD = new Date(s.end_date);
                if (!minS || sD < minS) minS = sD;
                if (!maxE || eD > maxE) maxE = eD;

                const dur = Math.max(1, (eD - sD) / 86400000 + 1);
                totalDur += dur;
                weightedProg += (Number(s.progress) * dur);
            });

            const avg = totalDur > 0 ? Math.round(weightedProg / totalDur) : 0;
            return {
                ...t,
                progress: avg,
                isParent: true,
                start_date: minS ? minS.toISOString().split('T')[0] : t.start_date,
                end_date: maxE ? maxE.toISOString().split('T')[0] : t.end_date
            };
        }
        return { ...t, isParent: false };
    });

    const filteredTasks = useMemo(() => {
        if (assigneeFilter === 'ALL') return tasks;

        const matches = (task) => {
            const taskResources = (data.task_resources || []).filter(tr => tr.task_id === task.id);
            const hasMatchingResource = taskResources.some(tr => {
                const res = (data.resources || []).find(r => r.id === tr.resource_id);
                return res && res.name === assigneeFilter;
            });

            const hasMatchingAssignment = (data.resource_assignments || []).some(ra => {
                if (ra.task_id !== task.id) return false;
                const res = (data.resources || []).find(r => r.id === ra.resource_id);
                return res && res.name === assigneeFilter;
            });

            if (task.assignee_name === assigneeFilter || hasMatchingResource || hasMatchingAssignment) return true;

            const children = tasks.filter(s => s.parent_id === task.id);
            return children.some(c => matches(c));
        };

        return tasks.filter(t => matches(t));
    }, [tasks, assigneeFilter, data.task_resources, data.resources, data.resource_assignments]);

    const expenses = data.expenses.filter(ex => ex.event_id === event.id);
    const budgets = data.budgets.filter(b => b.event_id === event.id);
    const materials = data.materials.filter(m => m.event_id === event.id);

    const matSumFromComputo = materials.reduce((a, b) => a + (Number(b.quantity_est || 0) * Number(b.unit_price || 0)), 0);

    // Dynamic Budget Calculation
    const bGlobalMat = Number(event.budget_materials_global || 0);
    const bGlobalMdo = Number(event.budget_mdo_global || 0);
    const bRubroMat = budgets.reduce((a, b) => a + Number(b.budget_amount || 0), 0);
    const bRubroMdo = budgets.reduce((a, b) => a + Number(b.budget_mdo || 0), 0);

    const tBudgets = data.task_budgets?.filter(tb => tb.event_id === event.id) || [];
    
    // Moved up from line 7817 to allow totalMdoBudget to use it
    const projectAssignments = data.resource_assignments?.filter(a => a.event_id === event.id) || [];
    const mdoFromResources = projectAssignments.reduce((sum, asig) => {
        const res = data.resources?.find(r => r.id === asig.resource_id);
        if (!res) return sum;
        
        const wDays = getWorkDays(new Date(asig.start_date), new Date(asig.end_date));
        const hours = wDays * parseFloat(res.daily_hours || 8) * (parseFloat(asig.percentage || 100) / 100);
        
        const cost = convertResourceCost(
            hours * parseFloat(res.hourly_rate || 0),
            res.currency, event.base_currency || event.display_currency,
            event.exchange_rate, globalFxRate
        ).value;

        return sum + cost;
    }, 0);

    const totalMaterialBudget = bGlobalMat > 0 ? bGlobalMat : (matSumFromComputo > 0 ? matSumFromComputo : bRubroMat);
    let totalMdoBudget = (event.sync_mdo_resources == 1) ? mdoFromResources : (bGlobalMdo > 0 ? bGlobalMdo : bRubroMdo);
    if (totalMdoBudget === 0 && event.mdo_budget_mode === 'POR_FASE') {
        totalMdoBudget = tBudgets.reduce((sum, b) => sum + Number(b.budget_mdo || 0), 0);
    }
    const effectiveMode = event.mdo_budget_mode || (tBudgets.length > 0 ? 'POR_FASE' : (Number(event.budget_mdo_global) > 0 ? 'GLOBAL' : (event.sync_mdo_resources == 1 ? 'POR_FASE' : null)));

    const rate = Number(globalFxRate) || 1000;
    const isProjectUsd = (event.base_currency || event.display_currency) === 'USD';

    const projectMovements = expenses.map(m => {
        const mRate = Number(m.exchange_rate || m.fx || 0);
        const movementsWithRate = expenses.filter(ex => Number(ex.exchange_rate || ex.fx || 0) > 0);
        const pRateLatest = movementsWithRate.length > 0
            ? Number(movementsWithRate.sort((a, b) => new Date(b.date || b.expense_date) - new Date(a.date || a.expense_date))[0].exchange_rate || movementsWithRate.sort((a, b) => new Date(b.date || b.expense_date) - new Date(a.date || a.expense_date))[0].fx)
            : rate;

        let divisor = 1;
        if (mRate > 0) {
            divisor = mRate < 1 ? (1 / mRate) : mRate;
        } else if ((m.currency_original || m.currency) !== (event.base_currency || event.display_currency)) {
            // ONLY fallback to project latest or global IF currencies are different
            divisor = pRateLatest < 1 ? (1 / pRateLatest) : pRateLatest;
        } else {
            divisor = 1;
        }

        let amtARS = 0;
        let amtUSD = 0;

        if (m.currency_original === 'ARS' || m.currency === 'ARS') {
            amtARS = Number(m.amount_original || 0);
            amtUSD = amtARS / (divisor || 1);
        } else {
            amtUSD = Number(m.amount_original || 0);
            amtARS = amtUSD * (divisor || 1);
        }

        return {
            ...m,
            amount_normalized_ars: amtARS,
            amount_normalized_usd: amtUSD,
            val: isProjectUsd ? amtUSD : amtARS
        };
    });
    const totalIncome = projectMovements
        .filter(m => m.movement_type === 'INCOME')
        .reduce((s, m) => s + m.val, 0);

    const totalExpense = projectMovements
        .filter(m => m.movement_type === 'EXPENSE')
        .reduce((s, m) => s + m.val, 0);

    const projectProfit = totalIncome - totalExpense;
    const profitabilityPerc = totalIncome > 0 ? Math.round((projectProfit / totalIncome) * 100) : 0;
    const profitColor = projectProfit >= 0 ? 'emerald' : 'rose';

    const totalMdoSpent = projectMovements
        .filter(m => m.expense_type === 'MANO_OBRA' && m.movement_type === 'EXPENSE')
        .reduce((s, m) => s + m.val, 0);

    // MDO Resources (Calculado)
    const projectResources = projectAssignments.map(a => data.resources?.find(r => r.id === a.resource_id)).filter(Boolean);
    const assignedResourcesInitials = projectResources.map(r => r.name?.[0]).filter(Boolean);
    const uniqueResourcesInitials = [...new Set(assignedResourcesInitials)];



    const totalMdoBudgetActual = totalMdoBudget > 0 ? totalMdoBudget : mdoFromResources;

    const unifiedTotalBudget = totalMaterialBudget + totalMdoBudget;
    const materialSpent = totalExpense - totalMdoSpent;

    // Nuevo: Presupuesto de Ingresos (Contrato)
    const incomeBudget = Number(event.budget_total_contract || 0) || unifiedTotalBudget;
    const progCobro = incomeBudget > 0 ? Math.round((totalIncome / incomeBudget) * 100) : 0;

    // Progreso físico y teórico unificado
    const { physicalProgress: progFisico, theoreticalProgress: theoreticalProg, projStart, projEnd } = calculateProgressMetrics(tasks, event.event_date, event.end_date);
    const progFinanciero = unifiedTotalBudget > 0 ? Math.round((totalExpense / unifiedTotalBudget) * 100) : 0;
    const theoreticalMdoVal = effectiveMode === 'GLOBAL'
        ? totalMdoBudget * (theoreticalProg / 100)
        : (tBudgets || []).reduce((sum, tb) => {
            const task = tasks.find(t => t.id === tb.task_id);
            if (!task) return sum;
            const s = task.start_date ? new Date(task.start_date) : null;
            const e = task.end_date ? new Date(task.end_date) : null;
            if (!s || !e || isNaN(s.getTime()) || isNaN(e.getTime())) return sum;
            const totalWD = getWorkDays(s, e);
            const today = new Date();
            const endForElapsed = today < e ? today : e;
            const elapsedWD = s <= endForElapsed ? getWorkDays(s, endForElapsed) : 0;
            const taskProg = totalWD > 0 ? (elapsedWD / totalWD) : 0;
            return sum + (Number(tb.budget_mdo || 0) * Math.min(1, taskProg));
        }, 0);
    const mdoDiff = theoreticalMdoVal - totalMdoSpent;
    const cur = event.base_currency || 'ARS';

    // Salud del Proyecto
    let healthColor = 'emerald';
    let healthIcon = <ShieldCheck size={24} />;
    let healthMsg = "ESTADO OPTIMO: EL AVANCE COINCIDE CON EL CRONOGRAMA.";

    if (progFisico || theoreticalProg) {
        const diff = theoreticalProg - progFisico;
        if (diff > 15) {
            healthColor = 'rose';
            healthIcon = <AlertTriangle size={24} />;
            healthMsg = "RIESGO: EL AVANCE REAL ESTA MUY POR DEBAJO DEL TEORICO.";
        } else if (diff > 5) {
            healthColor = 'amber';
            healthIcon = <Clock size={24} />;
            healthMsg = "PRECAUCION: LIGERO DESVIO RESPECTO AL CRONOGRAMA.";
        }
    }

    return (
        <div className="space-y-8 w-full px-4 sm:px-6 lg:px-8 pb-32">
            {/* Header with Title and Primary Actions - Sticky */}
            <div className="sticky top-[-1.5rem] lg:top-[-2.5rem] z-50 bg-white/90 backdrop-blur-md py-5 -mx-4 px-8 flex flex-col gap-5 border-b border-slate-200/50 shadow-sm rounded-b-[2.5rem]">
                <div className="flex items-center justify-between gap-6 w-full">
                <div className="flex items-center gap-6 min-w-0">
                    {client?.logo_url ? (
                        <div className="w-16 h-16 rounded-2xl border border-slate-100 p-2 flex items-center justify-center bg-white shadow-sm">
                            <img src={client.logo_url} alt="Client Logo" className="w-full h-full object-contain mix-blend-multiply" />
                        </div>
                    ) : (
                        <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-300">
                            <Globe size={32} />
                        </div>
                    )}
                    <div className="flex-1">
                        <div className="flex items-center gap-4">
                            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter text-slate-900 leading-none">{event.name}</h2>
                            {event.image_url && (
                                <div className="hidden md:block w-32 h-16 rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
                                    <img src={event.image_url} alt="Cover" className="w-full h-full object-cover" />
                                </div>
                            )}
                        </div>
                        <p className="text-slate-400 font-bold uppercase text-[10px] tracking-[0.3em] flex items-center gap-2 mt-2">
                            {event.location || 'SIN UBICACIÓN'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    {(userRole === 'MANAGER' || userRole === 'EDITOR') && (
                        <button onClick={() => { setModalData(event); setActiveModal('EDIT_EVENT'); }} className="p-3 hover:bg-slate-100 rounded-xl transition-all text-slate-400 hover:text-indigo-600" title="Editar Proyecto"><Edit3 size={20} /></button>
                    )}
                    {userRole === 'MANAGER' && (
                        <button onClick={() => { setActiveModal('MANAGE_MEMBERS'); }} className="p-3 hover:bg-slate-100 rounded-xl transition-all text-slate-400 hover:text-indigo-600" title="Gestión de Miembros"><Users size={20} /></button>
                    )}
                    {(() => {
                        const isMemberViaTable = (data.project_members || []).some(pm => pm.event_id === event.id && String(pm.user_id) === String(currentUser?.id));
                        if (isMemberViaTable) {
                            return (
                                <button
                                    onClick={async () => {
                                        if (confirm('¿Estás seguro que querés abandonar este proyecto? Perderás el acceso.')) {
                                            try {
                                                const token = localStorage.getItem('em_token');
                                                const headers = { 'Content-Type': 'application/json' };
                                                if (token) headers['Authorization'] = `Bearer ${token}`;

                                                await fetch(`${API_URL}?action=remove_project_member`, {
                                                    method: 'POST',
                                                    headers: headers,
                                                    body: JSON.stringify({ event_id: event.id, user_id: currentUser.id })
                                                });
                                                alert('Abandonaste el proyecto. Serás redirigido al inicio.');
                                                window.location.reload();
                                            } catch (e) {
                                                alert('Error al abandonar el proyecto.');
                                            }
                                        }
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 text-[9px] font-black uppercase tracking-widest rounded-xl bg-rose-50 text-rose-500 hover:bg-rose-100 transition-all border border-rose-100"
                                    title="Abandonar este proyecto"
                                >
                                    <LogOut size={14} /> Abandonar
                                </button>
                            );
                        }
                        return null;
                    })()}
                </div>
                </div>

                {/* Navegación por pestañas agrupadas por tema (sin scroll horizontal: hace wrap) */}
                <nav className="flex flex-wrap items-end gap-x-6 gap-y-3 w-full">
                    {(() => {
                        const isObra = ['CONSTRU', 'OBRA'].some(k => (event.type || '').toUpperCase().includes(k));
                        const groups = [
                            { label: 'General', tabs: ['RESUMEN', !isExternalProject && 'KPIs'] },
                            { label: 'Planificación', tabs: ['CRONOGRAMA', 'GANTT'] },
                            { label: 'Equipo', tabs: ['RECURSOS'] },
                            { label: 'Dinero', tabs: isExternalProject ? [] : ['PRESUPUESTO', 'FINANZAS', 'OC', 'RUBROS'] },
                            { label: isObra ? 'Obra' : 'Insumos', tabs: isObra ? ['COMPUTO', 'ESTRUCTURA'] : (isExternalProject ? [] : ['MATERIALES']) },
                            { label: 'Archivos', tabs: ['DOCUMENTACION'] },
                        ].map(g => ({ ...g, tabs: g.tabs.filter(Boolean) })).filter(g => g.tabs.length > 0);

                        const TAB_LABELS = { RESUMEN: 'Resumen', KPIs: 'KPIs', CRONOGRAMA: 'Cronograma', GANTT: 'Gantt', RECURSOS: 'Recursos', PRESUPUESTO: 'Presupuesto', FINANZAS: 'Finanzas', OC: 'Órdenes de compra', RUBROS: 'Rubros', COMPUTO: 'Cómputo', ESTRUCTURA: 'Estructura', MATERIALES: 'Materiales', DOCUMENTACION: 'Documentos' };
                        const labelFor = (id) => { const tr = t(id.toLowerCase()); return tr !== id.toLowerCase() ? tr : (TAB_LABELS[id] || id); };
                        const isActive = (id) => tab === id || (tab === 'PORTADA' && id === 'RESUMEN');

                        return groups.map(g => (
                            <div key={g.label} className="flex flex-col gap-1.5">
                                <span className={`text-[10px] font-bold uppercase tracking-wider px-1 ${g.tabs.some(isActive) ? 'text-indigo-600' : 'text-slate-400'}`}>{g.label}</span>
                                <div className="flex gap-1 bg-slate-100/70 p-1 rounded-xl border border-slate-200/60">
                                    {g.tabs.map(id => (
                                        <button key={id} onClick={() => setTab(id)} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${isActive(id) ? 'bg-indigo-600 shadow-md text-white' : 'text-slate-600 hover:bg-white hover:shadow-sm'}`}>
                                            {labelFor(id)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ));
                    })()}
                </nav>
            </div>

            {/* Project Cover is now in header */}


            {tab === 'DOCUMENTACION' && (
                <DocumentationTab
                    event={event}
                    data={data}
                    handleAction={handleAction}
                    currentUser={currentUser}
                    canEdit={userRole === 'MANAGER' || userRole === 'EDITOR'}
                />
            )}

            {
                tab === 'KPIs' && (
                    <div className="space-y-12 animate-in">
                        <div className="flex justify-between items-center px-8">
                            <div className="flex gap-4 items-center">
                                {client?.logo_url && (
                                    <div className="w-20 h-20 bg-white rounded-2xl p-2 shadow-sm border border-slate-100 flex items-center justify-center">
                                        <img src={client.logo_url} alt={client.name} className="max-w-full max-h-full object-contain" />
                                    </div>
                                )}
                                <div>
                                    <h3 className="text-3xl font-black uppercase tracking-tighter">Indicadores de Desempeño (KPIs)</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Monitoreo dinámico del estado del proyecto</p>
                                </div>
                            </div>
                            {canEdit && (
                                <button
                                    onClick={() => {
                                        let enabledKeys = [];
                                        try { 
                                            if (event.kpis_enabled) {
                                                const parsed = JSON.parse(event.kpis_enabled);
                                                if (Array.isArray(parsed)) enabledKeys = parsed;
                                            }
                                        } catch (e) { }
                                        
                                        if (enabledKeys.length === 0) {
                                            // Defaults
                                            const type = (event.type || '').toUpperCase();
                                            const defaults = (type.includes('CONSTRU') || type.includes('OBRA'))
                                                ? ['AVANCE_FISICO', 'AVANCE_TEORICO', 'DESVIO_PLAN', 'EJECUCION_PRESUPUESTO']
                                                : ['AVANCE_FISICO', 'AVANCE_TEORICO', 'MARGEN_RENTABILIDAD'];
                                            enabledKeys = defaults.map(k => ({ id: k, color: KPI_DEFS[k].color }));
                                        }
                                        setModalData({ kpiConfig: enabledKeys });
                                        setActiveModal('MANAGE_KPIS');
                                    }}
                                    className="px-6 py-3 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2"
                                >
                                    <Settings size={14} /> Configurar KPIs
                                </button>
                            )}
                        </div>


                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 bg-white p-16 rounded-[4rem] border border-slate-100 shadow-sm">
                            {(() => {
                                let enabledKeys = [];
                                try {
                                    if (event.kpis_enabled) enabledKeys = JSON.parse(event.kpis_enabled);
                                } catch (e) { }

                                // Check parsed length AND original absence (fixing the bug where empty selection restores defaults)
                                if (!event.kpis_enabled || !Array.isArray(enabledKeys)) {
                                    // Defaults
                                    const type = (event.type || '').toUpperCase();
                                    const defaultIDs = (type.includes('CONSTRU') || type.includes('OBRA'))
                                        ? ['AVANCE_FISICO', 'AVANCE_TEORICO', 'DESVIO_PLAN', 'EJECUCION_PRESUPUESTO']
                                        : ['AVANCE_FISICO', 'AVANCE_TEORICO', 'MARGEN_RENTABILIDAD'];
                                    // Convert defaults to object format
                                    enabledKeys = defaultIDs.map(k => ({ id: k, color: KPI_DEFS[k].color }));
                                }

                                // Normalize mixed types (strings vs objects)
                                const normalizedKeys = enabledKeys.map(k => typeof k === 'string' ? { id: k, color: KPI_DEFS[k].color || 'indigo' } : k);

                                return normalizedKeys.map(item => {
                                    const key = item.id;
                                    const customColor = item.color;
                                    const def = KPI_DEFS[key];
                                    if (!def) return null;

                                    let value = 0;
                                    switch (key) {
                                        case 'AVANCE_FISICO': value = progFisico; break;
                                        case 'AVANCE_TEORICO': value = theoreticalProg; break;
                                        case 'DESVIO_PLAN': value = progFisico - theoreticalProg; break;
                                        case 'EJECUCION_PRESUPUESTO': value = progFinanciero; break;
                                        case 'MARGEN_RENTABILIDAD': value = profitabilityPerc; break;
                                        case 'TIEMPO_CONSUMIDO':
                                            if (projStart && projEnd) {
                                                const total = (projEnd - projStart) || 1;
                                                const elapsed = (new Date() - projStart);
                                                value = Math.min(100, Math.max(0, (elapsed / total) * 100));
                                            }
                                            break;
                                    }

                                    let dynamicColor = customColor || def.color;
                                    // Auto-override logic for specific KPIs if user didn't force a color?
                                    // Let's respect user choice if present, else fallback to dynamic logic
                                    if (!customColor) {
                                        if (key === 'DESVIO_PLAN') {
                                            dynamicColor = value > -2 ? 'emerald' : (value < -15 ? 'rose' : 'amber');
                                        } else if (key === 'EJECUCION_PRESUPUESTO') {
                                            dynamicColor = value > 100 ? 'rose' : (value > 90 ? 'amber' : 'emerald');
                                        }
                                    }

                                    return (
                                        <div key={key} className="flex justify-center p-8 bg-slate-50/50 rounded-[3rem] border border-slate-50 transition-all hover:shadow-xl hover:bg-white hover:-translate-y-1">
                                            <GaugeChart
                                                value={value}
                                                label={def.label}
                                                color={dynamicColor}
                                                unit={def.unit}
                                                min={key === 'DESVIO_PLAN' ? -50 : 0}
                                                max={key === 'DESVIO_PLAN' ? 50 : (key === 'EJECUCION_PRESUPUESTO' ? 150 : 100)}
                                            />
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                )
            }

            {
                tab === 'PRESUPUESTO' && (
                    <div className="space-y-16 animate-in">
                        {/* Materials Section */}
                        <section className="space-y-8">
                            <div className="flex justify-between items-end px-8">
                                <div>
                                    <h3 className="text-4xl font-black uppercase tracking-tighter text-slate-900">Presupuesto de Materiales</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.3em] mt-2 flex items-center gap-2">
                                        <Package size={14} className="text-indigo-500" /> Control de suministros y compras por rubro
                                    </p>
                                </div>
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-2">
                                        {perms.canManageBudget && (
                                            <>
                                                <button onClick={async () => {
                                                    if (!confirm("¿Generar Rubros/Categorías basados en las Tareas Principales del Gantt?")) return;
                                                    const parents = tasks.filter(t => t.isParent);
                                                    let added = 0;
                                                    for (const p of parents) {
                                                        if (!data.categories.some(c => c.name === p.title && c.event_id === event.id)) {
                                                            await handleAction('add_category', { name: p.title, event_id: event.id });
                                                            added++;
                                                        }
                                                    }
                                                    alert(`Se crearon ${added} rubros nuevos.`);
                                                }} className="bg-indigo-50 text-indigo-600 px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-indigo-100 transition-all flex items-center gap-2">
                                                    <Plus size={14} /> Generar desde Gantt
                                                </button>
                                                <button onClick={async () => {
                                                    if (!confirm("¿Sincronizar Rubros con el Gantt? (Solo creará los faltantes)")) return;
                                                    const parents = tasks.filter(t => t.isParent);
                                                    let added = 0;
                                                    for (const p of parents) {
                                                        const exists = data.categories.find(c => c.name.toLowerCase() === p.title.toLowerCase() && c.event_id === event.id);
                                                        if (!exists) {
                                                            await handleAction('add_category', { name: p.title, event_id: event.id });
                                                            added++;
                                                        }
                                                    }
                                                    alert(`Sincronización completa. ${added} rubros creados.`);
                                                }} className="bg-slate-50 text-slate-500 px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-slate-100 transition-all flex items-center gap-2">
                                                    <RefreshCcw size={14} /> Sincronizar
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    <div className="text-right border-l border-slate-100 pl-6">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Materiales</p>
                                        <p className="text-3xl font-black text-indigo-600">{fCur(totalMaterialBudget, cur)}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {[
                                    { title: 'Presupuesto Mat.', val: totalMaterialBudget, color: 'indigo', icon: <DollarSign size={20} /> },
                                    { title: 'Ejecutado Mat.', val: totalExpense - totalMdoSpent, color: 'rose', icon: <ArrowDownRight size={20} /> },
                                    { title: 'Saldo Mat.', val: totalMaterialBudget - (totalExpense - totalMdoSpent), color: 'emerald', icon: <Wallet size={20} /> },
                                ].map((m, i) => (
                                    <div key={i} className="p-8 rounded-[2.5rem] bg-white border border-slate-200 shadow-sm relative overflow-hidden group transition-all hover:shadow-lg">
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className={`w-10 h-10 rounded-xl bg-${m.color}-50 text-${m.color}-600 flex items-center justify-center`}>{m.icon}</div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{m.title}</p>
                                        </div>
                                        <p className="text-2xl font-black text-slate-900">{fCur(m.val, cur)}</p>
                                        <div className={`absolute bottom-0 left-0 h-1 bg-${m.color}-500 w-full opacity-30`}></div>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-white rounded-[4rem] border border-slate-200 overflow-hidden shadow-sm">
                                <div className="p-10 border-b border-slate-50 flex justify-between items-center bg-slate-50/30">
                                    <h4 className="font-black uppercase text-[10px] tracking-widest text-slate-400 flex items-center gap-3">Detalle de Materiales por Rubro</h4>
                                    <div className="flex items-center gap-4">
                                        <select
                                            value={budgetSort}
                                            onChange={(e) => { setBudgetSort(e.target.value); localStorage.setItem('em_budget_sort', e.target.value); }}
                                            className="bg-white border border-slate-200 text-[10px] uppercase font-black rounded-xl p-3 text-slate-500"
                                        >
                                            <option value="PHASE_ASC">Fase ↓</option>
                                            <option value="ALPHABETIC_ASC">Nombre A-Z</option>
                                            <option value="AMOUNT_DESC">Mayor Monto</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead className="text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-50/50">
                                            <tr>
                                                <th className="px-10 py-6 border-r border-slate-100">Rubro / Categoría</th>
                                                <th className="px-6 py-6 text-right">Presupuesto</th>
                                                <th className="px-6 py-6 text-right">Egresos</th>
                                                <th className="px-6 py-6 text-right border-r border-slate-100">Saldo</th>
                                                <th className="px-6 py-6 w-20"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {data.categories.filter(c => c.event_id === event.id || budgets.some(b => b.category_id === c.id))
                                                .map(cat => {
                                                    const b = budgets.find(pb => pb.category_id === cat.id);
                                                    const bMat = b ? Number(b.budget_amount || 0) : 0;
                                                    const exMat = expenses.filter(ex => ex.category_id === cat.id && ex.expense_type !== 'MANO_OBRA' && ex.movement_type === 'EXPENSE')
                                                        .reduce((s, m) => s + Number(m.amount_converted || m.amount_original || 0), 0);
                                                    if (bMat === 0 && exMat === 0) return null;
                                                    return (
                                                        <tr key={cat.id} className="hover:bg-slate-50 transition-colors">
                                                            <td className="px-10 py-6 font-black uppercase text-slate-700 text-[11px] border-r border-slate-100">{cat.name}</td>
                                                            <td className="px-6 py-6 text-right font-medium text-slate-900">{fCur(bMat, cur)}</td>
                                                            <td className="px-6 py-6 text-right font-medium text-rose-500">{fCur(exMat, cur)}</td>
                                                            <td className={`px-6 py-6 text-right font-black border-r border-slate-100 ${bMat - exMat < 0 ? 'text-rose-600' : 'text-indigo-600'}`}>
                                                                {fCur(bMat - exMat, cur)}
                                                            </td>
                                                            <td className="px-6 py-6 text-right">
                                                                {perms.canManageBudget && (
                                                                    <button onClick={() => { setModalData({ id: cat.id, category_name: cat.name, amount: bMat, budget_mdo: b ? b.budget_mdo : 0 }); setActiveModal('BUDGET_SINGLE_ENTRY'); }} className="p-2 text-slate-300 hover:text-indigo-600 transition-all"><Edit3 size={14} /></button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>

                        <hr className="border-slate-100" />

                        {/* MDO Section */}
                        <section className="space-y-8">
                            <div className="flex justify-between items-end px-8">
                                <div>
                                    <h3 className="text-4xl font-black uppercase tracking-tighter text-slate-900">Presupuesto de Mano de Obra</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.3em] mt-2 flex items-center gap-2">
                                        <Hammer size={14} className="text-emerald-500" /> Gestión de jornales, subcontratos y certificados
                                    </p>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="text-right border-r border-slate-100 pr-8">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Modo Presupuesto</p>
                                        <p className="text-sm font-black text-indigo-600 uppercase">{effectiveMode === 'GLOBAL' ? 'Global' : 'Por Fase'}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total MDO</p>
                                        <p className="text-3xl font-black text-emerald-600">{fCur(totalMdoBudget, cur)}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                                {[
                                    { title: 'Presupuesto MDO', val: totalMdoBudget, color: 'emerald', icon: <DollarSign size={20} /> },
                                    { title: 'Certificado Teórico', val: theoreticalMdoVal, color: 'blue', icon: <Target size={20} /> },
                                    { title: 'Pagado MDO', val: totalMdoSpent, color: 'rose', icon: <ArrowDownRight size={20} /> },
                                    { title: 'Saldo MDO', val: totalMdoBudget - totalMdoSpent, color: 'indigo', icon: <Wallet size={20} /> },
                                ].map((m, i) => (
                                    <div key={i} className="p-8 rounded-[2.5rem] bg-white border border-slate-200 shadow-sm relative overflow-hidden group transition-all hover:shadow-lg">
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className={`w-10 h-10 rounded-xl bg-${m.color}-50 text-${m.color}-600 flex items-center justify-center`}>{m.icon}</div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{m.title}</p>
                                        </div>
                                        <p className="text-2xl font-black text-slate-900">{fCur(m.val, cur)}</p>
                                        <div className={`absolute bottom-0 left-0 h-1 bg-${m.color}-500 w-full opacity-30`}></div>
                                    </div>
                                ))}
                            </div>

                            {(event.mdo_budget_mode || totalMdoBudget > 0) && (
                                <div className="bg-white rounded-[4rem] border border-slate-200 overflow-hidden shadow-sm">
                                    <div className="p-10 border-b border-slate-50 bg-slate-50/30 flex justify-between items-center">
                                        <h4 className="font-black uppercase text-[10px] tracking-widest text-slate-400 flex items-center gap-3">Ejecución Presupuestaria MDO</h4>
                                        {perms.canManageBudget && (
                                            mdoFromResources > 0 ? (
                                                <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 px-6 py-3 rounded-2xl">
                                                    <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                                                    <p className="text-[9px] font-black text-amber-700 uppercase tracking-widest">MDO calculado desde Recursos — configuración manual deshabilitada</p>
                                                </div>
                                            ) : (
                                                <button onClick={() => setActiveModal('MDO_BUDGET_MODE')} className="bg-slate-900 text-white px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all">Configurar MDO</button>
                                            )
                                        )}
                                    </div>
                                    <MDOBudgetExecution
                                        event={event}
                                        expenses={expenses}
                                        tasks={tasks}
                                        taskBudgets={tBudgets}
                                        totalMdoBudget={totalMdoBudget}
                                        onEditExpense={onEditExpense}
                                        onDeleteExpense={(id) => handleAction('delete_expense', { id })}
                                        onEditBudget={(mode, budgetData) => {
                                            setModalData(budgetData);
                                            setActiveModal(mode === 'GLOBAL' ? 'MDO_BUDGET_GLOBAL' : 'MDO_BUDGET_PHASE');
                                        }}
                                        onDeleteBudget={(mode, taskId = null) => {
                                            if (confirm("¿Seguro que quieres eliminar este presupuesto de Mano de Obra?")) {
                                                handleAction('delete_mdo_budget', { event_id: event.id, mode, task_id: taskId });
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </section>
                    </div>
                )
            }

            {/* Hidden CSV Export Link */}
            <a id="download_csv" className="hidden"></a>

            {
                tab === 'CRONOGRAMA' && (
                    <div className="space-y-10 animate-in">
                        {/* Header with Total Progress */}
                        <div className="flex flex-col gap-6 px-8 bg-slate-900 p-8 rounded-[2.5rem] border border-slate-800 shadow-xl mb-8">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="font-black uppercase text-[11px] tracking-widest text-indigo-300 flex items-center gap-3"><Clock size={20} /> Cronograma de Obra</h3>
                                    <p className="text-[9px] font-bold text-slate-500 uppercase mt-2">Gestión de Tareas y Hitos</p>
                                </div>
                                <div className="flex items-center gap-8">
                                    <div className="text-right">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Avance Global</p>
                                        <p className="text-3xl font-black text-white tracking-tighter shadow-glow-indigo">
                                            {progFisico}%
                                        </p>
                                    </div>
                                    <div className="h-10 w-px bg-slate-700"></div>
                                    {perms.canEditTasks && (
                                        <div className="flex items-center gap-4">
                                            <button
                                                onClick={() => setActiveModal('IMPORT_TEMPLATE')}
                                                className="bg-slate-700 hover:bg-slate-600 text-white py-4 px-8 rounded-2xl text-[11px] uppercase font-black flex items-center gap-2 transition-all"
                                                title="Importar una plantilla de tareas a este proyecto"
                                            >
                                                <Upload size={16} /> Importar Plantilla
                                            </button>
                                            <button
                                                onClick={() => setActiveModal('SAVE_TEMPLATE')}
                                                className="bg-slate-700 hover:bg-slate-600 text-white py-4 px-8 rounded-2xl text-[11px] uppercase font-black flex items-center gap-2 transition-all"
                                                title="Exportar cronograma como template JSON para agregar a tasks_templates.json"
                                            >
                                                <Download size={16} /> Guardar Template
                                            </button>
                                            <button onClick={() => setActiveModal('AI_PLANNER')} className="bg-indigo-600 hover:bg-indigo-700 text-white py-4 px-8 rounded-2xl text-[11px] uppercase font-black flex items-center gap-2 shadow-lg shadow-indigo-600/20 transition-all hover:-translate-y-1 active:scale-95">✨ Asistente IA</button>
                                            <button onClick={() => setActiveModal('ADD_TASK')} className="btn-primary py-4 px-10 text-[11px] uppercase font-black">+ Nueva Tarea</button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-4 pt-4 border-t border-white/5">
                                <div className="flex items-center gap-3 bg-white/5 px-4 py-2 rounded-xl">
                                    <Users size={14} className="text-slate-400" />
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Filtrar por Responsable:</span>
                                    <select
                                        value={assigneeFilter}
                                        onChange={(e) => setAssigneeFilter(e.target.value)}
                                        className="bg-transparent text-white text-[10px] font-black uppercase outline-none cursor-pointer"
                                    >
                                        <option value="ALL" className="bg-slate-900">Todos</option>
                                        {Array.from(new Set([
                                            ...tasks.map(t => t.assignee_name).filter(Boolean),
                                            ...(data.resource_assignments || [])
                                                .filter(ra => ra.event_id === event.id)
                                                .map(ra => {
                                                    const res = (data.resources || []).find(r => r.id === ra.resource_id);
                                                    return res ? res.name : null;
                                                }).filter(Boolean)
                                        ])).sort().map(name => (
                                            <option key={name} value={name} className="bg-slate-900">{name}</option>
                                        ))}
                                    </select>
                                </div>
                                {assigneeFilter !== 'ALL' && (
                                    <button
                                        onClick={() => setAssigneeFilter('ALL')}
                                        className="text-[9px] font-black text-indigo-400 uppercase hover:text-indigo-300 transition-colors"
                                    >
                                        Limpiar Filtro
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="bg-white p-12 rounded-[3.5rem] border border-slate-200 shadow-sm space-y-10">
                            {(() => {
                                const renderTask = (task, level = 0) => {
                                    const subs = tasks.filter(s => s.parent_id === task.id);
                                    const isExpanded = expandedGroups[task.id];
                                    const hasAssignments = (data.resource_assignments || []).filter(ra => ra.task_id === task.id);
                                    const taskResources = (data.task_resources || appData?.task_resources || []).filter(tr => tr.task_id === task.id);
                                    const assignedUnits = hasAssignments.map(ra => {
                                        const res = (data.resources || []).find(r => r.id === ra.resource_id);
                                        return res ? res.name[0] : null;
                                    }).filter(Boolean);

                                    const isLeaf = subs.length === 0;
                                    let tProg = 0;
                                    let isDelayed = false;

                                    if (isLeaf && task.start_date && task.end_date) {
                                        const s = new Date(task.start_date);
                                        const e = new Date(task.end_date);
                                        const now = new Date();
                                        const totalWD = getWorkDays(s, e);
                                        const endForElapsed = now < e ? now : e;
                                        const elapsedWD = s <= endForElapsed ? getWorkDays(s, endForElapsed) : 0;
                                        tProg = totalWD > 0 ? Math.round((elapsedWD / totalWD) * 100) : 0;
                                        isDelayed = task.progress < (tProg - 5);
                                    }

                                    return (
                                        <div key={task.id} className="space-y-6">
                                            <div
                                                onClick={() => setExpandedGroups(prev => ({ ...prev, [task.id]: !prev[task.id] }))}
                                                style={{ marginLeft: `${level * 2}rem` }}
                                                className={`group border-l-4 ${subs.length > 0 ? 'border-amber-500 bg-amber-50/10' : (isDelayed ? 'border-rose-500 bg-rose-50/20' : 'border-indigo-500 bg-indigo-50/10')} pl-8 transition-all p-6 rounded-3xl cursor-pointer relative overflow-hidden active:scale-[0.98] hover:shadow-md`}
                                            >
                                                <div className="flex justify-between items-start mb-4 relative z-10">
                                                    <div className="flex items-center gap-6">
                                                        <div className="flex -space-x-3 mr-2">
                                                            {assignedUnits.map((initial, idx) => (
                                                                <div key={idx} className="w-10 h-10 bg-indigo-600 border-4 border-white text-white rounded-2xl flex items-center justify-center font-black text-xs shadow-lg" title="Recurso Asignado">
                                                                    {initial}
                                                                </div>
                                                            ))}
                                                            {assignedUnits.length === 0 && (
                                                                <div className={`w-10 h-10 ${subs.length > 0 ? 'bg-amber-500' : (isDelayed ? 'bg-rose-500' : 'bg-indigo-500')} text-white rounded-2xl flex items-center justify-center font-black text-xl shadow-lg`}>
                                                                    {subs.length > 0 ? (isExpanded ? '-' : '+') : (task.assignee_name?.[0] || '?')}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-4 mb-2">
                                                                <p className={`font-black text-xl uppercase tracking-tighter leading-none ${subs.length > 0 ? 'text-amber-700' : (isDelayed ? 'text-rose-700' : 'text-slate-900')}`}>{task.title}</p>
                                                                {subs.length > 0 && <span className="text-[9px] bg-amber-200 px-2 py-0.5 rounded text-amber-800 font-bold uppercase">Fase / Grupo</span>}
                                                                {isDelayed && <span className="text-[9px] bg-rose-600 px-2 py-0.5 rounded text-white font-black uppercase tracking-widest animate-pulse">RETRASADA {tProg}%</span>}
                                                                {task.assignee_name && <span className="text-[9px] bg-indigo-100 px-2 py-0.5 rounded text-indigo-700 font-bold uppercase tracking-widest">RESP: {task.assignee_name}</span>}
                                                                {taskResources.map(tr => {
                                                                    const res = (data.resources || []).find(r => r.id === tr.resource_id);
                                                                    if (!res) return null;
                                                                    return <span key={tr.id} className="text-[9px] bg-violet-100 px-2 py-0.5 rounded text-violet-700 font-bold uppercase tracking-widest border border-violet-200">{res.name}</span>;
                                                                })}
                                                            </div>
                                                            <div className="flex flex-wrap gap-2 mb-2">
                                                                {hasAssignments.map(ra => {
                                                                    const res = (data.resources || []).find(r => r.id === ra.resource_id);
                                                                    if (!res) return null;
                                                                    return (
                                                                        <span key={ra.id} className="text-[8px] bg-slate-100 px-2 py-0.5 rounded text-slate-600 font-black uppercase tracking-widest border border-slate-200">
                                                                            {res.name}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                            <div className="flex gap-4">
                                                                <p className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">{task.start_date.split('-').reverse().join('/')} AL {task.end_date.split('-').reverse().join('/')}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {perms.canEditTasks && (
                                                            <>
                                                                <button onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setModalData(task);
                                                                    setActiveModal('EDIT_TASK');
                                                                }} className="p-3 text-indigo-600 hover:bg-white rounded-xl transition-all shadow-sm bg-white/50"><Edit3 size={18} /></button>
                                                                <button onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (confirm("¿Eliminar tarea/fase?")) handleAction('delete_task', { id: task.id });
                                                                }} className="p-3 text-rose-400 hover:bg-white hover:text-rose-600 rounded-xl transition-all shadow-sm bg-white/50"><Trash2 size={18} /></button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <ProgressBar progress={task.progress} label={subs.length > 0 ? "Avance Ponderado de Fase" : "Avance Individual"} color={subs.length > 0 ? "amber" : (isDelayed ? "rose" : "indigo")} />
                                            </div>

                                            {isExpanded && subs.length > 0 && (
                                                <div className="space-y-4 border-l-2 border-slate-100 animate-in slide-in-from-top-4">
                                                    {subs.map(s => renderTask(s, level + 1))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                };

                                return filteredTasks
                                    .filter(t => !t.parent_id)
                                    .map(t => renderTask(t));
                            })()}
                        </div>
                    </div>
                )
            }

            {
                tab === 'GANTT' && (
                    <div className="space-y-6">
                        <ProjectGantt
                            onOpenSettings={() => setIsGanttPrefsOpen(true)}
                            tasks={filteredTasks}
                            onEdit={(t) => { setModalData(t); setActiveModal('EDIT_TASK'); }}
                            canEdit={perms.canEditTasks}
                            t={t}
                            handleAction={handleAction}
                            event={event}
                            categories={data.categories.filter(c => c.event_id === event.id || budgets.some(b => b.category_id === c.id) || expenses.some(e => e.category_id === c.id))}
                            expenses={expenses}
                            materials={data.materials}
                            taskBudgets={data.task_budgets}
                            prefs={ganttPrefs}
                            isManager={userRole === 'MANAGER'}
                            isExternalProject={isExternalProject}
                            uiPrefs={uiPrefs}
                            totalMdoBudget={totalMdoBudget}
                        />
                    </div>
                )
            }

            {
                tab === 'RUBROS' && (
                    <div className="space-y-10 animate-in">
                        <div className="flex justify-between items-center px-4">
                            <h3 className="font-black uppercase text-[11px] tracking-widest text-slate-400 flex items-center gap-3"><Briefcase size={20} /> Diccionario de Rubros</h3>
                            {perms.canManageBudget && (
                                <button onClick={() => {
                                    const n = prompt("Nombre del Rubro:");
                                    if (n) handleAction('add_category', { name: n, event_id: event.id });
                                }} className="btn-primary py-4 px-10 text-[11px] uppercase font-black">+ Crear Rubro</button>
                            )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {data.categories.filter(c => c.event_id === event.id || budgets.some(b => b.category_id === c.id) || expenses.some(e => e.category_id === c.id)).map(c => (
                                <div key={c.id} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 flex items-center justify-between shadow-sm group">
                                    <span className="font-black uppercase text-sm tracking-tight text-slate-700">{c.name}</span>
                                    {perms.canManageBudget && (
                                        <button onClick={() => { if (confirm("¿Eliminar este rubro del diccionario?")) handleAction('delete_category', { id: c.id }); }} className="opacity-0 group-hover:opacity-100 p-2 text-rose-300 hover:text-rose-500 transition-all"><Trash2 size={16} /></button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )
            }

            {
                tab === 'OC' && (
                    <div className="space-y-10 animate-in pb-20">
                        <div className="flex justify-between items-center px-8 bg-slate-900 p-8 rounded-[2.5rem] border border-slate-800 shadow-2xl">
                            <div>
                                <h3 className="font-black uppercase text-[11px] tracking-widest text-indigo-300 flex items-center gap-3"><FilePlus size={20} /> Órdenes de Compra (OC)</h3>
                                <p className="text-[9px] font-bold text-slate-500 uppercase mt-2">Seguimiento de autorizaciones de gasto</p>
                            </div>
                            <button
                                onClick={() => { setModalData({ event_id: event.id, client_id: event.client_id }); setActiveModal('PURCHASE_ORDER'); }}
                                className="btn-primary py-4 px-10 text-[11px] uppercase font-black"
                            >
                                + Nueva OC
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {(data.purchase_orders || []).filter(oc => oc.event_id === event.id).length === 0 ? (
                                <div className="col-span-full p-20 bg-white border-2 border-dashed border-slate-100 rounded-[3rem] text-center">
                                    <FileText size={48} className="mx-auto text-slate-100 mb-6" />
                                    <p className="text-slate-300 font-black uppercase text-xs tracking-widest italic">No hay Órdenes de Compra registradas</p>
                                </div>
                            ) : (
                                (data.purchase_orders || []).filter(oc => oc.event_id === event.id).map(oc => (
                                    <div key={oc.id} className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
                                        <div className="flex justify-between items-start mb-6">
                                            <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm group-hover:bg-indigo-600 group-hover:text-white transition-all">
                                                <Target size={24} />
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => { setModalData(oc); setActiveModal('PURCHASE_ORDER'); }} className="p-2 text-slate-300 hover:text-indigo-600 transition-all opacity-0 group-hover:opacity-100"><Edit3 size={16} /></button>
                                                <button onClick={() => { if (confirm("¿Eliminar OC?")) handleAction('delete_purchase_order', { id: oc.id }); }} className="p-2 text-slate-300 hover:text-rose-500 transition-all opacity-0 group-hover:opacity-100"><Trash2 size={16} /></button>
                                            </div>
                                        </div>
                                        <div className="space-y-2 mb-6">
                                            <h4 className="text-xl font-black text-slate-900 uppercase tracking-tighter">OC #{oc.oc_number}</h4>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{oc.description || 'Sin descripción'}</p>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="flex justify-between items-end">
                                                <div>
                                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Monto Total</p>
                                                    <p className="font-black text-lg text-slate-700">{fCur(oc.amount_total, cur)}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Disponible</p>
                                                    <p className={`font-black text-lg ${Number(oc.balance) <= 0 ? 'text-rose-500' : 'text-emerald-600'}`}>{fCur(oc.balance, cur)}</p>
                                                </div>
                                            </div>
                                            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full transition-all duration-1000 ${Number(oc.balance) <= 0 ? 'bg-rose-500' : 'bg-emerald-500'}`}
                                                    style={{ width: `${Math.max(0, Math.min(100, (Number(oc.balance) / Number(oc.amount_total)) * 100))}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )
            }

            {
                tab === 'FINANZAS' && (
                    <div className="space-y-10 animate-in">
                        <div className="flex justify-between items-center px-4 bg-slate-900 p-8 rounded-[2.5rem] border border-slate-800 shadow-2xl">
                            <div className="flex items-center gap-6">
                                <h3 className="font-black uppercase text-[11px] tracking-widest text-indigo-300">Libro de Cuenta Corriente</h3>
                                <div className="flex items-center bg-white/5 rounded-2xl p-1">
                                    <button onClick={() => setPayFilter(p => ({ ...p, group: !p.group }))} className={`px-6 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${payFilter.group ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400'}`}>{payFilter.group ? 'Rubros Agrupados' : 'Vista Lista'}</button>
                                </div>
                            </div>
                            <div className="flex gap-4 items-center">
                                <input type="date" className="bg-white/5 border border-white/10 text-white text-[10px] p-3 rounded-xl font-bold uppercase" onChange={e => setPayFilter(p => ({ ...p, start: e.target.value }))} />
                                <input type="date" className="bg-white/5 border border-white/10 text-white text-[10px] p-3 rounded-xl font-bold uppercase" onChange={e => setPayFilter(p => ({ ...p, end: e.target.value }))} />
                                <button onClick={() => {
                                    const movs = projectMovements.sort((a, b) => new Date(a.expense_date) - new Date(b.expense_date));
                                    let table = `
                                    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
                                    <head>
                                        <meta charset="UTF-8">
                                        <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Movimientos Financieros</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
                                    </head>
                                    <body>
                                    <table border="1">
                                        <thead>
                                            <tr style="background-color: #4f46e5; color: white; font-family: sans-serif;">
                                                <th style="padding: 10px;">FECHA</th>
                                                <th style="padding: 10px;">RUBRO</th>
                                                <th style="padding: 10px;">TIPO</th>
                                                <th style="padding: 10px;">CONCEPTO</th>
                                                <th style="padding: 10px;">PROVEEDOR</th>
                                                <th style="padding: 10px;">COMPROBANTE</th>
                                                <th style="padding: 10px;">MONTO original</th>
                                                <th style="padding: 10px;">MONEDA</th>
                                                <th style="padding: 10px;">MONTO conversion</th>
                                            </tr>
                                        </thead>
                                        <tbody style="font-family: sans-serif;">`;

                                    movs.forEach(m => {
                                        table += `<tr>
                                            <td>${m.expense_date?.split(' ')[0]}</td>
                                            <td>${m.category_name || '-'}</td>
                                            <td>${m.movement_type === 'INCOME' ? 'COBRO' : 'PAGO'}</td>
                                            <td>${m.description}</td>
                                            <td>${m.supplier || '-'}</td>
                                            <td>${m.invoice_number || '-'}</td>
                                            <td style="text-align: right;">${Number(m.amount_original).toLocaleString('es-AR')}</td>
                                            <td>${m.currency_original || cur}</td>
                                            <td style="text-align: right;">${Number(m.amount_converted).toLocaleString('es-AR')}</td>
                                        </tr>`;
                                    });

                                    table += `</tbody></table></body></html>`;

                                    const blob = new Blob(['\uFEFF' + table], { type: 'application/vnd.ms-excel;charset=utf-8' });
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `Finanzas_${event.name}.xls`;
                                    a.click();
                                }} className="bg-emerald-50 text-emerald-600 px-6 py-4 rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-emerald-100 transition-all flex items-center gap-2">
                                    <FileText size={16} /> Exportar Excel
                                </button>
                                {perms.canEditExpenses && (
                                    <button onClick={onAddExpense} className="btn-primary py-4 px-10 text-[11px] uppercase font-black">Registrar Movimiento</button>
                                )}
                            </div>
                        </div>

                        <div className="px-4">
                            <CashFlowChart
                                movements={projectMovements}
                                baseCurrency={event.base_currency || 'ARS'}
                                type="PROJECT"
                                fCur={fCur}
                                fNum={fNum}
                                conversionRate={1}
                                embedded={true}
                                projects={data.projects || []}
                                clients={data.clients || []}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-4">
                            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm flex items-center justify-between">
                                <div>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Ingresos</p>
                                    <p className="text-2xl font-black text-emerald-600 tracking-tighter">{fCur(totalIncome, cur)}</p>
                                </div>
                                <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center shadow-inner"><TrendingUp size={24} /></div>
                            </div>
                            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm flex items-center justify-between">
                                <div>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Egresos</p>
                                    <p className="text-2xl font-black text-rose-600 tracking-tighter">{fCur(totalExpense, cur)}</p>
                                </div>
                                <div className="w-12 h-12 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center shadow-inner"><TrendingDown size={24} /></div>
                            </div>
                            <div className={`p-8 rounded-[2.5rem] border shadow-sm flex items-center justify-between ${projectProfit >= 0 ? 'bg-indigo-600 border-indigo-500 text-white shadow-indigo-200' : 'bg-rose-900 border-rose-800 text-white'}`}>
                                <div>
                                    <p className="text-[9px] font-black opacity-60 uppercase tracking-widest mb-1">Resultado Proyecto</p>
                                    <p className="text-2xl font-black tracking-tighter">{fCur(projectProfit, cur)}</p>
                                </div>
                                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center"><Wallet size={24} /></div>
                            </div>
                        </div>

                        <div className="bg-white rounded-[4rem] border border-slate-200 overflow-hidden shadow-sm">
                            {(() => {
                                let filtered = projectMovements.filter(ex => {
                                    const d = ex.expense_date?.split(' ')[0];
                                    if (payFilter.start && d < payFilter.start) return false;
                                    if (payFilter.end && d > payFilter.end) return false;
                                    return true;
                                }).sort((a, b) => new Date(b.expense_date) - new Date(a.expense_date));

                                if (payFilter.group) {
                                    const groups = data.categories.filter(c => filtered.some(e => e.category_id === c.id));
                                    return (
                                        <div className="divide-y divide-slate-100">
                                            {groups.map(c => {
                                                const sub = filtered.filter(e => e.category_id === c.id);
                                                const subIncome = sub.filter(e => e.movement_type === 'INCOME').reduce((a, v) => a + v.val, 0);
                                                const subExpense = sub.filter(e => e.movement_type === 'EXPENSE').reduce((a, v) => a + v.val, 0);
                                                const subNet = subIncome - subExpense;
                                                const isExpanded = expandedGroups['pay_' + c.id];
                                                return (
                                                    <div key={c.id}>
                                                        <div onClick={() => setExpandedGroups(prev => ({ ...prev, ['pay_' + c.id]: !prev['pay_' + c.id] }))} className="p-8 px-12 bg-slate-50/50 flex justify-between items-center cursor-pointer hover:bg-indigo-50/30 transition-all border-l-8 border-indigo-500">
                                                            <div>
                                                                <p className="font-black uppercase text-xs tracking-widest text-slate-800">{c.name}</p>
                                                                <p className="text-[9px] font-bold text-slate-400 uppercase mt-1">{sub.length} Movimientos Registrados</p>
                                                            </div>
                                                            <div className="flex items-center gap-8">
                                                                <div className="text-right">
                                                                    <p className={`font-black text-xl tracking-tighter ${subNet >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fCur(subNet, cur)}</p>
                                                                    <p className="text-[8px] font-black text-slate-400 uppercase">Sub-Resultado</p>
                                                                </div>
                                                                <div className={`transition-transform duration-500 ${isExpanded ? 'rotate-180' : ''}`}><ChevronDown size={20} className="text-slate-300" /></div>
                                                            </div>
                                                        </div>
                                                        {isExpanded && (
                                                            <table className="w-full text-left">
                                                                <tbody className="divide-y divide-slate-50">
                                                                    {sub.map(ex => (
                                                                        <tr key={ex.id} className={`hover:bg-slate-50 transition-colors ${perms.canEditExpenses ? 'cursor-pointer' : 'cursor-default'} group`} onClick={() => { if (perms.canEditExpenses && onEditExpense) { onEditExpense(ex); } }}>
                                                                            <td className="w-12 pl-12 py-6">
                                                                                <div className={`w-2 h-10 rounded-full ${ex.movement_type === 'INCOME' ? 'bg-emerald-500' : 'bg-indigo-500'}`}></div>
                                                                            </td>
                                                                            <td className="w-48 px-8 py-6 text-[11px] font-black text-slate-400">{ex.expense_date?.split(' ')[0]}</td>
                                                                            <td className="px-12 py-6">
                                                                                <p className="text-[11px] font-black uppercase text-slate-700 leading-none mb-1">{ex.description}</p>
                                                                                <div className="flex gap-2 items-center">
                                                                                    {ex.movement_type === 'INCOME' && <span className="text-[8px] bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-md font-black uppercase">Cobro</span>}
                                                                                    {ex.linked_movement_id && <span className="text-[8px] bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-md font-black uppercase flex items-center gap-1"><LinkIcon size={8} /> Vinculado</span>}
                                                                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">{ex.supplier || 'Proveedor Gen.'} {ex.invoice_number ? `• FACT ${ex.invoice_number}` : ''}</p>
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-12 py-6 text-right w-64">
                                                                                <div className="flex flex-col items-end">
                                                                                    <p className={`font-black text-lg tracking-tighter ${ex.movement_type === 'INCOME' ? 'text-emerald-600' : 'text-slate-900'}`}>
                                                                                        {ex.movement_type === 'INCOME' ? '+' : '-'} {Number(ex.val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                    </p>
                                                                                    {ex.currency_original && ex.currency_original !== cur && (
                                                                                        <p className="text-[9px] font-bold text-slate-400 uppercase bg-slate-100 px-2 py-0.5 rounded-md mt-1">
                                                                                            Original: {ex.currency_original} {Number(ex.amount_original).toLocaleString()}
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-12 py-6 text-right w-20 text-indigo-300 opacity-0 group-hover:opacity-100 transition-all"><Edit3 size={14} /></td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                }

                                return (
                                    <table className="w-full text-left">
                                        <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">
                                            <tr>
                                                <th className="w-20"></th>
                                                <th className="px-12 py-8">Fecha</th>
                                                <th className="px-12 py-8">Concepto / Rubro</th>
                                                <th className="px-12 py-8 text-right">Importe ({cur})</th>
                                                <th className="w-20 text-right pr-12"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {filtered.map(ex => (
                                                <tr key={ex.id} className="hover:bg-indigo-50/30 transition-colors cursor-pointer group" onClick={() => { if (onEditExpense) onEditExpense(ex); }}>
                                                    <td className="pl-12">
                                                        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${ex.movement_type === 'INCOME' ? 'bg-emerald-50 text-emerald-500 shadow-sm shadow-emerald-100' : 'bg-indigo-50 text-indigo-500 shadow-sm shadow-indigo-100'}`}>
                                                            {ex.movement_type === 'INCOME' ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />}
                                                        </div>
                                                    </td>
                                                    <td className="px-12 py-8 text-[11px] font-bold text-slate-500">{ex.expense_date?.split(' ')[0]}</td>
                                                    <td className="px-12 py-8">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="text-[11px] font-black uppercase text-indigo-600 leading-none mb-2">{ex.category_name}</p>
                                                                <div className="flex gap-2 items-center">
                                                                    <p className="text-[10px] font-bold text-slate-800 uppercase tracking-tight">{ex.description}</p>
                                                                    {ex.linked_movement_id && <LinkIcon size={12} className="text-slate-300" />}
                                                                </div>
                                                            </div>
                                                            <div className="opacity-0 group-hover:opacity-100 transition-all text-indigo-400"><Edit3 size={16} /></div>
                                                        </div>
                                                    </td>
                                                    <td className="px-12 py-8 text-right">
                                                        <div className="flex flex-col items-end">
                                                            <p className={`font-black text-lg tracking-tighter ${ex.movement_type === 'INCOME' ? 'text-emerald-600' : 'text-slate-900'}`}>
                                                                {ex.movement_type === 'INCOME' ? '+' : '-'} {Number(ex.val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </p>
                                                            {ex.currency_original && ex.currency_original !== cur && (
                                                                <p className="text-[9px] font-bold text-slate-400 uppercase bg-slate-100 px-2 py-0.5 rounded-md mt-1">
                                                                    Original: {ex.currency_original} {Number(ex.amount_original).toLocaleString()}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                );
                            })()}
                        </div>
                    </div>
                )
            }

            {(tab === 'RESUMEN' || tab === 'PORTADA') && (
                <div className="space-y-10 animate-in">
                    {/* Panel operativo: lo que el usuario necesita ver primero al entrar al proyecto */}
                    {(() => {
                        const todayISO = new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD local
                        const in14 = new Date(Date.now() + 14 * 86400000).toLocaleDateString('sv-SE');
                        const day = (d) => (d || '').split('T')[0];
                        const pending = tasks.filter(tk => !tk.isParent && Number(tk.progress || 0) < 100 && day(tk.end_date));
                        const overdue = pending.filter(tk => day(tk.end_date) < todayISO).sort((a, b) => day(a.end_date).localeCompare(day(b.end_date)));
                        const upcoming = pending.filter(tk => day(tk.end_date) >= todayISO && day(tk.end_date) <= in14).sort((a, b) => day(a.end_date).localeCompare(day(b.end_date)));
                        const team = [...new Map(projectResources.map(r => [r.id, r])).values()];
                        const openTask = (tk) => perms.canEditTasks ? (setModalData(tk), setActiveModal('EDIT_TASK')) : setTab('CRONOGRAMA');
                        const diff = progFisico - theoreticalProg;
                        const card = "bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col gap-4 min-h-[200px]";
                        const head = "text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2";
                        const TaskRow = ({ tk, tone }) => (
                            <button onClick={() => openTask(tk)} className="w-full flex items-center justify-between gap-3 text-left px-3 py-2 rounded-xl hover:bg-slate-50 transition-all">
                                <span className="text-sm font-semibold text-slate-700 truncate">{tk.title}</span>
                                <span className={`text-xs font-bold shrink-0 ${tone}`}>{fDate(tk.end_date)}</span>
                            </button>
                        );
                        return (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                                <div className={card}>
                                    <h3 className={head}><TrendingUp size={16} className="text-indigo-600" /> Avance</h3>
                                    <div className="flex items-end gap-4">
                                        <p className="text-4xl font-black text-slate-900 tracking-tight">{progFisico}%</p>
                                        <p className="text-sm font-semibold text-slate-500 pb-1">real · {theoreticalProg}% esperado</p>
                                    </div>
                                    <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
                                        <div className="absolute inset-y-0 left-0 bg-indigo-500 rounded-full" style={{ width: `${Math.min(100, progFisico)}%` }}></div>
                                        <div className="absolute inset-y-0 w-0.5 bg-rose-500" style={{ left: `${Math.min(100, theoreticalProg)}%` }} title="Avance esperado a hoy"></div>
                                    </div>
                                    <span className={`self-start px-3 py-1 rounded-full text-xs font-bold ${diff >= -5 ? 'bg-emerald-50 text-emerald-700' : diff >= -15 ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700'}`}>
                                        {diff >= 0 ? `Al día (+${diff}%)` : diff >= -5 ? `Levemente atrasado (${diff}%)` : `Atrasado (${diff}%)`}
                                    </span>
                                </div>

                                <div className={card}>
                                    <h3 className={head}><AlertTriangle size={16} className={overdue.length ? 'text-rose-500' : 'text-slate-400'} /> Tareas atrasadas <span className={`ml-auto px-2 py-0.5 rounded-full text-xs ${overdue.length ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-500'}`}>{overdue.length}</span></h3>
                                    {overdue.length === 0
                                        ? <p className="text-sm text-slate-500 m-auto">Sin tareas vencidas 👌</p>
                                        : <div className="-mx-3">{overdue.slice(0, 4).map(tk => <TaskRow key={tk.id} tk={tk} tone="text-rose-600" />)}</div>}
                                    {overdue.length > 4 && <button onClick={() => setTab('CRONOGRAMA')} className="text-xs font-bold text-indigo-600 hover:underline self-start mt-auto">Ver las {overdue.length} en el cronograma →</button>}
                                </div>

                                <div className={card}>
                                    <h3 className={head}><Clock size={16} className="text-amber-500" /> Vencen en 14 días <span className="ml-auto px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500">{upcoming.length}</span></h3>
                                    {upcoming.length === 0
                                        ? <p className="text-sm text-slate-500 m-auto">Nada por vencer en las próximas 2 semanas</p>
                                        : <div className="-mx-3">{upcoming.slice(0, 4).map(tk => <TaskRow key={tk.id} tk={tk} tone="text-amber-600" />)}</div>}
                                    {upcoming.length > 4 && <button onClick={() => setTab('CRONOGRAMA')} className="text-xs font-bold text-indigo-600 hover:underline self-start mt-auto">Ver todas →</button>}
                                </div>

                                <div className={card}>
                                    <h3 className={head}><Users size={16} className="text-indigo-600" /> Equipo asignado <span className="ml-auto px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500">{team.length}</span></h3>
                                    {team.length === 0
                                        ? <p className="text-sm text-slate-500 m-auto text-center">Todavía no hay recursos asignados</p>
                                        : <div className="flex flex-col gap-2">{team.slice(0, 4).map(r => (
                                            <div key={r.id} className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center text-xs font-black shrink-0">{(r.name || '?')[0]}</div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold text-slate-700 truncate">{r.name}</p>
                                                    {r.position && <p className="text-xs text-slate-500 truncate">{r.position}</p>}
                                                </div>
                                            </div>
                                        ))}</div>}
                                    <button onClick={() => setTab('RECURSOS')} className="text-xs font-bold text-indigo-600 hover:underline self-start mt-auto">{team.length ? 'Gestionar recursos →' : 'Asignar recursos →'}</button>
                                </div>
                            </div>
                        );
                    })()}

                    <PortfolioTab
                        event={event}
                        healthColor={healthColor}
                        client={client}
                    />

                    {/* Resumen de Ejecución Unificado */}
                    {!isExternalProject && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-sm relative overflow-hidden">
                                <div className="flex justify-between items-center mb-10">
                                    <h3 className="font-black uppercase text-[10px] tracking-widest text-slate-400 flex items-center gap-2">
                                        <TrendingUp size={16} className="text-emerald-600" /> Control de Cobranza
                                    </h3>
                                    <div className="bg-emerald-600/5 px-6 py-2 rounded-2xl">
                                        <span className="text-[10px] font-black uppercase text-emerald-600 tracking-wider">Ingresos ({cur})</span>
                                    </div>
                                </div>

                                <div className="space-y-8">
                                    <div className="flex justify-between items-end">
                                        <div>
                                            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Valor Contrato ({cur})</p>
                                            <p className="text-3xl font-black text-slate-900 tracking-tighter">{fNum(incomeBudget)}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Recaudado</p>
                                            <p className="text-2xl font-black text-emerald-600 tracking-tighter">{fNum(totalIncome)}</p>
                                        </div>
                                    </div>

                                    <div className="pt-6 border-t border-slate-50">
                                        <div className="flex justify-between items-center mb-2">
                                            <p className="text-[9px] font-black text-slate-400 uppercase">Progreso de Cobro</p>
                                            <p className="text-xs font-black text-emerald-600">{progCobro}%</p>
                                        </div>
                                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden shadow-inner">
                                            <div className="h-full bg-emerald-500 transition-all duration-1000" style={{ width: `${Math.min(100, progCobro)}%` }}></div>
                                        </div>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase text-right mt-2">Pendiente: {fCur(incomeBudget - totalIncome, cur)}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-sm relative overflow-hidden">
                                <div className="flex justify-between items-center mb-10">
                                    <h3 className="font-black uppercase text-[10px] tracking-widest text-slate-400 flex items-center gap-2">
                                        <Wallet size={16} className="text-indigo-600" /> Control de Costos
                                    </h3>
                                    <div className="bg-indigo-600/5 px-6 py-2 rounded-2xl">
                                        <span className="text-[10px] font-black uppercase text-indigo-600 tracking-wider">Operación ({cur})</span>
                                    </div>
                                </div>

                                <div className="space-y-8">
                                    <div className="flex justify-between items-end">
                                        <div>
                                            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Presupuesto Ejecución</p>
                                            <p className="text-3xl font-black text-slate-900 tracking-tighter">{fNum(unifiedTotalBudget)}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Ejecutado (Pagos)</p>
                                            <p className="text-2xl font-black text-indigo-600 tracking-tighter">{fNum(totalExpense)}</p>
                                        </div>
                                    </div>

                                    <div className="pt-6 border-t border-slate-50">
                                        <div className="flex justify-between items-center mb-2">
                                            <p className="text-[9px] font-black text-slate-400 uppercase">Uso del Gasto</p>
                                            <p className={`text-xs font-black ${progFinanciero > 100 ? 'text-rose-500' : 'text-indigo-600'}`}>{progFinanciero}%</p>
                                        </div>
                                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden shadow-inner">
                                            <div className={`h-full transition-all duration-1000 ${progFinanciero > 100 ? 'bg-rose-500' : 'bg-indigo-500'}`} style={{ width: `${Math.min(100, progFinanciero)}%` }}></div>
                                        </div>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase text-right mt-2">Disponible: {fCur(unifiedTotalBudget - totalExpense, cur)}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-900 p-10 rounded-[2.5rem] border border-slate-800 shadow-2xl relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16"></div>
                                <div className="flex justify-between items-center mb-10 relative z-10">
                                    <h3 className="font-black uppercase text-[10px] tracking-widest text-indigo-300 flex items-center gap-2">
                                        <TrendingUp size={16} /> Estado de Resultados
                                    </h3>
                                    <div className="bg-white/10 px-6 py-2 rounded-2xl">
                                        <span className="text-[10px] font-black uppercase text-white tracking-wider">Flujo de Caja ({cur})</span>
                                    </div>
                                </div>

                                <div className="space-y-8 relative z-10">
                                    <div className="grid grid-cols-2 gap-8">
                                        <div>
                                            <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-1">Ingresos</p>
                                            <p className="text-2xl font-black text-white tracking-tighter">{fCur(totalIncome, cur)}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-1">Egresos</p>
                                            <p className="text-2xl font-black text-white tracking-tighter">{fCur(totalExpense, cur)}</p>
                                        </div>
                                    </div>

                                    <div className={`p-8 rounded-3xl border ${projectProfit >= 0 ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-rose-500/10 border-rose-500/20'}`}>
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Utilidad Neta del Proyecto</p>
                                                <p className={`text-4xl font-black tracking-tighter ${projectProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                                    {fCur(projectProfit, cur)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">ROI %</p>
                                                <p className={`text-xl font-black ${projectProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{profitabilityPerc}%</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-10">
                        <div className="flex justify-between items-center mb-10">
                            <h3 className={`font-black uppercase text-[12px] tracking-widest ${healthColor === 'emerald' ? 'text-emerald-500' : healthColor === 'amber' ? 'text-amber-500' : 'text-rose-500'} flex items-center gap-3`}>{healthIcon} Diagnóstico de Salud</h3>
                            <div className="bg-indigo-600 text-white px-6 py-2 rounded-full font-black text-[10px]">{progFisico}% REAL</div>
                        </div>
                        <div className="space-y-10">
                            <div className="space-y-4">
                                <div className="flex justify-between items-end">
                                    <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Avance Real vs Teórico</p>
                                    <p className="text-xs font-black text-slate-900">{progFisico}% / {theoreticalProg}%</p>
                                </div>
                                <div className="h-4 bg-slate-100 rounded-full overflow-hidden flex shadow-inner">
                                    <div className="bg-indigo-500 h-full transition-all duration-1000" style={{ width: `${progFisico}%` }}></div>
                                    <div className="bg-slate-300/30 h-full transition-all duration-1000 border-l border-white" style={{ width: `${Math.max(0, theoreticalProg - progFisico)}%` }}></div>
                                </div>
                                <p className="text-[9px] font-bold text-slate-400 uppercase text-center mt-2">Diferencia: {theoreticalProg - progFisico}% basado en tiempo transcurrido</p>
                            </div>

                            <div className="space-y-4 pt-4 border-t border-slate-50">
                                <div className="flex justify-between items-end">
                                    <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Ejecución Presupuestaria (Mat + MDO)</p>
                                    <p className="text-xs font-black text-slate-900">{fCur(totalExpense, cur)} / {fCur(unifiedTotalBudget, cur)}</p>
                                </div>
                                <ProgressBar progress={progFinanciero} label="" color={progFinanciero > 100 ? "rose" : "emerald"} />
                            </div>
                        </div>
                        <div className={`mt-12 p-10 rounded-[3rem] border-2 shadow-2xl transition-all ${healthColor === 'emerald' ? 'bg-emerald-50/50 border-emerald-100' : healthColor === 'amber' ? 'bg-amber-50/50 border-amber-100' : 'bg-rose-50/50 border-rose-100'}`}>
                            <p className="text-[11px] font-black uppercase text-slate-500 mb-4 tracking-widest">Análisis del Gestor</p>
                            <p className={`text-2xl font-black tracking-tighter leading-tight ${healthColor === 'emerald' ? 'text-emerald-700' : healthColor === 'amber' ? 'text-amber-700' : 'text-rose-700'}`}>
                                {healthMsg}
                            </p>
                        </div>
                    </div>
                </div>
            )
            }
            {
                tab === 'MATERIALES' && (
                    <div className="bg-white rounded-[4rem] border border-slate-200 overflow-hidden shadow-sm animate-in">
                        <div className="p-12 border-b border-slate-50 flex justify-between items-center bg-slate-50/20">
                            <div>
                                <h3 className="font-black uppercase text-[11px] tracking-widest text-slate-400 flex items-center gap-3"><Package size={20} /> Estimación de Materiales</h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase mt-2">Carga manual de insumos y materiales para seguimiento de stock</p>
                            </div>
                            <div className="flex items-center gap-4">
                                <button onClick={() => {
                                    const mats = (data.materials || []).filter(m => m.event_id === event.id);
                                    let table = `
                                    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
                                    <head><meta charset="UTF-8"></head>
                                    <body><table border="1">
                                        <thead>
                                            <tr style="background-color: #4f46e5; color: white; font-family: sans-serif;">
                                                <th style="padding: 10px;">MATERIAL</th>
                                                <th style="padding: 10px;">UNIDAD</th>
                                                <th style="padding: 10px;">CANT. ESTIMADA</th>
                                                <th style="padding: 10px;">CANT. REAL</th>
                                                <th style="padding: 10px;">SALDO</th>
                                                <th style="padding: 10px;">NOTAS</th>
                                            </tr>
                                        </thead>
                                        <tbody style="font-family: sans-serif;">`;
                                    mats.forEach(m => {
                                        table += `<tr>
                                            <td>${m.name}</td>
                                            <td style="text-align: center;">${m.unit}</td>
                                            <td style="text-align: right;">${Number(m.quantity_est).toLocaleString('es-AR')}</td>
                                            <td style="text-align: right;">${Number(m.quantity_real).toLocaleString('es-AR')}</td>
                                            <td style="text-align: right;">${(Number(m.quantity_est) - Number(m.quantity_real)).toLocaleString('es-AR')}</td>
                                            <td>${m.notes || '-'}</td>
                                        </tr>`;
                                    });
                                    table += `</tbody></table></body></html>`;
                                    const blob = new Blob(['\uFEFF' + table], { type: 'application/vnd.ms-excel;charset=utf-8' });
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `Stock_Materiales_${event.name}.xls`;
                                    a.click();
                                }} className="bg-emerald-50 text-emerald-600 px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-100 transition-all flex items-center gap-2">
                                    <FileText size={16} /> Exportar Excel
                                </button>
                                {perms.canManageStructure && (
                                    <button onClick={() => setActiveModal('ADD_MATERIAL')} className="btn-primary py-4 px-10 text-[11px] uppercase font-black tracking-widest">+ Cargar Material</button>
                                )}
                            </div>
                        </div>

                        <div className="p-12">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                                {(() => {
                                    const mats = (data.materials || []).filter(m => m.event_id === event.id);
                                    const totalEstM = mats.reduce((sum, m) => sum + (parseFloat(m.quantity_est || 0) * parseFloat(m.unit_price || 0)), 0);
                                    const totalRealM = mats.reduce((sum, m) => sum + (parseFloat(m.quantity_real || 0) * parseFloat(m.unit_price || 0)), 0);
                                    const totalSaldoM = totalEstM - totalRealM;

                                    return (
                                        <>
                                            <div className="p-8 bg-slate-50 rounded-[2rem] border border-slate-100">
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Presupuesto Est.</p>
                                                <p className="text-2xl font-black text-slate-900">{fCur(totalEstM)}</p>
                                            </div>
                                            <div className="p-8 bg-emerald-50 rounded-[2rem] border border-emerald-100">
                                                <p className="text-[9px] font-black text-emerald-400 uppercase tracking-widest mb-2">Consumo Real</p>
                                                <p className="text-2xl font-black text-emerald-600">{fCur(totalRealM)}</p>
                                            </div>
                                            <div className={`p-8 rounded-[2rem] border ${totalSaldoM < 0 ? 'bg-rose-50 border-rose-100' : 'bg-indigo-50 border-indigo-100'}`}>
                                                <p className={`text-[9px] font-black ${totalSaldoM < 0 ? 'text-rose-400' : 'text-indigo-400'} uppercase tracking-widest mb-2`}>Saldo ($)</p>
                                                <p className={`text-2xl font-black ${totalSaldoM < 0 ? 'text-rose-600' : 'text-indigo-600'}`}>{fCur(totalSaldoM)}</p>
                                            </div>
                                            <div className="p-8 bg-slate-50 rounded-[2rem] border border-slate-100">
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Mat. Pendientes</p>
                                                <p className="text-2xl font-black text-slate-600">
                                                    {mats.filter(m => m.quantity_real < m.quantity_est).length}
                                                </p>
                                            </div>
                                        </>
                                    )
                                })()}
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead className="text-[9px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">
                                        <tr>
                                            <th className="px-6 py-4">Insumo / Material</th>
                                            <th className="px-6 py-4">Unidad</th>
                                            <th className="px-6 py-4 text-right">Cant. Estimada</th>
                                            <th className="px-6 py-4 text-right">Cant. Consumida</th>
                                            <th className="px-6 py-4 text-right">Saldo</th>
                                            <th className="px-6 py-4 text-right">Total Est. ($)</th>
                                            <th className="px-6 py-4 text-right">Total Cons. ($)</th>
                                            <th className="px-6 py-4 text-right">Saldo ($)</th>
                                            <th className="px-6 py-4"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {(data.materials || []).filter(m => m.event_id === event.id).map(m => {
                                            const saldo = m.quantity_est - m.quantity_real;
                                            const totalEst = parseFloat(m.quantity_est || 0) * parseFloat(m.unit_price || 0);
                                            const totalReal = parseFloat(m.quantity_real || 0) * parseFloat(m.unit_price || 0);
                                            const saldoCost = totalEst - totalReal;
                                            return (
                                                <tr key={m.id} className="hover:bg-slate-50 transition-all group">
                                                    <td className="px-6 py-6 font-black uppercase text-slate-700 text-[11px]">{m.name}</td>
                                                    <td className="px-6 py-6 text-slate-500 font-bold uppercase text-[10px]">{m.unit}</td>
                                                    <td className="px-6 py-6 text-right font-medium text-slate-600">{Number(m.quantity_est).toLocaleString()}</td>
                                                    <td className="px-6 py-6 text-right font-medium text-emerald-600">{Number(m.quantity_real).toLocaleString()}</td>
                                                    <td className={`px-6 py-6 text-right font-black ${saldo < 0 ? 'text-rose-600' : 'text-indigo-600'}`}>
                                                        {Number(saldo).toLocaleString()}
                                                    </td>
                                                    <td className="px-6 py-6 text-right font-bold text-slate-700">${Number(totalEst.toFixed(0)).toLocaleString()}</td>
                                                    <td className="px-6 py-6 text-right font-bold text-emerald-600">${Number(totalReal.toFixed(0)).toLocaleString()}</td>
                                                    <td className={`px-6 py-6 text-right font-black ${saldoCost < 0 ? 'text-rose-600' : 'text-indigo-600'}`}>
                                                        ${Number(saldoCost.toFixed(0)).toLocaleString()}
                                                    </td>
                                                    <td className="px-6 py-6 text-right">
                                                        {perms.canManageStructure && (
                                                            <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button onClick={() => { setModalData(m); setActiveModal('EDIT_MATERIAL'); }} className="p-2 text-slate-400 hover:text-indigo-600"><Edit3 size={16} /></button>
                                                                <button onClick={() => { if (confirm('¿Borrar este material?')) handleAction('delete_material', { id: m.id }); }} className="p-2 text-slate-400 hover:text-rose-600"><Trash2 size={16} /></button>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                        {(data.materials || []).filter(m => m.event_id === event.id).length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="py-20 text-center">
                                                    <p className="text-[10px] font-black uppercase text-slate-300 tracking-widest italic">No hay materiales cargados para este proyecto.</p>
                                                    <button onClick={() => setActiveModal('ADD_MATERIAL')} className="mt-4 text-indigo-600 text-[9px] font-black uppercase hover:underline">Comenzar Carga Manual</button>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                tab === 'COMPUTO' && (
                    <div className="bg-white rounded-[4rem] border border-slate-200 overflow-hidden shadow-sm animate-in">
                        <div className="p-12 border-b border-slate-50 flex justify-between items-center">
                            <div className="flex items-center gap-6">
                                <h3 className="font-black uppercase text-[11px] tracking-widest text-slate-400 flex items-center gap-3"><FileText size={20} /> Cómputo de Materiales</h3>
                                {perms.canManageStructure && (
                                    <button onClick={() => setActiveModal('CALCULATE_MATERIALS')} className="bg-indigo-50 text-indigo-600 px-6 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-indigo-100 transition-all flex items-center gap-2">
                                        <Zap size={14} /> Asistente de Cálculo
                                    </button>
                                )}
                                <button onClick={() => {
                                    const mats = (data.materials || []).filter(m => m.event_id === event.id);

                                    // Grouping Logic
                                    const groups = {
                                        'HIERROS Y ACEROS': mats.filter(m => /hierro|malla|clavo|acero|alambre/i.test(m.name)),
                                        'AGREGADOS Y CONCRETO': mats.filter(m => /cemento|arena|piedra|cal|hormig|concreto/i.test(m.name) && !/hierro|malla/i.test(m.name)),
                                        'MAMPOSTERIA Y CERAMICOS': mats.filter(m => /ladrillo|bloque|ceramico/i.test(m.name)),
                                        'OTROS MATERIALES': mats.filter(m => !/hierro|malla|cemento|arena|piedra|cal|hormig|ladrillo|bloque|ceramico/i.test(m.name))
                                    };

                                    let table = ``;
                                    table += `
                                    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
                                    <head>
                                        <meta charset="UTF-8">
                                        <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Computo de Materiales</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
                                    </head>
                                    <body>
                                    <table border="1">
                                        <thead>
                                            <tr style="background-color: #4f46e5; color: white; font-family: sans-serif;">
                                                <th style="padding: 10px;">RUBRO</th>
                                                <th style="padding: 10px;">ELEMENTO / NOTAS</th>
                                                <th style="padding: 10px;">MATERIAL</th>
                                                <th style="padding: 10px;">DETALLE ADICIONAL</th>
                                                <th style="padding: 10px;">UNIDAD</th>
                                                <th style="padding: 10px;">CANT. ESTIMADA</th>
                                                <th style="padding: 10px;">CANT. REAL</th>
                                                <th style="padding: 10px;">UNID. PENDIENTE</th>
                                                <th style="padding: 10px;">PRECIO UNIT.</th>
                                                <th style="padding: 10px;">TOTAL EST ($)</th>
                                                <th style="padding: 10px;">TOTAL REAL ($)</th>
                                                <th style="padding: 10px;">SALDO ($)</th>
                                            </tr>
                                        </thead>
                                        <tbody style="font-family: sans-serif;">`;

                                    for (const [groupName, items] of Object.entries(groups)) {
                                        if (items.length === 0) continue;
                                        table += `<tr style="background-color: #e0e7ff; font-weight: bold;"><td colspan="10">${groupName}</td></tr>`;
                                        items.forEach(m => {
                                            const bars = getBarCount(m.name, m.quantity_est);
                                            const up = parseFloat(m.unit_price || 0);
                                            const qe = parseFloat(m.quantity_est || 0);
                                            const qr = parseFloat(m.quantity_real || 0);
                                            const saldoUnid = qe - qr;
                                            const tEst = qe * up;
                                            const tReal = qr * up;
                                            const tSaldo = tEst - tReal;

                                            table += `<tr>
                                            <td>${groupName}</td>
                                            <td>${m.notes || '-'}</td>
                                            <td>${m.name}</td>
                                            <td>${bars ? `Eq. ${bars} barras de 12m` : '-'}</td>
                                            <td style="text-align: center;">${m.unit}</td>
                                            <td style="text-align: right;">${qe.toLocaleString('es-AR')}</td>
                                            <td style="text-align: right;">${qr.toLocaleString('es-AR')}</td>
                                            <td style="text-align: right; color: ${saldoUnid < 0 ? 'red' : 'green'}">${saldoUnid.toLocaleString('es-AR')}</td>
                                            <td style="text-align: right;">${up.toLocaleString('es-AR')}</td>
                                            <td style="text-align: right;">${tEst.toLocaleString('es-AR')}</td>
                                            <td style="text-align: right;">${tReal.toLocaleString('es-AR')}</td>
                                            <td style="text-align: right; font-weight: bold; color: ${tSaldo < 0 ? 'red' : 'black'}">${tSaldo.toLocaleString('es-AR')}</td>
                                        </tr>`;
                                        });
                                    }

                                    table += `</tbody></table></body></html>`;

                                    const blob = new Blob(['\uFEFF' + table], { type: 'application/vnd.ms-excel;charset=utf-8' });
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `Computo_${event.name}.xls`; // Excel Extension
                                    a.click();
                                }} className="bg-emerald-50 text-emerald-600 px-6 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-emerald-100 transition-all flex items-center gap-2">
                                    <FileText size={14} /> Exportar Excel
                                </button>
                            </div>
                            {perms.canManageStructure && (
                                <button onClick={() => setActiveModal('ADD_MATERIAL')} className="btn-primary py-4 px-10 text-[11px] uppercase font-black">+ Agregar Manual</button>
                            )}
                        </div>

                        {/* Calculated Elements List (Mirroring Structure) */}
                        <div className="p-12 border-b border-slate-50 bg-slate-50/50">
                            <h4 className="font-black uppercase text-[10px] text-slate-400 mb-6 tracking-widest">Elementos Calculados</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {(() => {
                                    // Filter elements explicitly created for Computo (not pure structural, or maybe all?)
                                    // Let's show all for now, or filter by type if we implement types strictly.
                                    // For now, show those with type 'ALBAÑILERIA' or custom ones we added.
                                    const elems = (data.structures || []).filter(s => s.event_id === event.id && (s.type === 'ALBAÑILERIA' || s.details?.includes('Materiales:')));

                                    if (elems.length === 0) return <p className="text-xs text-slate-300 font-bold col-span-full">No hay elementos de cómputo registrados.</p>;

                                    return elems.map(s => (
                                        <div key={s.id} onClick={() => { setModalData(s); setActiveModal('EDIT_STRUCTURE'); }} className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all relative group cursor-pointer">
                                            <div className="flex justify-between items-start mb-2">
                                                <h5 className="font-black text-indigo-900 uppercase text-xs">{s.name}</h5>
                                                {perms.canManageStructure && (
                                                    <button onClick={async (e) => {
                                                        e.stopPropagation();
                                                        const id = s.id;
                                                        const name = s.name;
                                                        if (confirm(`¿Eliminar elemento "${name}"? Esto borrará los materiales asociados.`)) {
                                                            // Cascade delete materials that reference this structure in notes
                                                            const linkedMats = (data.materials || []).filter(m => m.event_id === event.id && m.notes === name);
                                                            let deletedCount = 0;
                                                            async function deleteLinked() {
                                                                for (const mat of linkedMats) {
                                                                    await handleAction('delete_material', { id: mat.id });
                                                                    deletedCount++;
                                                                }
                                                                await handleAction('delete_structure', { id: id });
                                                                if (deletedCount > 0) alert(`Se eliminó la estructura y ${deletedCount} materiales asociados.`);
                                                            }
                                                            deleteLinked();
                                                        }
                                                    }} className="text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                                                )}
                                            </div>
                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">{s.type}</p>
                                            <p className="text-[10px] text-slate-500 whitespace-pre-line leading-relaxed">{s.details}</p>
                                        </div>
                                    ));
                                })()}
                            </div>
                        </div>

                        <table className="w-full text-left">
                            <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">
                                <tr>
                                    <th className="px-12 py-8">Material</th>
                                    <th className="px-12 py-8 text-center">Unidad</th>
                                    <th className="px-12 py-8 text-right">Cant. Estimada</th>
                                    <th className="px-12 py-8 text-right">Cant. Real</th>
                                    <th className="px-12 py-8 text-right">Saldo</th>
                                    <th className="px-12 py-8 text-right">Total Est. ($)</th>
                                    <th className="px-12 py-8 text-right">Total Cons. ($)</th>
                                    <th className="px-12 py-8 text-right">Saldo ($)</th>
                                    <th className="px-8"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {(() => {
                                    const mats = (data.materials || []).filter(m => m.event_id === event.id);
                                    const grouped = {};

                                    // Group by base unique name
                                    mats.forEach(m => {
                                        // Remove parenthesis context for grouping "Arena (Muro)" -> "Arena"
                                        const base = m.name.split('(')[0].trim();
                                        if (!grouped[base]) grouped[base] = { id: base, name: base, unit: m.unit, total_est: 0, total_real: 0, cost_est: 0, cost_real: 0, children: [] };
                                        grouped[base].total_est += parseFloat(m.quantity_est || 0);
                                        grouped[base].total_real += parseFloat(m.quantity_real || 0);
                                        grouped[base].cost_est += parseFloat(m.quantity_est || 0) * parseFloat(m.unit_price || 0);
                                        grouped[base].cost_real += parseFloat(m.quantity_real || 0) * parseFloat(m.unit_price || 0);
                                        grouped[base].children.push(m);
                                    });

                                    return Object.values(grouped).map(g => {
                                        const saldoCant = g.total_est - g.total_real;
                                        const saldoCost = g.cost_est - g.cost_real;
                                        const isExpanded = modalData?.expandedGroup === g.id;
                                        const barCount = getBarCount(g.name, g.total_est);

                                        return (
                                            <React.Fragment key={g.id}>
                                                <tr className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setModalData(prev => ({ ...prev, expandedGroup: isExpanded ? null : g.id }))}>
                                                    <td className="px-12 py-8 font-black uppercase text-slate-700 text-sm flex items-center gap-2">
                                                        <div className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}><ChevronRight size={16} className="text-indigo-400" /></div>
                                                        <div>
                                                            {g.name}
                                                            {barCount && <span className="text-[10px] text-indigo-500 font-bold block normal-case">Eq. {barCount} barras de 12m</span>}
                                                        </div>
                                                        {g.children.length > 1 && <span className="bg-slate-100 text-slate-500 text-[9px] px-2 py-0.5 rounded-full">{g.children.length} ítems</span>}
                                                    </td>
                                                    <td className="px-12 py-8 text-center text-xs font-bold text-slate-500 uppercase">{g.unit}</td>
                                                    <td className="px-12 py-8 text-right font-bold text-slate-900">{g.total_est.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                                    <td className="px-12 py-8 text-right font-bold text-slate-900">{g.total_real.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                                    <td className={`px-12 py-8 text-right font-bold ${saldoCant < 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                                                        {saldoCant.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                                                    </td>
                                                    <td className="px-12 py-8 text-right font-bold text-slate-900">{fCur(g.cost_est)}</td>
                                                    <td className="px-12 py-8 text-right font-bold text-slate-900">{fCur(g.cost_real)}</td>
                                                    <td className={`px-12 py-8 text-right font-bold ${saldoCost < 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                                                        {fCur(saldoCost)}
                                                    </td>
                                                    <td className="px-8 text-right"></td>
                                                </tr>
                                                {isExpanded && g.children.map(m => {
                                                    const dChild = Number(m.quantity_real) - Number(m.quantity_est);
                                                    return (
                                                        <tr key={m.id} className="bg-slate-50/50 hover:bg-indigo-50/10 text-xs">
                                                            <td className="px-12 py-4 pl-20 text-slate-500 font-bold uppercase truncate max-w-xs block">
                                                                {m.name}
                                                                {m.notes && <span className="text-[9px] text-slate-400 block font-normal normal-case">{m.notes}</span>}
                                                            </td>
                                                            <td className="px-12 py-4 text-center text-[9px] text-slate-400">{m.unit}</td>
                                                            <td className="px-12 py-4 text-right text-slate-500">{Number(m.quantity_est).toLocaleString()}</td>
                                                            <td className="px-12 py-4 text-right text-slate-500">{Number(m.quantity_real).toLocaleString()}</td>
                                                            <td className="px-12 py-4 text-right text-slate-400">{dChild > 0 ? '+' : ''}{dChild.toLocaleString()}</td>
                                                            <td className="px-8 text-right">
                                                                {perms.canManageStructure && (
                                                                    <button onClick={(e) => { e.stopPropagation(); setModalData(m); setActiveModal('EDIT_MATERIAL'); }} className="p-2 text-indigo-300 hover:text-indigo-600"><Edit3 size={14} /></button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    });
                                })()}
                            </tbody>
                        </table>
                    </div>
                )
            }

            {
                tab === 'ESTRUCTURA' && (
                    <div className="space-y-10 animate-in">
                        <div className="flex justify-between items-center px-4">
                            <h3 className="font-black uppercase text-[11px] tracking-widest text-slate-400 flex items-center gap-3"><AlertOctagon size={20} /> Cálculo Estructural</h3>
                            {perms.canManageStructure && (
                                <div className="flex gap-4">
                                    <button onClick={() => setActiveModal('ADD_STRUCTURE_CALC')} className="bg-emerald-50 text-emerald-600 px-6 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-emerald-100 transition-all flex items-center gap-2"><Zap size={14} /> Nueva Calculadora</button>
                                </div>
                            )}
                        </div>
                        {(() => {
                            const structures = (data.structures || []).filter(s => s.event_id === event.id && s.type !== 'ALBAÑILERIA');
                            if (structures.length === 0) return <div className="text-center p-20 text-slate-300 font-bold uppercase text-xs tracking-widest border-2 border-dashed border-slate-100 rounded-[3rem]">No hay elementos estructurales calculados</div>;
                            return (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {structures.map(s => (
                                        <div key={s.id} onClick={() => { setModalData(s); setActiveModal('EDIT_STRUCTURE'); }} className="bg-white p-10 rounded-[2.5rem] border border-slate-200 hover:border-indigo-400 cursor-pointer shadow-sm transition-all group relative overflow-hidden">
                                            <div className={`absolute top-0 right-0 w-2 h-full transition-all ${s.status === 'TERMINADO' ? 'bg-emerald-500' : s.status === 'EN EJECUCION' ? 'bg-indigo-500' : 'bg-slate-300'}`}></div>
                                            <div className="flex justify-between items-start mb-2">
                                                <h4 className="text-xl font-black uppercase text-slate-800">{s.name}</h4>
                                                {s.quantity > 1 && <span className="bg-indigo-600 text-white px-2 py-1 rounded text-[10px] font-black">x{s.quantity}</span>}
                                            </div>
                                            <p className="text-[10px] font-bold uppercase text-slate-400 tracking-widest mb-4">{s.type}</p>
                                            <div className="flex justify-between items-end">
                                                <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${s.status === 'TERMINADO' ? 'bg-emerald-100 text-emerald-600' :
                                                    s.status === 'EN EJECUCION' ? 'bg-indigo-100 text-indigo-600' :
                                                        'bg-slate-100 text-slate-500'
                                                    }`}>{s.status}</span>
                                                {s.details && <span className="text-[9px] font-black text-slate-300 uppercase">{s.details.match(/Nivel: ([^,\n]+)/)?.[0]}</span>}
                                            </div>
                                            {s.details && <p className="mt-6 text-[10px] text-slate-500 leading-tight border-t border-slate-50 pt-4 whitespace-pre-line">{s.details}</p>}
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}

                        {/* Material Summary Section */}
                        <div className="bg-slate-900 rounded-[3rem] p-12 mt-12 animate-in slide-in-from-bottom-10">
                            <h4 className="text-white font-black uppercase text-[12px] tracking-widest flex items-center gap-3 mb-8"><Zap size={20} className="text-amber-400" /> Resumen de Insumos Estructurales</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                                {(() => {
                                    // Aggregate structure-related materials
                                    const structMaterials = (data.materials || []).filter(m => m.event_id === event.id &&
                                        (m.name.toLowerCase().includes('hormi') || m.name.toLowerCase().includes('hierro') || m.name.toLowerCase().includes('malla')));
                                    console.log("Found structural materials:", structMaterials);
                                    const grouped = {};
                                    structMaterials.forEach(m => {
                                        const baseName = m.name.split('(')[0].trim();
                                        if (!grouped[baseName]) grouped[baseName] = { id: baseName, name: baseName, unit: m.unit, qty: 0, items: [] };
                                        grouped[baseName].qty += parseFloat(m.quantity_est || 0);
                                        grouped[baseName].items.push(m);
                                    });

                                    return Object.values(grouped).map(g => (
                                        <div key={g.id} onClick={() => setSummaryDetail(g)} className="bg-white/5 border border-white/10 p-6 rounded-2xl cursor-pointer hover:bg-white/10 transition-all group">
                                            <p className="text-[10px] font-black uppercase text-slate-400 mb-2 group-hover:text-white transition-colors">{g.name}</p>
                                            <p className="text-2xl font-black text-amber-400 tracking-tighter">{g.qty.toLocaleString(undefined, { maximumFractionDigits: 1 })} <span className="text-[10px] text-white/50 align-top">{g.unit}</span></p>
                                            <div className="mt-4 pt-4 border-t border-white/5 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <span className="text-[9px] font-bold text-indigo-400 uppercase">Ver {g.items.length} detalles</span>
                                                <ArrowRight size={12} className="text-indigo-400" />
                                            </div>
                                        </div>
                                    ));
                                })()}
                                {(!data.materials || data.materials.filter(m => m.event_id === event.id).length === 0) && <p className="text-white/30 font-bold text-xs">No hay materiales calculados aún.</p>}
                            </div>
                        </div>

                        {summaryDetail && (
                            <Modal title={`Detalle Insumo: ${summaryDetail.name}`} onClose={() => setSummaryDetail(null)}>
                                <div className="bg-slate-50 p-6 rounded-2xl">
                                    <div className="flex justify-between items-end mb-6 pb-6 border-b border-slate-200">
                                        <div>
                                            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Total Acumulado</p>
                                            <p className="text-4xl font-black text-indigo-900 tracking-tighter">{summaryDetail.qty.toLocaleString()} <span className="text-base text-slate-400">{summaryDetail.unit}</span></p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Items</p>
                                            <p className="text-xl font-black text-slate-700">{summaryDetail.items.length}</p>
                                        </div>
                                    </div>
                                    <div className="max-h-[300px] overflow-y-auto space-y-3 pr-2">
                                        {summaryDetail.items.map(m => (
                                            <div key={m.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex justify-between items-center">
                                                <div>
                                                    <p className="text-xs font-black text-slate-700 uppercase mb-1">{m.notes?.includes('Calculado') ? m.notes.split('\n')[0] : m.notes || 'Manual'}</p>
                                                    <p className="text-[9px] font-bold text-slate-400 uppercase">{m.name}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-lg font-black text-indigo-600">{Number(m.quantity_est).toLocaleString()}</p>
                                                    <p className="text-[9px] font-bold text-slate-300 uppercase">{m.unit}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Modal>
                        )}
                    </div>
                )
            }
            {
                tab === 'RECURSOS' && (
                    <div className="space-y-10 animate-in">
                        <div className="flex justify-between items-center px-8">
                            <div>
                                <h3 className="text-3xl font-black uppercase tracking-tighter">Recursos del Proyecto</h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Personal asignado y costos proyectados</p>
                            </div>
                            {canEdit && (
                                <button
                                    onClick={() => { setModalData({ event_id: event.id }); setActiveModal('ASSIGN_RESOURCE'); }}
                                    className="bg-slate-900 text-white px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-3 shadow-lg"
                                >
                                    <Plus size={16} /> Asignar Recurso
                                </button>
                            )}
                        </div>

                        <div className="px-8">
                            <ResourceAssignmentsTable
                                assignments={data.resource_assignments.filter(a => a.event_id === event.id)}
                                resources={data.resources}
                                projects={data.projects}
                                onEdit={(a) => { setModalData(a); setActiveModal('ASSIGN_RESOURCE'); }}
                                onDelete={(id) => handleAction('delete_resource_assignment', { id })}
                                onSaveLog={(log) => handleAction('save_resource_hours_log', log)}
                                onDeleteLog={(id) => handleAction('delete_resource_hours_log', { id })}
                                showProjectColumn={false}
                                canEdit={canEdit}
                                allAssignments={data.resource_assignments}
                                resourceHoursLog={data.resource_hours_log}
                                globalFxRate={globalFxRate}
                            />
                        </div>

                    </div>
                )
            }

            {/* ========== PURCHASING / OC MODAL ========== */}
            {activeModal === 'PURCHASE_ORDER' && (
                <PurchaseOrderModal
                    onClose={() => setActiveModal(null)}
                    onSave={(oc) => handleAction('save_purchase_order', oc)}
                    onDelete={(id) => handleAction('delete_purchase_order', { id })}
                    project={event}
                    data={modalData}
                    purchaseOrders={data.purchase_orders || []}
                    fCur={fCur}
                />
            )}

            {/* ========== GANTT PREFERENCES MODAL ========== */}
            <GanttSettingsModal
                isOpen={isGanttPrefsOpen}
                onClose={() => setIsGanttPrefsOpen(false)}
                prefs={ganttPrefs}
                setPrefs={setGanttPrefs}
                eventId={event.id}
                handleAction={handleAction}
            />
        </div>
    );
}

// ===================== PROJECT ANALYTICS DASHBOARD =====================
function ProjectAnalyticsDashboard({ projects, tasks, t, onSelectProject, fCur, globalFxRate }) {
    const [popup, setPopup] = React.useState(null); // { title, projects: [] }
    const [groupView, setGroupView] = React.useState('ALL'); // 'ALL' or 'CLIENT'
    const [expandedClients, setExpandedClients] = React.useState(new Set());
    const COLORS = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#3b82f6', '#84cc16', '#f97316', '#14b8a6', '#a78bfa'];

    const statusLabel = (p) => {
        const lc = (p.life_cycle_status || '').toUpperCase();
        if (lc === 'FINALIZADO' || lc === 'COMPLETED') return '🟣 Finalizado';
        if (lc === 'TAREAS_COMPLETADAS') return '🔵 Tareas OK';
        if (lc === 'EN_CURSO') return '🟢 En Curso';
        if (lc === 'RETRASO_LEVE') return '🟡 Retraso Leve';
        if (lc === 'RETRASO_GRAVE') return '🔴 Retraso Grave';

        // Dynamic Fallback based on progress
        const prog = p.avgProg ?? 0;
        if (prog >= 100) return '🟣 Finalizado';
        if (prog > 0) return '🟢 En Curso';

        return '⚪ No Iniciado';
    };

    const ProjectPopup = ({ data, onClose }) => data ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-8" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={onClose}>
            <div className="bg-white rounded-[2rem] shadow-2xl p-8 max-w-xl w-full max-h-[80vh] overflow-y-auto animate-in fade-in zoom-in-95" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <h4 className="text-base font-black uppercase tracking-tight">{data.title}</h4>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 transition-all"><X size={16} /></button>
                </div>
                <div className="space-y-3">
                    {data.projects.map(p => (
                        <div key={p.id} onClick={() => { onClose(); onSelectProject && onSelectProject(p.id); }}
                            className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 hover:bg-indigo-50 cursor-pointer transition-all group">
                            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-slate-100 text-indigo-600 shrink-0">
                                <Briefcase size={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-black text-sm truncate">{p.name}</p>
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{statusLabel(p)} • {p.avgProg ?? 0}% avance</p>
                            </div>
                            <ArrowRight size={16} className="text-slate-300 group-hover:text-indigo-600 transition-all shrink-0" />
                        </div>
                    ))}
                    {data.projects.length === 0 && <p className="text-center text-slate-400 text-sm py-4">{t('sin_datos') || 'Sin datos'}</p>}
                </div>
            </div>
        </div>
    ) : null;

    // Enrich projects with avgProg and prog_teorico using standard calculation
    const enriched = projects.map(p => {
        const projTasks = tasks.filter(t => t.event_id === p.id);
        const { physicalProgress, theoreticalProgress } = calculateProgressMetrics(projTasks, p.event_date, p.end_date);
        return { ...p, avgProg: physicalProgress, prog_teorico: theoreticalProgress };
    });

    // Chart 1: By Year
    const byYear = {};
    enriched.forEach(p => {
        const yr = p.start_date ? new Date(p.start_date).getFullYear() : (p.event_date ? new Date(p.event_date).getFullYear() : null);
        if (yr) {
            if (!byYear[yr]) byYear[yr] = { total: [], statuses: {} };
            byYear[yr].total.push(p);
            const s = statusLabel(p);
            byYear[yr].statuses[s] = (byYear[yr].statuses[s] || []);
            byYear[yr].statuses[s].push(p);
        }
    });
    const yearKeys = Object.keys(byYear).sort();
    const yearMax = Math.max(1, ...Object.values(byYear).map(v => v.total.length));

    // Chart 2: By Zone/Country
    const byZone = {};
    enriched.forEach(p => {
        const z = p.country || p.region || p.location || 'Sin zona';
        byZone[z] = (byZone[z] || []); byZone[z].push(p);
    });
    const zoneKeys = Object.keys(byZone);
    const zoneTotal = enriched.length || 1;

    // Chart 3: By Status
    const statusMap = { '⚪ No Iniciado': [], '🟢 En Curso': [], '🟡 Retraso Leve': [], '🔴 Retraso Grave': [], '🔵 Tareas OK': [], '🟣 Finalizado': [] };
    enriched.forEach(p => {
        const s = statusLabel(p);
        if (statusMap[s]) statusMap[s].push(p);
        else if (s.includes('Finalizado')) statusMap['🟣 Finalizado'].push(p);
        else if (s.includes('En Curso')) statusMap['🟢 En Curso'].push(p);
        else if (s.includes('No Iniciado')) statusMap['⚪ No Iniciado'].push(p);
    });
    const statusKeys = Object.keys(statusMap).filter(k => statusMap[k].length > 0);
    const statusTotal = enriched.length || 1;

    // Chart 4: By Progress Range
    const rangeMap = { '0-25%': [], '25-50%': [], '50-75%': [], '75-100%': [] };
    enriched.forEach(p => {
        const pg = p.avgProg;
        if (pg < 25) rangeMap['0-25%'].push(p);
        else if (pg < 50) rangeMap['25-50%'].push(p);
        else if (pg < 75) rangeMap['50-75%'].push(p);
        else rangeMap['75-100%'].push(p);
    });
    const rangeMax = Math.max(1, ...Object.values(rangeMap).map(v => v.length));

    // Chart 5: Budget Top-10 (Normalized to USD for visual comparison)
    const normalizedEnriched = enriched.map(p => {
        const amount = Number(p.budget_total_contract || 0);
        let rate = parseFloat(p.exchange_rate || 0);
        if (rate <= 1) rate = parseFloat(globalFxRate || 1000); 

        const currency = (p.base_currency || p.display_currency || 'ARS').toUpperCase();
        const isUSD = currency === 'USD';
        
        // If it's USD, the value is already normalized. 
        // If it's ARS (or other), convert to USD using the rate.
        const budgetUSD = isUSD ? amount : amount / rate;
        return { ...p, _budgetUSD: budgetUSD };
    });

    const budgetProjects = [...normalizedEnriched]
        .filter(p => p._budgetUSD > 0)
        .sort((a, b) => b._budgetUSD - a._budgetUSD)
        .slice(0, 10);

    const budgetMax = Math.max(1, ...budgetProjects.map(p => p._budgetUSD));

    // Chart 6: Physical vs Theoretical
    const vsProjects = enriched.filter(p => {
        const pt = tasks.filter(t2 => t2.event_id === p.id);
        return pt.length > 0;
    }).sort((a, b) => {
        if (groupView === 'ALL') {
            // Chronological: Oldest to Newest
            const dateA = new Date(a.start_date || a.event_date || '9999-12-31');
            const dateB = new Date(b.start_date || b.event_date || '9999-12-31');
            return dateA - dateB;
        }
        // For CLIENT view, keep deviation sorting or alphabetical? 
        // User didn't specify project sort inside client, sticking to deviation or just alphabetical?
        // Let's stick to deviation inside client for relevance.
        const devA = (a.prog_teorico || 0) - (a.avgProg || 0);
        const devB = (b.prog_teorico || 0) - (b.avgProg || 0);
        return devB - devA;
    });

    // Grouping by client helper
    const projectsByClient = {};
    if (groupView === 'CLIENT') {
        vsProjects.forEach(p => {
            const client = p.client_name || p.client || 'Sin Cliente';
            if (!projectsByClient[client]) projectsByClient[client] = [];
            projectsByClient[client].push(p);
        });
    }
    const sortedClients = Object.keys(projectsByClient).sort((a, b) => a.localeCompare(b));

    const toggleClient = (client) => {
        const next = new Set(expandedClients);
        if (next.has(client)) next.delete(client);
        else next.add(client);
        setExpandedClients(next);
    };

    const ProjectItem = ({ p, setPopup }) => {
        const physical = p.avgProg || 0;
        const projected = p.prog_teorico || 0;
        return (
            <div className="cursor-pointer group" onClick={() => setPopup({ title: p.name, projects: [p] })}>
                <div className="flex justify-between items-center mb-1.5">
                    <span className="text-[9px] font-black text-slate-600 truncate max-w-[150px] uppercase tracking-tight">{p.name}</span>
                    <span className="text-[9px] font-black tracking-tighter">
                        <span className="text-emerald-500">{physical}% F</span> <span className="text-slate-300 mx-1">/</span> <span className="text-rose-500">{projected}% T</span>
                    </span>
                </div>
                <div className="relative pt-1 space-y-1">
                    <div className="h-4 bg-slate-100 rounded-full overflow-hidden relative shadow-inner">
                        <div className="h-full rounded-full bg-emerald-500 transition-all duration-500 shadow-sm" style={{ width: `${physical}%` }}></div>
                    </div>
                    <div className="h-2 bg-slate-50 rounded-full overflow-hidden relative shadow-inner">
                        <div className="h-full rounded-full bg-rose-500 transition-all duration-500 opacity-80" style={{ width: `${projected}%` }}></div>
                    </div>
                </div>
            </div>
        );
    };

    const PieChart = ({ data, keys, total, colors, label }) => {
        const r = 80, cx = 100, cy = 100;
        let startAngle = -Math.PI / 2;
        const segments = keys.map((k, i) => {
            const count = data[k].length;
            const angle = (count / total) * 2 * Math.PI;
            const endAngle = startAngle + angle;
            const x1 = cx + r * Math.cos(startAngle), y1 = cy + r * Math.sin(startAngle);
            const x2 = cx + r * Math.cos(endAngle), y2 = cy + r * Math.sin(endAngle);
            const large = angle > Math.PI ? 1 : 0;
            const path = count === total
                ? `M ${cx} ${cy} m -${r} 0 a ${r} ${r} 0 1 1 ${2 * r} 0 a ${r} ${r} 0 1 1 -${2 * r} 0`
                : `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
            const midAngle = startAngle + angle / 2;
            startAngle = endAngle;
            return { k, count, path, color: colors[i % colors.length], midAngle };
        });
        return (
            <div className="flex flex-col items-center gap-4">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">{label}</p>
                <svg viewBox="0 0 200 200" className="w-48 h-48">
                    {segments.map((s, i) => (
                        <path key={i} d={s.path} fill={s.color} stroke="white" strokeWidth="2"
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setPopup({ title: `${label}: ${s.k}`, projects: data[s.k] })} />
                    ))}
                </svg>
                <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center max-w-xs">
                    {segments.map((s, i) => (
                        <div key={i} className="flex items-center gap-1 cursor-pointer hover:opacity-75" onClick={() => setPopup({ title: `${label}: ${s.k}`, projects: data[s.k] })}>
                            <span className="w-3 h-3 rounded-full shrink-0" style={{ background: s.color }}></span>
                            <span className="text-[9px] font-bold text-slate-500 uppercase">{s.k.replace(/[🟣🟢🟡🔴🔵⚪]/u, '').trim()} ({s.count})</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const BarChart = ({ data, keys, max, colors, label, getProjects, horizontal }) => {
        const statusColors = {
            '⚪ No Iniciado': '#94a3b8',
            '🟢 En Curso': '#10b981',
            '🟡 Retraso Leve': '#f59e0b',
            '🔴 Retraso Grave': '#ef4444',
            '🔵 Tareas OK': '#3b82f6',
            '🟣 Finalizado': '#8b5cf6'
        };

        return (
            <div className="flex flex-col gap-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</p>
                {horizontal ? (
                    <div className="space-y-4">
                        {keys.map((k, i) => {
                            const val = data[k];
                            // If val is an object with 'statuses', it's the yearly stacked view
                            const isStacked = val && typeof val === 'object' && val.statuses;
                            const count = isStacked ? val.total.length : (Array.isArray(val) ? val.length : val);
                            const displayVal = isStacked ? val.total.length : (Array.isArray(val) ? val.length : (fNum ? fNum(val) : val));

                            return (
                                <div key={k} className="flex items-center gap-3 cursor-pointer group" onClick={() => setPopup({ title: `${label}: ${k}`, projects: getProjects ? getProjects(k) : (Array.isArray(val) ? val : []) })}>
                                    <span className="text-[9px] font-bold text-slate-500 w-24 truncate text-right shrink-0">{k}</span>
                                    <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden flex shadow-inner">
                                        {isStacked ? (
                                            Object.entries(val.statuses).map(([status, pList], idx) => (
                                                <div
                                                    key={status}
                                                    className="h-full first:rounded-l-full last:rounded-r-full hover:brightness-110 transition-all border-r border-white/10 last:border-0"
                                                    style={{
                                                        width: `${(pList.length / max) * 100}%`,
                                                        background: statusColors[status] || colors[idx % colors.length]
                                                    }}
                                                    title={`${status}: ${pList.length}`}
                                                ></div>
                                            ))
                                        ) : (
                                            <div className="h-full rounded-full group-hover:opacity-80 transition-all" style={{ width: `${(count / max) * 100}%`, background: colors[i % colors.length] }}></div>
                                        )}
                                    </div>
                                    <span className="text-[9px] font-black text-slate-600 w-10 text-left shrink-0">{displayVal}</span>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex items-stretch gap-3 h-64 pt-10 px-4 w-full border-b border-slate-50">
                        {keys.map((k, i) => {
                            const val = data[k];
                            const count = Array.isArray(val) ? val.length : val;
                            const pct = Math.min(100, (count / (max || 1)) * 100);
                            const color = colors[i % colors.length];
                            return (
                                <div key={k} className="flex flex-col justify-end items-center gap-2 flex-1 cursor-pointer group min-w-0 h-full" onClick={() => setPopup({ title: `${label}: ${k}`, projects: getProjects ? getProjects(k) : (Array.isArray(val) ? val : []) })}>
                                    <span className="text-[11px] font-black transition-transform group-hover:scale-110 mb-1" style={{ color }}>{count}</span>
                                    <div className="w-full max-w-[50px] rounded-t-3xl group-hover:brightness-110 transition-all shadow-xl border-x border-t border-white/20 relative"
                                        style={{
                                            height: `${Math.max(12, pct)}%`,
                                            background: `linear-gradient(180deg, ${color} 0%, ${color}dd 100%)`,
                                            minWidth: '28px'
                                        }}>
                                        {count === 0 && <div className="absolute inset-0 bg-slate-50/20 rounded-t-3xl border border-dashed border-slate-200" />}
                                    </div>
                                    <span className="text-[9px] font-black text-slate-500 truncate w-full text-center mt-2 group-hover:text-slate-800 transition-colors uppercase tracking-widest">{k}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
            `}</style>
            <ProjectPopup data={popup} onClose={() => setPopup(null)} />

            {/* Row 1: Pie charts */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm flex justify-center">
                    {zoneKeys.length > 0
                        ? <PieChart data={byZone} keys={zoneKeys} total={zoneTotal} colors={COLORS} label={t('proyectos_por_zona') || 'Por Zona'} />
                        : <p className="text-slate-300 text-xs font-bold uppercase self-center">{t('sin_datos')}</p>}
                </div>
                <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm flex justify-center">
                    {statusKeys.length > 0
                        ? <PieChart data={statusMap} keys={statusKeys} total={statusTotal} colors={['#94a3b8', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6']} label={t('proyectos_por_estatus') || 'Por Estatus'} />
                        : <p className="text-slate-300 text-xs font-bold uppercase self-center">{t('sin_datos')}</p>}
                </div>
                <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-4">{t('click_para_ver') || 'Clic en un segmento para ver proyectos'}</p>
                    {yearKeys.length > 0
                        ? <BarChart
                            data={byYear}
                            keys={yearKeys} max={yearMax} colors={COLORS}
                            label={t('proyectos_por_anio') || 'Por Año'}
                            getProjects={k => byYear[k].total || []}
                            horizontal={true}
                        />
                        : <p className="text-slate-300 text-xs font-bold uppercase">{t('sin_datos')}</p>}
                </div>
            </div>

            {/* Row 2: Bar charts */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
                    {Object.values(rangeMap).some(v => v.length > 0)
                        ? <BarChart
                            data={Object.fromEntries(Object.keys(rangeMap).map(k => [k, rangeMap[k]]))}
                            keys={Object.keys(rangeMap)} max={rangeMax} colors={['#94a3b8', '#f59e0b', '#3b82f6', '#10b981']}
                            label={t('proyectos_por_avance') || 'Por Avance'}
                            getProjects={k => rangeMap[k]}
                        />
                        : <p className="text-slate-300 text-xs font-bold uppercase">{t('sin_datos')}</p>}
                </div>
                <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-4">{t('presupuesto_por_proyecto') || 'Presupuesto (Top 10)'}</p>
                    {budgetProjects.length > 0 ? (
                        <div className="space-y-2">
                            {budgetProjects.map((p, i) => (
                                <div key={p.id} className="flex items-center gap-3 cursor-pointer group" onClick={() => setPopup({ title: p.name, projects: [p] })}>
                                    <span className="text-[9px] font-bold text-slate-500 w-28 truncate text-right shrink-0">{p.name}</span>
                                    <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                                        <div className="h-full rounded-full group-hover:opacity-80 transition-all" style={{ width: `${(p._budgetUSD / budgetMax) * 100}%`, background: COLORS[i % COLORS.length] }}></div>
                                    </div>
                                    {/* La barra está normalizada a USD: la etiqueta muestra el mismo valor; el monto original va en el tooltip */}
                                    {(() => {
                                        const origCur = (p.base_currency || p.display_currency || 'ARS').toUpperCase();
                                        const usdLabel = 'USD ' + Math.round(p._budgetUSD).toLocaleString('es-AR');
                                        const origLabel = fCur ? fCur(Number(p.budget_total_contract), origCur) : Number(p.budget_total_contract).toLocaleString('es-AR');
                                        return (
                                            <span className="text-[9px] font-black text-slate-600 w-24 text-left shrink-0 truncate" title={origCur === 'USD' ? origLabel : `${origLabel} (convertido a USD)`}>
                                                {usdLabel}{origCur !== 'USD' && <span className="text-slate-400 font-bold"> *</span>}
                                            </span>
                                        );
                                    })()}
                                </div>
                            ))}
                            {budgetProjects.some(p => (p.base_currency || p.display_currency || 'ARS').toUpperCase() !== 'USD') && (
                                <p className="text-[9px] text-slate-400 pt-2">* Monto en pesos convertido a USD para comparar. Pasá el mouse para ver el original.</p>
                            )}
                        </div>
                    ) : <p className="text-slate-300 text-xs font-bold uppercase">{t('sin_datos')}</p>}
                </div>
                <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col h-full overflow-hidden">
                    <div className="flex justify-between items-center mb-6">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{t('avance_fisico_vs_teorico') || 'Avance Físico vs Teórico'}</p>
                        <div className="flex bg-slate-100 p-1 rounded-xl">
                            <button
                                onClick={() => setGroupView('ALL')}
                                className={`px-3 py-1 rounded-lg text-[8px] font-black uppercase transition-all ${groupView === 'ALL' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400'}`}
                            >
                                Todos
                            </button>
                            <button
                                onClick={() => setGroupView('CLIENT')}
                                className={`px-3 py-1 rounded-lg text-[8px] font-black uppercase transition-all ${groupView === 'CLIENT' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400'}`}
                            >
                                Por Cliente
                            </button>
                        </div>
                    </div>
                    {vsProjects.length > 0 ? (
                        <div className="space-y-4 overflow-y-auto pr-2 max-h-[500px] custom-scrollbar flex-1">
                            {groupView === 'CLIENT' ? (
                                sortedClients.map((client) => {
                                    const cProj = projectsByClient[client];
                                    const isExpanded = expandedClients.has(client);
                                    return (
                                        <div key={client} className="space-y-3">
                                            <div
                                                onClick={() => toggleClient(client)}
                                                className="bg-slate-50 px-4 py-3 rounded-xl sticky top-0 z-10 border border-slate-100 shadow-sm flex justify-between items-center cursor-pointer group hover:bg-slate-100 transition-all"
                                            >
                                                <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{client} ({cProj.length})</p>
                                                <ChevronDown size={14} className={`text-slate-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                                            </div>
                                            {isExpanded && (
                                                <div className="space-y-4 pl-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                                    {cProj.map(p => (
                                                        <ProjectItem key={p.id} p={p} setPopup={setPopup} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                vsProjects.map((p) => (
                                    <ProjectItem key={p.id} p={p} setPopup={setPopup} />
                                ))
                            )}
                        </div>
                    ) : (
                        <div className="flex-1 flex items-center justify-center">
                            <p className="text-slate-300 text-xs font-bold uppercase">{t('sin_datos')}</p>
                        </div>
                    )}
                    {vsProjects.length > 0 && (
                        <div className="flex items-center gap-4 mt-6 pt-6 border-t border-slate-50">
                            <div className="flex items-center gap-1.5">
                                <div className="w-3 h-2.5 bg-emerald-500 rounded-sm"></div>
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Físico</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-3 h-1 bg-rose-500 rounded-full"></div>
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Teórico</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function MetricCircle({ label, value, color }) {
    const theme = { indigo: 'text-indigo-400', emerald: 'text-emerald-400', rose: 'text-rose-400', slate: 'text-slate-400' };
    return (
        <div className="text-center bg-white/5 p-8 rounded-[2rem] border border-white/10 min-w-[150px] shadow-inner backdrop-blur-sm">
            <p className="text-[9px] font-black uppercase text-slate-500 mb-2 tracking-[0.3em]">{label}</p>
            <p className={`text-5xl font-black ${theme[color] || 'text-white'} tracking-tighter`}>{value}%</p>
        </div>
    );
}

function SummaryBox({ label, value, color }) {
    const theme = { slate: 'bg-slate-50 text-slate-900', emerald: 'bg-emerald-50 text-emerald-600' };
    return (
        <div className={`${theme[color] || 'bg-slate-50'} p-10 rounded-[2.5rem] border border-slate-100 shadow-inner`}>
            <p className="text-[10px] font-black uppercase text-slate-400 mb-2">{label}</p>
            <p className="text-3xl font-black tracking-tighter">{value}</p>
        </div>
    );
}

const ARG_REGIONS_MAP = {
    'NOA': ['JUJUY', 'SALTA', 'TUCUMAN', 'CATAMARCA', 'SANTIAGO DEL ESTERO'],
    'NEA': ['FORMOSA', 'CHACO', 'CORRIENTES', 'MISIONES'],
    'CUYO': ['SAN JUAN', 'SAN LUIS', 'MENDOZA', 'LA RIOJA'],
    'PAMPEANA': ['CORDOBA', 'SANTA FE', 'ENTRE RIOS', 'BUENOS AIRES', 'LA PAMPA', 'CABA', 'CIUDAD AUTONOMA DE BUENOS AIRES', 'CAPITAL FEDERAL'],
    'PATAGONIA': ['NEUQUEN', 'RIO NEGRO', 'CHUBUT', 'SANTA CRUZ', 'TIERRA DEL FUEGO']
};

function getGeoZone(province) {
    if (!province) return 'OTRO';
    const p = province.toUpperCase().trim();
    for (const [zone, provinces] of Object.entries(ARG_REGIONS_MAP)) {
        if (provinces.includes(p)) return zone;
    }
    return 'OTRO';
}

function ManagerDashboard({ projects, tasks, expenses, budgets, resources, assignments, clients, onSelectProject, fDate, fNum, fCur, currentUser, handleAction, setActiveModal, setModalData, activeModal, aiPptChatHistory, setAiPptChatHistory, isAiPptLoading, setIsAiPptLoading, generatePPTFromJSON, globalFxRate }) {
    const [filterRegion, setFilterRegion] = useState('ALL');
    const [filterCountry, setFilterCountry] = useState('ALL');
    const [filterProvince, setFilterProvince] = useState('ALL');
    const [filterGeoZone, setFilterGeoZone] = useState('ALL');
    const [filterLifeCycle, setFilterLifeCycle] = useState('ALL');
    const [filterGroup, setFilterGroup] = useState('ALL');
    const [isExporting, setIsExporting] = useState(false);
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markerInstances = useRef({});
    const prevFiltersRef = useRef('');
    const prevProjectsSerialized = useRef('');
    const isFirstRender = useRef(true);

    const menuOptions = useMemo(() => {
        const countries = Array.from(new Set((projects || []).map(p => (p.country || 'S/D').toUpperCase()))).sort();
        const provinces = Array.from(new Set((projects || []).filter(p => filterCountry === 'ALL' || (p.country || '').toUpperCase() === filterCountry).map(p => (p.province_region || 'S/D').toUpperCase()))).sort();
        const geoZones = Object.keys(ARG_REGIONS_MAP);
        const legacyRegions = Array.from(new Set((projects || []).map(p => p.province_region).filter(Boolean))).sort();

        // Agile Grouping: use client name or a 'project_group' if it exists. 
        // For now, let's group by Client as a proxy for "Parent Project".
        const groups = Array.from(new Set((projects || []).map(p => (p.client_name || 'Sin Grupo').toUpperCase()))).sort();

        return { countries, provinces, geoZones, legacyRegions, groups };
    }, [projects, filterCountry]);

    const projectRates = useMemo(() => {
        const rates = {};
        const gRate = Number(globalFxRate) || 1000;
        (projects || []).forEach(p => {
            const pMovements = (expenses || []).filter(e => e.event_id === p.id);
            const txWithRate = pMovements.filter(m => Number(m.exchange_rate || m.fx || 0) > 0);
            rates[p.id] = txWithRate.length > 0
                ? Number(txWithRate.sort((a, b) => new Date(b.date || b.expense_date) - new Date(a.date || a.expense_date))[0].exchange_rate || txWithRate.sort((a, b) => new Date(b.date || b.expense_date) - new Date(a.date || a.expense_date))[0].fx)
                : gRate;
        });
        return rates;
    }, [projects, expenses, globalFxRate]);

    const projectsWithStatus = useMemo(() => {
        return (projects || []).map(p => {
            const projTasks = (tasks || []).filter(t => t.event_id === p.id);
            const { physicalProgress, theoreticalProgress } = calculateProgressMetrics(projTasks, p.event_date, p.end_date);

            // Calculamos "Cobrado" filtrando solo movimientos de tipo INCOME y normalizando
            const projIncome = (expenses || []).filter(e => e.event_id === p.id && e.movement_type === 'INCOME');
            const rate = Number(globalFxRate) || 1000;
            const isUsd = p.base_currency === 'USD';

            const totalIncome = projIncome.reduce((sum, m) => {
                const mCur = m.currency_original || 'ARS';
                const pCur = p.base_currency || 'ARS';

                let baseRate = Number(m.exchange_rate || m.fx || 0);
                if (mCur === pCur) {
                    baseRate = 1;
                } else if (baseRate <= 0) {
                    baseRate = projectRates[p.id] || rate;
                } else {
                    baseRate = baseRate < 1 ? (1 / baseRate) : baseRate;
                }

                let val = 0;
                if (mCur === 'ARS') {
                    const amtARS = Number(m.amount_original || 0);
                    val = isUsd ? (amtARS / (baseRate || 1)) : amtARS;
                } else {
                    const amtUSD = Number(m.amount_original || 0);
                    val = isUsd ? amtUSD : (amtUSD * (baseRate || 1));
                }
                return sum + val;
            }, 0);

            const budgetTotal = parseFloat(p.budget_total_contract || 0);
            const executionCobro = budgetTotal > 0 ? (totalIncome / budgetTotal) * 100 : 0;

            let unifiedStatus = { id: 'EN_CURSO', label: 'En Curso', color: '#22c55e' }; // Verde por defecto

            // 1. Prioridad: Retrasos Críticos
            if (physicalProgress < theoreticalProgress - 10) {
                unifiedStatus = { id: 'RETRASO_GRAVE', label: 'Retraso Grave', color: '#f43f5e' };
            }
            // 2. Tareas al 100%?
            else if (physicalProgress >= 100) {
                // Finalizado total solo si cobró >= 99.5% del contrato (para absorber redondeos)
                if (executionCobro >= 99.5) {
                    unifiedStatus = { id: 'FINALIZADO', label: 'Finalizado Total', color: '#6366f1' };
                } else {
                    unifiedStatus = { id: 'TAREAS_COMPLETADAS', label: 'Tareas OK / Saldo', color: '#06b6d4' };
                }
            }
            // 3. Retrasos Leves
            else if (physicalProgress < theoreticalProgress - 2) {
                unifiedStatus = { id: 'RETRASO_LEVE', label: 'Retraso Leve', color: '#f59e0b' };
            }
            // 4. No Iniciado / En Curso
            else if (physicalProgress <= 0) {
                unifiedStatus = { id: 'NO_INICIADO', label: 'No Iniciado', color: '#94a3b8' };
            } else {
                unifiedStatus = { id: 'EN_CURSO', label: 'En Curso OK', color: '#22c55e' };
            }

            return { ...p, physicalProgress, theoreticalProgress, budgetExecution: executionCobro, unifiedStatus };
        });
    }, [projects, tasks, expenses]);


    const filteredProjects = useMemo(() => {
        return projectsWithStatus.filter(p => {
            // Filtro Grupo / Proyecto Padre
            if (filterGroup !== 'ALL' && (p.client_name || 'Sin Grupo').toUpperCase() !== filterGroup) return false;

            // Filtro Pais
            const pCountry = (p.country || 'S/D').toUpperCase();
            if (filterCountry !== 'ALL' && pCountry !== filterCountry) return false;

            if (filterCountry === 'ARGENTINA') {
                const pProv = (p.province_region || 'S/D').toUpperCase();
                if (filterProvince !== 'ALL' && pProv !== filterProvince) return false;
                if (filterGeoZone !== 'ALL' && getGeoZone(p.province_region) !== filterGeoZone) return false;
            } else if (filterCountry === 'ALL') {
                if (filterRegion !== 'ALL' && (p.province_region || '').toUpperCase() !== filterRegion) return false;
            }

            if (filterLifeCycle !== 'ALL' && p.unifiedStatus.id !== filterLifeCycle) return false;
            return true;
        });
    }, [projectsWithStatus, filterRegion, filterCountry, filterProvince, filterGeoZone, filterLifeCycle, filterGroup]);

    const zoneSummary = useMemo(() => {
        const summary = {};
        filteredProjects.forEach(p => {
            let zone = 'OTRO';
            if (filterCountry === 'ARGENTINA') {
                zone = getGeoZone(p.province_region);
            } else if (filterCountry !== 'ALL') {
                zone = (p.province_region || 'TODOS/AS').toUpperCase();
            } else {
                // Si es ALL, agrupamos por PAIS - PROVINCIA
                zone = `${p.country || 'S/D'} - ${p.province_region || 'S/D'}`.toUpperCase();
            }

            if (!summary[zone]) summary[zone] = { count: 0, avgProg: 0, statusCounts: {} };
            summary[zone].count++;
            summary[zone].avgProg += p.physicalProgress;
            const sId = p.unifiedStatus.id;
            summary[zone].statusCounts[sId] = (summary[zone].statusCounts[sId] || 0) + 1;
        });
        Object.keys(summary).forEach(z => summary[z].avgProg = Math.round(summary[z].avgProg / summary[z].count));
        return summary;
    }, [filteredProjects, filterCountry]);

    const exportToPPT = async (filterSpecs = null) => {
        if (typeof PptxGenJS === 'undefined') {
            alert("Biblioteca PPT no cargada. Por favor, refresque la página.");
            return;
        }
        setIsExporting(true);
        try {
            const pptx = new PptxGenJS();
            pptx.layout = 'LAYOUT_WIDE';

            // SLIDE 1: Carátula
            let slide1 = pptx.addSlide();
            slide1.background = { color: '0f172a' };
            slide1.addText("Reporte Consolidado de Operaciones", { x: 0.5, y: 1.5, w: '90%', fontSize: 44, bold: true, color: 'ffffff', fontFace: 'Arial' });
            slide1.addText("frssoluciones | Gestión Estratégica", { x: 0.5, y: 2.5, w: '90%', fontSize: 18, color: '818cf8', fontFace: 'Arial' });
            slide1.addText(`Total Proyectos: ${filteredProjects.length} | Fecha: ${new Date().toLocaleDateString()}`, { x: 0.5, y: 4.5, w: '90%', fontSize: 14, color: '94a3b8' });

            // SLIDE 2: Dashboard Mixto (Mapa + Gráfico de Torta)
            let slide2 = pptx.addSlide();
            slide2.addText("Ubicación y Estado de Proyectos", { x: 0.5, y: 0.3, fontSize: 24, bold: true, color: '0f172a' });

            // Datos para el gráfico de torta
            const statusNames = [];
            const statusValues = [];
            const tempStats = {};
            filteredProjects.forEach(p => tempStats[p.unifiedStatus.label] = (tempStats[p.unifiedStatus.label] || 0) + 1);
            Object.entries(tempStats).forEach(([name, val]) => { statusNames.push(name); statusValues.push(val); });

            if (mapRef.current) {
                try {
                    // Improved capture settings for Leaflet
                    const canvas = await html2canvas(mapRef.current, {
                        useCORS: true,
                        allowTaint: true,
                        logging: false,
                        scale: 2, // Better quality
                        backgroundColor: null,
                        removeContainer: true
                    });
                    const imgData = canvas.toDataURL('image/png');
                    slide2.addImage({ data: imgData, x: 0.5, y: 1, w: 7.5, h: 4.5 });
                } catch (mapError) {
                    console.warn("Map capture failed, skipping map slide content", mapError);
                    slide2.addText("(Mapa no disponible en la exportación)", { x: 0.5, y: 2.5, w: 7.5, h: 1, fontSize: 14, color: '94a3b8', align: 'center' });
                }
            }

            slide2.addChart(pptx.ChartType.pie, [
                { name: 'Estatus', labels: statusNames, values: statusValues }
            ], {
                x: 8.2, y: 1.5, w: 4, h: 3.5,
                showPercent: true, showLegend: true, legendPos: 'b',
                title: 'Desglose por Estado (%)', titleFontSize: 12
            });

            // SLIDE 3+: Grilla de Proyectos
            const CHUNK_SIZE = 12;
            for (let i = 0; i < filteredProjects.length; i += CHUNK_SIZE) {
                let slideGrid = pptx.addSlide();
                slideGrid.addText(`Listado de Proyectos (${i + 1} - ${Math.min(i + CHUNK_SIZE, filteredProjects.length)})`, { x: 0.5, y: 0.3, fontSize: 18, bold: true });

                const tableData = [
                    ['Proyecto', 'Ubicación', 'Estatus', 'Avance %']
                ];
                filteredProjects.slice(i, i + CHUNK_SIZE).forEach(p => {
                    tableData.push([p.name, p.city || 'S/D', p.unifiedStatus.label, `${p.physicalProgress}%`]);
                });

                slideGrid.addTable(tableData, { x: 0.5, y: 0.8, w: 12, fontSize: 10, border: { pt: 1, color: 'f1f5f9' }, fill: { color: 'ffffff' } });
            }

            pptx.writeFile({ fileName: `Reporte_Proyectos_${new Date().toISOString().split('T')[0]}.pptx` });
        } catch (error) {
            console.error(error);
            alert("Error al generar PPT: " + error.message);
        }
        setIsExporting(false);
    };

    const centerOnUser = () => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(pos => {
            const { latitude, longitude } = pos.coords;
            if (mapInstance.current) {
                mapInstance.current.setView([latitude, longitude], 15);
                L.marker([latitude, longitude], {
                    icon: L.divIcon({
                        className: 'user-location-marker',
                        html: '<div class="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-lg animate-pulse"></div>',
                        iconSize: [16, 16]
                    })
                }).addTo(mapInstance.current).bindPopup("Tu ubicación actual").openPopup();
            }
        }, err => {
            console.error("Error obteniendo ubicación", err);
        });
    };

    const copyMapToClipboard = async () => {
        if (!mapRef.current) return;
        try {
            const canvas = await html2canvas(mapRef.current, { useCORS: true, logging: false });
            canvas.toBlob(async (blob) => {
                if (blob) {
                    await navigator.clipboard.write([
                        new ClipboardItem({ 'image/png': blob })
                    ]);
                    alert("Copiado al portapapeles");
                }
            });
        } catch (e) {
            console.error("Error al copiar", e);
            alert("No se pudo copiar la imagen");
        }
    };

    // 1. Hook de Inicialización del Mapa (Una sola vez al montar)
    useEffect(() => {
        if (!mapRef.current || mapInstance.current) return;

        // Initialize Map
        if (typeof L === 'undefined') return;

        mapInstance.current = L.map(mapRef.current, {
            zoomControl: false,
            attributionControl: false,
            zoomSnap: 1,
            zoomDelta: 1,
            wheelPxPerZoomLevel: 120
        }).setView([-34.6037, -58.3816], 4); // Buenos Aires como centro inicial

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            noWrap: true,
            bounds: [[-90, -180], [90, 180]]
        }).addTo(mapInstance.current);

        L.control.zoom({ position: 'bottomright' }).addTo(mapInstance.current);

        // Refresco de tamaño con múltiples intentos para asegurar que el div esté listo y dimensionado
        const timer1 = setTimeout(() => {
            if (mapInstance.current) mapInstance.current.invalidateSize();
        }, 500);
        const timer2 = setTimeout(() => {
            if (mapInstance.current) mapInstance.current.invalidateSize();
        }, 1500);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            if (mapInstance.current) {
                try {
                    mapInstance.current.remove();
                } catch (e) { console.error(e); }
                mapInstance.current = null;
            }
        };
    }, []);

    // 2. Hook de Actualización de Marcadores (Cada vez que cambian los filtros o proyectos)
    useEffect(() => {
        if (!mapInstance.current) return;

        // Optimización: Si los proyectos filtrados no cambiaron, evitar re-crear marcadores
        const projectsSerialized = JSON.stringify(filteredProjects.map(p => ({ id: p.id, lat: p.latitude, lng: p.longitude, status: p.unifiedStatus.id })));
        if (projectsSerialized === prevProjectsSerialized.current) return;
        prevProjectsSerialized.current = projectsSerialized;

        // Limpiar capas anteriores
        mapInstance.current.eachLayer(layer => {
            if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
                // No remover el marcador de ubicación del usuario si existe
                if (layer.options && layer.options.icon && layer.options.icon.options && layer.options.icon.options.className === 'user-location-marker') {
                    return;
                }
                mapInstance.current.removeLayer(layer);
            }
        });

        const markers = [];
        markerInstances.current = {};

        // Agrupar proyectos por coordenadas exactas
        const grouped = {};
        filteredProjects.forEach(p => {
            let lat = parseFloat(p.latitude);
            let lng = parseFloat(p.longitude);

            // Fallback: If project has no valid location, try to use Client's location
            if (isNaN(lat) || lat === 0 || isNaN(lng) || lng === 0) {
                if (p.client_id && clients) {
                    const client = clients.find(c => c.id === p.client_id);
                    if (client && client.latitude && client.longitude) {
                        lat = parseFloat(client.latitude);
                        lng = parseFloat(client.longitude);
                    }
                }
            }

            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
                if (!grouped[key]) grouped[key] = [];
                grouped[key].push(p);
            }
        });

        Object.entries(grouped).forEach(([coordsKey, projs]) => {
            const [lat, lng] = coordsKey.split(',').map(parseFloat);

            // Determinar color prioritario (el más crítico)
            const statusPriority = ['RETRASO_GRAVE', 'RETRASO_LEVE', 'EN_CURSO', 'NO_INICIADO', 'TAREAS_COMPLETADAS', 'FINALIZADO'];
            let topProj = projs[0];
            projs.forEach(p => {
                if (statusPriority.indexOf(p.unifiedStatus.id) < statusPriority.indexOf(topProj.unifiedStatus.id)) {
                    topProj = p;
                }
            });

            const hasMultiple = projs.length > 1;
            const marker = L.marker([lat, lng], {
                icon: L.divIcon({
                    className: 'custom-project-marker',
                    html: `
                        <div style="width: 26px; height: 26px; background-color: ${topProj.unifiedStatus.color}; border: 3px solid white; border-radius: 50%; box-shadow: 0 0 12px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; ${hasMultiple ? 'border-style: double; border-width: 5px;' : ''}">
                            ${hasMultiple ? `<span style="color: white; font-size: 8px; font-weight: 900;">${projs.length}</span>` : ''}
                        </div>
                    `,
                    iconSize: [26, 26],
                    iconAnchor: [13, 13]
                })
            }).addTo(mapInstance.current);

            projs.forEach(p => { markerInstances.current[p.id] = marker; });

            const popupHtml = `
                <div style="min-width: 250px; max-height: 400px; overflow-y: auto; padding: 4px; font-family: inherit;">
                    ${projs.map((p, idx) => `
                        <div style="${idx > 0 ? 'margin-top: 20px; border-top: 1px solid #f1f5f9; padding-top: 16px;' : ''}">
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                                <span style="font-size: 8px; font-weight: 900; text-transform: uppercase; color: ${p.unifiedStatus.color}; letter-spacing: 0.1em;">${p.type || 'Proyecto'}</span>
                                <span style="font-size: 8px; font-weight: 900; background: ${p.unifiedStatus.color}20; color: ${p.unifiedStatus.color}; padding: 2px 6px; border-radius: 99px;">${p.unifiedStatus.label}</span>
                            </div>
                            <h4 style="font-weight: 900; color: #0f172a; text-transform: uppercase; font-size: 13px; margin: 0 0 4px 0; line-height: 1.2;">${p.name}</h4>
                            <p style="font-size: 9px; font-weight: 800; color: #64748b; text-transform: uppercase; margin-bottom: 12px; letter-spacing: 0.05em;">Cliente: ${p.client_name || 'Sin Cliente'}</p>
                            <div style="margin-bottom: 15px; background: #f8fafc; padding: 10px; border-radius: 12px;">
                                <div style="display: flex; justify-content: space-between; font-size: 9px; font-weight: 800; margin-bottom: 6px; color: #64748b;">
                                    <span>AVANCE FÍSICO</span>
                                    <span style="color: ${p.unifiedStatus.color}">${p.physicalProgress}%</span>
                                </div>
                                <div style="width: 100%; height: 8px; background: #e2e8f0; border-radius: 99px; overflow: hidden;">
                                    <div style="height: 100%; background: ${p.unifiedStatus.color}; width: ${p.physicalProgress}%"></div>
                                </div>
                            </div>
                            <button onclick="window.dispatchProjectSelect('${p.id}')" style="width: 100%; background: #0f172a; color: white; font-size: 9px; font-weight: 900; text-transform: uppercase; padding: 12px; border-radius: 12px; border: none; cursor: pointer; transition: all 0.2s; box-shadow: 0 4px 10px rgba(0,0,0,0.1);">Abrir Dashboard</button>
                        </div>
                    `).join('')}
                </div>
            `;

            marker.bindPopup(popupHtml, { className: 'custom-map-popup', maxWidth: 350 });

            // Add hover behavior
            marker.on('mouseover', function (e) {
                if (!this.isPopupOpen()) {
                    this.openPopup();
                }
            });

            markers.push([lat, lng]);
        });

        const currentFilters = `${filterRegion}-${filterCountry}-${filterProvince}-${filterGeoZone}-${filterLifeCycle}-${filterGroup}`;
        const filtersChanged = currentFilters !== prevFiltersRef.current;

        if (markers.length > 0 && (isFirstRender.current || filtersChanged)) {
            mapInstance.current.fitBounds(markers, { padding: [100, 100], maxZoom: 14 });
            isFirstRender.current = false;
        }
        prevFiltersRef.current = currentFilters;

        window.dispatchProjectSelect = (id) => onSelectProject(id);
    }, [filteredProjects, onSelectProject]);


    return (
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm gap-8">
                <div>
                    <h3 className="text-4xl font-black uppercase tracking-tighter leading-none italic text-slate-800">Mapa de Operaciones</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-3">Geolocalización y Salud de Proyectos en Tiempo Real</p>
                </div>
                <div className="flex flex-wrap gap-4 items-end">
                    <div className="space-y-2">
                        <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">Proyecto Padre / Grupo</label>
                        <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)} className="bg-slate-50 border-2 border-slate-100 rounded-2xl py-3 px-6 text-[10px] font-black uppercase focus:border-indigo-500 outline-none transition-all">
                            <option value="ALL">Todos los Grupos</option>
                            {menuOptions.groups.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">País</label>
                        <select value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setFilterProvince('ALL'); setFilterGeoZone('ALL'); }} className="bg-slate-50 border-2 border-slate-100 rounded-2xl py-3 px-6 text-[10px] font-black uppercase focus:border-indigo-500 outline-none transition-all">
                            <option value="ALL">Todos los Países</option>
                            {menuOptions.countries.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>

                    {filterCountry === 'ARGENTINA' ? (
                        <>
                            <div className="space-y-2">
                                <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">Zona Geográfica</label>
                                <select value={filterGeoZone} onChange={e => setFilterGeoZone(e.target.value)} className="bg-slate-50 border-2 border-slate-100 rounded-2xl py-3 px-6 text-[10px] font-black uppercase focus:border-indigo-500 outline-none transition-all">
                                    <option value="ALL">Todas las Zonas</option>
                                    {menuOptions.geoZones.map(z => <option key={z} value={z}>{z}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">Provincia</label>
                                <select value={filterProvince} onChange={e => setFilterProvince(e.target.value)} className="bg-slate-50 border-2 border-slate-100 rounded-2xl py-3 px-6 text-[10px] font-black uppercase focus:border-indigo-500 outline-none transition-all">
                                    <option value="ALL">Todas las Provincias</option>
                                    {menuOptions.provinces.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">Región / Estado</label>
                            <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)} className="bg-slate-50 border-2 border-slate-100 rounded-2xl py-3 px-6 text-[10px] font-black uppercase focus:border-indigo-500 outline-none transition-all">
                                <option value="ALL">Todas las Regiones</option>
                                {menuOptions.legacyRegions.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">Estatus del Proyecto</label>
                        <select value={filterLifeCycle} onChange={e => setFilterLifeCycle(e.target.value)} className="bg-slate-50 border-2 border-slate-100 rounded-2xl py-3 px-6 text-[10px] font-black uppercase focus:border-indigo-500 outline-none transition-all">
                            <option value="ALL">Todos los Estados</option>
                            <option value="NO_INICIADO">⚪ No Iniciado</option>
                            <option value="RETRASO_GRAVE">🔴 Retraso Grave</option>
                            <option value="RETRASO_LEVE">🟡 Retraso Leve</option>
                            <option value="EN_CURSO">🟢 En Curso OK</option>
                            <option value="TAREAS_COMPLETADAS">🔵 Tareas OK / Saldo</option>
                            <option value="FINALIZADO">🟣 Finalizado Total</option>
                        </select>
                    </div>

                    <button
                        onClick={() => setActiveModal('PPT_CHOICE')}
                        disabled={isExporting}
                        className="bg-slate-900 hover:bg-black text-white py-3 px-10 rounded-2xl text-[10px] uppercase font-black flex items-center gap-4 shadow-xl shadow-slate-200 transition-all disabled:opacity-50 h-[52px] group"
                    >
                        {isExporting ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <FileText size={18} className="text-indigo-400 group-hover:scale-110 transition-transform" />}
                        Exportar Reporte PPT
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
                <div className="lg:col-span-3 bg-white p-4 rounded-[4rem] border border-slate-200 shadow-2xl relative overflow-hidden" style={{ height: '650px' }}>
                    <div ref={mapRef} className="w-full h-full rounded-[3rem] z-0" />

                    {/* Botón Mi Ubicación */}
                    <button
                        onClick={centerOnUser}
                        className="absolute top-10 right-10 z-10 bg-white p-4 rounded-2xl border border-slate-200 shadow-xl text-slate-600 hover:text-indigo-600 hover:scale-105 active:scale-95 transition-all outline-none"
                        title="Mi Ubicación"
                    >
                        <Navigation size={20} className="rotate-45" />
                    </button>

                    <button
                        onClick={copyMapToClipboard}
                        className="absolute top-28 right-10 z-10 bg-white p-4 rounded-2xl border border-slate-200 shadow-xl text-slate-600 hover:text-emerald-600 hover:scale-105 active:scale-95 transition-all outline-none"
                        title="Copiar Mapa"
                    >
                        <Copy size={20} />
                    </button>

                    <div className="absolute bottom-10 left-10 z-10 bg-white/90 backdrop-blur-md p-6 rounded-3xl border border-slate-200 shadow-xl grid grid-cols-2 gap-x-6 gap-y-2">
                        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-slate-400 shadow-sm" /> <span className="text-[8px] font-black uppercase text-slate-600">No Iniciado</span></div>
                        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-rose-500 shadow-sm" /> <span className="text-[8px] font-black uppercase text-slate-600">Retraso Grave</span></div>
                        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-amber-500 shadow-sm" /> <span className="text-[8px] font-black uppercase text-slate-600">Retraso Leve</span></div>
                        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500 shadow-sm" /> <span className="text-[8px] font-black uppercase text-slate-600">En Curso OK</span></div>
                        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-cyan-500 shadow-sm" /> <span className="text-[8px] font-black uppercase text-slate-600">Tareas OK / Saldo</span></div>
                        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-indigo-600 shadow-sm" /> <span className="text-[8px] font-black uppercase text-slate-600">Finalizado</span></div>
                    </div>
                </div>

                <div className="space-y-6 overflow-y-auto pr-4 custom-scrollbar" style={{ maxHeight: '650px' }}>
                    <div className="p-8 bg-gradient-to-br from-slate-800 to-slate-950 rounded-[2.5rem] text-white shadow-xl">
                        <p className="text-[10px] font-black uppercase text-indigo-400 tracking-[0.2em] mb-2">Filtro Activo</p>
                        <h4 className="text-3xl font-black tracking-tighter">{filteredProjects.length} <span className="text-sm font-medium opacity-60">Proyectos</span></h4>
                    </div>
                    {filteredProjects.length === 0 ? (
                        <div className="py-20 text-center bg-white border-2 border-dashed border-slate-100 rounded-[3rem]">
                            <p className="text-[10px] font-black uppercase text-slate-300 tracking-widest italic">No hay proyectos que coincidan</p>
                        </div>
                    ) : filteredProjects.map(p => (
                        <div
                            key={p.id}
                            className="p-8 bg-white border-2 border-slate-50 rounded-[2.5rem] hover:border-indigo-500 transition-all cursor-pointer group shadow-sm flex flex-col gap-6 hover:-translate-y-1"
                            onClick={() => onSelectProject(p.id)}
                            onMouseEnter={() => {
                                const m = markerInstances.current[p.id];
                                if (m && mapInstance.current) {
                                    if (!m.isPopupOpen()) {
                                        m.openPopup();
                                    }
                                    mapInstance.current.panTo(m.getLatLng());
                                }
                            }}
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex items-center gap-4">
                                    <div className="w-4 h-4 rounded-full shadow-lg animate-pulse" style={{ backgroundColor: p.unifiedStatus.color }} />
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <p className="font-black text-base uppercase text-slate-800 tracking-tight group-hover:text-indigo-600 leading-none">{p.name}</p>
                                            <span className="text-[7px] font-black px-2 py-0.5 rounded-full uppercase" style={{ backgroundColor: `${p.unifiedStatus.color}20`, color: p.unifiedStatus.color }}>
                                                {p.unifiedStatus.label}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{p.city || 'Ubicación Desconocida'}</p>
                                            <span className="text-[9px] font-black text-indigo-500/50 uppercase leading-none">|</span>
                                            <p className="text-[10px] font-black text-indigo-600 uppercase tracking-tight">{p.client_name || 'S/D'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-all">
                                    <ArrowRight size={20} />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="flex justify-between text-[10px] font-black uppercase tracking-widest mb-1">
                                    <span className="text-slate-400">Avance Real vs Teórico</span>
                                    <span style={{ color: p.unifiedStatus.color }}>{p.physicalProgress}% / {p.theoreticalProgress}%</span>
                                </div>
                                <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden relative shadow-inner">
                                    <div className="absolute inset-y-0 left-0 bg-slate-200 opacity-50" style={{ width: `${p.theoreticalProgress}%` }} />
                                    <div className="h-full relative z-10 transition-all duration-1000" style={{ width: `${p.physicalProgress}%`, backgroundColor: p.unifiedStatus.color, boxShadow: `0 0 10px ${p.unifiedStatus.color}66` }} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Operative Summary Grid */}
            <div className="bg-white p-12 rounded-[4rem] border border-slate-200 shadow-sm space-y-8 mt-12">
                <div>
                    <h4 className="text-2xl font-black uppercase tracking-tight text-slate-800">Resumen por Zona Operativa</h4>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Métricas de ejecución agrupadas por ubicación</p>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-separate border-spacing-y-2">
                        <thead>
                            <tr className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                                <th className="px-6 py-4">Zona / Región</th>
                                <th className="px-6 py-4">Proyectos</th>
                                <th className="px-6 py-4">Avance Promedio</th>
                                <th className="px-6 py-4">Distribución de Estados</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(zoneSummary).map(([zone, data]) => (
                                <tr key={zone} className="bg-slate-50/50 hover:bg-white transition-all group rounded-2xl">
                                    <td className="px-6 py-6 font-black text-slate-800 uppercase text-xs tracking-tight">{zone}</td>
                                    <td className="px-6 py-6">
                                        <span className="bg-white px-4 py-1.5 rounded-xl border border-slate-200 text-xs font-black text-slate-600 shadow-sm">{data.count}</span>
                                    </td>
                                    <td className="px-6 py-6">
                                        <div className="flex items-center gap-4">
                                            <span className="text-xs font-black text-slate-800">{data.avgProg}%</span>
                                            <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                                                <div className="h-full bg-indigo-500" style={{ width: `${data.avgProg}%` }} />
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6">
                                        <div className="flex gap-2">
                                            {Object.entries(data.statusCounts).map(([status, count]) => {
                                                const colors = {
                                                    'RETRASO_GRAVE': 'bg-rose-500',
                                                    'RETRASO_LEVE': 'bg-amber-500',
                                                    'EN_CURSO': 'bg-emerald-500',
                                                    'FINALIZADO': 'bg-indigo-600',
                                                    'TAREAS_COMPLETADAS': 'bg-cyan-500',
                                                    'NO_INICIADO': 'bg-slate-400'
                                                };
                                                return (
                                                    <div key={status} className={`${colors[status] || 'bg-slate-200'} w-6 h-6 rounded-lg flex items-center justify-center text-[8px] font-black text-white shadow-sm`} title={`${status}: ${count}`}>
                                                        {count}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {
                activeModal === 'PPT_CHOICE' && (
                    <Modal title="Exportar Reporte PPT" onClose={() => setActiveModal(null)}>
                        <div className="p-8 space-y-8 animate-in slide-in-from-bottom-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <button
                                    onClick={() => { exportToPPT(); setActiveModal(null); }}
                                    className="group p-10 bg-white border-2 border-slate-100 rounded-[3rem] hover:border-indigo-500 hover:shadow-2xl transition-all text-left flex flex-col gap-6"
                                >
                                    <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                                        <Zap size={28} />
                                    </div>
                                    <div>
                                        <h4 className="font-black text-lg uppercase tracking-tight text-slate-800 mb-2">Reporte Directo</h4>
                                        <p className="text-xs text-slate-500 font-medium leading-relaxed">Generación instantánea con los datos actuales del proyecto.</p>
                                    </div>
                                </button>

                                <button
                                    onClick={() => setActiveModal('REPORTS_GENERATOR')}
                                    className="group p-10 bg-slate-900 border-2 border-slate-800 rounded-[3rem] hover:border-indigo-400 hover:shadow-2xl transition-all text-left flex flex-col gap-6"
                                >
                                    <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-all shadow-lg shadow-indigo-500/20">
                                        <Filter size={28} />
                                    </div>
                                    <div>
                                        <h4 className="font-black text-lg uppercase tracking-tight text-white mb-2">Generador Filtrado</h4>
                                        <p className="text-xs text-slate-400 font-medium leading-relaxed">Reportes masivos por cliente, estado, zona o con pendientes.</p>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </Modal>
                )
            }

            {
                activeModal === 'AI_PPT_PLANNER' && (
                    <Modal title="Asistente de Reportes PPT" onClose={() => setActiveModal(null)}>
                        <div className="space-y-6">
                            <div className="bg-slate-50/50 rounded-[2rem] border border-slate-100 p-6 space-y-4 h-[400px] overflow-y-auto custom-scrollbar flex flex-col" id="ai-ppt-chat-container">
                                {aiPptChatHistory.length === 0 ? (
                                    <div className="flex-1 flex flex-col items-center justify-center text-center p-10 space-y-4">
                                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-indigo-500 shadow-sm">
                                            <Zap size={32} />
                                        </div>
                                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest leading-loose">
                                            Dime qué necesitas mostrar en el reporte.<br />
                                            "Quiero un resumen trimestral de gastos por región"<br />
                                            "Necesito un gráfico de avance de obra para el Cliente X"
                                        </p>
                                    </div>
                                ) : (
                                    aiPptChatHistory.map((msg, i) => (
                                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}>
                                            <div className={`max-w-[85%] px-5 py-3 rounded-2xl text-sm leading-relaxed ${msg.role === 'user' ? 'bg-slate-900 text-white shadow-indigo-100' : 'bg-white border border-slate-100 text-slate-700 shadow-sm'}`}>
                                                <div className="whitespace-pre-wrap">{msg.content}</div>
                                            </div>
                                        </div>
                                    ))
                                )}
                                {isAiPptLoading && (
                                    <div className="flex justify-start animate-pulse">
                                        <div className="bg-white border border-slate-100 px-5 py-3 rounded-2xl flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                            <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                            <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <form className="relative" onSubmit={async (e) => {
                                e.preventDefault();
                                const input = e.target.elements.prompt;
                                const prompt = input.value;
                                if (!prompt || isAiPptLoading) return;

                                const newHistory = [...aiPptChatHistory, { role: 'user', content: prompt }];
                                setAiPptChatHistory(newHistory);
                                input.value = '';
                                setIsAiPptLoading(true);

                                try {
                                    const token = localStorage.getItem('em_token');
                                    const headers = { 'Content-Type': 'application/json' };
                                    if (token) headers['Authorization'] = `Bearer ${token}`;

                                    const res = await fetch(`${API_URL}?action=ai_ppt_assistant_chat`, {
                                        method: 'POST',
                                        headers: headers,
                                        body: JSON.stringify({
                                            messages: newHistory,
                                            project_context: {
                                                summary: `Contexto consolidado de ${filteredProjects.length} proyectos activos.`,
                                                selected_project_id: selectedEventId || null
                                            },
                                            company_id: currentUser?.company_id || currentUser?.companyId
                                        })
                                    });
                                    const data = await res.json();
                                    if (data.status === 'success') {
                                        const reply = data.reply;
                                        const pptMatch = reply.match(/\[PPT_DATA\]([\s\S]*?)\[\/PPT_DATA\]/);

                                        if (pptMatch) {
                                            try {
                                                let jsonStr = pptMatch[1].trim();
                                                jsonStr = jsonStr.replace(/^```json/, '').replace(/^```/, '').replace(/```$/, '').trim();
                                                const pptData = JSON.parse(jsonStr);
                                                const cleanReply = reply.replace(/\[PPT_DATA\][\s\S]*?\[\/PPT_DATA\]/, "¡Listo! He generado el archivo PowerPoint con la estructura solicitada. Se descargará automáticamente.");
                                                setAiPptChatHistory(prev => [...prev, { role: 'assistant', content: cleanReply }]);
                                                setTimeout(() => {
                                                    generatePPTFromJSON(pptData, "Reporte_Consolidado");
                                                }, 500);
                                            } catch (e) {
                                                console.error("PPT JSON Parse Error", e);
                                                setAiPptChatHistory(prev => [...prev, { role: 'assistant', content: reply + "\n\n(Error al procesar el JSON. Intenta ser más específico.)" }]);
                                            }
                                        } else {
                                            setAiPptChatHistory(prev => [...prev, { role: 'assistant', content: reply }]);
                                        }
                                    } else {
                                        alert(data.message);
                                    }
                                } catch (e) {
                                    alert("Error en la conexión con la IA");
                                } finally {
                                    setIsAiPptLoading(false);
                                }
                            }}>
                                <textarea name="prompt" rows="3" className="input-base pr-20 pt-4 resize-none h-24" placeholder="Describe el contenido o formato del PPT..." onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.target.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true })); } }}></textarea>
                                <button type="submit" className="absolute right-4 bottom-4 p-4 bg-slate-900 text-white rounded-2xl hover:bg-black transition-all shadow-xl shadow-indigo-100/20" disabled={isAiPptLoading}>
                                    <Send size={20} />
                                </button>
                            </form>
                        </div>
                    </Modal>
                )
            }
        </div>
    );
}

function SidebarButton({ icon, label, active, onClick, collapsed }) {

    return (
        <button
            onClick={onClick}
            title={collapsed ? label : ""}
            className={`w-full flex items-center ${collapsed ? 'justify-center px-0' : 'px-8'} py-4 rounded-2xl transition-all duration-300 font-bold text-[11px] uppercase tracking-[0.2em] relative group ${active
                ? 'bg-white/10 text-white shadow-xl'
                : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
                }`}
        >
            {active && <div className="absolute left-0 w-1.5 h-6 bg-indigo-500 rounded-r-full shadow-[0_0_15px_rgba(99,102,241,0.5)]"></div>}
            <span className={`${active ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'} transition-colors ${collapsed ? '' : ''}`}>{icon}</span>
            {!collapsed && <span className="truncate">{label}</span>}
        </button>
    );
}


function TeamManager({ team, onAdd, onEdit, currentUser }) {
    const [searchTerm, setSearchTerm] = React.useState('');

    const filteredTeam = team.filter(u =>
        (!currentUser?.company_id || String(u.company_id) === String(currentUser.company_id)) &&
        (u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (u.position || '').toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const sortedTeam = [...filteredTeam].sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div className="space-y-12 animate-in pb-20">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-8 px-4">
                <div className="space-y-4">
                    <h3 className="text-4xl font-black uppercase tracking-tighter">Equipo</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">Gestión de miembros y colaboradores</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 flex-1 max-w-2xl bg-white p-2 rounded-[2rem] border border-slate-100 shadow-sm">
                    <div className="relative flex-1">
                        <Search className="absolute left-6 top-5 text-slate-300" size={20} />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, cargo o email..."
                            className="w-full pl-14 py-4 pr-6 bg-slate-50 border-none rounded-2xl font-bold text-slate-600 focus:ring-4 focus:ring-indigo-500/10 placeholder:text-slate-300 transition-all outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button onClick={onAdd} className="btn-primary py-4 px-10 text-[11px] uppercase tracking-widest shadow-xl shadow-indigo-100 bg-indigo-600 whitespace-nowrap">
                        <UserPlus size={20} /> Asociar Miembro
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-[3rem] border border-slate-100 shadow-sm overflow-hidden">
                {sortedTeam.length === 0 ? (
                    <div className="p-20 text-center">
                        <p className="text-slate-400 font-black uppercase text-xs tracking-widest italic">No se encontraron miembros</p>
                    </div>
                ) : (
                    sortedTeam.map((u, idx) => {
                        const isExternal = u.company_id && currentUser?.company_id && (String(u.company_id) !== String(currentUser.company_id));
                        return (
                            <div key={u.id} className={`flex items-center gap-6 px-8 py-5 hover:bg-slate-50/70 transition-all group ${idx > 0 ? 'border-t border-slate-50' : ''}`}>
                                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-black text-xl shadow-lg uppercase shrink-0">
                                    {u.name[0]}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="font-black text-sm uppercase tracking-tight text-slate-800 truncate">{u.name}</p>
                                        {isExternal && (
                                            <span className="px-2 py-0.5 bg-slate-100 text-slate-400 text-[7px] font-black uppercase rounded">Externo</span>
                                        )}
                                    </div>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate">
                                        {u.role === 'COMPANY_ADMIN' ? 'Administrador' : (u.role === 'COMPANY_USER' ? 'Colaborador' : u.role)} {u.position ? ` • ${u.position}` : ''}
                                    </p>
                                </div>
                                <div className="hidden sm:block text-right">
                                    <p className="text-[10px] text-indigo-400 font-black tracking-widest">{u.email}</p>
                                </div>
                                {!isExternal && (
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => onEdit(u)} className="p-2.5 bg-slate-50 rounded-xl hover:bg-indigo-100 hover:text-indigo-600 transition-all text-slate-300">
                                            <Edit3 size={15} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

function ClientRow({ c, idx, contacts, projects, onEdit, onDelete }) {
    const [expanded, setExpanded] = React.useState(false);
    const clientContacts = contacts.filter(con => con.client_id === c.id);
    const clientProjects = projects.filter(p => p.client_id === c.id);

    return (
        <div className={`${idx > 0 ? 'border-t border-slate-50' : ''}`}>
            {/* Row */}
            <div
                className="flex items-center gap-6 px-8 py-5 cursor-pointer hover:bg-slate-50/70 transition-all group"
                onClick={() => setExpanded(prev => !prev)}
            >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-base shrink-0 transition-all duration-300 ${expanded ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 group-hover:bg-indigo-50 group-hover:text-indigo-600'}`}>
                    {c.name[0]}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-black text-sm uppercase tracking-tight text-slate-800 truncate">{c.name}</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate">{c.email || 'Sin correo'} {c.phone ? `• ${c.phone}` : ''}</p>
                </div>
                <div className="flex items-center gap-6 shrink-0 text-right">
                    <div className="hidden sm:block">
                        <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">{clientContacts.length} contacto{clientContacts.length !== 1 ? 's' : ''}</p>
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{clientProjects.length} proyecto{clientProjects.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={(e) => { e.stopPropagation(); onEdit(c); }} className="p-2.5 bg-slate-50 rounded-xl hover:bg-indigo-100 hover:text-indigo-600 transition-all text-slate-300">
                            <Edit3 size={15} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); if (confirm("¿Eliminar cliente y toda su información asociada?")) onDelete(c.id); }} className="p-2.5 bg-slate-50 rounded-xl hover:bg-rose-100 hover:text-rose-600 transition-all text-slate-300">
                            <Trash2 size={15} />
                        </button>
                        <div className={`p-2 transition-transform duration-200 text-slate-300 ${expanded ? 'rotate-180' : ''}`}>
                            <ChevronDown size={16} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded details */}
            {expanded && (
                <div className="px-8 pb-8 pt-2 bg-gradient-to-b from-slate-50/50 to-white border-t border-slate-100 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
                        {/* Contactos */}
                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Contactos Clave ({clientContacts.length})</p>
                                <button onClick={() => onEdit(c)} className="text-[9px] font-black text-indigo-500 uppercase tracking-widest hover:text-indigo-700">Gestionar</button>
                            </div>
                            <div className="space-y-2">
                                {clientContacts.length > 0 ? clientContacts.slice(0, 5).map(con => (
                                    <div key={con.id} className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-slate-100">
                                        <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center font-black text-xs text-indigo-600">{con.name[0]}</div>
                                        <div>
                                            <p className="font-black text-[11px] uppercase text-slate-800 leading-tight">{con.name}</p>
                                            <p className="text-[9px] font-bold text-slate-400 uppercase">{con.position || 'Representante'}</p>
                                        </div>
                                    </div>
                                )) : (
                                    <p className="text-[10px] text-slate-300 italic font-black uppercase tracking-widest p-4 text-center bg-white rounded-2xl border border-dashed border-slate-100">Sin contactos registrados</p>
                                )}
                            </div>
                        </div>
                        {/* Proyectos */}
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">Proyectos ({clientProjects.length})</p>
                            <div className="flex flex-wrap gap-2">
                                {clientProjects.length > 0 ? clientProjects.map(p => (
                                    <span key={p.id} className="px-3 py-1.5 bg-white text-slate-500 rounded-xl text-[9px] font-black uppercase tracking-widest border border-slate-100 shadow-sm">{p.name}</span>
                                )) : (
                                    <span className="px-3 py-1.5 bg-rose-50 text-rose-400 rounded-xl text-[9px] font-black uppercase tracking-widest border border-rose-100 italic">Sin historial de proyectos</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ClientsManager({ clients, onAdd, onEdit, onDelete, projects, contacts = [] }) {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredClients = clients.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const sortedClients = [...filteredClients].sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div className="space-y-12 animate-in pb-20">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-8 px-4">
                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-slate-900 rounded-[2rem] flex items-center justify-center text-white shadow-2xl">
                            <Users size={32} />
                        </div>
                        <div>
                            <h3 className="text-4xl font-black uppercase tracking-tighter text-slate-800">Cartera de Clientes</h3>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">Administración de cuentas y empresas vinculadas</p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 flex-1 max-w-2xl bg-white p-2 rounded-[2rem] border border-slate-100 shadow-sm">
                    <div className="relative flex-1">
                        <Search className="absolute left-6 top-5 text-slate-300" size={20} />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, empresa o email..."
                            className="w-full pl-14 py-4 pr-6 bg-slate-50 border-none rounded-2xl font-bold text-slate-600 focus:ring-4 focus:ring-indigo-500/10 placeholder:text-slate-300 transition-all outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button onClick={onAdd} className="btn-primary py-4 px-10 text-[11px] uppercase tracking-widest shadow-xl shadow-indigo-100 whitespace-nowrap bg-indigo-600">
                        <Plus size={20} /> Nuevo Cliente
                    </button>
                </div>
            </div>

            {sortedClients.length === 0 ? (
                <div className="bg-white rounded-[4rem] p-32 text-center border border-slate-100 shadow-sm flex flex-col items-center justify-center">
                    <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center text-slate-200 mb-8">
                        <Contact size={48} />
                    </div>
                    <h4 className="text-xl font-black uppercase text-slate-800 tracking-tight mb-2">No se encontraron resultados</h4>
                    <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest italic">Intenta ajustar los términos de búsqueda</p>
                </div>
            ) : (
                <div className="bg-white rounded-[3rem] border border-slate-100 shadow-sm overflow-hidden">
                    {sortedClients.map((c, idx) => (
                        <ClientRow
                            key={c.id}
                            c={c}
                            idx={idx}
                            contacts={contacts}
                            projects={projects}
                            onEdit={onEdit}
                            onDelete={onDelete}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function CompanySettings({ data, currentUser, appSettings, handleAction }) {
    const alertSettings = data.alert_settings || {
        frequency: 'DAILY',
        send_hour: '08:00',
        is_active: 1,
        send_days: '1,2,3,4,5'
    };

    const subscriptions = data.alert_subscriptions || [];
    const team = data.team || [];

    const [subSearch, setSubSearch] = useState('');
    const [connectorToken, setConnectorToken] = useState('');
    const connectorConfig = data.connector_config || null;

    const handleUpdateSettings = async (updates) => {
        await handleAction('update_alert_settings', {
            company_id: currentUser.company_id,
            ...alertSettings,
            ...updates
        });
    };

    const handleUpdateSub = async (userId, alertType, active) => {
        await handleAction('update_alert_subscription', {
            company_id: currentUser.company_id,
            user_id: userId,
            alert_type: alertType,
            is_active: active ? 1 : 0
        });
    };

    const handleSaveConnector = async () => {
        if (!connectorToken) return;
        await handleAction('import_connector_token', { token: connectorToken });
        setConnectorToken('');
    };

    const filteredTeam = (team || []).filter(u =>
        u.company_id === currentUser?.company_id &&
        u.name.toLowerCase().includes(subSearch.toLowerCase())
    );

    return (
        <div className="space-y-12 animate-in pb-20">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-8 px-4">
                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-slate-900 rounded-[2rem] flex items-center justify-center text-white shadow-2xl">
                            <Settings2 size={32} />
                        </div>
                        <div>
                            <h3 className="text-4xl font-black uppercase tracking-tighter text-slate-800">Configuración de Empresa</h3>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">Gestión de alertas, reportes automáticos y preferencias globales</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-4 space-y-8">
                    <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm">
                        <h4 className="font-black text-sm uppercase tracking-widest text-slate-400 mb-8 flex items-center gap-3">
                            <Bell size={18} className="text-indigo-600" /> Reportes Automáticos
                        </h4>

                        <div className="space-y-8">
                            <div className="flex items-center justify-between p-6 bg-slate-50 rounded-3xl border border-slate-100">
                                <div>
                                    <p className="font-black text-xs uppercase text-slate-700">Estado del Módulo</p>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase mt-1">{alertSettings.is_active ? 'Activo y Programado' : 'Desactivado'}</p>
                                </div>
                                <button
                                    onClick={() => handleUpdateSettings({ is_active: parseInt(alertSettings.is_active) ? 0 : 1 })}
                                    className={`w-14 h-8 rounded-full transition-all relative ${parseInt(alertSettings.is_active) ? 'bg-indigo-600' : 'bg-slate-300'}`}
                                >
                                    <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${parseInt(alertSettings.is_active) ? 'right-1 shadow-md' : 'left-1 shadow-sm'}`}></div>
                                </button>
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Frecuencia de Envío</label>
                                <div className="grid grid-cols-2 gap-4">
                                    <button
                                        onClick={() => handleUpdateSettings({ frequency: 'DAILY' })}
                                        className={`py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border ${alertSettings.frequency === 'DAILY' ? 'bg-indigo-600 border-indigo-400 text-white shadow-lg' : 'bg-white border-slate-100 text-slate-400 hover:border-indigo-200'}`}
                                    >
                                        Diario
                                    </button>
                                    <button
                                        onClick={() => handleUpdateSettings({ frequency: 'WEEKLY' })}
                                        className={`py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border ${alertSettings.frequency === 'WEEKLY' ? 'bg-indigo-600 border-indigo-400 text-white shadow-lg' : 'bg-white border-slate-100 text-slate-400 hover:border-indigo-200'}`}
                                    >
                                        Semanal
                                    </button>
                                </div>
                            </div>

                            {alertSettings.frequency === 'WEEKLY' && (
                                <div className="space-y-4 animate-in slide-in-from-top-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Días de Envío</label>
                                    <div className="flex justify-between gap-1">
                                        {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((day, idx) => {
                                            const currentDays = (alertSettings.send_days || '1').split(',');
                                            const isActive = currentDays.includes(String(idx));
                                            return (
                                                <button
                                                    key={idx}
                                                    onClick={() => {
                                                        let nextDays;
                                                        if (isActive) {
                                                            nextDays = currentDays.filter(d => d !== String(idx));
                                                            if (nextDays.length === 0) nextDays = ['1'];
                                                        } else {
                                                            nextDays = [...currentDays, String(idx)];
                                                        }
                                                        handleUpdateSettings({ send_days: nextDays.join(',') });
                                                    }}
                                                    className={`w-10 h-10 rounded-xl text-[10px] font-black transition-all flex items-center justify-center border ${isActive ? 'bg-indigo-600 border-indigo-400 text-white shadow-md' : 'bg-white border-slate-100 text-slate-400 hover:border-indigo-200'}`}
                                                >
                                                    {day}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-4">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Hora de Envío (Formato 24h)</label>
                                <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                                    <Clock size={16} className="text-slate-400" />
                                    <select
                                        value={alertSettings.send_hour}
                                        onChange={(e) => handleUpdateSettings({ send_hour: e.target.value })}
                                        className="flex-1 bg-transparent border-none font-black text-indigo-600 text-sm focus:ring-0 outline-none"
                                    >
                                        {Array.from({ length: 48 }).map((_, i) => {
                                            const hour = Math.floor(i / 2);
                                            const minutes = i % 2 === 0 ? '00' : '30';
                                            const time = `${String(hour).padStart(2, '0')}:${minutes}`;
                                            return <option key={time} value={time}>{time} hs</option>;
                                        })}
                                    </select>
                                </div>
                            </div>

                            <div className="p-6 bg-indigo-50/50 rounded-3xl border border-indigo-100">
                                <p className="text-[9px] font-bold text-indigo-600 uppercase leading-relaxed tracking-tight">
                                    * Los reportes se envían a los suscriptores configurados analizando desviaciones de MDO y deudas pendientes.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm">
                        <h4 className="font-black text-sm uppercase tracking-widest text-slate-400 mb-8 flex items-center gap-3">
                            <Zap size={18} className="text-amber-500" /> Conexión Gestión Clientes
                        </h4>

                        <div className="space-y-8">
                            {/* TOKEN LOCAL */}
                            <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                                <label className="text-[10px] font-black uppercase text-indigo-600 tracking-widest ml-1 mb-2 block">Token de Conexión Local (Este Portal)</label>
                                {(() => {
                                    const myComp = (data.companies || []).find(c => c.id === currentUser?.company_id);
                                    if (myComp?.connector_token) {
                                        return (
                                            <div className="flex gap-3">
                                                <input readOnly value={myComp.connector_token} className="flex-1 bg-white border border-slate-200 rounded-2xl px-6 py-4 text-[10px] font-mono text-slate-600 outline-none shadow-sm" />
                                                <button
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(myComp.connector_token);
                                                        alert("Token copiado al portapapeles. Pégalo en el Portal de Clientes.");
                                                    }}
                                                    className="px-6 py-4 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase hover:bg-indigo-500 transition-all shadow-lg"
                                                >
                                                    Copiar
                                                </button>
                                            </div>
                                        );
                                    }
                                    return (
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const res = await handleAction('generate_connector_token', { company_id: currentUser.company_id });
                                                    if (res && res.status === 'success') {
                                                        alert("¡Token generado exitosamente!");
                                                        handleAction('sync_full');
                                                    }
                                                } catch (err) {
                                                    alert("Error al generar token: " + err.message);
                                                }
                                            }}
                                            className="w-full py-4 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-500 transition-all shadow-lg"
                                        >
                                            Generar Token de Conexión
                                        </button>
                                    );
                                })()}
                                <p className="text-[9px] text-slate-400 mt-3 px-1 italic">Copia este token y pégalo en la sección de configuración del Portal de Clientes.</p>
                            </div>

                            {/* CONECTOR REMOTO */}
                            <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                                <label className="text-[10px] font-black uppercase text-emerald-600 tracking-widest ml-1 mb-2 block">Conexión con Gestión Clientes (Remoto)</label>
                                {data.connector_config ? (
                                    <div className="mb-4 p-4 bg-emerald-50 rounded-2xl border border-emerald-100 flex items-center gap-3">
                                        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600">
                                            <Globe size={20} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-black text-[10px] uppercase text-emerald-700">Conectado exitosamente ✅</p>
                                            <p className="text-[9px] font-bold text-emerald-600/70 truncate mt-0.5">{data.connector_config.partner_url}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-[10px] text-slate-400 mb-4 ml-1 italic">Vincule este portal con el de Clientes para habilitar la sincronización de cobros.</p>
                                )}

                                <div className="flex flex-col gap-3">
                                    <input
                                        type="text"
                                        placeholder="Pegar token generado en Portal Clientes..."
                                        className="w-full px-6 py-4 bg-white border border-slate-200 rounded-2xl text-[10px] font-bold text-slate-600 focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none shadow-sm"
                                        value={connectorToken}
                                        onChange={(e) => setConnectorToken(e.target.value)}
                                    />
                                    <button
                                        onClick={handleSaveConnector}
                                        className="w-full py-4 bg-emerald-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-500 transition-all shadow-lg disabled:opacity-50"
                                        disabled={!connectorToken}
                                    >
                                        {data.connector_config ? 'Actualizar Conexión' : 'Conectar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-8 space-y-8">
                    <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                            <h4 className="font-black text-sm uppercase tracking-widest text-slate-400 flex items-center gap-3">
                                <UserCheck size={18} className="text-indigo-600" /> Suscriptores por Alerta
                            </h4>
                            <div className="relative flex-1 max-w-xs">
                                <Search className="absolute left-4 top-3 text-slate-300" size={14} />
                                <input
                                    type="text"
                                    placeholder="Buscar miembro..."
                                    className="w-full pl-10 py-2.5 pr-4 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold text-slate-600 focus:ring-4 focus:ring-indigo-500/10 placeholder:text-slate-300 transition-all outline-none"
                                    value={subSearch}
                                    onChange={(e) => setSubSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="text-[9px] font-black uppercase text-slate-400 tracking-widest border-b border-slate-50">
                                        <th className="py-6 text-left pl-4 font-black">Miembro del Equipo</th>
                                        <th className="py-6 text-center">Desvíos MDO</th>
                                        <th className="py-6 text-center">Saldos Pendientes</th>
                                        <th className="py-6 text-center">Tareas Retrasadas</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {(filteredTeam || []).map(u => {
                                        const isMdoSub = (subscriptions || []).some(s => s.user_id === u.id && s.alert_type === 'MDO_DRIFT' && s.is_active == 1);
                                        const isPaySub = (subscriptions || []).some(s => s.user_id === u.id && s.alert_type === 'PAYMENT_DUE' && s.is_active == 1);
                                        const isStallSub = (subscriptions || []).some(s => s.user_id === u.id && s.alert_type === 'TASK_STALED' && s.is_active == 1);

                                        return (
                                            <tr key={u.id} className="group hover:bg-slate-50/50 transition-colors">
                                                <td className="py-6 pl-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center font-black text-xs text-indigo-600 uppercase border border-indigo-100">{u.name[0]}</div>
                                                        <div>
                                                            <p className="font-black text-[11px] uppercase text-slate-800">{u.name}</p>
                                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter mt-0.5">{u.role}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-6 text-center">
                                                    <button onClick={() => handleUpdateSub(u.id, 'MDO_DRIFT', !isMdoSub)} className={`w-10 h-10 mx-auto rounded-xl flex items-center justify-center transition-all ${isMdoSub ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-sm' : 'bg-slate-50 text-slate-300 border border-transparent'}`}>
                                                        <Bell size={16} />
                                                    </button>
                                                </td>
                                                <td className="py-6 text-center">
                                                    <button onClick={() => handleUpdateSub(u.id, 'PAYMENT_DUE', !isPaySub)} className={`w-10 h-10 mx-auto rounded-xl flex items-center justify-center transition-all ${isPaySub ? 'bg-indigo-50 text-indigo-600 border border-indigo-100 shadow-sm' : 'bg-slate-50 text-slate-300 border border-transparent'}`}>
                                                        <Bell size={16} />
                                                    </button>
                                                </td>
                                                <td className="py-6 text-center">
                                                    <button onClick={() => handleUpdateSub(u.id, 'TASK_STALED', !isStallSub)} className={`w-10 h-10 mx-auto rounded-xl flex items-center justify-center transition-all ${isStallSub ? 'bg-amber-50 text-amber-600 border border-amber-100 shadow-sm' : 'bg-slate-50 text-slate-300 border border-transparent'}`}>
                                                        <Bell size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function FinancialPanel({
    data, onSelectProject, setActiveModal, setModalData, currentUser, t, uiPrefs, setUiPrefs, fNum, fCur, globalFxRate, setGlobalFxRate, onEditExpense }) {
    const [expandedClients, setExpandedClients] = useState({});
    const [clientSearch, setClientSearch] = useState('');
    const [activeSubTab, setActiveSubTab] = useState('CLIENTS'); // CLIENTS, MONTHLY, CALENDAR
    const [expandedYears, setExpandedYears] = useState({});
    const [selectedMonthCalendar, setSelectedMonthCalendar] = useState(() => {
        const d = new Date();
        return { year: d.getFullYear(), month: d.getMonth() + 1 };
    }); // {year, month}

    const isViewUsd = uiPrefs.financialMode === 'USD';
    const currentMode = uiPrefs.financialMode;

    const formatV = (val) => fCur(val, currentMode);

    const clients = [...(data.clients || [])].sort((a, b) => a.name.localeCompare(b.name));
    let projects = data.projects || [];
    const rawExpenses = data.expenses || [];

    // Filter projects for COMPANY_ADMIN if they are looking at financial panel
    if (currentUser && currentUser.role === 'COMPANY_ADMIN') {
        projects = projects.filter(p => p.company_id === currentUser.company_id);
    }

    // Unified project-specific rates (with sanity check)
    const projectRates = useMemo(() => {
        const rates = {};
        const gRate = Number(globalFxRate) || 1000;
        projects.forEach(p => {
            const pMovements = (data.expenses || []).filter(e => e.event_id === p.id);
            const txWithRate = pMovements.filter(m => Number(m.exchange_rate || m.fx || 0) > 0);
            rates[p.id] = txWithRate.length > 0
                ? Number(txWithRate.sort((a, b) => new Date(b.date || b.expense_date) - new Date(a.date || a.expense_date))[0].exchange_rate || txWithRate.sort((a, b) => new Date(b.date || b.expense_date) - new Date(a.date || a.expense_date))[0].fx)
                : gRate;
        });
        return rates;
    }, [projects, data.expenses, globalFxRate]);

    // Unified movement normalization helper
    const getMovementNormalized = useCallback((m) => {
        const p = projects.find(proj => proj.id === m.event_id);
        const pCur = (p?.base_currency || p?.display_currency || 'ARS');
        const mCur = (m.currency_original || m.currency || 'ARS');

        // 1. Determine the rate used for the BASE calculation (Project Currency)
        // If it's already in project currency, rate is 1.
        // If not, we MUST use the transaction's specific rate if available.
        let baseRate = Number(m.exchange_rate || m.fx || 0);

        if (mCur === pCur) {
            baseRate = 1;
        } else if (baseRate <= 0) {
            // Fallback to project/global rate ONLY if transaction has no rate (legacy or error)
            const fallbackRate = projectRates[m.event_id] || Number(globalFxRate) || 1000;
            baseRate = fallbackRate < 1 ? (1 / fallbackRate) : fallbackRate;
        } else {
            // Respect the saved rate. Handle inverse notation (1/1000 vs 1000)
            baseRate = baseRate < 1 ? (1 / baseRate) : baseRate;
        }

        let amtARS = 0;
        let amtUSD = 0;

        // Calculate Project Base Values
        if (mCur === 'ARS') {
            amtARS = Number(m.amount_original || 0);
            amtUSD = amtARS / (baseRate || 1);
        } else {
            amtUSD = Number(m.amount_original || 0);
            amtARS = amtUSD * (baseRate || 1);
        }

        let displayVal = isViewUsd ? amtUSD : amtARS;

        return {
            ...m,
            amount_normalized_ars: amtARS,
            amount_normalized_usd: amtUSD,
            val: displayVal
        };
    }, [globalFxRate, isViewUsd, projectRates, projects]);

    const allowedProjectIds = projects.map(p => p.id);
    const expenses = rawExpenses.filter(e => allowedProjectIds.includes(e.event_id)).map(getMovementNormalized);

    const toggleClient = (clientId) => {
        setExpandedClients(prev => ({
            ...prev,
            [clientId]: !prev[clientId]
        }));
    };

    // Agrupar proyectos por cliente
    const clientsWithProjects = clients.map(client => {
        const clientProjects = projects.filter(p => p.client_id === client.id);

        const totals = clientProjects.reduce((acc, p) => {
            const isUsd = (p.base_currency === 'USD');
            const rate = Number(globalFxRate);
            const pMovements = expenses.filter(e => e.event_id === p.id);
            const pIncomes = pMovements.filter(m => m.movement_type === 'INCOME');
            const pExpenses = pMovements.filter(m => m.movement_type === 'EXPENSE');

            // Get effective rate from pre-calculated map
            const pRate = projectRates[p.id] || rate;

            // 1. VENTA / INGRESOS (Contract Value)
            const pContractBase = Number(p.budget_total_contract || 0);
            const pIncomeBase = pIncomes.reduce((s, m) => s + (isUsd ? m.amount_normalized_usd : m.amount_normalized_ars), 0);
            const pIncomeValue = pIncomes.reduce((s, m) => s + m.val, 0);

            const pPendingBase = Math.max(0, pContractBase - pIncomeBase);
            const pPendingARS = pPendingBase * (isUsd ? pRate : 1);
            const pBudgetARS = (pContractBase * (isUsd ? pRate : 1));

            // 2. GASTOS / COSTOS
            const pBudgetGlobal = Number(p.budget_materials_global || 0) + Number(p.budget_mdo_global || 0);
            const pBudgetRubro = (data.budgets || []).filter(b => b.event_id === p.id).reduce((s, b) => s + Number(b.budget_amount || 0) + Number(b.budget_mdo || 0), 0);
            const pCostBudgetBase = pBudgetGlobal > 0 ? pBudgetGlobal : pBudgetRubro;

            const pExpenseARS = pExpenses.reduce((s, m) => s + m.amount_normalized_ars, 0);
            const pExpenseBase = pExpenses.reduce((s, m) => s + (isUsd ? m.amount_normalized_usd : m.amount_normalized_ars), 0);
            const pExpenseValue = pExpenses.reduce((s, m) => s + m.val, 0);

            const pCostBudgetARS = pCostBudgetBase * (isUsd ? pRate : 1);
            const pCostPendingARS = Math.max(0, pCostBudgetARS - pExpenseARS);

            const pOCs = (data.purchase_orders || []).filter(oc => oc.event_id === p.id);
            const pOcTotalBase = pOCs.reduce((s, oc) => s + Number(oc.amount_total || 0), 0);
            const pOcBalanceBase = pOCs.reduce((s, oc) => s + Number(oc.balance || 0), 0);
            const pOcTotalARS = pOcTotalBase * (isUsd ? pRate : 1);
            const pOcBalanceARS = pOcBalanceBase * (isUsd ? pRate : 1);

            const pValIncome = pIncomeValue;
            const pValExpense = pExpenseValue;
            const pValBudget = isViewUsd ? (isUsd ? pContractBase : (pBudgetARS / (Number(globalFxRate) || 1000))) : pBudgetARS;
            const pValPending = isViewUsd ? (isUsd ? pPendingBase : (pPendingARS / (Number(globalFxRate) || 1000))) : pPendingARS;
            const pValCostBudget = isViewUsd ? (isUsd ? pCostBudgetBase : (pCostBudgetARS / (Number(globalFxRate) || 1000))) : pCostBudgetARS;
            const pValCostPending = isViewUsd ? (isUsd ? (pCostBudgetBase - pExpenseBase) : (pCostPendingARS / (Number(globalFxRate) || 1000))) : pCostPendingARS;

            return {
                income: acc.income + pValIncome,
                expense: acc.expense + pValExpense,
                profit: acc.profit + (pValIncome - pValExpense),
                budget: acc.budget + pValBudget,
                pending: acc.pending + pValPending,
                costBudget: (acc.costBudget || 0) + pValCostBudget,
                costPending: (acc.costPending || 0) + pValCostPending,
                ocTotal: (acc.ocTotal || 0) + (isViewUsd ? (isUsd ? pOcTotalBase : (pOcTotalARS / (Number(globalFxRate) || 1000))) : pOcTotalARS),
                ocBalance: (acc.ocBalance || 0) + (isViewUsd ? (isUsd ? pOcBalanceBase : (pOcBalanceARS / (Number(globalFxRate) || 1000))) : pOcBalanceARS)
            };
        }, { income: 0, expense: 0, profit: 0, budget: 0, pending: 0, costBudget: 0, costPending: 0, ocTotal: 0, ocBalance: 0 });

        return { ...client, projects: clientProjects, totals };
    });

    // Filtrado por búsqueda (Cliente o Proyecto)
    const finalClientsToShow = clientsWithProjects.filter(c => {
        if (!clientSearch) return c.projects.length > 0;
        const search = clientSearch.toLowerCase();
        const matchesClient = c.name.toLowerCase().includes(search);
        const matchesProject = c.projects.some(p => p.name.toLowerCase().includes(search));
        return matchesClient || matchesProject;
    });

    // Proyectos sin cliente (Totales globales vs mostrados)
    const allOrphans = projects.filter(p => !p.client_id);
    const orphanedProjectsToShow = allOrphans.filter(p => !clientSearch || p.name.toLowerCase().includes(clientSearch.toLowerCase()));

    const orphanedTotals = allOrphans.reduce((acc, p) => {
        const isUsd = (p.base_currency === 'USD');
        const rate = Number(globalFxRate);
        const pMovements = expenses.filter(e => e.event_id === p.id);
        const pIncomes = pMovements.filter(m => m.movement_type === 'INCOME');
        const pExpenses = pMovements.filter(m => m.movement_type === 'EXPENSE');

        // Get effective rate from pre-calculated map
        const pRate = projectRates[p.id] || rate;

        // 1. VENTA / INGRESOS (Contract Value)
        const pContractBase = Number(p.budget_total_contract || 0);
        const pIncomeBase = pIncomes.reduce((s, m) => s + (isUsd ? m.amount_normalized_usd : m.amount_normalized_ars), 0);
        const pIncomeValue = pIncomes.reduce((s, m) => s + m.val, 0);

        const pPendingBase = Math.max(0, pContractBase - pIncomeBase);
        const pPendingARS = pPendingBase * (isUsd ? pRate : 1);
        const pBudgetARS = (pContractBase * (isUsd ? pRate : 1));

        // 2. GASTOS / COSTOS
        const pBudgetGlobal = Number(p.budget_materials_global || 0) + Number(p.budget_mdo_global || 0);
        const pBudgetRubro = (data.budgets || []).filter(b => b.event_id === p.id).reduce((s, b) => s + Number(b.budget_amount || 0) + Number(b.budget_mdo || 0), 0);
        const pCostBudgetBase = pBudgetGlobal > 0 ? pBudgetGlobal : pBudgetRubro;

        const pExpenseARS = pExpenses.reduce((s, m) => s + m.amount_normalized_ars, 0);
        const pExpenseBase = pExpenses.reduce((s, m) => s + (isUsd ? m.amount_normalized_usd : m.amount_normalized_ars), 0);
        const pExpenseValue = pExpenses.reduce((s, m) => s + m.val, 0);

        const pCostBudgetARS = pCostBudgetBase * (isUsd ? pRate : 1);
        const pCostPendingARS = Math.max(0, pCostBudgetARS - pExpenseARS);

        const pOCs = (data.purchase_orders || []).filter(oc => oc.event_id === p.id);
        const pOcTotalBase = pOCs.reduce((s, oc) => s + Number(oc.amount_total || 0), 0);
        const pOcBalanceBase = pOCs.reduce((s, oc) => s + Number(oc.balance || 0), 0);
        const pOcTotalARS = pOcTotalBase * (isUsd ? pRate : 1);
        const pOcBalanceARS = pOcBalanceBase * (isUsd ? pRate : 1);

        const pValIncome = pIncomeValue;
        const pValExpense = pExpenseValue;
        const pValBudget = isViewUsd ? (isUsd ? pContractBase : (pBudgetARS / (Number(globalFxRate) || 1000))) : pBudgetARS;
        const pValPending = isViewUsd ? (isUsd ? pPendingBase : (pPendingARS / (Number(globalFxRate) || 1000))) : pPendingARS;
        const pValCostBudget = isViewUsd ? (isUsd ? pCostBudgetBase : (pCostBudgetARS / (Number(globalFxRate) || 1000))) : pCostBudgetARS;
        const pValCostPending = isViewUsd ? (isUsd ? (pCostBudgetBase - pExpenseBase) : (pCostPendingARS / (Number(globalFxRate) || 1000))) : pCostPendingARS;

        return {
            income: acc.income + pValIncome,
            expense: acc.expense + pValExpense,
            profit: acc.profit + (pValIncome - pValExpense),
            budget: acc.budget + pValBudget,
            pending: acc.pending + pValPending,
            costBudget: (acc.costBudget || 0) + pValCostBudget,
            costPending: (acc.costPending || 0) + pValCostPending,
            ocTotal: (acc.ocTotal || 0) + (isViewUsd ? (isUsd ? pOcTotalBase : (pOcTotalARS / (Number(globalFxRate) || 1000))) : pOcTotalARS),
            ocBalance: (acc.ocBalance || 0) + (isViewUsd ? (isUsd ? pOcBalanceBase : (pOcBalanceARS / (Number(globalFxRate) || 1000))) : pOcBalanceARS)
        };
    }, { income: 0, expense: 0, profit: 0, budget: 0, pending: 0, costBudget: 0, costPending: 0, ocTotal: 0, ocBalance: 0 });

    const allIncomes = clientsWithProjects.reduce((a, b) => a + b.totals.income, 0) + orphanedTotals.income;
    const allExpenses = clientsWithProjects.reduce((a, b) => a + b.totals.expense, 0) + orphanedTotals.expense;
    const allBudgets = clientsWithProjects.reduce((a, b) => a + b.totals.budget, 0) + orphanedTotals.budget;
    const allPending = clientsWithProjects.reduce((a, b) => a + b.totals.pending, 0) + orphanedTotals.pending;

    // Diagnóstico: proyectos que hacen que "Cobrado" supere a "Venta Total"
    // (cobros sin valor de contrato cargado, o cobrado mayor al contrato). El saldo se calcula por proyecto con mínimo 0.
    const incomeIssues = projects.map(p => {
        const isUsd = p.base_currency === 'USD';
        const contract = Number(p.budget_total_contract || 0);
        const income = expenses.filter(e => e.event_id === p.id && e.movement_type === 'INCOME')
            .reduce((s, m) => s + (isUsd ? m.amount_normalized_usd : m.amount_normalized_ars), 0);
        if (income <= 0) return null;
        if (contract <= 0) return { p, reason: 'sin valor de contrato' };
        if (income > contract * 1.01) return { p, reason: 'cobrado mayor al contrato' };
        return null;
    }).filter(Boolean);
    const allCostBudgets = clientsWithProjects.reduce((a, b) => a + (b.totals.costBudget || 0), 0) + orphanedTotals.costBudget;
    const allCostPending = clientsWithProjects.reduce((a, b) => a + (b.totals.costPending || 0), 0) + orphanedTotals.costPending;
    const allProfit = allIncomes - allExpenses;

    return (
        <div className="space-y-12 animate-in pb-20">
            {/* Header Global Sticky */}
            <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md pt-6 pb-6 mb-6 border-b border-slate-100 -mx-4 px-8 space-y-6">
                <div className="space-y-1">
                    <h3 className="text-3xl font-black uppercase tracking-tight text-slate-800 leading-none">Control Financiero Global</h3>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.2em]">Resumen de Rentabilidad y Proyecciones</p>
                </div>
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="bg-white px-5 py-3 rounded-2xl border border-indigo-100 shadow-sm flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600 cursor-pointer hover:bg-indigo-100 transition-all shadow-sm" onClick={() => setUiPrefs({ financialMode: isViewUsd ? 'ARS' : 'USD' })}>
                            <DollarSign size={16} />
                        </div>
                        <div>
                            <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5 leading-none">Moneda Vista</p>
                            <p className="font-black text-sm text-indigo-600 leading-none">{currentMode}</p>
                        </div>
                    </div>
                    <div className="bg-white px-5 py-3 rounded-2xl border border-indigo-100 shadow-sm flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600"><TrendingUp size={16} /></div>
                        <div>
                            <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5 leading-none">Ref. Dólar ($)</p>
                            <input
                                type="number"
                                className="w-16 bg-transparent border-none p-0 font-black text-sm focus:ring-0 text-indigo-600 leading-none"
                                value={globalFxRate}
                                onChange={(e) => {
                                    setGlobalFxRate(e.target.value);
                                    localStorage.setItem('em_global_fx', e.target.value);
                                }}
                            />
                        </div>
                    </div>
                    <div className="bg-white px-5 py-3 rounded-2xl border border-slate-100 shadow-sm text-center">
                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 leading-none">VENTA TOTAL</p>
                        <p className="text-base font-black text-slate-900 tracking-tighter leading-none">{formatV(allBudgets)}</p>
                    </div>
                    <div className="bg-white px-5 py-3 rounded-2xl border border-slate-100 shadow-sm text-center">
                        <p className="text-[8px] font-black text-emerald-400 uppercase tracking-widest mb-1 leading-none">COBRADO</p>
                        <p className="text-base font-black text-emerald-600 tracking-tighter leading-none">{formatV(allIncomes)}</p>
                    </div>
                    <div className="bg-white px-5 py-3 rounded-2xl border border-indigo-100 shadow-sm text-center">
                        <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest mb-1 leading-none">SALDO COBRO</p>
                        <p className="text-base font-black text-indigo-600 tracking-tighter leading-none">{formatV(allPending)}</p>
                    </div>
                    <div className="bg-white px-5 py-3 rounded-2xl border border-slate-100 shadow-sm text-center">
                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 leading-none">PPTO. GASTOS</p>
                        <p className="text-base font-black text-slate-900 tracking-tighter leading-none">{formatV(allCostBudgets)}</p>
                    </div>
                    <div className="bg-white px-5 py-3 rounded-2xl border border-slate-100 shadow-sm text-center">
                        <p className="text-[8px] font-black text-rose-400 uppercase tracking-widest mb-1 leading-none">EGRESOS</p>
                        <p className="text-base font-black text-rose-600 tracking-tighter leading-none">{formatV(allExpenses)}</p>
                    </div>
                    <div className="px-6 py-4 rounded-2xl border shadow-lg text-center bg-indigo-600 text-white border-indigo-500 shadow-indigo-100">
                        <p className="text-[8px] font-black text-white/50 uppercase tracking-widest mb-1 leading-none">UTILIDAD</p>
                        <p className="text-xl font-black tracking-tighter leading-none">{formatV(allProfit)}</p>
                    </div>
                    <div className="bg-white px-5 py-3 rounded-2xl border border-indigo-100 shadow-sm text-center">
                        <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest mb-1 leading-none">ROI %</p>
                        <p className="text-base font-black text-indigo-600 tracking-tighter leading-none">
                            {allIncomes > 0 ? Math.round((allProfit / allIncomes) * 100) : 0}%
                        </p>
                    </div>
                </div>

                {incomeIssues.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-100 rounded-2xl px-5 py-3 text-xs text-amber-800">
                        <AlertTriangle size={14} className="shrink-0" />
                        <span className="font-bold">Revisá el valor de contrato:</span>
                        <span>estos proyectos tienen cobros que no se descuentan del saldo, por eso "Cobrado" puede superar a "Venta Total":</span>
                        {incomeIssues.map(({ p, reason }) => (
                            <button key={p.id} onClick={() => onSelectProject && onSelectProject(p.id)} className="px-2.5 py-1 rounded-lg bg-white border border-amber-200 font-semibold hover:border-amber-400 transition-all" title="Abrir proyecto">
                                {p.name} <span className="text-amber-600 font-normal">({reason})</span>
                            </button>
                        ))}
                    </div>
                )}

                {/* Sub-Tabs Nav & Search (Also inside sticky) */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex bg-slate-100/50 p-1 rounded-2xl border border-slate-100">
                        <button
                            onClick={() => setActiveSubTab('CASHFLOW')}
                            className={`px-6 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeSubTab === 'CASHFLOW' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            <TrendingUp size={14} /> Flujo de Caja
                        </button>
                        <button
                            onClick={() => setActiveSubTab('CLIENTS')}
                            className={`px-6 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeSubTab === 'CLIENTS' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            <Wallet size={14} /> Saldos por Clientes
                        </button>

                        <button
                            onClick={() => setActiveSubTab('CALENDAR')}
                            className={`px-6 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeSubTab === 'CALENDAR' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            Vista Calendario
                        </button>
                    </div>

                    {activeSubTab === 'CLIENTS' && (
                        <div className="bg-white px-4 py-2 rounded-xl border border-slate-100 shadow-sm flex items-center gap-3 w-full md:w-auto">
                            <Search size={14} className="text-slate-400" />
                            <input
                                type="text"
                                className="bg-transparent border-none p-0 font-bold text-xs focus:ring-0 text-slate-700 placeholder:text-slate-300 min-w-48"
                                placeholder="Buscar cliente o proyecto..."
                                value={clientSearch}
                                onChange={(e) => setClientSearch(e.target.value)}
                            />
                        </div>
                    )}
                </div>
            </div>


            {activeSubTab === 'CASHFLOW' ? (
                <div className="animate-in">
                    <CashFlowChart
                        movements={expenses}
                        baseCurrency={currentMode}
                        type="COMPANY"
                        fCur={fCur}
                        fNum={fNum}
                        conversionRate={1}
                        projects={projects}
                        clients={clients}
                    />
                </div>
            ) : activeSubTab === 'CLIENTS' ? (
                /* Lista de Clientes con Acordeón */
                <div className="space-y-6">
                    {finalClientsToShow.map(c => {
                        const isExpanded = expandedClients[c.id];
                        const margin = c.totals.income > 0 ? Math.round((c.totals.profit / c.totals.income) * 100) : 0;

                        return (
                            <div key={c.id} className="bg-white rounded-[3rem] border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                {/* Client Row (Trigger) */}
                                <div
                                    onClick={() => toggleClient(c.id)}
                                    className={`px-8 py-4 grid grid-cols-12 items-center cursor-pointer transition-colors ${isExpanded ? 'bg-slate-900 border-b border-slate-800 text-white' : 'hover:bg-slate-50'}`}
                                >
                                    <div className="col-span-3 flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors overflow-hidden border border-slate-100 shrink-0 ${isExpanded ? 'bg-indigo-600' : 'bg-slate-100 text-slate-400'}`}>
                                            {c.logo_url ? (
                                                <img src={c.logo_url} alt={c.name} className="w-full h-full object-contain p-1.5 mix-blend-multiply bg-white" />
                                            ) : (
                                                <Building2 size={18} className={isExpanded ? 'text-white' : ''} />
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <h4 className={`text-base font-black uppercase tracking-tighter truncate ${isExpanded ? 'text-white' : 'text-slate-800'}`}>{c.name}</h4>
                                            <p className={`text-[9px] font-bold uppercase tracking-widest ${isExpanded ? 'text-indigo-400' : 'text-slate-400'}`}>
                                                {c.projects.length} Proyectos
                                            </p>
                                        </div>
                                    </div>

                                    <div className="col-span-1 text-right border-r border-slate-100/10 pr-4">
                                        <p className={`text-[7px] font-black uppercase tracking-widest mb-0.5 ${isExpanded ? 'text-white/30' : 'text-slate-400'}`}>VENTA</p>
                                        <p className={`text-sm font-black tracking-tighter ${isExpanded ? 'text-white' : 'text-slate-800'}`}>{formatV(c.totals.budget)}</p>
                                    </div>

                                    <div className="col-span-1 text-right border-r border-slate-100/10 pr-4">
                                        <p className={`text-[7px] font-black uppercase tracking-widest mb-0.5 ${isExpanded ? 'text-white/30' : 'text-slate-400'}`}>COBRADO</p>
                                        <p className={`text-sm font-black tracking-tighter ${isExpanded ? 'text-emerald-400' : 'text-emerald-600'}`}>{formatV(c.totals.income)}</p>
                                    </div>
                                    <div className="col-span-1 text-right border-r border-slate-100/10 pr-4">
                                        <p className={`text-[7px] font-black uppercase tracking-widest mb-0.5 ${isExpanded ? 'text-white/30' : 'text-slate-400'}`}>S. COBRO</p>
                                        <p className={`text-sm font-black tracking-tighter ${isExpanded ? 'text-indigo-300' : 'text-indigo-500'}`}>{formatV(c.totals.pending)}</p>
                                        <p className={`text-[6px] font-bold uppercase ${isExpanded ? 'text-indigo-400/50' : 'text-slate-300'}`}>OC: {formatV(c.totals.ocBalance)}</p>
                                    </div>
                                    <div className="col-span-1 text-right border-r border-slate-100/10 pr-4">
                                        <p className={`text-[7px] font-black uppercase tracking-widest mb-0.5 ${isExpanded ? 'text-white/30' : 'text-slate-400'}`}>P. GASTOS</p>
                                        <p className={`text-sm font-black tracking-tighter ${isExpanded ? 'text-white/80' : 'text-slate-800'}`}>{formatV(c.totals.costBudget)}</p>
                                    </div>
                                    <div className="col-span-1 text-right border-r border-slate-100/10 pr-4">
                                        <p className={`text-[7px] font-black uppercase tracking-widest mb-0.5 ${isExpanded ? 'text-white/30' : 'text-slate-400'}`}>EJECUTADO</p>
                                        <p className={`text-sm font-black tracking-tighter ${isExpanded ? 'text-rose-400' : 'text-rose-600'}`}>{formatV(c.totals.expense)}</p>
                                    </div>
                                    <div className="col-span-1 text-right border-r border-indigo-100/10 pr-4 pl-2">
                                        <p className={`text-[7px] font-black uppercase tracking-widest mb-0.5 ${isExpanded ? 'text-white/30' : 'text-slate-400'}`}>S. GASTO</p>
                                        <p className={`text-sm font-black tracking-tighter ${isExpanded ? 'text-orange-300' : 'text-orange-500'}`}>{formatV(c.totals.costBudget - c.totals.expense)}</p>
                                    </div>
                                    <div className="col-span-1 text-right border-r border-indigo-100/10 pr-4">
                                        <p className={`text-[7px] font-black uppercase tracking-widest mb-0.5 ${isExpanded ? 'text-white/30' : 'text-slate-400'}`}>UTILIDAD</p>
                                        <p className={`text-sm font-black tracking-tighter ${c.totals.profit >= 0 ? (isExpanded ? 'text-white' : 'text-emerald-500') : 'text-rose-500'}`}>{formatV(c.totals.profit)}</p>
                                    </div>
                                    <div className="col-span-1 text-right border-r border-indigo-100/10 pr-4">
                                        <p className={`text-[7px] font-black uppercase tracking-widest mb-0.5 ${isExpanded ? 'text-white/30' : 'text-slate-400'}`}>ROI %</p>
                                        <p className={`text-sm font-black tracking-tighter ${isExpanded ? 'text-white' : 'text-slate-800'}`}>{margin}%</p>
                                    </div>

                                    <div className="col-span-1 text-right flex items-center justify-end gap-4 min-w-[80px]">
                                        <div className={`p-2 rounded-full transition-transform ${isExpanded ? 'rotate-180 bg-white/10 text-white' : 'text-slate-300 bg-slate-50'}`}>
                                            <ChevronDown size={20} />
                                        </div>
                                    </div>
                                </div>

                                {/* Projects Sub-rows */}
                                {isExpanded && (
                                    <div className="bg-white">
                                        {c.projects.length === 0 ? (
                                            <div className="p-12 text-center text-slate-300 font-bold uppercase text-[10px] tracking-widest italic">No hay proyectos para este cliente</div>
                                        ) : (
                                            <div className="divide-y divide-slate-50">
                                                {c.projects.map(p => {
                                                    const pMovements = expenses.filter(e => e.event_id === p.id);
                                                    const pIncomes = pMovements.filter(m => m.movement_type === 'INCOME');
                                                    const pExpenses = pMovements.filter(m => m.movement_type === 'EXPENSE');

                                                    const isUsd = p.base_currency === 'USD';
                                                    const rate = Number(globalFxRate);

                                                    // Get effective rate from transactions
                                                    const txWithRate = pMovements.filter(m => Number(m.exchange_rate) > 1);
                                                    const pRate = txWithRate.length > 0
                                                        ? Number(txWithRate.sort((a, b) => new Date(b.date || b.expense_date) - new Date(a.date || a.expense_date))[0].exchange_rate)
                                                        : rate;

                                                    // 1. VENTA / COBRANZA
                                                    const pContractBase = Number(p.budget_total_contract || 0);
                                                    const pIncomeBase = pIncomes.reduce((s, m) => s + (isUsd ? m.amount_normalized_usd : m.amount_normalized_ars), 0);
                                                    const pValIncome = pIncomes.reduce((s, m) => s + m.val, 0);

                                                    const pPendingBase = Math.max(0, pContractBase - pIncomeBase);
                                                    const pPendingARS = pPendingBase * (isUsd ? pRate : 1);
                                                    const pBudgetARS = (pContractBase * (isUsd ? pRate : 1));

                                                    // 2. GASTOS / COSTOS
                                                    const bGlobal = Number(p.budget_materials_global || 0) + Number(p.budget_mdo_global || 0);
                                                    const bRubro = (data.budgets || []).filter(b => b.event_id === p.id).reduce((s, b) => s + Number(b.budget_amount || 0) + Number(b.budget_mdo || 0), 0);
                                                    const pCostBudgetBase = bGlobal > 0 ? bGlobal : bRubro;

                                                    const pExpenseBase = pExpenses.reduce((s, m) => s + (isUsd ? m.amount_normalized_usd : m.amount_normalized_ars), 0);
                                                    const pExpenseARS = pExpenses.reduce((s, m) => s + m.amount_normalized_ars, 0);
                                                    const pValExpense = pExpenses.reduce((s, m) => s + m.val, 0);

                                                    const pCostBudgetARS = pCostBudgetBase * (isUsd ? pRate : 1);
                                                    const pCostPendingARS = Math.max(0, pCostBudgetARS - pExpenseARS);

                                                    const pOCs = (data.purchase_orders || []).filter(oc => oc.event_id === p.id);
                                                    const pOcTotalBase = pOCs.reduce((s, oc) => s + Number(oc.amount_total || 0), 0);
                                                    const pOcBalanceBase = pOCs.reduce((s, oc) => s + Number(oc.balance || 0), 0);
                                                    const pOcTotalARS = pOcTotalBase * (isUsd ? pRate : 1);
                                                    const pOcBalanceARS = pOcBalanceBase * (isUsd ? pRate : 1);

                                                    // Get visual values
                                                    let pValBudget = 0;
                                                    let pValPending = 0;
                                                    let pValCostBudget = 0;
                                                    let pValCostPending = 0;
                                                    const gRate = Number(globalFxRate) || 1000;

                                                    if (isViewUsd) {
                                                        pValBudget = isUsd ? pContractBase : (pContractBase / gRate);
                                                        pValPending = isUsd ? pPendingBase : (pPendingBase / gRate);
                                                        pValCostBudget = isUsd ? pCostBudgetBase : (pCostBudgetBase / gRate);
                                                        pValCostPending = isUsd ? (pCostBudgetBase - pExpenseBase) : ((pCostBudgetBase - (pExpenseARS / gRate))); // This logic is tricky, but let's stick to base consistency
                                                        // Simplified:
                                                        pValCostPending = pValCostBudget - pValExpense;
                                                    } else {
                                                        pValBudget = isUsd ? (pContractBase * gRate) : pContractBase;
                                                        pValPending = isUsd ? (pPendingBase * gRate) : pPendingBase;
                                                        pValCostBudget = isUsd ? (pCostBudgetBase * gRate) : pCostBudgetBase;
                                                        pValCostPending = pValCostBudget - pValExpense;
                                                    }

                                                    // RESULTADOS
                                                    const pProfit = pValIncome - pValExpense;
                                                    const pMargin = pValIncome > 0 ? Math.round((pProfit / pValIncome) * 100) : 0;

                                                    return (
                                                        <div
                                                            key={p.id}
                                                            onClick={() => onSelectProject(p.id)}
                                                            className="px-8 py-6 grid grid-cols-12 items-center hover:bg-indigo-50/30 transition-all cursor-pointer group border-b border-slate-50 last:border-0"
                                                        >
                                                            <div className="col-span-3">
                                                                <h5 className="font-black text-sm uppercase tracking-tight text-slate-700 group-hover:text-indigo-600 transition-colors truncate">{p.name}</h5>
                                                                <p className="text-[8px] font-bold text-slate-400 uppercase mt-1">{isUsd ? 'Base USD' : 'Base ARS'}</p>
                                                            </div>

                                                            <div className="col-span-1 text-right pr-4">
                                                                <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Venta</p>
                                                                <p className="font-black text-[11px] text-slate-900">{formatV(pValBudget)}</p>
                                                            </div>

                                                            <div className="col-span-1 text-right pr-4">
                                                                <p className="text-[7px] font-black text-emerald-400 uppercase tracking-widest mb-0.5">Cobrado</p>
                                                                <p className="font-black text-[11px] text-emerald-600 focus:outline-none">{formatV(pValIncome)}</p>
                                                            </div>

                                                            <div className="col-span-1 text-right border-r border-slate-100 pr-4">
                                                                <p className="text-[7px] font-black text-indigo-400 uppercase tracking-widest mb-0.5">S. Cobro</p>
                                                                <p className="font-black text-[11px] text-indigo-600">{formatV(pValPending)}</p>
                                                                <p className="text-[6px] font-bold text-slate-300 uppercase leading-none mt-1">OC Pendiente: {formatV(isViewUsd ? (isUsd ? pOcBalanceBase : (pOcBalanceARS / rate)) : pOcBalanceARS)}</p>
                                                            </div>

                                                            <div className="col-span-1 text-right pr-4 pl-4">
                                                                <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5">P. Gasto</p>
                                                                <p className="font-black text-[11px] text-slate-900">{formatV(pValCostBudget)}</p>
                                                            </div>

                                                            <div className="col-span-1 text-right pr-4">
                                                                <p className="text-[7px] font-black text-rose-400 uppercase tracking-widest mb-0.5">Ejecutado</p>
                                                                <p className="font-black text-[11px] text-rose-600">{formatV(pValExpense)}</p>
                                                            </div>

                                                            <div className="col-span-1 text-right border-r border-slate-100 pr-4">
                                                                <p className="text-[7px] font-black text-orange-400 uppercase tracking-widest mb-0.5">S. Gasto</p>
                                                                <p className="font-black text-[11px] text-orange-600">{formatV(pValCostPending)}</p>
                                                            </div>

                                                            <div className="col-span-1 text-right pr-4 pl-4">
                                                                <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Utilidad</p>
                                                                <p className={`font-black text-[11px] ${pProfit >= 0 ? 'text-slate-900' : 'text-rose-600'}`}>{formatV(pProfit)}</p>
                                                            </div>

                                                            <div className="col-span-1 text-right">
                                                                <p className="text-[7px] font-black text-indigo-400 uppercase tracking-widest mb-0.5">Margen</p>
                                                                <p className="font-black text-[10px] text-indigo-600">{pMargin}%</p>
                                                            </div>

                                                            <div className="col-span-1 flex justify-end">
                                                                <div className="p-1.5 bg-slate-50 rounded-lg group-hover:bg-indigo-600 group-hover:text-white transition-all">
                                                                    <ArrowRight size={12} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Proyectos Huérfanos */}
                    {orphanedProjectsToShow.length > 0 ? (
                        <div className="bg-slate-50 rounded-[3rem] border-2 border-dashed border-slate-200 overflow-hidden">
                            <div className="px-8 py-4 flex justify-between items-center">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-slate-300">
                                        <AlertTriangle size={18} />
                                    </div>
                                    <div>
                                        <h4 className="text-base font-black text-slate-400 uppercase tracking-tighter italic">Proyectos sin Cliente</h4>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Requieren asignación para reporte consolidado</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Huérfanos</p>
                                    <p className={`text-xl font-black tracking-tighter ${orphanedTotals.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{formatV(orphanedTotals.profit)}</p>
                                </div>
                            </div>
                            <div className="divide-y divide-slate-100 bg-white/40">
                                {orphanedProjectsToShow.map(p => {
                                    const pMovements = expenses.filter(e => e.event_id === p.id);
                                    const pIncomes = pMovements.filter(m => m.movement_type === 'INCOME');
                                    const pExpenses = pMovements.filter(m => m.movement_type === 'EXPENSE');

                                    const isUsd = (p.base_currency === 'USD');
                                    const rate = Number(globalFxRate);

                                    // 1. VENTA / COBRANZA
                                    const pContractBase = Number(p.budget_total_contract || 0);
                                    const pIncomeBase = pIncomes.reduce((s, m) => s + (isUsd ? m.amount_normalized_usd : m.amount_normalized_ars), 0);
                                    const pValIncome = pIncomes.reduce((s, m) => s + m.val, 0);

                                    const pPendingBase = Math.max(0, pContractBase - pIncomeBase);
                                    const pPendingARS = pPendingBase * (isUsd ? rate : 1);
                                    const pBudgetARS = (pContractBase * (isUsd ? rate : 1));

                                    // 2. GASTOS / COSTOS
                                    const bGlobal = Number(p.budget_materials_global || 0) + Number(p.budget_mdo_global || 0);
                                    const bRubro = (data.budgets || []).filter(b => b.event_id === p.id).reduce((s, b) => s + Number(b.budget_amount || 0) + Number(b.budget_mdo || 0), 0);
                                    const pCostBudgetBase = bGlobal > 0 ? bGlobal : bRubro;

                                    const pExpenseBase = pExpenses.reduce((s, m) => s + (isUsd ? m.amount_normalized_usd : m.amount_normalized_ars), 0);
                                    const pExpenseARS = pExpenses.reduce((s, m) => s + m.amount_normalized_ars, 0);
                                    const pValExpense = pExpenses.reduce((s, m) => s + m.val, 0);

                                    const pCostBudgetARS = pCostBudgetBase * (isUsd ? rate : 1);
                                    const pCostPendingARS = Math.max(0, pCostBudgetARS - pExpenseARS);

                                    const pValBudget = isViewUsd ? (isUsd ? pContractBase : (pContractBase / rate)) : (isUsd ? pContractBase * rate : pContractBase);
                                    const pValPending = isViewUsd ? (isUsd ? pPendingBase : (pPendingBase / rate)) : (isUsd ? pPendingBase * rate : pPendingBase);
                                    const pValCostBudget = isViewUsd ? (isUsd ? pCostBudgetBase : (pCostBudgetBase / rate)) : (isUsd ? pCostBudgetBase * rate : pCostBudgetBase);
                                    const pValCostPending = pValCostBudget - pValExpense;

                                    const pProfit = pValIncome - pValExpense;

                                    return (
                                        <div key={p.id} className="px-8 py-6 grid grid-cols-12 items-center hover:bg-white/60 transition-all group">
                                            <div className="col-span-3 flex items-center justify-between pr-8">
                                                <div>
                                                    <h5 className="font-black text-sm uppercase tracking-tight text-slate-400 cursor-pointer hover:text-indigo-600 truncate" onClick={() => onSelectProject(p.id)}>{p.name}</h5>
                                                    <p className="text-[7px] font-bold text-rose-300 uppercase mt-0.5 tracking-widest">Sin Cliente</p>
                                                </div>
                                                <button onClick={() => { setModalData(p); setActiveModal('EDIT_EVENT'); }} className="p-2 bg-indigo-50 text-indigo-400 rounded-xl hover:bg-indigo-600 hover:text-white transition-all opacity-0 group-hover:opacity-100" title="Asignar Cliente">
                                                    <UserPlus size={14} />
                                                </button>
                                            </div>

                                            <div className="col-span-1 text-right pr-4">
                                                <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Venta</p>
                                                <p className="font-black text-[11px] text-slate-400">{formatV(pValBudget)}</p>
                                            </div>
                                            <div className="col-span-1 text-right pr-4">
                                                <p className="text-[7px] font-black text-emerald-400 uppercase tracking-widest mb-0.5">Cobrado</p>
                                                <p className="font-black text-[11px] text-emerald-400">{formatV(pValIncome)}</p>
                                            </div>
                                            <div className="col-span-1 text-right border-r border-slate-100 pr-4">
                                                <p className="text-[7px] font-black text-indigo-400 uppercase tracking-widest mb-0.5">S. Cobro</p>
                                                <p className="font-black text-[11px] text-indigo-400">{formatV(pValPending)}</p>
                                            </div>
                                            <div className="col-span-1 text-right pr-4 pl-4">
                                                <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5">P. Gasto</p>
                                                <p className="font-black text-[11px] text-slate-400">{formatV(pValCostBudget)}</p>
                                            </div>
                                            <div className="col-span-1 text-right pr-4">
                                                <p className="text-[7px] font-black text-rose-400 uppercase tracking-widest mb-0.5">Ejecutado</p>
                                                <p className="font-black text-[11px] text-rose-400">{formatV(pValExpense)}</p>
                                            </div>
                                            <div className="col-span-1 text-right border-r border-slate-100 pr-4">
                                                <p className="text-[7px] font-black text-orange-400 uppercase tracking-widest mb-0.5">S. Gasto</p>
                                                <p className="font-black text-[11px] text-orange-400">{formatV(pValCostPending)}</p>
                                            </div>
                                            <div className="col-span-1 text-right pr-4 pl-4">
                                                <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Resultado</p>
                                                <p className={`font-black text-[11px] ${pProfit >= 0 ? 'text-slate-400' : 'text-rose-400'}`}>{formatV(pProfit)}</p>
                                            </div>
                                            <div className="col-span-2 text-right">
                                                <div className={`mt-1 inline-flex px-2 py-0.5 rounded text-[7px] font-black uppercase tracking-widest ${pProfit >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                                                    {pProfit >= 0 ? 'Profit' : 'Loss'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : activeSubTab === 'MONTHLY' ? (
                <div className="space-y-8 animate-in px-4">
                    {(() => {
                        const monthlyData = {};
                        const rate = Number(globalFxRate);
                        expenses.forEach(e => {
                            if (!e.date) return;
                            const d = new Date(e.date);
                            const year = d.getFullYear();
                            const month = d.getMonth() + 1;
                            if (!monthlyData[year]) monthlyData[year] = { totalIncome: 0, totalExpense: 0, totalPending: 0, months: {} };
                            if (!monthlyData[year].months[month]) monthlyData[year].months[month] = { income: 0, expense: 0, pending: 0 };

                            const val = e.val;
                            if (e.movement_type === 'INCOME') {
                                monthlyData[year].totalIncome += val;
                                monthlyData[year].months[month].income += val;
                            } else {
                                monthlyData[year].totalExpense += val;
                                monthlyData[year].months[month].expense += val;
                            }
                        });

                        // Agregar saldos pendientes de proyectos activos en esos años/meses
                        projects.forEach(p => {
                            const d = new Date(p.event_date || p.start_date);
                            if (isNaN(d.getTime())) return;
                            const year = d.getFullYear();
                            const month = d.getMonth() + 1;

                            // Calcular pValPending usando la misma lógica que en la vista de clientes
                            const pMovements = expenses.filter(e => e.event_id === p.id);
                            const pIncomes = pMovements.filter(m => m.movement_type === 'INCOME');
                            const isUsd = p.base_currency === 'USD';

                            // 1. Calculate base values in project currency
                            const pIncomeBase = pIncomes.reduce((s, m) => s + (isUsd ? m.amount_normalized_usd : m.amount_normalized_ars), 0);
                            const pContractBase = Number(p.budget_total_contract || 0);
                            const pPendingBase = Math.max(0, pContractBase - pIncomeBase);

                            // 2. Calculate visual value (pValPending)
                            let pValPending = 0;
                            const gRate = Number(globalFxRate) || 1000;
                            if (isViewUsd) {
                                pValPending = isUsd ? pPendingBase : (pPendingBase / gRate);
                            } else {
                                pValPending = isUsd ? (pPendingBase * gRate) : pPendingBase;
                            }

                            if (pValPending > 0) {
                                if (!monthlyData[year]) monthlyData[year] = { totalIncome: 0, totalExpense: 0, totalPending: 0, months: {} };
                                if (!monthlyData[year].months[month]) monthlyData[year].months[month] = { income: 0, expense: 0, pending: 0 };
                                monthlyData[year].totalPending += pValPending;
                                monthlyData[year].months[month].pending += pValPending;
                            }
                        });

                        const sortedYears = Object.keys(monthlyData).sort((a, b) => b - a);

                        const handleExport = () => {
                            const monthsNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
                            let table = `<table border="1"><thead><tr style="background-color: #f1f5f9; font-weight: bold;"><th>Año</th><th>Mes</th><th>Ingresos (ARS)</th><th>Egresos (ARS)</th><th>Resultado (ARS)</th></tr></thead><tbody>`;
                            sortedYears.forEach(y => {
                                const yData = monthlyData[y];
                                Object.keys(yData.months).sort((a, b) => b - a).forEach(m => {
                                    const mData = yData.months[m];
                                    table += `<tr><td>${y}</td><td>${monthsNames[m - 1]}</td><td>${mData.income.toFixed(2)}</td><td>${mData.expense.toFixed(2)}</td><td>${(mData.income - mData.expense).toFixed(2)}</td></tr>`;
                                });
                                table += `<tr style="background-color: #f8fafc; font-weight: bold;"><td>TOTAL ${y}</td><td></td><td>${yData.totalIncome.toFixed(2)}</td><td>${yData.totalExpense.toFixed(2)}</td><td>${(yData.totalIncome - yData.totalExpense).toFixed(2)}</td></tr>`;
                            });
                            table += '</tbody></table>';
                            const template = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Estado Resultados</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body>${table}</body></html>`;
                            const blob = new Blob(['\uFEFF' + template], { type: 'application/vnd.ms-excel;charset=utf-8' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `Reporte_Financial_${new Date().toISOString().split('T')[0]}.xls`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                        };

                        return (
                            <div className="space-y-6">
                                <div className="flex justify-between items-center px-4">
                                    <h4 className="font-black text-xs uppercase text-slate-400 tracking-widest">Resumen Histórico de Resultados ({currentMode} proyectado)</h4>
                                    <button onClick={handleExport} className="btn-primary py-3 px-6 text-[10px] uppercase tracking-widest flex items-center gap-2"><FileText size={16} /> Exportar Reporte</button>
                                </div>
                                <div className="space-y-4">
                                    {sortedYears.map(y => {
                                        const yData = monthlyData[y];
                                        const isExpanded = expandedYears[y];
                                        const profit = yData.totalIncome - yData.totalExpense;
                                        return (
                                            <div key={y} className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm">
                                                <div onClick={() => setExpandedYears(prev => ({ ...prev, [y]: !prev[y] }))} className={`px-12 py-6 flex items-center justify-between cursor-pointer transition-colors ${isExpanded ? 'bg-indigo-600 text-white' : 'hover:bg-slate-50'}`}>
                                                    <div className="w-[300px] shrink-0 flex items-center gap-6">
                                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-black ${isExpanded ? 'bg-white/20' : 'bg-slate-900 text-white'}`}>{y}</div>
                                                        <h5 className="text-xl font-black uppercase tracking-tighter">Año Fiscal {y}</h5>
                                                    </div>
                                                    <div className="flex-1 grid grid-cols-12 gap-4 text-right items-center">
                                                        <div className="col-span-3 pr-4">
                                                            <p className={`text-[8px] font-black uppercase tracking-widest mb-1 ${isExpanded ? 'text-white/50' : 'text-slate-400'}`}>Ingresos Anuales</p>
                                                            <p className="font-black text-lg">{formatV(yData.totalIncome)}</p>
                                                        </div>
                                                        <div className="col-span-3 pr-4 border-l border-white/10">
                                                            <p className={`text-[8px] font-black uppercase tracking-widest mb-1 ${isExpanded ? 'text-white/50' : 'text-slate-400'}`}>Egresos Anuales</p>
                                                            <p className="font-black text-lg">{formatV(yData.totalExpense)}</p>
                                                        </div>
                                                        <div className="col-span-3 pr-4 border-l border-white/10">
                                                            <p className={`text-[8px] font-black uppercase tracking-widest mb-1 ${isExpanded ? 'text-white/50' : 'text-indigo-200'}`}>Pendientes Anuales</p>
                                                            <p className="font-black text-lg">{formatV(yData.totalPending)}</p>
                                                        </div>
                                                        <div className="col-span-3 pr-4 border-l border-white/10">
                                                            <p className={`text-[8px] font-black uppercase tracking-widest mb-1 ${isExpanded ? 'text-white/50' : 'text-slate-400'}`}>Resultado Neto</p>
                                                            <p className={`font-black text-lg ${profit >= 0 ? (isExpanded ? 'text-white' : 'text-emerald-600') : (isExpanded ? 'text-rose-200' : 'text-rose-600')}`}>{formatV(profit)}</p>
                                                        </div>
                                                    </div>
                                                    <ChevronDown size={20} className={`shrink-0 ml-8 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                </div>
                                                {isExpanded && (
                                                    <div className="divide-y divide-slate-50">
                                                        {Object.keys(yData.months).sort((a, b) => b - a).map(m => {
                                                            const mData = yData.months[m];
                                                            const mProfit = mData.income - mData.expense;
                                                            const monthsNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
                                                            return (
                                                                <div key={m} className="px-12 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                                                    <div className="w-[300px] shrink-0">
                                                                        <p className="font-black text-slate-700 uppercase tracking-tight">{monthsNames[m - 1]}</p>
                                                                    </div>
                                                                    <div className="flex-1 grid grid-cols-12 gap-4 text-right items-center">
                                                                        <div className="col-span-3 pr-4">
                                                                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Ingresos</p>
                                                                            <p className="font-bold text-sm text-emerald-600">{formatV(mData.income)}</p>
                                                                        </div>
                                                                        <div className="col-span-3 pr-4 border-l border-slate-100">
                                                                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Egresos / Pagos</p>
                                                                            <p className="font-bold text-sm text-rose-500">{formatV(mData.expense)}</p>
                                                                        </div>
                                                                        <div className="col-span-3 pr-4 border-l border-slate-100">
                                                                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Pendiente</p>
                                                                            <p className="font-bold text-sm text-indigo-500">{formatV(mData.pending)}</p>
                                                                        </div>
                                                                        <div className="col-span-3 pr-4 border-l border-slate-100">
                                                                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Resultado</p>
                                                                            <p className={`font-black text-base ${mProfit >= 0 ? 'text-slate-900' : 'text-rose-600'}`}>{formatV(mProfit)}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="shrink-0 ml-8 w-5"></div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            ) : activeSubTab === 'RESOURCES' ? (
                <div className="animate-in pb-20">
                    <ResourceManager
                        resources={resources || []}
                        assignments={assignments || []}
                        projects={projects || []}
                        resourceInactivity={appData.resource_inactivity || []}
                        onAdd={() => { setModalData({}); setActiveModal('ADD_RESOURCE'); }}
                        onEdit={(data) => { setModalData(data); setActiveModal('EDIT_RESOURCE'); }}
                        onDelete={(id) => handleAction('delete_resource', { id })}
                        onAssign={(res) => { setModalData(res); setActiveModal('ASSIGN_RESOURCE'); }}
                        onEditAssignment={(asig) => { setModalData(asig); setActiveModal('ASSIGN_RESOURCE'); }}
                        onDeleteAssignment={(id) => handleAction('delete_resource_assignment', { id })}
                        currentUser={currentUser}
                        setActiveModal={setActiveModal}
                        setModalData={setModalData}
                        globalFxRate={globalFxRate}
                    />
                </div>
            ) : (
                <div className="animate-in px-4">
                    {selectedMonthCalendar ? (
                        <CalendarView
                            events={data.expenses}
                            projects={data.projects}
                            clients={data.clients}
                            budgets={data.budgets}
                            t={t}
                            initialDate={new Date(selectedMonthCalendar.year, selectedMonthCalendar.month - 1, 1)}
                            onBack={() => setSelectedMonthCalendar(null)}
                            fNum={fNum}
                            fCur={fCur}
                            uiPrefs={uiPrefs}
                            globalFxRate={globalFxRate}
                        />
                    ) : (
                        <div className="space-y-8">
                            <div className="flex justify-between items-center px-4">
                                <h4 className="font-black text-xs uppercase text-slate-400 tracking-widest">Resumen Histórico de Resultados ({currentMode} proyectado)</h4>
                                <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest italic">Selecciona un mes para ver el calendario detallado</p>
                            </div>
                            {(() => {
                                const monthlyData = {};
                                const rate = Number(globalFxRate);
                                expenses.forEach(e => {
                                    const dateStr = e.expense_date || e.date || e.start_date;
                                    if (!dateStr) return;
                                    const d = new Date(dateStr);
                                    const year = d.getFullYear();
                                    const month = d.getMonth() + 1;
                                    if (!monthlyData[year]) monthlyData[year] = { totalIncome: 0, totalExpense: 0, months: {} };
                                    if (!monthlyData[year].months[month]) monthlyData[year].months[month] = { income: 0, expense: 0 };

                                    const val = e.val;
                                    if (e.movement_type === 'INCOME') {
                                        monthlyData[year].totalIncome += val;
                                        monthlyData[year].months[month].income += val;
                                    } else {
                                        monthlyData[year].totalExpense += val;
                                        monthlyData[year].months[month].expense += val;
                                    }
                                });
                                const sortedYears = Object.keys(monthlyData).sort((a, b) => b - a);

                                return (
                                    <div className="space-y-4">
                                        {sortedYears.map(y => {
                                            const yData = monthlyData[y];
                                            const isExpanded = expandedYears[y];
                                            const profit = yData.totalIncome - yData.totalExpense;
                                            return (
                                                <div key={y} className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm">
                                                    <div onClick={() => setExpandedYears(prev => ({ ...prev, [y]: !prev[y] }))} className={`px-12 py-6 flex items-center justify-between cursor-pointer transition-colors ${isExpanded ? 'bg-indigo-600 text-white' : 'hover:bg-slate-50'}`}>
                                                        <div className="w-[300px] shrink-0 flex items-center gap-6">
                                                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-black ${isExpanded ? 'bg-white/20' : 'bg-slate-900 text-white'}`}>{y}</div>
                                                            <h5 className="text-xl font-black uppercase tracking-tighter">Año Fiscal {y}</h5>
                                                        </div>
                                                        <div className="flex-1 grid grid-cols-12 gap-4 text-right items-center">
                                                            <div className="col-span-4 pr-4">
                                                                <p className={`text-[8px] font-black uppercase tracking-widest mb-1 ${isExpanded ? 'text-white/50' : 'text-slate-400'}`}>Ingresos Anuales</p>
                                                                <p className="font-black text-lg">{formatV(yData.totalIncome)}</p>
                                                            </div>
                                                            <div className="col-span-4 pr-4 border-l border-white/10">
                                                                <p className={`text-[8px] font-black uppercase tracking-widest mb-1 ${isExpanded ? 'text-white/50' : 'text-slate-400'}`}>Egresos Anuales</p>
                                                                <p className="font-black text-lg">{formatV(yData.totalExpense)}</p>
                                                            </div>
                                                            <div className="col-span-4 pr-4 border-l border-white/10">
                                                                <p className={`text-[8px] font-black uppercase tracking-widest mb-1 ${isExpanded ? 'text-white/50' : 'text-slate-400'}`}>Resultado Neto</p>
                                                                <p className={`font-black text-lg ${profit >= 0 ? (isExpanded ? 'text-white' : 'text-emerald-600') : (isExpanded ? 'text-rose-200' : 'text-rose-600')}`}>{formatV(profit)}</p>
                                                            </div>
                                                        </div>
                                                        <ChevronDown size={20} className={`shrink-0 ml-8 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                    </div>
                                                    {isExpanded && (
                                                        <div className="divide-y divide-slate-50">
                                                            {Object.keys(yData.months).sort((a, b) => b - a).map(m => {
                                                                const mData = yData.months[m];
                                                                const mProfit = mData.income - mData.expense;
                                                                const monthsNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
                                                                return (
                                                                    <div key={m} onClick={() => setSelectedMonthCalendar({ year: parseInt(y), month: parseInt(m) })} className="px-12 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer group">
                                                                        <div className="w-[300px] shrink-0">
                                                                            <p className="font-black text-slate-700 uppercase tracking-tight group-hover:text-indigo-600">{monthsNames[m - 1]}</p>
                                                                        </div>
                                                                        <div className="flex-1 grid grid-cols-12 gap-4 text-right items-center">
                                                                            <div className="col-span-4 pr-4">
                                                                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Ingresos</p>
                                                                                <p className="font-bold text-sm text-emerald-600">{formatV(mData.income)}</p>
                                                                            </div>
                                                                            <div className="col-span-4 pr-4 border-l border-slate-100">
                                                                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Egresos / Pagos</p>
                                                                                <p className="font-bold text-sm text-rose-500">{formatV(mData.expense)}</p>
                                                                            </div>
                                                                            <div className="col-span-4 pr-4 border-l border-slate-100">
                                                                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Resultado</p>
                                                                                <p className={`font-black text-base ${mProfit >= 0 ? 'text-slate-900' : 'text-rose-600'}`}>{formatV(mProfit)}</p>
                                                                            </div>
                                                                        </div>
                                                                        <div className="shrink-0 ml-8 w-5 flex items-center justify-center">
                                                                            <ArrowRight size={14} className="text-slate-300 group-hover:text-indigo-600 transition-colors" />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function CalendarView({ events, projects = [], clients = [], budgets = [], t, initialDate, onBack, fNum, fCur, uiPrefs, globalFxRate }) {
    const isViewUsd = uiPrefs.financialMode === 'USD';
    const currentMode = uiPrefs.financialMode;
    const rate = Number(globalFxRate) || 1000;

    const formatV = (val) => fCur(val, currentMode);
    const [currentDate, setCurrentDate] = useState(initialDate || new Date());
    const [selectedDayInfo, setSelectedDayInfo] = useState(null); // { day, movements }

    const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (year, month) => {
        let day = new Date(year, month, 1).getDay();
        return day === 0 ? 6 : day - 1; // Adjust to Monday start
    };

    const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const days = daysInMonth(year, month);
    const firstDay = firstDayOfMonth(year, month);

    const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
    const dayLabels = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

    // Group events by day
    const eventsByDay = {};
    events.forEach(ev => {
        const dateStr = ev.expense_date || ev.date || ev.start_date;
        if (!dateStr) return;
        const date = new Date(dateStr);
        if (date.getFullYear() === year && date.getMonth() === month) {
            const day = date.getDate();
            if (!eventsByDay[day]) eventsByDay[day] = [];

            // Enrich movement info
            const project = projects.find(p => p.id === ev.event_id);
            const client = project ? clients.find(c => c.id === project.client_id) : null;

            const isProjectUsd = (project?.base_currency || project?.display_currency) === 'USD';

            // Get project-specific rate if transaction doesn't have one
            const projectMovements = events.filter(e => e.event_id === ev.event_id);
            const txWithRate = events.filter(m => m.event_id === ev.event_id && Number(m.exchange_rate || m.fx || 0) > 0);
            const pRate = txWithRate.length > 0
                ? Number(txWithRate.sort((a, b) => new Date(b.date || b.expense_date) - new Date(a.date || a.expense_date))[0].exchange_rate || txWithRate.sort((a, b) => new Date(b.date || b.expense_date) - new Date(a.date || a.expense_date))[0].fx)
                : rate;

            let mRate = Number(ev.exchange_rate || ev.fx || 0);
            let divisor = 1;
            if (mRate > 0) {
                divisor = mRate < 1 ? (1 / mRate) : mRate;
            } else {
                divisor = pRate < 1 ? (1 / pRate) : pRate;
            }
            let currentAmtARS = 0;
            if (ev.currency_original === 'ARS' || ev.currency === 'ARS') {
                currentAmtARS = Number(ev.amount_original || 0);
            } else {
                currentAmtARS = Number(ev.amount_original || 0) * (divisor || 1);
            }

            eventsByDay[day].push({
                ...ev,
                amountARS: currentAmtARS,
                projectName: project ? project.name : 'Gasto General',
                clientName: client ? client.name : 'Sin Empresa'
            });
        }
    });

    return (
        <div className="bg-white rounded-[3rem] border border-slate-200 shadow-xl overflow-hidden animate-in flex flex-col min-h-[800px]">
            <div className="bg-slate-900 p-10 flex justify-between items-center text-white shrink-0">
                <div className="flex items-center gap-6">
                    <button onClick={onBack} className="p-4 bg-white/10 hover:bg-white rounded-2xl transition-all text-white hover:text-slate-900 active:scale-95 border border-white/10 group">
                        <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                    </button>
                    <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/20"><Calendar size={24} /></div>
                    <div>
                        <h3 className="text-3xl font-black uppercase tracking-tighter leading-none">{monthNames[month]} {year}</h3>
                        <p className="text-[10px] font-black uppercase text-indigo-300 mt-2 tracking-widest leading-none">Calendario Mensual de Movimientos</p>
                    </div>
                </div>
                <div className="flex items-center gap-8">
                    {(() => {
                        const monthPending = projects.reduce((acc, p) => {
                            const d = new Date(p.event_date || p.start_date);
                            if (d.getFullYear() === year && d.getMonth() === month) {
                                const pMovements = events.filter(e => e.event_id === p.id);
                                const pIncomes = pMovements.filter(m => m.movement_type === 'INCOME');

                                const txWithRate = pMovements.filter(m => Number(m.exchange_rate) > 1);
                                const pRate = txWithRate.length > 0
                                    ? Number(txWithRate.sort((a, b) => new Date(b.date || b.expense_date) - new Date(a.date || a.expense_date))[0].exchange_rate)
                                    : Number(globalFxRate);

                                const pIncome = pIncomes.reduce((s, m) => s + Number(m.amount_converted || m.amount_original || 0), 0);
                                const pBudget = budgets.filter(b => b.event_id === p.id).reduce((s, b) => s + Number(b.budget_amount || 0), 0) + Number(p.budget_mdo_global || 0);
                                const pendingBase = Math.max(0, pBudget - pIncome);
                                return acc + (pendingBase * (p.base_currency === 'USD' ? pRate : 1));
                            }
                            return acc;
                        }, 0);

                        if (monthPending <= 0) return null;

                        return (
                            <div className="text-right border-r border-white/10 pr-8">
                                <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest mb-1">Pendiente Mes</p>
                                <p className="text-xl font-black text-indigo-300">{formatV(monthPending)}</p>
                            </div>
                        );
                    })()}
                    <div className="flex gap-2 mr-4 bg-white/5 p-1 rounded-2xl">
                        <button onClick={prevMonth} className="p-3 hover:bg-white/10 rounded-xl transition-all text-white"><ChevronLeft size={20} /></button>
                        <button onClick={nextMonth} className="p-3 hover:bg-white/10 rounded-xl transition-all text-white"><ChevronRight size={20} /></button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/50">
                {dayLabels.map(d => (
                    <div key={d} className="py-6 text-center text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{d}</div>
                ))}
            </div>

            <div className="grid grid-cols-7 flex-1 auto-rows-fr divide-x divide-y divide-slate-100 min-h-0">
                {[...Array(firstDay)].map((_, i) => (
                    <div key={`empty-${i}`} className="bg-slate-50/30"></div>
                ))}
                {[...Array(days)].map((_, i) => {
                    const day = i + 1;
                    const dayMovements = eventsByDay[day] || [];
                    const dayIncome = dayMovements.filter(e => e.movement_type === 'INCOME').reduce((s, e) => s + e.amountARS, 0);
                    const dayExpense = dayMovements.filter(e => e.movement_type === 'EXPENSE').reduce((s, e) => s + e.amountARS, 0);

                    return (
                        <div
                            key={day}
                            onClick={() => dayMovements.length > 0 && setSelectedDayInfo({ day, movements: dayMovements })}
                            className={`p-4 transition-all group relative overflow-hidden flex flex-col min-h-[140px] ${dayMovements.length > 0 ? 'cursor-pointer hover:bg-indigo-50/50 hover:shadow-[inset_0_0_20px_rgba(79,70,229,0.05)]' : 'opacity-40'}`}
                        >
                            <div className="flex justify-between items-start mb-2">
                                <span className={`font-black text-xl leading-none ${dayMovements.length > 0 ? 'text-slate-800 group-hover:text-indigo-600' : 'text-slate-300'}`}>{day}</span>
                                {dayMovements.length > 0 && <span className="text-[9px] font-black bg-slate-900 text-white px-2 py-0.5 rounded-lg group-hover:bg-indigo-600 transition-colors">{dayMovements.length}</span>}
                            </div>

                            <div className="mt-auto space-y-1.5">
                                {dayIncome > 0 && (
                                    <div className="bg-emerald-50 text-emerald-600 p-2 rounded-xl border border-emerald-100 flex items-center justify-between">
                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                                        <p className="text-[10px] font-black tracking-tighter leading-none mt-0.5">{formatV(dayIncome)}</p>
                                    </div>
                                )}
                                {dayExpense > 0 && (
                                    <div className="bg-rose-50 text-rose-600 p-2 rounded-xl border border-rose-100 flex items-center justify-between">
                                        <div className="w-1.5 h-1.5 rounded-full bg-rose-400"></div>
                                        <p className="text-[10px] font-black tracking-tighter leading-none mt-0.5">{formatV(dayExpense)}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Daily Movements Modal */}
            {selectedDayInfo && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-8 bg-slate-900/60 backdrop-blur-sm animate-in">
                    <div className="bg-white w-full max-w-4xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-full">
                        <div className="p-10 bg-slate-900 flex justify-between items-center text-white shrink-0">
                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-xl shadow-indigo-500/20 text-3xl font-black">
                                    {selectedDayInfo.day}
                                </div>
                                <div>
                                    <h4 className="text-3xl font-black uppercase tracking-tighter leading-none">Detalle del Día</h4>
                                    <p className="text-[10px] font-black uppercase text-indigo-300 mt-2 tracking-widest leading-none">{selectedDayInfo.day} de {monthNames[month]}, {year}</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedDayInfo(null)} className="p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all border border-white/10"><X size={24} /></button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-10 space-y-6">
                            {selectedDayInfo.movements.map((mv, idx) => (
                                <div key={idx} className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100 group hover:border-indigo-200 transition-all flex flex-col md:flex-row md:items-center justify-between gap-8">
                                    <div className="flex items-start gap-6 flex-1">
                                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-sm ${mv.movement_type === 'INCOME' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                                            {mv.movement_type === 'INCOME' ? <ArrowUpRight size={28} /> : <ArrowDownLeft size={28} />}
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-3">
                                                <span className="text-[9px] font-black bg-slate-900 text-white px-2 py-0.5 rounded-lg uppercase tracking-widest">{mv.category || 'Sin Categoría'}</span>
                                                <span className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">{mv.clientName}</span>
                                            </div>
                                            <h5 className="text-xl font-black uppercase tracking-tighter text-slate-800">{mv.description || 'Sin descripción'}</h5>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                                <Briefcase size={12} /> {mv.projectName}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Impacto Financiero</p>
                                        <p className={`text-3xl font-black tracking-tighter ${mv.movement_type === 'INCOME' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                            {mv.movement_type === 'INCOME' ? '+' : '-'} {formatV(mv.amountARS)}
                                        </p>
                                        <p className="text-[9px] font-bold text-slate-300 uppercase mt-1">Ref: {mv.id?.slice(-6).toUpperCase() || 'SYS-AUTO'}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="p-8 bg-slate-50 border-t border-slate-100 flex justify-between items-center shrink-0">
                            <div className="flex gap-8">
                                <div>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Ingresos</p>
                                    <p className="text-xl font-black text-emerald-600">{formatV(selectedDayInfo.movements.filter(m => m.movement_type === 'INCOME').reduce((s, m) => s + m.amountARS, 0))}</p>
                                </div>
                                <div>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Egresos</p>
                                    <p className="text-xl font-black text-rose-600">{formatV(selectedDayInfo.movements.filter(m => m.movement_type === 'EXPENSE').reduce((s, m) => s + m.amountARS, 0))}</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedDayInfo(null)} className="btn-primary py-4 px-10 text-[11px] uppercase tracking-widest shadow-xl">Cerrar Detalle</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ResourceAssignmentModal({ modalData, appData, handleAction, fDate, onClose, currentUser }) {
    const [selectedType, setSelectedType] = useState('');
    // Filter resources logic:
    // 1. If we are in a PROJECT context (modalData.event_id exists), we might want to see resources from OTHER companies if they are assigned to this project?
    //    Actually, usually you assign YOUR resources. But if I am a Project Manager managing external resources, I should be able to see them.
    //    For now, let's keep showing "My Company Resources" primarily, BUT if we are editing an assignment of an external resource, we must include it.

    // Simplification: Always show my company resources + the resource being edited if it's external
    const myCompanyId = currentUser?.company_id || currentUser?.companyId;
    let availableResources = (appData.resources || []).filter(r => r.company_id === myCompanyId);

    // If we are editing and the resource is not in the list (external), add it properly
    if (modalData.resource_id) {
        const currentRes = (appData.resources || []).find(r => r.id === modalData.resource_id);
        if (currentRes && !availableResources.find(r => r.id === currentRes.id)) {
            availableResources.push(currentRes);
        }
    }

    const resourceTypes = [...new Set(availableResources.map(r => r.position))].sort();
    const filteredResources = availableResources.filter(r => !selectedType || r.position === selectedType);

    return (
        <Modal
            title={modalData.resource_id ? 'Editar Asignación' :
                (modalData.event_id ? `Asignar Recurso a ${appData.projects.find(p => p.id === modalData.event_id)?.name}` : `Asignar ${modalData?.name} a Proyecto`)}
            onClose={onClose}
        >
            <form className="space-y-6" onSubmit={e => {
                e.preventDefault();
                const fd = new FormData(e.target);
                const percentage = parseFloat(fd.get('percentage'));
                const eventId = fd.get('event_id') || modalData.event_id;
                const resourceId = fd.get('resource_id') || modalData.id;
                const proj = appData.projects.find(p => p.id === eventId);
                const currentAssignments = appData.resource_assignments.filter(a => a.resource_id === resourceId && a.id !== modalData.id);
                const newStart = new Date(fd.get('start_date'));
                const newEnd = new Date(fd.get('end_date'));
                const inactivity = (appData.resource_inactivity || []).filter(i => i.resource_id === resourceId);
                const overlapsInactivity = inactivity.find(i => {
                    const iStart = new Date(i.start_date);
                    const iEnd = new Date(i.end_date);
                    return newStart <= iEnd && newEnd >= iStart;
                });

                if (overlapsInactivity) {
                    if (!confirm(`¡ATENCION! El recurso tiene un periodo de INACTIVIDAD registrado (${overlapsInactivity.reason || 'Sin motivo'}) entre ${fDate(overlapsInactivity.start_date)} y ${fDate(overlapsInactivity.end_date)}. ¿Deseas continuar con la asignación de todas formas?`)) return;
                }

                let peakLoad = percentage;
                currentAssignments.forEach(a => {
                    const aStart = new Date(a.start_date);
                    const aEnd = new Date(a.end_date);
                    if (newStart <= aEnd && newEnd >= aStart) peakLoad += parseFloat(a.percentage);
                });

                if (peakLoad > 100) {
                    const res = appData.resources?.find(r => r.id === resourceId);
                    if (!res?.allow_overtime || res.allow_overtime == 0) {
                        alert(`ERROR: Este recurso no tiene habilitadas las horas extra. La carga estimada en este período alcanza el ${peakLoad.toFixed(0)}% (límite 100%). Para permitir la sobre-asignación, habilité la opción "Puede trabajar más del 100%" en la configuración del recurso.`);
                        return;
                    }
                    if (!confirm(`¡ADVERTENCIA! Durante el período seleccionado, la carga total del recurso alcanzará un pico del ${peakLoad.toFixed(0)}%. Este recurso tiene habilitadas las horas extra. ¿Deseas continuar?`)) return;
                }

                if (modalData.resource_id) {
                    handleAction('update_resource_assignment', {
                        id: modalData.id,
                        percentage: percentage,
                        start_date: fd.get('start_date'),
                        end_date: fd.get('end_date'),
                        resource_id: resourceId,
                        event_id: eventId,
                        task_id: modalData.task_id || null
                    });
                } else {
                    handleAction('assign_resource', {
                        resource_id: resourceId,
                        event_id: eventId,
                        task_id: modalData.task_id || null,
                        percentage: percentage,
                        start_date: fd.get('start_date'),
                        end_date: fd.get('end_date')
                    });
                }
            }}>
                {/* Selector de Proyecto solo si no venimos de un proyecto o no es edición */}
                {!modalData.event_id && !modalData.resource_id && (
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Proyecto</label>
                        <select name="event_id" required className="input-base font-black uppercase text-xs">
                            <option value="">-- Seleccionar Proyecto --</option>
                            {appData.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                )}
                {modalData.event_id && !modalData.resource_id && <input type="hidden" name="event_id" value={modalData.event_id} />}
                {modalData.resource_id && <input type="hidden" name="event_id" value={modalData.event_id} />}
                {modalData.task_id && <div className="bg-indigo-50 p-4 rounded-xl text-center"><p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Asignando a Tarea Específica</p></div>}


                {/* Selector de Recurso con filtro por tipo solo si venimos de un proyecto */}
                {modalData.event_id && !modalData.resource_id && (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Tipo de Recurso</label>
                            <select
                                value={selectedType}
                                onChange={(e) => setSelectedType(e.target.value)}
                                className="input-base font-black uppercase text-xs"
                            >
                                <option value="">-- TODOS LOS TIPOS --</option>
                                {resourceTypes.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Recurso</label>
                            <select name="resource_id" required className="input-base font-black uppercase text-xs">
                                <option value="">-- SELECCIONAR RECURSO --</option>
                                {filteredResources.map(r => (
                                    <option key={r.id} value={r.id}>{r.name} ({r.position})</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
                {!modalData.event_id && !modalData.resource_id && <input type="hidden" name="resource_id" value={modalData.id} />}
                {modalData.resource_id && <input type="hidden" name="resource_id" value={modalData.resource_id} />}

                <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">% del tiempo a dedicar</label>
                    <input name="percentage" type="number" min="1" max="100" required className="input-base font-black" placeholder="Ej: 50" defaultValue={modalData.percentage || 100} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Desde</label>
                        <input name="start_date" type="date" required className="input-base font-black" defaultValue={modalData.start_date || new Date().toISOString().split('T')[0]} />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Hasta</label>
                        <input name="end_date" type="date" required className="input-base font-black" defaultValue={modalData.end_date || ''} />
                    </div>
                </div>
                <button type="submit" className="btn-primary w-full py-5 uppercase font-black tracking-widest bg-indigo-600 shadow-xl shadow-indigo-100">
                    {modalData.resource_id ? 'Guardar Cambios' : 'Confirmar Asignación'}
                </button>
            </form>
        </Modal >
    );
}

function Modal({ title, children, onClose, wide = false }) {
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-md animate-in">
            <div className={`bg-white rounded-[4rem] w-full ${wide ? 'max-w-4xl' : 'max-w-2xl'} shadow-2xl p-16 relative flex flex-col max-h-[90vh] overflow-hidden animate-in scale-in border border-white/20`}>
                <button onClick={onClose} className="absolute top-12 right-12 text-slate-300 hover:text-indigo-600 transition-colors z-20"><X size={24} /></button>
                {title && (
                    <div className="shrink-0 pr-12">
                        <h4 className="text-3xl font-black uppercase tracking-tighter mb-10 border-b-8 border-indigo-600 pb-3 inline-block leading-none">{title}</h4>
                    </div>
                )}
                <div className="flex-1 overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-indigo-100 scrollbar-track-transparent">
                    {children}
                </div>
            </div>
        </div>
    );
}

function AuthView({ onLogin, onRegister, isLoading, appSettings = {} }) {
    const [mode, setMode] = useState('LOGIN'); // 'LOGIN' or 'SIGNUP'
    const [email, setEmail] = useState('');
    const [pass, setPass] = useState('');
    const [name, setName] = useState('');
    const [key, setKey] = useState('');
    const [magic, setMagic] = useState('');
    const [showMagic, setShowMagic] = useState(false);

    return (
        <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-8 relative overflow-hidden font-sans">
            {/* Soft modern background elements */}
            <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-indigo-50 blur-[120px] rounded-full opacity-50"></div>
            <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-blue-50 blur-[120px] rounded-full opacity-50"></div>

            <div className="max-w-md w-full bg-white rounded-[3rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] p-12 relative overflow-hidden animate-in border border-slate-100 flex flex-col gap-8">
                <div className="text-center relative z-10">
                    {appSettings.app_logo ? (
                        <div className="inline-flex w-32 h-20 items-center justify-center mb-6">
                            <img src={appSettings.app_logo} alt="App Logo" className="max-w-full max-h-full object-contain drop-shadow-md" />
                        </div>
                    ) : (
                        <div className="inline-flex w-16 h-16 bg-indigo-600 rounded-2xl items-center justify-center text-white mb-6 shadow-xl shadow-indigo-200">
                            <Zap size={28} fill="currentColor" />
                        </div>
                    )}
                    <h1 className="text-3xl font-black uppercase tracking-tighter text-slate-800 leading-none">Gestión de Proyectos</h1>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.3em] mt-3">Gestiona tus Proyectos</p>
                </div>

                <div className="flex bg-slate-100 p-1.5 rounded-2xl relative z-10">
                    <button onClick={() => setMode('LOGIN')} className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${mode === 'LOGIN' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Iniciar Sesión</button>
                    <button onClick={() => setMode('SIGNUP')} className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${mode === 'SIGNUP' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Registrarse</button>
                    <button onClick={() => setMode('FORGOT_PASS')} className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${mode === 'FORGOT_PASS' ? 'bg-white text-indigo-600 shadow-sm' : 'hidden'}`}>Recuperar</button>
                </div>

                <form className="space-y-5 relative z-10" onSubmit={(e) => {
                    e.preventDefault();
                    if (mode === 'LOGIN') {
                        onLogin(email, pass, magic);
                    } else if (mode === 'FORGOT_PASS') {
                        if (!email) return alert("Ingresa tu correo electrónico");
                        fetch(`${API_URL}?action=forgot_password`, { method: 'POST', body: JSON.stringify({ email }) })
                            .then(r => r.json())
                            .then(d => { alert(d.message || "Revisa tu correo"); setMode('LOGIN'); })
                            .catch(() => alert("Error de conexión"));
                    } else {
                        onRegister(name, email, pass, key);
                    }
                }}>
                    {mode === 'SIGNUP' && (
                        <div className="space-y-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Nombre Completo</label>
                            <input autoComplete="name" className="input-base border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-bold" placeholder="Juan Pérez" value={name} onChange={e => setName(e.target.value)} />
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Correo Electrónico</label>
                        <div className="relative">
                            <Contact size={16} className="absolute left-4 top-4 text-slate-300" />
                            <input autoComplete="username" className="input-base pl-12 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-bold" placeholder="tu@email.com" value={email} onChange={e => setEmail(e.target.value)} />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <div className="flex justify-between items-center px-1">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Contraseña</label>
                            {mode === 'LOGIN' && <button type="button" onClick={() => setMode('FORGOT_PASS')} className="text-[9px] font-black text-indigo-500 uppercase tracking-widest hover:text-indigo-600 transition-colors">¿Olvidaste tu contraseña?</button>}
                        </div>
                        <div className="relative">
                            <ShieldCheck size={16} className="absolute left-4 top-4 text-slate-300" />
                            <input autoComplete={mode === 'SIGNUP' ? "new-password" : "current-password"} className="input-base pl-12 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-bold" type="password" placeholder="••••••••" value={pass} onChange={e => setPass(e.target.value)} />
                        </div>
                    </div>

                    {mode === 'SIGNUP' && (
                        <div className="space-y-2">
                            <label className="text-[9px] font-black text-indigo-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Zap size={10} /> Llave de Acceso</label>
                            <input className="input-base bg-indigo-50 border-indigo-100 text-indigo-600 placeholder:text-indigo-300 font-black tracking-widest uppercase" placeholder="COM-XXXXXX" value={key} onChange={e => setKey(e.target.value.toUpperCase())} />
                        </div>
                    )}

                    {mode === 'LOGIN' && (
                        <div className="pt-2 text-center">
                            {!showMagic ? (
                                <button
                                    type="button"
                                    onClick={() => setShowMagic(true)}
                                    className="text-[10px] font-black text-slate-300 uppercase tracking-widest hover:text-indigo-600 transition-all flex items-center justify-center gap-2 mx-auto py-2 px-4 rounded-xl hover:bg-indigo-50"
                                >
                                    <Shield size={12} /> Login de Administrador Maestro
                                </button>
                            ) : (
                                <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                                    <div className="flex items-center gap-4">
                                        <div className="h-px flex-1 bg-indigo-100"></div>
                                        <span className="text-[8px] font-black text-indigo-400 uppercase tracking-widest">Master Access Code</span>
                                        <div className="h-px flex-1 bg-indigo-100"></div>
                                    </div>
                                    <input
                                        autoComplete="current-password"
                                        className="input-base bg-indigo-50 border-indigo-100 text-center font-black tracking-[0.2em] focus:bg-white focus:border-indigo-200 transition-all placeholder:text-indigo-200 uppercase"
                                        type="password"
                                        placeholder="Ingrese Código Maestro"
                                        value={magic}
                                        onChange={e => setMagic(e.target.value.toUpperCase())}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowMagic(false)}
                                        className="text-[8px] font-black text-rose-400 uppercase tracking-widest hover:text-rose-600 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="btn-primary w-full py-5 rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 hover:shadow-indigo-200 active:scale-[0.98] transition-all bg-indigo-600"
                        >
                            {isLoading ? <Loader2 size={18} className="animate-spin mx-auto text-white/50" /> : (mode === 'LOGIN' ? 'Iniciar Sesión' : (mode === 'FORGOT_PASS' ? 'Enviar Instrucciones' : 'Crear Cuenta'))}
                        </button>
                        {mode === 'FORGOT_PASS' && (
                            <button type="button" onClick={() => setMode('LOGIN')} className="w-full py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-all">Volver al Inicio</button>
                        )}
                    </div>
                </form>
            </div>

            <div className="mt-12 text-center animate-in" style={{ animationDelay: '0.2s' }}>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Sistema de Gestión v20.0</p>
                <div className="flex items-center justify-center gap-2">
                    <span className="text-[9px] font-bold text-slate-300 uppercase">Powered by</span>
                    <span className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">frssoluciones</span>
                </div>
            </div>
        </div>
    );
}

function SuperAdminPanel({ companies, onAdd, onEdit, onDelete, onViewUsers, appSettings = {}, handleAction }) {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredCompanies = companies.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.access_key && c.access_key.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.license_expires_at && c.license_expires_at.includes(searchTerm))
    );

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            const token = localStorage.getItem('em_token');
            const headers = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const res = await fetch(`${API_URL}?action=upload_file`, {
                method: 'POST',
                body: formData,
                headers: headers
            });
            const data = await res.json();
            if (data.status === 'success') {
                await handleAction('update_app_settings', {
                    setting_key: 'app_logo',
                    setting_value: data.url
                });
                alert("Logo actualizado correctamente. Recarga la página para ver todos los cambios.");
                window.location.reload(); // Recargar para aplicar a favicon y todo el sistema
            } else {
                alert("Error al subir el logo: " + data.message);
            }
        } catch (err) {
            alert("Error de conexión al subir el logo");
        }
    };

    return (
        <div className="space-y-12 animate-in text-slate-900 pb-20">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-8 px-4">
                <div className="space-y-2">
                    <h3 className="text-4xl font-black uppercase tracking-tighter">Gestión Maestro</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Control Global de Licencias y Accesos</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 flex-1 max-w-2xl bg-white p-2 rounded-[2rem] border border-slate-100 shadow-sm self-center">
                    <div className="relative flex-1">
                        <Search className="absolute left-6 top-5 text-slate-300" size={20} />
                        <input
                            type="text"
                            placeholder="Buscar por empresa, llave o fecha..."
                            className="w-full pl-14 py-4 pr-6 bg-slate-50 border-none rounded-2xl font-bold text-slate-600 focus:ring-4 focus:ring-indigo-500/10 placeholder:text-slate-300 transition-all outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button onClick={onAdd} className="btn-primary py-4 px-10 text-[11px] uppercase tracking-widest shadow-xl shadow-indigo-100 whitespace-nowrap">
                        <Zap size={20} /> Nueva Licencia
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
                {filteredCompanies.map(c => {
                    const usagePercent = Math.round((c.user_count / c.max_users) * 100);
                    // Priorizamos el estatus real de la LLAVE (is_active) sobre el texto de la tabla companies
                    const isKeyActive = c.key_active !== undefined && c.key_active !== null ? (parseInt(c.key_active) === 1) : null;
                    const isCompanyActive = (c.status || '').toString().toUpperCase() === 'ACTIVE';
                    const isActive = isKeyActive !== null ? isKeyActive : isCompanyActive;

                    return (
                        <div key={c.id} onDoubleClick={() => onViewUsers && onViewUsers(c)} className="bg-white p-10 rounded-[3rem] border border-slate-200 flex items-center justify-between shadow-sm hover:border-indigo-400 transition-all group relative overflow-hidden cursor-pointer select-none">
                            <div className={`absolute left-0 top-0 bottom-0 w-2 ${isActive ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                            <div className="flex items-center gap-8">
                                <div className={`w-20 h-20 rounded-[2rem] flex items-center justify-center text-white font-black text-3xl shadow-2xl transition-transform group-hover:scale-110 ${isActive ? 'bg-gradient-to-br from-emerald-400 to-emerald-600' : 'bg-gradient-to-br from-rose-400 to-rose-600'}`}>
                                    {c.name[0]}
                                </div>
                                <div>
                                    <p className="font-black text-3xl uppercase tracking-tighter leading-none mb-3">{c.name}</p>
                                    <div className="flex items-center gap-6">
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(c.access_key);
                                                    alert("Llave copiada al portapapeles: " + c.access_key);
                                                }}
                                                className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-indigo-600 transition-colors flex items-center gap-2 group"
                                                title="Click para copiar"
                                            >
                                                Key: {c.access_key || 'N/A'}
                                                <Copy size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </button>
                                            <button onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(c.access_key);
                                                alert("Llave copiada: " + c.access_key);
                                            }} className="p-1 hover:text-indigo-600 transition-colors" title="Copiar Llave"><Share2 size={12} /></button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (confirm("¿Seguro que desea REGENERAR la llave de acceso? La llave actual quedará invalidada.")) {
                                                        handleAction('regenerate_access_key', { id: c.id });
                                                    }
                                                }}
                                                className="p-1 hover:text-rose-500 transition-colors"
                                                title="Regenerar Llave"
                                            >
                                                <RefreshCw size={12} />
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Clock size={12} className="text-indigo-400" />
                                            <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Vence: {c.license_expires_at || 'Ilimitado'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-12 text-right">
                                <div className="min-w-[180px] space-y-3">
                                    <div className="flex justify-between items-end mb-1">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Capacidad</p>
                                        <p className="text-xs font-black text-slate-900">{c.user_count} / {c.max_users}</p>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden shadow-inner">
                                        <div
                                            className={`h-full transition-all duration-1000 ${usagePercent >= 100 ? 'bg-rose-500' : 'bg-indigo-500'}`}
                                            style={{ width: `${Math.min(100, usagePercent)}%` }}
                                        ></div>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <button onClick={() => onViewUsers && onViewUsers(c)} className="p-5 bg-slate-50 rounded-2xl hover:bg-indigo-50 hover:text-indigo-600 transition-all text-slate-400" title="Ver Usuarios"><Users size={20} /></button>
                                    <button onClick={() => onEdit(c)} className="p-5 bg-slate-50 rounded-2xl hover:bg-indigo-50 hover:text-indigo-600 transition-all text-slate-400"><Edit3 size={20} /></button>
                                    <button onClick={() => onDelete(c.id)} className="p-5 bg-slate-50 rounded-2xl hover:bg-rose-50 hover:text-rose-600 transition-all text-slate-400"><Trash2 size={20} /></button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* General App Settings */}
            <div className="pt-12 border-t border-slate-100">
                <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 bg-slate-900 rounded-[1.5rem] flex items-center justify-center text-white">
                        <Layout size={24} />
                    </div>
                    <div>
                        <h4 className="text-2xl font-black uppercase tracking-tighter">Configuración General</h4>
                        <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">Personalización de Marca y Sistema</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                    <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm space-y-6">
                        <div className="flex justify-between items-start">
                            <div>
                                <h5 className="font-black text-lg uppercase tracking-tight">Logo de la Aplicación</h5>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Se muestra en Login, Sidebar y Favicon</p>
                            </div>
                            {appSettings.app_logo && (
                                <div className="w-20 h-20 bg-slate-50 rounded-2xl border border-slate-100 p-2 flex items-center justify-center overflow-hidden">
                                    <img src={appSettings.app_logo} alt="Current Logo" className="max-w-full max-h-full object-contain" />
                                </div>
                            )}
                        </div>

                        <div className="flex gap-4">
                            <label className="flex-1">
                                <div className="btn-primary py-4 px-6 text-[10px] uppercase font-black text-center cursor-pointer inline-flex items-center justify-center gap-3 w-full">
                                    <FilePlus size={18} /> Seleccionar Nuevo Logo
                                </div>
                                <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                            </label>
                            {appSettings.app_logo && (
                                <button
                                    onClick={() => {
                                        if (confirm("¿Eliminar el logo personalizado?")) {
                                            handleAction('update_app_settings', { setting_key: 'app_logo', setting_value: '' });
                                            window.location.reload();
                                        }
                                    }}
                                    className="p-4 bg-rose-50 text-rose-500 rounded-2xl hover:bg-rose-100 transition-all"
                                    title="Quitar Logo"
                                >
                                    <Trash2 size={20} />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm space-y-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600">
                                <Zap size={24} />
                            </div>
                            <div>
                                <h5 className="font-black text-lg uppercase tracking-tight">Inteligencia Artificial</h5>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Configuración de OpenAI</p>
                            </div>
                        </div>

                        <div className="space-y-4 pt-4">
                            <label className="text-[10px] font-black uppercase text-slate-400 ml-2">OpenAI API Key</label>
                            <div className="flex gap-2">
                                <input
                                    type="password"
                                    id="openai_api_key_input_master"
                                    className="input-base text-xs py-3 flex-1"
                                    placeholder="sk-..."
                                    defaultValue={appSettings.openai_api_key}
                                />
                                <button
                                    onClick={async () => {
                                        const key = document.getElementById('openai_api_key_input_master').value;
                                        if (!key) return;
                                        await handleAction('update_public_config', { openai_api_key: key });
                                        alert("Configuración de IA actualizada.");
                                    }}
                                    className="bg-slate-900 text-white px-6 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-md"
                                >
                                    Guardar
                                </button>
                            </div>
                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tight leading-relaxed">
                                Esta clave es global y permite la generación de cronogramas para todas las empresas del portal.
                            </p>
                        </div>
                    </div>

                    <div className="bg-slate-50 p-10 rounded-[3rem] border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-center">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-slate-200 mb-4 shadow-inner">
                            <Zap size={32} />
                        </div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Próximamente</p>
                        <p className="text-xs font-bold text-slate-300 mt-2 px-8">Configuración de colores corporativos y fuentes personalizadas.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ProjectGantt({ onOpenSettings, tasks: allTasks, onEdit, canEdit, t, handleAction, event, categories, expenses, materials, taskBudgets, prefs = {}, isManager = false, isExternalProject = false, uiPrefs, totalMdoBudget: passedMdoBudget }) {
    const [zoom, setZoom] = useState('DIAS'); // DIAS, SEMANAS, MESES
    const [onlyParents, setOnlyParents] = useState(false);
    const scrollRef = useRef(null);
    const topScrollRef = useRef(null);

    const { colorMode = 'INDIGO', showDependencies = true, compactMode = false } = prefs;

    // Sincronización de scrollbars
    const syncScroll = (source, target) => {
        if (!source.current || !target.current) return;
        target.current.scrollLeft = source.current.scrollLeft;
    };

    // Sincronizar scroll bars
    useEffect(() => {
        const top = topScrollRef.current;
        const bottom = scrollRef.current;
        if (!top || !bottom) return;

        const onTopScroll = () => syncScroll(topScrollRef, scrollRef);
        const onBottomScroll = () => syncScroll(scrollRef, topScrollRef);

        top.addEventListener('scroll', onTopScroll);
        bottom.addEventListener('scroll', onBottomScroll);

        return () => {
            top.removeEventListener('scroll', onTopScroll);
            bottom.removeEventListener('scroll', onBottomScroll);
        };
    }, []);

    // Filtrar tareas según el toggle
    const currentTasks = onlyParents ? allTasks.filter(tsk => tsk.isParent || tsk.is_milestone == 1) : allTasks;
    const validTasks = currentTasks.filter(tsk => tsk.start_date && tsk.end_date);

    // Lógica de ordenamiento: Agrupar Hijos bajo sus Padres
    const sortedTasks = useMemo(() => {
        const result = [];
        const map = {};
        validTasks.forEach(t => {
            if (!map[t.parent_id || 'root']) map[t.parent_id || 'root'] = [];
            map[t.parent_id || 'root'].push(t);
        });

        const traverse = (parentId, level) => {
            const children = map[parentId] || [];
            children.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
            children.forEach(c => {
                result.push({ ...c, indentLevel: level });
                traverse(c.id, level + 1);
            });
        };

        traverse('root', 0);
        return result;
    }, [validTasks]);

    const dates = sortedTasks.flatMap(tsk => [new Date(tsk.start_date), new Date(tsk.end_date)]);
    const filteredDates = dates.filter(d => !isNaN(d.getTime()) && d.getFullYear() > 2000);

    // Default to project start date or today if no valid task dates
    let minDateBase = new Date(event.start_date);
    if (isNaN(minDateBase.getTime()) || minDateBase.getFullYear() < 2000) {
        minDateBase = new Date();
    }

    const minDate = filteredDates.length > 0
        ? new Date(Math.min(...filteredDates, minDateBase))
        : minDateBase;

    minDate.setDate(minDate.getDate() - 2);

    const maxDateBase = new Date(event.end_date || event.start_date);
    const maxDate = filteredDates.length > 0
        ? new Date(Math.max(...filteredDates, maxDateBase))
        : new Date(minDateBase.getTime() + 30 * 86400000);

    maxDate.setDate(maxDate.getDate() + 10);

    const totalDays = Math.max(1, Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)));
    let dayWidth = 40;
    if (zoom === 'SEMANAS') dayWidth = 15;
    if (zoom === 'MESES') dayWidth = 5;

    const rowHeight = compactMode ? 36 : 48;
    const today = new Date();
    const todayOffset = (Math.ceil((today - minDate) / 86400000)) * dayWidth;

    useEffect(() => {
        if (scrollRef.current && todayOffset > 0) {
            scrollRef.current.scrollLeft = Math.max(0, todayOffset - 200);
            if (topScrollRef.current) topScrollRef.current.scrollLeft = Math.max(0, todayOffset - 200);
        }
    }, [todayOffset]); // Only on mount

    // Salud y Avances unificados
    const { physicalProgress: progFisico, theoreticalProgress: theoreticalProg } = calculateProgressMetrics(allTasks, event.event_date, event.end_date);

    // Cálculo de Mano de Obra (MDO) para el Header
    const mdoExpenses = expenses.filter(e => e.expense_type === 'MANO_OBRA' && e.movement_type === 'EXPENSE');
    const totalMdoSpent = mdoExpenses.reduce((sum, e) => sum + Number(e.amount_converted || e.amount_original || 0), 0);

    let totalMdoBudget = passedMdoBudget;
    if (totalMdoBudget === undefined || totalMdoBudget === null) {
        totalMdoBudget = 0;
        if (event.mdo_budget_mode === 'GLOBAL') {
            totalMdoBudget = Number(event.budget_mdo_global || 0);
        } else if (event.mdo_budget_mode === 'POR_FASE') {
            totalMdoBudget = (taskBudgets || [])
                .filter(tb => tb.event_id === event.id)
                .reduce((sum, tb) => sum + Number(tb.budget_mdo || 0), 0);
        }
    }

    const mdoRealProg = totalMdoBudget > 0 ? Math.round((totalMdoSpent / totalMdoBudget) * 100) : 0;
    const theoreticalMdoVal = useMemo(() => {
        if (!event.mdo_budget_mode || totalMdoBudget === 0) return 0;
        if (event.mdo_budget_mode === 'GLOBAL') {
            return totalMdoBudget * (theoreticalProg / 100);
        } else {
            // Consistent calculation for POR_FASE mode matching MDOBudgetExecution
            return (taskBudgets || [])
                .filter(tb => tb.event_id === event.id)
                .reduce((sum, tb) => {
                    const task = allTasks.find(t => t.id === tb.task_id);
                    if (!task) return sum;
                    const s = task.start_date ? new Date(task.start_date) : null;
                    const e = task.end_date ? new Date(task.end_date) : null;
                    if (!s || !e || isNaN(s.getTime()) || isNaN(e.getTime())) return sum;
                    const totalWD = getWorkDays(s, e);
                    const today = new Date();
                    const endForElapsed = today < e ? today : e;
                    const elapsedWD = s <= endForElapsed ? getWorkDays(s, endForElapsed) : 0;
                    const taskProg = totalWD > 0 ? (elapsedWD / totalWD) : 0;
                    return sum + (Number(tb.budget_mdo) * taskProg);
                }, 0);
        }
    }, [event.mdo_budget_mode, totalMdoBudget, theoreticalProg, taskBudgets, allTasks, event.id]);

    const mdoDiffVal = theoreticalMdoVal - totalMdoSpent;
    const cur = event.base_currency === 'USD' ? 'USD ' : '$ ';

    // El early return va DESPUÉS de todos los hooks (useEffect/useMemo) para no romper el orden de hooks (React #300)
    if (sortedTasks.length === 0) return (
        <div className="bg-white p-20 rounded-[4rem] text-center border-2 border-dashed border-slate-100">
            <p className="text-slate-300 font-black uppercase text-xs tracking-widest">{t('no_tasks_gantt') || 'No hay tareas suficientes para generar el Gantt'}</p>
            {onlyParents && (
                <button onClick={() => setOnlyParents(false)} className="mt-6 px-6 py-3 rounded-xl text-[9px] font-black uppercase bg-indigo-50 text-indigo-600 border border-indigo-100 hover:bg-indigo-100">
                    Ver Detalle
                </button>
            )}
        </div>
    );

    return (
        <div className="bg-white rounded-[4rem] border border-slate-200 shadow-sm overflow-hidden animate-in">
            {/* Scroll Superior */}
            <div ref={topScrollRef} className="overflow-x-auto overflow-y-hidden h-4 bg-slate-50 border-b border-slate-100 custom-scrollbar-minimal sticky top-0 z-[60]">
                <div style={{ width: totalDays * dayWidth + 300, height: '1px' }}></div>
            </div>

            <div className="p-12 border-b border-slate-50 flex justify-between items-center gap-8 bg-white z-30 relative">
                <div className="flex items-center gap-6">
                    <div>
                        <h3 className="font-black uppercase text-[11px] tracking-widest text-slate-400 mb-1">Diagrama Gantt del Proyecto</h3>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-3">
                                <p className="text-2xl font-black text-indigo-900 tracking-tighter">
                                    {progFisico}% <span className="text-[10px] text-slate-400 align-middle">REAL</span>
                                </p>
                                <div className="h-8 w-px bg-slate-200"></div>
                                <p className="text-2xl font-black text-rose-500 tracking-tighter">
                                    {theoreticalProg}% <span className="text-[10px] text-slate-400 align-middle">TEÓRICO</span>
                                </p>
                            </div>

                            {!isExternalProject && (event.mdo_budget_mode || totalMdoBudget > 0) && (
                                <>
                                    <div className="h-8 w-px bg-slate-200 mx-2"></div>
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-3">
                                            <p className="text-2xl font-black text-blue-600 tracking-tighter">
                                                {mdoRealProg}% <span className="text-[10px] text-slate-400 align-middle">MDO REAL</span>
                                                <span className="block text-[10px] text-blue-400 mt-0.5 opacity-80 leading-none">{cur} {totalMdoSpent.toLocaleString()}</span>
                                            </p>
                                            <div className="h-8 w-px bg-slate-200"></div>
                                            <p className="text-2xl font-black text-cyan-500 tracking-tighter">
                                                {theoreticalProg}% <span className="text-[10px] text-slate-400 align-middle">MDO TEÓRICO</span>
                                                <span className="block text-[10px] text-cyan-400 mt-0.5 opacity-80 leading-none">{cur} {theoreticalMdoVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                            </p>
                                            {totalMdoBudget > 0 && (
                                                <div className={`px-4 py-2 rounded-2xl border flex flex-col justify-center ${mdoDiffVal >= 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                                    <p className="text-[8px] font-black uppercase text-slate-400 tracking-widest leading-none mb-1">Diferencia</p>
                                                    <p className={`text-sm font-black tracking-tighter leading-none ${mdoDiffVal >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                        {mdoDiffVal >= 0 ? '+' : ''}{mdoDiffVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            {Math.abs(theoreticalProg - progFisico) > 5 && (
                                <div className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase flex items-center gap-2 border ${progFisico >= theoreticalProg
                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                    : 'bg-rose-50 text-rose-600 border-rose-100 animate-pulse'
                                    }`}>
                                    <AlertOctagon size={14} />
                                    {progFisico >= theoreticalProg
                                        ? `Adelantado: +${progFisico - theoreticalProg}%`
                                        : `Retrasado: ${progFisico - theoreticalProg}%`
                                    }
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex bg-slate-100 p-1 rounded-2xl">
                        {['DIAS', 'SEMANAS', 'MESES'].map(z => (
                            <button
                                key={z}
                                onClick={() => setZoom(z)}
                                className={`px-6 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${zoom === z ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                {z}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setOnlyParents(!onlyParents)}
                        className={`px-6 py-3 rounded-xl text-[9px] font-black uppercase transition-all border ${onlyParents ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-white text-indigo-700 border-slate-200 hover:border-indigo-300'}`}
                    >
                        {onlyParents ? 'Ver Detalle' : 'Solo Fases'}
                    </button>
                    {onOpenSettings && (
                        <button
                            onClick={onOpenSettings}
                            title="Configurar visualización del Gantt"
                            className="p-3 rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 hover:border-indigo-300 transition-all"
                        >
                            <Settings size={16} />
                        </button>
                    )}
                    {isManager && (
                        <button
                            onClick={async () => {
                                const typed = prompt(`¡PELIGRO! Se eliminarán TODAS las tareas del cronograma.\n\nPara confirmar, escribí el nombre del proyecto:\n${event.name}`);
                                if (typed === null) return;
                                if (typed.trim().toLowerCase() !== String(event.name || '').trim().toLowerCase()) {
                                    alert('El nombre no coincide. No se borró nada.');
                                    return;
                                }
                                {
                                    await handleAction('delete_all_tasks', { event_id: event.id });

                                    // Opcional: Limpiar Rubros si el usuario quiere dejar el proyecto totalmente limpio
                                    if (confirm("¿Deseas también eliminar los Rubros (Categorías) que quedaron vacíos en toda la empresa?")) {
                                        await handleAction('cleanup_company_categories', { company_id: event.company_id });
                                    }
                                }
                            }}
                            className="bg-rose-50 text-rose-500 px-6 py-3 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-rose-100 transition-all border border-rose-100"
                        >
                            <Trash2 size={14} className="inline mr-2" /> Limpiar Todo
                        </button>
                    )}
                </div>
            </div>

            {/* Contenedor con Scroll Horizontal */}
            <div ref={scrollRef} className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-indigo-100 bg-white">
                <div style={{ width: totalDays * dayWidth + 300 }} className="relative">
                    {/* Header de Fechas - DOBLE FILA STICKY */}
                    <div className="sticky top-0 z-40 shadow-sm">
                        {/* Fila MESES */}
                        <div className="flex bg-slate-900 border-b border-white/10 h-10">
                            <div className="w-[300px] shrink-0 bg-slate-950 border-r border-white/10"></div>
                            <div className="flex flex-1">
                                {(() => {
                                    const months = [];
                                    let curr = new Date(minDate);
                                    let currentMonth = curr.getMonth();
                                    let daysCount = 0;
                                    for (let i = 0; i < totalDays; i++) {
                                        const d = new Date(minDate); d.setDate(minDate.getDate() + i);
                                        if (d.getMonth() !== currentMonth) {
                                            months.push({ date: new Date(curr), days: daysCount });
                                            currentMonth = d.getMonth();
                                            curr = d;
                                            daysCount = 1;
                                        } else {
                                            daysCount++;
                                        }
                                    }
                                    months.push({ date: new Date(curr), days: daysCount });
                                    return months.map((m, idx) => (
                                        <div key={idx} style={{ width: m.days * dayWidth }} className="shrink-0 text-[10px] font-black text-white uppercase tracking-widest pl-4 flex items-center bg-slate-900 border-r border-white/5 truncate">
                                            {m.date.toLocaleString('default', { month: 'long', year: 'numeric' })}
                                        </div>
                                    ));
                                })()}
                            </div>
                        </div>
                        {/* Fila DIAS */}
                        <div className="flex bg-slate-800 border-b-2 border-indigo-500/30 h-10">
                            <div className="w-[300px] shrink-0 font-black text-[10px] text-white/70 uppercase px-6 tracking-[0.2em] flex items-center bg-slate-900 border-r border-white/10">
                                Estructura / Tarea
                            </div>
                            <div className="flex">
                                {Array.from({ length: totalDays }).map((_, i) => {
                                    const d = new Date(minDate);
                                    d.setDate(d.getDate() + i);
                                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                                    const isMonday = d.getDay() === 1;

                                    if (zoom === 'DIAS') return (
                                        <div key={i} style={{ width: dayWidth }} className={`shrink-0 text-[9px] font-bold flex items-center justify-center border-l border-white/5 ${isWeekend ? 'text-white/30 bg-white/5' : 'text-white/90'}`}>
                                            {d.getDate()}
                                        </div>
                                    );
                                    if (zoom === 'SEMANAS' && isMonday) return <div key={i} style={{ width: dayWidth * 7 }} className="shrink-0 text-[9px] font-black text-white/70 border-l border-white/10 px-2 flex items-center">Sem {Math.ceil(d.getDate() / 7)}</div>;
                                    if (zoom === 'MESES' && d.getDate() === 1) return <div key={i} style={{ width: dayWidth * 30 }} className="shrink-0 text-[9px] font-black text-white/70 border-l border-white/20 pl-4 flex items-center">Inicio Mes</div>;
                                    return null;
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="relative">
                        {/* Línea de HOY */}
                        {todayOffset > 0 && todayOffset < (totalDays * dayWidth) && (
                            <div
                                style={{ left: 300 + todayOffset }}
                                className="absolute top-0 bottom-0 w-px border-l-2 border-dashed border-blue-600 z-[45] pointer-events-none"
                                title="Hoy"
                            >
                                <div className="bg-blue-600 text-white text-[8px] font-black px-2 py-0.5 rounded-full absolute top-2 -left-4 shadow-lg">HOY</div>
                            </div>
                        )}

                        {showDependencies && (
                            <svg className="absolute top-0 left-0 w-full h-full pointer-events-none z-0 overflow-visible">
                                <defs>
                                    <marker id="arrowhead" markerWidth="6" markerHeight="4" refX="5" refY="2" orientation="auto">
                                        <polygon points="0 0, 6 2, 0 4" fill="#cbd5e1" />
                                    </marker>
                                </defs>
                                {sortedTasks.map((task, idx) => {
                                    if (task.dependency_id) {
                                        const dep = sortedTasks.find(d => d.id === task.dependency_id);
                                        if (dep) {
                                            const depIdx = sortedTasks.indexOf(dep);
                                            if (depIdx === -1) return null;
                                            const x1 = 300 + (Math.ceil((new Date(dep.end_date) - minDate) / 86400000) + 1) * dayWidth;
                                            const y1 = depIdx * rowHeight + rowHeight / 2;
                                            const x2 = 300 + (Math.ceil((new Date(task.start_date) - minDate) / 86400000)) * dayWidth;
                                            const y2 = idx * rowHeight + rowHeight / 2;
                                            const midX = x1 + 10;
                                            return <path key={`dep-${task.id}`} d={`M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`} fill="none" stroke="#cbd5e1" strokeWidth="2" markerEnd="url(#arrowhead)" />;
                                        }
                                    }
                                    return null;
                                })}
                            </svg>
                        )}

                        <div className="relative z-10">
                            {sortedTasks.map((task) => {
                                const start = new Date(task.start_date);
                                const end = new Date(task.end_date);
                                const offset = (Math.ceil((start - minDate) / 86400000)) * dayWidth;
                                const width = (Math.ceil((end - start) / 86400000) + 1) * dayWidth;
                                const isMilestone = task.is_milestone == 1;

                                // Calcular avance teórico basado en el tiempo transcurrido
                                const taskDuration = Math.max(1, Math.ceil((end - start) / 86400000) + 1);
                                const elapsedDays = Math.max(0, Math.ceil((today - start) / 86400000));
                                const theoreticalProgress = start <= today ? Math.min(100, Math.round((elapsedDays / taskDuration) * 100)) : 0;
                                const hasTheoreticalProgress = theoreticalProgress > 0 && !isMilestone;

                                let barClass = "bg-teal-50 border-teal-200 text-teal-700"; // Default
                                let barStyle = {};

                                if (colorMode === 'CUSTOM') {
                                    const pColor = prefs.phaseColor || '#4f46e5';
                                    const sColor = prefs.subTaskColor || '#10b981';
                                    if (task.isParent) {
                                        barStyle = { backgroundColor: pColor, borderColor: pColor, color: 'white' };
                                    } else {
                                        barStyle = { backgroundColor: `${sColor}15`, borderColor: sColor, color: sColor };
                                    }
                                } else if (task.isParent) {
                                    barClass = "bg-indigo-900 border-indigo-950 text-white";
                                } else if (colorMode === 'CATEGORY') {
                                    const catIdx = (categories || []).findIndex(c => c.name === task.category_id || c.id === task.category_id);
                                    const catColors = [
                                        'bg-blue-50 border-blue-200 text-blue-700',
                                        'bg-emerald-50 border-emerald-200 text-emerald-700',
                                        'bg-purple-50 border-purple-200 text-purple-700',
                                        'bg-orange-50 border-orange-200 text-orange-700',
                                        'bg-rose-50 border-rose-200 text-rose-700'
                                    ];
                                    if (catIdx !== -1) barClass = catColors[catIdx % catColors.length];
                                } else if (colorMode === 'STATUS') {
                                    if (Number(task.progress) === 100) barClass = "bg-emerald-50 border-emerald-200 text-emerald-700";
                                    else if (Number(task.progress) > 0) barClass = "bg-amber-50 border-amber-200 text-amber-700";
                                    else barClass = "bg-slate-50 border-slate-200 text-slate-500";
                                }

                                const isDelayed = !task.isParent && !isMilestone && Number(task.progress) < (theoreticalProgress - 5);

                                return (
                                    <div key={task.id} style={{ height: rowHeight }} className={`flex items-center group ${canEdit ? 'cursor-pointer hover:bg-slate-50' : 'bg-white'} transition-all border-b border-slate-50 ${task.isParent ? 'bg-slate-50/50' : ''}`} onClick={() => canEdit && onEdit(task)}>
                                        <div
                                            className="w-[300px] shrink-0 flex items-center gap-3 pl-4 border-r border-slate-100 h-full bg-white z-20 sticky left-0 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)]"
                                            style={{ paddingLeft: `${16 + (task.indentLevel || 0) * 16}px` }}
                                        >
                                            {task.assignee_name && !task.isParent && <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-[8px] font-black text-indigo-600 shrink-0 border border-indigo-200">{task.assignee_name[0]}</div>}
                                            <div className="overflow-hidden">
                                                <div className="flex items-center gap-2">
                                                    <p className={`font-black uppercase truncate ${isMilestone ? 'text-amber-600' : task.isParent ? 'text-indigo-900' : (isDelayed ? 'text-rose-600' : 'text-slate-500')}`} style={{ color: colorMode === 'CUSTOM' ? prefs.labelColor : '', fontSize: `${uiPrefs.ganttFontSize}px` }}>{task.title}</p>
                                                    {isDelayed && <span className="text-[7px] bg-rose-600 text-white px-1 rounded-sm font-black animate-pulse uppercase tracking-tighter">Retrasada</span>}
                                                </div>
                                                {task.isParent && <span className="text-[7px] bg-indigo-100 text-indigo-700 px-1 rounded uppercase font-bold tracking-wider">Fase Principal</span>}
                                            </div>
                                        </div>
                                        <div className="relative flex-1 h-full">
                                            {isMilestone ? (
                                                <div className={`absolute w-6 h-6 rotate-45 border-4 border-white shadow-lg z-10 flex items-center justify-center ${compactMode ? 'mt-2' : 'mt-3'}`} style={{ left: offset, top: compactMode ? '6px' : '12px' }}>
                                                    <div className="w-2 h-2 bg-amber-600 rounded-full"></div>
                                                </div>
                                            ) : (
                                                <>
                                                    {/* Línea roja de avance teórico */}
                                                    {hasTheoreticalProgress && (
                                                        <div
                                                            style={{ left: offset, width: width }}
                                                            className={`absolute h-1 z-10 flex items-center ${compactMode ? 'mt-0.5' : 'mt-1'}`}
                                                        >
                                                            <div
                                                                className="h-full bg-rose-500 rounded-full shadow-sm relative"
                                                                style={{ width: `${theoreticalProgress}%` }}
                                                                title={`Avance teórico: ${theoreticalProgress}%`}
                                                            >
                                                                {!compactMode && (
                                                                    <span className="absolute -left-1 -top-4 font-black text-rose-500 whitespace-nowrap bg-white/80 px-1 rounded text-[7px]">
                                                                        T: {theoreticalProgress}%
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Barra de progreso real */}
                                                    <div
                                                        style={{ left: offset, width: width, height: compactMode ? '18px' : '24px', top: compactMode ? '9px' : '12px', ...barStyle }}
                                                        className={`absolute rounded-md flex items-center px-2 border shadow-sm transition-all group/bar overflow-hidden ${barClass} ${task.is_critical == 1 ? 'ring-2 ring-rose-500 ring-offset-2 ring-offset-white shadow-[0_0_15px_rgba(244,63,94,0.3)]' : ''} ${isDelayed ? 'ring-2 ring-rose-500/50 ring-offset-1 ring-offset-white' : ''}`}
                                                    >
                                                        {task.is_critical == 1 && !task.isParent && (
                                                            <div className="absolute -top-3 -left-1 bg-rose-500 text-white text-[6px] font-black px-1 rounded-sm uppercase tracking-tighter shadow-sm z-30">Crítico</div>
                                                        )}
                                                        {isDelayed && !task.isParent && (
                                                            <div className="absolute -top-3 right-0 bg-rose-600 text-white text-[6px] font-black px-1 rounded-sm uppercase tracking-tighter shadow-sm z-30 animate-pulse">Retraso</div>
                                                        )}
                                                        {task.isParent && (
                                                            <div className="h-full absolute left-0 top-0 bg-indigo-600/80 transition-all duration-700" style={{ width: `${task.progress}%`, backgroundColor: colorMode === 'CUSTOM' ? `${prefs.phaseColor}CC` : '' }}></div>
                                                        )}
                                                        {!task.isParent && Number(task.progress) > 0 && (
                                                            <div className="h-full absolute left-0 top-0 rounded-l-md transition-all duration-700" style={{ width: `${task.progress}%`, background: colorMode === 'CUSTOM' ? `${prefs.subTaskColor || '#10b981'}40` : (colorMode === 'STATUS' ? (Number(task.progress) === 100 ? 'rgba(16,185,129,0.35)' : 'rgba(245,158,11,0.35)') : (isDelayed ? 'rgba(244,63,94,0.2)' : 'rgba(99,102,241,0.25)')) }}></div>
                                                        )}

                                                        {/* Prevent text overlap: Only show if width > 30px, else show on hover */}
                                                        {(width > 30 || task.isParent) ? (
                                                            <span className={`relative z-10 font-black tracking-wider ${task.isParent ? 'text-white' : ''}`} style={{ color: (colorMode === 'CUSTOM' && task.isParent) ? 'white' : (isDelayed && !task.isParent ? '#e11d48' : barStyle.color), fontSize: `${uiPrefs.ganttFontSize - 3}px` }}>
                                                                {task.progress}%
                                                            </span>
                                                        ) : (
                                                            <span className="opacity-0 group-hover/bar:opacity-100 absolute -right-8 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-[9px] px-1 rounded z-40">
                                                                {task.progress}%
                                                            </span>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ========== ROLES & PERMISSIONS MANAGER ==========

function RoleManager({ roles, companyId, onAdd, onEdit, onDelete }) {
    const modules = [
        { id: 'PROJECTS', label: 'Proyectos' },
        { id: 'TEAM', label: 'Equipo' },
        { id: 'CLIENTS', label: 'Clientes' },
        { id: 'FINANCIAL', label: 'Finanzas' },
        { id: 'SCHEDULE', label: 'Cronograma' },
        { id: 'BUDGET', label: 'Presupuesto' },
        { id: 'RESOURCES', label: 'Recursos' },
        { id: 'DOCUMENTATION', label: 'Documentación' },
        { id: 'PURCHASE_ORDERS', label: 'OC (Órdenes de Compra)' }
    ];

    return (
        <div className="space-y-12 animate-in pb-20">
            <div className="flex justify-between items-center px-4">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-slate-900 rounded-[2rem] flex items-center justify-center text-white shadow-2xl">
                        <ShieldCheck size={32} />
                    </div>
                    <div>
                        <h3 className="text-4xl font-black uppercase tracking-tighter text-slate-800">Roles y Permisos</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">Configuración de perfiles de acceso</p>
                    </div>
                </div>
                <button onClick={onAdd} className="btn-primary py-5 px-10 text-[11px] uppercase tracking-widest shadow-xl shadow-indigo-100 bg-indigo-600">
                    <Plus size={20} /> Nuevo Rol
                </button>
            </div>

            <div className="bg-white rounded-[3rem] border border-slate-200 shadow-sm overflow-hidden">
                {!roles || roles.length === 0 ? (
                    <div className="p-20 text-center">
                        <p className="text-slate-400 font-black uppercase text-xs tracking-widest italic">No has definido roles personalizados</p>
                    </div>
                ) : (
                    roles.map((role, idx) => {
                        const perms = typeof role.permissions === 'string' ? JSON.parse(role.permissions) : (role.permissions || {});
                        const accessCount = modules.filter(m => perms[m.id] && perms[m.id] !== 'NONE').length;
                        const [expanded, setExpanded] = React.useState(false);

                        return (
                            <div key={role.id} className={`${idx > 0 ? 'border-t border-slate-100' : ''}`}>
                                {/* Row */}
                                <div
                                    className="flex items-center gap-5 px-8 py-5 cursor-pointer hover:bg-slate-50/70 transition-all group"
                                    onClick={() => setExpanded(prev => !prev)}
                                >
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${expanded ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-indigo-50 group-hover:text-indigo-600'}`}>
                                        <ShieldCheck size={18} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-black text-sm uppercase tracking-tight text-slate-800">{role.name}</p>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{accessCount} de {modules.length} módulos con acceso • ID: {role.id.substring(0, 12)}…</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={(e) => { e.stopPropagation(); onEdit(role); }} className="p-2.5 bg-slate-50 rounded-xl hover:bg-indigo-100 hover:text-indigo-600 transition-all text-slate-300">
                                            <Edit3 size={15} />
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); onDelete(role.id); }} className="p-2.5 bg-slate-50 rounded-xl hover:bg-rose-100 hover:text-rose-600 transition-all text-slate-300">
                                            <Trash2 size={15} />
                                        </button>
                                        <div className={`p-2 transition-transform duration-200 text-slate-300 ${expanded ? 'rotate-180' : ''}`}>
                                            <ChevronDown size={16} />
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded permissions */}
                                {expanded && (
                                    <div className="px-8 pb-6 pt-2 bg-gradient-to-b from-slate-50/50 to-white border-t border-slate-100 animate-in fade-in slide-in-from-top-2 duration-200">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                                            {modules.map(mod => {
                                                const access = perms[mod.id] || 'NONE';
                                                return (
                                                    <div key={mod.id} className="flex justify-between items-center p-3 bg-white rounded-xl border border-slate-100">
                                                        <span className="text-[11px] font-bold text-slate-600 uppercase tracking-tight">{mod.label}</span>
                                                        <span className={`text-[9px] font-black px-3 py-1 rounded-full uppercase ${access === 'DELETE' ? 'bg-rose-100 text-rose-600' : access === 'EDIT' ? 'bg-indigo-100 text-indigo-600' : access === 'VIEW' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                                                            {access === 'DELETE' ? 'Ver/Edit/Borr' : access === 'EDIT' ? 'Ver/Edit' : access === 'VIEW' ? 'Solo Lectura' : 'Sin Acceso'}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

// ========== RESOURCES MANAGEMENT (HELPERS) ==========

const getResourceMaxLoad = (resourceId, targetAssignments) => {
    const resAsigs = targetAssignments.filter(a => a.resource_id === resourceId);
    if (resAsigs.length === 0) return 0;

    // Collect all critical dates (starts and ends)
    const datePoints = new Set();
    resAsigs.forEach(a => {
        datePoints.add(a.start_date);
        datePoints.add(a.end_date);
    });

    const sortedPoints = Array.from(datePoints).sort();
    let maxLoad = 0;

    // Check each point (and the day after each point to cover intervals)
    sortedPoints.forEach(dateStr => {
        const checkDate = new Date(dateStr);
        const loadAtPoint = resAsigs.reduce((sum, a) => {
            const s = new Date(a.start_date);
            const e = new Date(a.end_date);
            if (checkDate >= s && checkDate <= e) return sum + parseFloat(a.percentage || 0);
            return sum;
        }, 0);
        if (loadAtPoint > maxLoad) maxLoad = loadAtPoint;
    });

    return maxLoad;
};

function ResourceAssignmentsTable({ assignments, resources, projects, onEdit, onDelete, onSaveLog, onDeleteLog, showProjectColumn = true, showResourceColumn = true, canEdit, allAssignments, resourceHoursLog = [], globalFxRate }) {
    // Helper to calculate max daily load for a resource
    const getResourceMaxLoad = (resourceId, targetAssignments) => {
        const resAsigs = targetAssignments.filter(a => a.resource_id === resourceId);
        if (resAsigs.length === 0) return 0;

        // Collect all critical dates (starts and ends)
        const datePoints = new Set();
        resAsigs.forEach(a => {
            datePoints.add(a.start_date);
            datePoints.add(a.end_date);
        });

        const sortedPoints = Array.from(datePoints).sort();
        let maxLoad = 0;

        // Check each point (and the day after each point to cover intervals)
        sortedPoints.forEach(dateStr => {
            const checkDate = new Date(dateStr);
            const loadAtPoint = resAsigs.reduce((sum, a) => {
                const s = new Date(a.start_date);
                const e = new Date(a.end_date);
                if (checkDate >= s && checkDate <= e) return sum + parseFloat(a.percentage || 0);
                return sum;
            }, 0);
            if (loadAtPoint > maxLoad) maxLoad = loadAtPoint;
        });

        return maxLoad;
    };

    const [expandedRow, setExpandedRow] = React.useState(null);
    const [editingLogId, setEditingLogId] = React.useState(null);
    const [logEditValue, setLogEditValue] = React.useState({ hours: 0, details: '' });
    const [linkPopover, setLinkPopover] = React.useState(null); // { resId, projId, fromDate, toDate }

    if (assignments.length === 0) return (
        <div className="p-20 bg-white border-2 border-dashed border-slate-100 rounded-[3rem] text-center">
            <p className="text-slate-300 font-black uppercase text-xs tracking-widest italic">No hay asignaciones cargadas</p>
        </div>
    );

    const targetAssignments = allAssignments || assignments;

    return (
        <div className="overflow-x-auto bg-white rounded-[2rem] border border-slate-100 shadow-sm animate-in fade-in duration-500">
            <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50 text-[9px] font-black uppercase text-slate-400 tracking-widest border-b border-slate-100">
                    <tr>
                        {showProjectColumn && <th className="px-8 py-6">Proyecto</th>}
                        {showResourceColumn && <th className="px-8 py-6">Recurso / Cargo</th>}
                        <th className="px-8 py-6 text-center">Dedicación (%)</th>
                        <th className="px-8 py-6">Período de Asignación</th>
                        <th className="px-8 py-6 text-right">Días Hab.</th>
                        <th className="px-8 py-6 text-right">Hs. Presup.</th>
                        <th className="px-8 py-6 text-right">Hs. Reales</th>
                        <th className="px-8 py-6 text-right">Costo Estimado</th>
                        <th className="px-8 py-6 text-right">Costo Real</th>
                        <th className="px-8 py-6"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {assignments.map(asig => {
                        const res = resources.find(r => r.id === asig.resource_id);
                        const proj = projects.find(p => p.id === asig.event_id);
                        if (!res || !proj) return null;

                        const wDays = getWorkDays(new Date(asig.start_date), new Date(asig.end_date));
                        const hours = wDays * parseFloat(res.daily_hours) * (parseFloat(asig.percentage) / 100);
                        
                        const projectCurrency = normCurrency(proj.base_currency || proj.display_currency);
                        const toProjectCur = (amt) => convertResourceCost(amt, res.currency, projectCurrency, proj.exchange_rate, globalFxRate);
                        const { value: cost, converted: isConverted } = toProjectCur(hours * parseFloat(res.hourly_rate || 0));
                        const displayCurrency = projectCurrency;

                        // Real logged hours: sum all daily log entries for this resource+project within assignment period
                        const matchingLogs = (resourceHoursLog || []).filter(l =>
                            String(l.resource_id).trim() === String(asig.resource_id).trim() &&
                            String(l.event_id).trim() === String(asig.event_id).trim() &&
                            l.date >= asig.start_date &&
                            l.date <= asig.end_date
                        );
                        const realHours = matchingLogs.length > 0
                            ? matchingLogs.reduce((sum, l) => sum + parseFloat(l.hours || 0), 0)
                            : null;

                        // Total load across all assignments in scope (Calculating DAILY match)
                        const totalLoad = getResourceMaxLoad(res.id, targetAssignments);

                        const isExpanded = expandedRow === asig.id;

                        return (
                            <React.Fragment key={asig.id}>
                                <tr className="hover:bg-slate-50/50 transition-all group">
                                    {showProjectColumn && (
                                        <td className="px-8 py-6">
                                            <p className="text-[11px] font-black uppercase text-slate-800 leading-tight">{proj.name}</p>
                                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1">ID: {proj.id.substring(0, 8)}</p>
                                        </td>
                                    )}
                                    {showResourceColumn && (
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="relative">
                                                    <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white text-sm font-black uppercase shadow-sm">{res.name[0]}</div>
                                                    {totalLoad > 100 && (
                                                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white animate-pulse">
                                                            <AlertTriangle size={8} className="text-white" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-black uppercase text-slate-700">{res.name}</p>
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">{res.position}</p>
                                                        {totalLoad > 100 && (
                                                            <span className="text-[8px] font-black text-rose-500 uppercase bg-rose-50 px-2 py-0.5 rounded border border-rose-100 italic">Sobrecarga: {totalLoad}%</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    )}
                                    <td className="px-8 py-6 text-center">
                                        <div className="inline-flex flex-col items-center">
                                            <span className={`text-[11px] font-black px-4 py-1.5 rounded-full ${parseFloat(asig.percentage) > 100 ? 'bg-rose-50 text-rose-600' : 'bg-indigo-50 text-indigo-600'}`} title={parseFloat(asig.percentage) > 100 ? 'Dedicación mayor al 100%' : 'Porcentaje de la jornada asignado a este proyecto'}>
                                                {asig.percentage}%
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-2 text-[10px] font-bold text-slate-600 uppercase tracking-tighter">
                                            <Calendar size={12} className="text-slate-300" />
                                            <span>{asig.start_date.split('-').reverse().join('/')}</span>
                                            <span className="text-slate-300">{'\u2192'}</span>
                                            <span>{asig.end_date.split('-').reverse().join('/')}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 text-right text-[11px] font-black text-slate-500">
                                        {wDays} <span className="text-[9px] text-slate-300 font-bold uppercase">días</span>
                                    </td>
                                    <td className="px-8 py-6 text-right">
                                        <p className="text-[11px] font-black text-slate-500">{hours.toFixed(1)} <span className="text-[9px] font-bold text-slate-300 uppercase">hs</span></p>
                                    </td>
                                    <td className="px-8 py-6 text-right">
                                        {realHours !== null ? (
                                            <div className="flex flex-col items-end cursor-pointer" onClick={() => setExpandedRow(isExpanded ? null : asig.id)} title="Click para ver detalle diario">
                                                <p className={`text-[11px] font-black ${realHours >= hours ? 'text-emerald-600' : 'text-amber-500'}`}>{realHours.toFixed(1)} <span className="text-[9px] font-bold uppercase">hs</span></p>
                                                <p className="text-[8px] font-bold text-slate-300 uppercase">{realHours >= hours ? 'Completado' : `${((realHours / hours) * 100).toFixed(0)}%`}</p>
                                                <p className="text-[7px] font-bold text-indigo-400 uppercase mt-0.5 flex items-center gap-1">
                                                    <Eye size={8} /> {isExpanded ? 'Ocultar' : 'Ver detalle'}
                                                </p>
                                            </div>
                                        ) : (() => {
                                            const orphanLogs = (resourceHoursLog || []).filter(l =>
                                                String(l.resource_id).trim() === String(asig.resource_id).trim() &&
                                                String(l.event_id).trim() === String(asig.event_id).trim()
                                            );
                                            if (orphanLogs.length > 0) {
                                                const orphanTotal = orphanLogs.reduce((sum, l) => sum + parseFloat(l.hours || 0), 0);
                                                return (
                                                    <div className="flex flex-col items-end opacity-60" title="Horas registradas fuera del periodo de asignación">
                                                        <p className="text-[11px] font-black text-rose-400 flex items-center gap-1">
                                                            <AlertOctagon size={10} /> {orphanTotal.toFixed(1)} hs
                                                        </p>
                                                        <p className="text-[7px] font-bold text-rose-300 uppercase">Fuera de fecha</p>
                                                    </div>
                                                );
                                            }
                                            return <span className="text-slate-200 text-[10px] font-bold">{'\u2014'}</span>;
                                        })()}
                                    </td>
                                    <td className="px-8 py-6 text-right">
                                        <p className="text-[11px] font-black text-slate-900 tracking-tighter">{displayCurrency} {Math.round(cost).toLocaleString()}</p>
                                        <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                                            {isConverted && <span className="text-slate-500 mr-2" title={`Tarifa en ${normCurrency(res.currency)} convertida a ${projectCurrency} (cotización ${parseFloat(proj.exchange_rate) > 1 ? 'del proyecto: ' + proj.exchange_rate : 'global: ' + (globalFxRate || 1000)})`}>(convertido de {normCurrency(res.currency)})</span>}
                                            {hours.toFixed(1)} hs totales
                                        </p>
                                    </td>
                                    <td className="px-8 py-6 text-right">
                                        {realHours !== null ? (() => {
                                            const realCost = toProjectCur(realHours * parseFloat(res.hourly_rate || 0)).value;
                                            const isOver = realCost > cost;
                                            return (
                                                <div className="flex flex-col items-end">
                                                    <p className={`text-[11px] font-black tracking-tighter ${isOver ? 'text-rose-600' : 'text-emerald-600'}`}>
                                                        {displayCurrency} {Math.round(realCost).toLocaleString()}
                                                    </p>
                                                    <p className={`text-[8px] font-bold uppercase tracking-widest mt-1 ${isOver ? 'text-rose-300' : 'text-emerald-300'}`}>
                                                        {isOver ? `+${displayCurrency} ${Math.round(realCost - cost).toLocaleString()}` : `-${displayCurrency} ${Math.round(cost - realCost).toLocaleString()}`}
                                                    </p>
                                                </div>
                                            );
                                        })() : (
                                            <span className="text-slate-200 text-[10px] font-bold">{'\u2014'}</span>
                                        )}
                                    </td>
                                    <td className="px-8 py-6 text-right relative">
                                        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (linkPopover?.resId === res.id && linkPopover?.projId === proj.id) {
                                                        setLinkPopover(null);
                                                    } else {
                                                        setLinkPopover({
                                                            resId: res.id,
                                                            projId: proj.id,
                                                            fromDate: asig.start_date,
                                                            toDate: asig.end_date
                                                        });
                                                    }
                                                }}
                                                className={`w-9 h-9 border rounded-xl flex items-center justify-center transition-all shadow-sm ${linkPopover?.resId === res.id && linkPopover?.projId === proj.id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-100 text-slate-300 hover:text-indigo-600 hover:border-indigo-100'}`}
                                                title="Copiar Link para Carga de Horas"
                                            >
                                                <LinkIcon size={16} />
                                            </button>
                                            {canEdit && (
                                                <>
                                                    <button onClick={() => onEdit(asig)} className="w-9 h-9 bg-white border border-slate-100 rounded-xl flex items-center justify-center text-slate-300 hover:text-indigo-600 hover:border-indigo-100 shadow-sm transition-all"><Edit3 size={16} /></button>
                                                    <button onClick={() => { if (confirm("¿Eliminar asignación?")) onDelete(asig.id); }} className="w-9 h-9 bg-white border border-slate-100 rounded-xl flex items-center justify-center text-slate-300 hover:text-rose-500 hover:border-rose-100 shadow-sm transition-all"><Trash2 size={16} /></button>
                                                </>
                                            )}
                                        </div>

                                        {linkPopover?.resId === res.id && linkPopover?.projId === proj.id && (
                                            <div className="absolute right-full top-0 mr-4 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 w-72 animate-in slide-in-from-right-2 fade-in duration-200 text-left" onClick={e => e.stopPropagation()}>
                                                <div className="flex justify-between items-center mb-4">
                                                    <p className="text-[9px] font-black uppercase text-slate-500 tracking-widest">Link de Imputación de Horas</p>
                                                    <button onClick={() => setLinkPopover(null)} className="text-slate-300 hover:text-slate-500 transition-colors"><X size={14} /></button>
                                                </div>
                                                <div className="flex gap-2 mb-4">
                                                    <div className="flex-1">
                                                        <label className="text-[8px] font-bold text-slate-400 uppercase block mb-1">Desde</label>
                                                        <input type="date" value={linkPopover.fromDate} onChange={e => setLinkPopover({ ...linkPopover, fromDate: e.target.value })} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-bold text-slate-700 outline-none focus:border-indigo-400" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <label className="text-[8px] font-bold text-slate-400 uppercase block mb-1">Hasta</label>
                                                        <input type="date" value={linkPopover.toDate} onChange={e => setLinkPopover({ ...linkPopover, toDate: e.target.value })} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-bold text-slate-700 outline-none focus:border-indigo-400" />
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        const btn = document.activeElement;
                                                        fetch(API_URL + '?action=get_resource_token&resource_id=' + res.id, { headers: { 'Authorization': `Bearer ${localStorage.getItem('em_token') || ''}` } })
                                                            .then(r => r.json())
                                                            .then(d => {
                                                                if (d.status === 'success') {
                                                                    let link = `${window.location.protocol}//${window.location.host}${window.location.pathname.replace('index.html', '')}hours.html?t=${d.token}`;
                                                                    link += `&project_id=${proj.id}`;
                                                                    if (linkPopover.fromDate) link += `&from=${linkPopover.fromDate}`;
                                                                    if (linkPopover.toDate) link += `&to=${linkPopover.toDate}`;

                                                                    navigator.clipboard.writeText(link).then(() => {
                                                                        const oldText = btn.innerText;
                                                                        btn.innerText = '¡COPIADO!';
                                                                        btn.classList.replace('bg-indigo-600', 'bg-emerald-500');
                                                                        setTimeout(() => {
                                                                            btn.innerText = oldText;
                                                                            btn.classList.replace('bg-emerald-500', 'bg-indigo-600');
                                                                            setLinkPopover(null);
                                                                        }, 2000);
                                                                    });
                                                                } else {
                                                                    alert("Error al generar token: " + (d.message || "Error desconocido"));
                                                                }
                                                            });
                                                    }}
                                                    className="w-full py-3 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 shadow-lg shadow-indigo-600/20 transition-all active:scale-95"
                                                >
                                                    Generar y Copiar Link
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                                {/* Expandable detail row */}
                                {isExpanded && matchingLogs.length > 0 && (
                                    <tr>
                                        <td colSpan={99} className="px-8 py-4 bg-indigo-50/30">
                                            <div className="max-w-xl ml-auto">
                                                <p className="text-[9px] font-black uppercase text-indigo-600 tracking-widest mb-3 flex items-center gap-2">
                                                    <Clock size={12} /> Detalle de horas imputadas — {res.name} / {proj.name}
                                                </p>
                                                <div className="bg-white rounded-2xl border border-indigo-100 overflow-hidden shadow-sm">
                                                    <table className="w-full text-left">
                                                        <thead>
                                                            <tr className="text-[8px] font-black uppercase text-slate-400 tracking-widest bg-slate-50 border-b border-slate-100">
                                                                <th className="px-4 py-3">Fecha</th>
                                                                <th className="px-4 py-3 text-center">Horas</th>
                                                                <th className="px-4 py-3">Detalle</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-50">
                                                            {matchingLogs
                                                                .sort((a, b) => a.date.localeCompare(b.date))
                                                                .map((log, idx) => {
                                                                    const logDate = new Date(log.date + 'T12:00:00');
                                                                    const dayName = logDate.toLocaleDateString('es-ES', { weekday: 'short' });
                                                                    return (
                                                                        <tr key={log.id || idx} className="hover:bg-indigo-50/20 transition-colors">
                                                                            <td className="px-4 py-2.5">
                                                                                <span className="text-[10px] font-black text-slate-700">{log.date.split('-').reverse().join('/')}</span>
                                                                                <span className="text-[8px] font-bold text-indigo-400 uppercase ml-2">{dayName}</span>
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-center">
                                                                                {editingLogId === log.id ? (
                                                                                    <input
                                                                                        type="number"
                                                                                        value={logEditValue.hours}
                                                                                        onChange={e => setLogEditValue({ ...logEditValue, hours: e.target.value })}
                                                                                        className="w-16 bg-white border border-indigo-200 rounded p-1 text-[10px] font-black text-indigo-600 text-center outline-none focus:border-indigo-500"
                                                                                        autoFocus
                                                                                    />
                                                                                ) : (
                                                                                    <span className="text-[10px] font-black text-indigo-600">{parseFloat(log.hours).toFixed(1)}h</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-[9px] text-slate-500 font-medium">
                                                                                {editingLogId === log.id ? (
                                                                                    <div className="flex items-center gap-2">
                                                                                        <input
                                                                                            type="text"
                                                                                            value={logEditValue.details}
                                                                                            onChange={e => setLogEditValue({ ...logEditValue, details: e.target.value })}
                                                                                            className="flex-1 bg-white border border-indigo-200 rounded p-1 text-[10px] outline-none focus:border-indigo-500"
                                                                                            placeholder="Detalle..."
                                                                                        />
                                                                                        <button
                                                                                            onClick={async () => {
                                                                                                await onSaveLog({ ...log, hours: logEditValue.hours, details: logEditValue.details });
                                                                                                setEditingLogId(null);
                                                                                            }}
                                                                                            className="bg-emerald-500 text-white p-1 rounded hover:bg-emerald-600 transition-colors"
                                                                                        >
                                                                                            <Check size={12} />
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={() => setEditingLogId(null)}
                                                                                            className="bg-slate-200 text-slate-500 p-1 rounded hover:bg-slate-300 transition-colors"
                                                                                        >
                                                                                            <X size={12} />
                                                                                        </button>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="flex items-center justify-between group/detail">
                                                                                        <span>{log.details || '—'}</span>
                                                                                        {canEdit && (
                                                                                            <div className="flex items-center gap-1 opacity-0 group-hover/detail:opacity-100 transition-opacity">
                                                                                                <button
                                                                                                    onClick={() => {
                                                                                                        setEditingLogId(log.id);
                                                                                                        setLogEditValue({ hours: log.hours, details: log.details || '' });
                                                                                                    }}
                                                                                                    className="text-slate-300 hover:text-indigo-600 p-1"
                                                                                                    title="Editar registro"
                                                                                                >
                                                                                                    <Edit3 size={10} />
                                                                                                </button>
                                                                                                <button
                                                                                                    onClick={() => {
                                                                                                        if (confirm("¿Eliminar este registro de horas?")) {
                                                                                                            onDeleteLog(log.id);
                                                                                                        }
                                                                                                    }}
                                                                                                    className="text-slate-300 hover:text-rose-500 p-1"
                                                                                                    title="Eliminar registro"
                                                                                                >
                                                                                                    <Trash2 size={10} />
                                                                                                </button>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            <tr className="bg-slate-50 border-t border-slate-200">
                                                                <td className="px-4 py-2.5 text-[9px] font-black text-slate-500 uppercase">Total</td>
                                                                <td className="px-4 py-2.5 text-center text-[10px] font-black text-indigo-700">{realHours.toFixed(1)}h</td>
                                                                <td className="px-4 py-2.5 text-[8px] font-bold text-slate-400 uppercase">{matchingLogs.length} registros</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}


function ResourceUsageMatrix({ resources, assignments, projects, resourceInactivity = [], groupBy = 'RESOURCE' }) {
    const [weekOffset, setWeekOffset] = useState(0);
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [viewMetric, setViewMetric] = useState('PERCENTAGE'); // 'PERCENTAGE' or 'COST'

    const toggleGroup = (id) => {
        setCollapsedGroups(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const weeks = useMemo(() => {
        const result = [];
        let curr = new Date();
        // Empezar en el lunes de esta semana + el offset de semanas
        curr.setDate(curr.getDate() - (curr.getDay() === 0 ? 6 : curr.getDay() - 1) + (weekOffset * 7));

        for (let i = 0; i < 12; i++) {
            const start = new Date(curr);
            const end = new Date(curr);
            end.setDate(end.getDate() + 6);
            result.push({
                index: i,
                start,
                end,
                label: `Sem ${i + 1 + weekOffset}`,
                display: start.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })
            });
            curr.setDate(curr.getDate() + 7);
        }
        return result;
    }, [weekOffset]);

    const getAllocationInWeek = (asig, week) => {
        const asigStart = new Date(asig.start_date);
        const asigEnd = new Date(asig.end_date);

        if (asigEnd < week.start || asigStart > week.end) return 0;

        // Return percentage if it overlaps ANY day of the week
        // BUT we will use a different aggregator later for daily accuracy
        return parseFloat(asig.percentage);
    };

    const getDailyMaxInWeek = (resAsigs, week) => {
        if (!resAsigs || resAsigs.length === 0) return 0;

        // Check load for each day of the week
        let maxLoad = 0;
        for (let i = 0; i < 7; i++) {
            const checkDate = new Date(week.start);
            checkDate.setDate(checkDate.getDate() + i);

            const loadOnDay = resAsigs.reduce((sum, a) => {
                const s = new Date(a.start_date);
                const e = new Date(a.end_date);
                if (checkDate >= s && checkDate <= e) return sum + parseFloat(a.percentage || 0);
                return sum;
            }, 0);

            if (loadOnDay > maxLoad) maxLoad = loadOnDay;
        }
        return maxLoad;
    };

    const getInactivityInWeek = (resId, week) => {
        const inactivity = (resourceInactivity || []).filter(i => i.resource_id === resId);
        return inactivity.find(i => {
            const iStart = new Date(i.start_date);
            const iEnd = new Date(i.end_date);
            return iStart <= week.end && iEnd >= week.start;
        });
    };

    const calculateCost = (res, percentage, week = null) => {
        if (!res) return 0;
        // Simplificación: consideramos que si hay solapamiento, el costo es proporcional a los hs/día * vlr_hora * %
        // En una semana laboral de 5 días
        const dailyHours = parseFloat(res.daily_hours) * (percentage / 100);
        return dailyHours * parseFloat(res.hourly_rate) * 5; // Costo Semanal
    };

    const renderHeader = () => (
        <thead>
            <tr className="bg-slate-900 text-[9px] font-black uppercase text-slate-400 tracking-widest">
                <th className="px-6 py-4 sticky left-0 bg-slate-900 z-10 w-64 border-r border-slate-800 text-white">
                    {groupBy === 'RESOURCE' ? 'Recurso / Proyectos' : 'Proyecto / Recursos'}
                </th>
                {weeks.map(w => (
                    <th key={w.index} className="px-4 py-4 text-center border-r border-slate-800 min-w-[80px]">
                        <p className="text-white">{w.label}</p>
                        <p className="text-slate-500 font-bold">{w.display}</p>
                    </th>
                ))}
            </tr>
        </thead>
    );

    const Rows = useMemo(() => {
        if (groupBy === 'RESOURCE') {
            return resources.map(res => {
                const resAsigs = assignments.filter(a => a.resource_id === res.id);
                if (resAsigs.length === 0) return null;
                const isCollapsed = Boolean(collapsedGroups[res.id]);

                return (
                    <React.Fragment key={res.id}>
                        <tr className="bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition-all" onClick={() => toggleGroup(res.id)}>
                            <td className="px-6 py-4 sticky left-0 bg-slate-50 z-10 border-r border-slate-200 shadow-[2px_0_5px_rgba(0,0,0,0.05)]">
                                <div className="flex items-center gap-3">
                                    {isCollapsed ? <ChevronRight size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                                    <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center text-white text-[10px] font-black">{res.name[0]}</div>
                                    <div>
                                        <p className="text-[11px] font-black uppercase text-slate-800">{res.name}</p>
                                        <p className="text-[8px] font-bold text-slate-400 uppercase">TOTAL CARGA</p>
                                    </div>
                                </div>
                            </td>
                            {weeks.map(w => {
                                const total = getDailyMaxInWeek(resAsigs, w);
                                const inactivity = getInactivityInWeek(res.id, w);
                                const costVal = calculateCost(res, total);
                                return (
                                    <td key={w.index} className={`px-4 py-4 text-center border-r border-slate-100 font-black text-xs relative ${inactivity ? 'bg-rose-50/30' : ''}`}>
                                        {inactivity && <div className="absolute inset-0 bg-rose-500/5 pointer-events-none hatch-pattern"></div>}
                                        {viewMetric === 'PERCENTAGE' ? (
                                            <span className={total > 100 ? 'text-rose-600' : total > 0 ? 'text-indigo-600' : 'text-slate-300'}>
                                                {total > 0 ? `${total}%` : '-'}
                                            </span>
                                        ) : (
                                            <span className="text-slate-600 font-bold">
                                                {total > 0 ? `$${Math.round(costVal).toLocaleString()}` : '-'}
                                            </span>
                                        )}
                                        {inactivity && <span className="absolute bottom-1 left-0 w-full text-[6px] text-rose-400 uppercase font-black">{inactivity.reason?.substring(0, 10)}</span>}
                                    </td>
                                );
                            })}
                        </tr>
                        {!isCollapsed && resAsigs.map(asig => {
                            const proj = projects.find(p => p.id === asig.event_id);
                            return (
                                <tr key={asig.id} className="border-b border-slate-100 group hover:bg-white transition-all">
                                    <td className="px-6 py-3 sticky left-0 bg-white z-10 border-r border-slate-100 shadow-[2px_0_5px_rgba(0,0,0,0.02)] pl-12 text-[10px] font-bold text-slate-500 uppercase">
                                        {proj?.name || 'Desconocido'}
                                    </td>
                                    {weeks.map(w => {
                                        const perc = getAllocationInWeek(asig, w);
                                        return (
                                            <td key={w.index} className={`px-4 py-3 text-center border-r border-slate-50 text-[10px] font-bold ${perc > 0 ? 'text-slate-600 bg-slate-50/30' : 'text-slate-200'}`}>
                                                {perc > 0 ? `${perc}%` : ''}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </React.Fragment>
                );
            });
        } else {
            return projects.map(proj => {
                const projAsigs = assignments.filter(a => a.event_id === proj.id);
                if (projAsigs.length === 0) return null;
                const isCollapsed = Boolean(collapsedGroups[proj.id]);

                return (
                    <React.Fragment key={proj.id}>
                        <tr className="bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition-all" onClick={() => toggleGroup(proj.id)}>
                            <td className="px-6 py-4 sticky left-0 bg-slate-50 z-10 border-r border-slate-200 shadow-[2px_0_5px_rgba(0,0,0,0.05)]">
                                <div className="flex items-center gap-3">
                                    {isCollapsed ? <ChevronRight size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                                    <div>
                                        <p className="text-[11px] font-black uppercase text-slate-800">{proj.name}</p>
                                        <p className="text-[8px] font-bold text-slate-400 uppercase">RECURSOS ASIGNADOS</p>
                                    </div>
                                </div>
                            </td>
                            {weeks.map(w => {
                                const count = projAsigs.filter(a => getAllocationInWeek(a, w) > 0).length;
                                return (
                                    <td key={w.index} className="px-4 py-4 text-center border-r border-slate-100 font-black text-[10px] text-slate-400">
                                        {count > 0 ? `${count} res.` : '-'}
                                    </td>
                                );
                            })}
                        </tr>
                        {!isCollapsed && projAsigs.map(asig => {
                            const res = resources.find(r => r.id === asig.resource_id);
                            return (
                                <tr key={asig.id} className="border-b border-slate-100 group hover:bg-white transition-all">
                                    <td className="px-6 py-3 sticky left-0 bg-white z-10 border-r border-slate-100 shadow-[2px_0_5px_rgba(0,0,0,0.02)] pl-12 text-[10px] font-bold text-slate-500 uppercase">
                                        {res?.name || 'Desconocido'}
                                    </td>
                                    {weeks.map(w => {
                                        const perc = getAllocationInWeek(asig, w);
                                        const inactivity = getInactivityInWeek(asig.resource_id, w);
                                        const costVal = calculateCost(res, perc);
                                        return (
                                            <td key={w.index} className={`px-4 py-3 text-center border-r border-slate-50 text-[10px] font-bold relative ${perc > 0 ? 'bg-slate-50/30' : ''} ${inactivity ? 'bg-rose-50/30' : ''}`}>
                                                {inactivity && <div className="absolute inset-0 bg-rose-500/5 pointer-events-none hatch-pattern"></div>}
                                                {viewMetric === 'PERCENTAGE' ? (
                                                    <span className={perc > 0 ? 'text-slate-600' : 'text-slate-200'}>
                                                        {perc > 0 ? `${perc}%` : ''}
                                                    </span>
                                                ) : (
                                                    <span className={perc > 0 ? 'text-slate-600' : 'text-slate-200'}>
                                                        {perc > 0 ? `$${Math.round(costVal).toLocaleString()}` : ''}
                                                    </span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </React.Fragment>
                );
            });
        }
    }, [groupBy, resources, assignments, projects, weeks, collapsedGroups, viewMetric]);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center bg-white px-8 py-4 rounded-2xl border border-slate-100 shadow-sm">
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setWeekOffset(prev => prev - 4)} className="p-3 bg-slate-50 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-indigo-600 transition-all border border-transparent hover:border-indigo-100"><ChevronLeft size={20} /></button>
                        <button onClick={() => setWeekOffset(0)} className="px-6 py-2.5 bg-slate-50 rounded-xl text-[10px] font-black uppercase text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 transition-all">Hoy</button>
                        <button onClick={() => setWeekOffset(prev => prev + 4)} className="p-3 bg-slate-50 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-indigo-600 transition-all border border-transparent hover:border-indigo-100"><ChevronRight size={20} /></button>
                    </div>

                    <div className="h-8 w-px bg-slate-100"></div>

                    <div className="flex bg-slate-100 p-1 rounded-xl">
                        <button onClick={() => setViewMetric('PERCENTAGE')} className={`px-4 py-2 rounded-lg text-[9px] font-black uppercase transition-all ${viewMetric === 'PERCENTAGE' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>Carga (%)</button>
                        <button onClick={() => setViewMetric('COST')} className={`px-4 py-2 rounded-lg text-[9px] font-black uppercase transition-all ${viewMetric === 'COST' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>Costo ($)</button>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-rose-500/10 border border-rose-200 rounded"></div>
                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Inactividad</span>
                    </div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Mostrando 12 semanas ({weeks[0].display} - {weeks[11].display})</p>
                </div>
            </div>

            <div className="overflow-x-auto rounded-[2rem] border border-slate-200 shadow-xl bg-white animate-in zoom-in duration-300 max-w-full">
                <table className="w-full text-left border-collapse min-w-[1200px]">
                    {renderHeader()}
                    <tbody>
                        {Rows}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function AssignmentCard({ asig, res, proj, cost, onEdit, onDelete }) {
    return (
        <div className="p-6 bg-white rounded-3xl border border-slate-100 flex flex-col gap-4 relative group shadow-sm hover:shadow-md transition-all">
            <div className="absolute -top-2 -right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-all">
                <button onClick={() => onEdit(asig)} className="w-8 h-8 bg-white border border-slate-100 rounded-full flex items-center justify-center text-slate-300 hover:text-indigo-600 shadow-md"><Edit3 size={14} /></button>
                <button onClick={() => { if (confirm("¿Eliminar asignación?")) onDelete(asig.id); }} className="w-8 h-8 bg-white border border-slate-100 rounded-full flex items-center justify-center text-slate-300 hover:text-rose-500 shadow-md"><X size={14} /></button>
            </div>
            <div className="flex justify-between items-start">
                <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white shrink-0 uppercase font-black text-base">{res ? res.name[0] : '?'}</div>
                <div className="text-right">
                    <span className="text-xl font-black text-indigo-600 block">{asig.percentage}%</span>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Costo: ARS {Math.round(cost || 0).toLocaleString()}</span>
                </div>
            </div>
            <div>
                <h5 className="text-[11px] font-black uppercase text-slate-800 leading-tight">{res ? res.name : 'Recurso Eliminado'}</h5>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">En {proj ? proj.name : 'Proyecto Eliminado'}</p>
            </div>
            <div className="pt-3 border-t border-slate-100 flex justify-between items-center text-[9px] font-black uppercase text-slate-400">
                <span>Desde: {asig.start_date.split('-').reverse().join('/')}</span>
                <span>Hasta: {asig.end_date.split('-').reverse().join('/')}</span>
            </div>
        </div>
    );
}


function ResourceManager({ resources, assignments, projects, resourceInactivity, resourceHoursLog = [], onAdd, onEdit, onDelete, onAssign, onEditAssignment, onDeleteAssignment, currentUser, setActiveModal, setModalData, globalFxRate }) {
    const [viewMode, setViewMode] = useState('LIST');
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('ALL');
    const [expandedResId, setExpandedResId] = useState(null);

    const filteredResources = resources.filter(r => {
        const matchesSearch = r.name.toLowerCase().includes(search.toLowerCase()) || (r.position || '').toLowerCase().includes(search.toLowerCase());
        const matchesType = typeFilter === 'ALL' || r.position === typeFilter;
        return matchesSearch && matchesType;
    });

    const positions = [...new Set(resources.map(r => r.position).filter(Boolean))];

    return (
        <div className="space-y-12 animate-in pb-20">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-8 px-4">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-slate-800 rounded-[2rem] flex items-center justify-center text-white shadow-2xl">
                        <Briefcase size={32} />
                    </div>
                    <div>
                        <h3 className="text-4xl font-black uppercase tracking-tighter text-slate-800">Control de Recursos</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">Asignación de tiempo y costos de mano de obra</p>
                    </div>
                </div>

                <div className="flex flex-wrap gap-4">
                    <button onClick={() => exportResourceUsageToExcel(resources, assignments, projects)} className="px-6 py-2.5 bg-emerald-50 text-emerald-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 hover:text-white transition-all border border-emerald-100 shadow-sm flex items-center gap-2">
                        <Download size={14} /> Exportar
                    </button>
                    <div className="flex bg-white p-1.5 rounded-2xl shadow-sm border border-slate-100">
                        <button onClick={() => setViewMode('LIST')} className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all ${viewMode === 'LIST' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50'}`}>Recursos</button>
                        <button onClick={() => setViewMode('DASHBOARD')} className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all ${viewMode === 'DASHBOARD' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50'}`}>Control de Carga</button>
                    </div>
                    <div className="flex items-center gap-3 bg-white px-6 py-2 rounded-2xl border border-slate-100 shadow-sm min-w-[200px]">
                        <Search size={16} className="text-slate-400" />
                        <input
                            type="text"
                            placeholder="Buscar recurso o cargo..."
                            className="bg-transparent border-none text-[10px] font-bold uppercase focus:ring-0 w-full"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="bg-white px-6 py-3 rounded-2xl border border-slate-100 shadow-sm text-[10px] font-black uppercase text-slate-500 focus:ring-0"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                    >
                        <option value="ALL">Todos los tipos</option>
                        {positions.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <button onClick={onAdd} className="btn-primary py-4 px-10 text-[11px] uppercase tracking-widest bg-indigo-600 shadow-xl shadow-indigo-100">
                        <Plus size={20} /> Nuevo Recurso
                    </button>
                </div>
            </div>

            {viewMode === 'LIST' ? (
                (() => {
                    if (filteredResources.length === 0) return (
                        <div className="p-20 bg-white border-2 border-dashed border-slate-100 rounded-[3rem] text-center">
                            <p className="text-slate-400 font-black uppercase text-xs tracking-widest italic">No hay recursos que coincidan con la búsqueda</p>
                        </div>
                    );

                    const grouped = {};
                    filteredResources.forEach(res => {
                        const group = res.position || 'Sin cargo';
                        if (!grouped[group]) grouped[group] = [];
                        grouped[group].push(res);
                    });
                    const groupKeys = Object.keys(grouped).sort();

                    return (
                        <div className="space-y-6">
                            {groupKeys.map(groupName => (
                                <div key={groupName} className="space-y-3">
                                    <div className="flex items-center gap-4 px-4">
                                        <div className="w-1.5 h-6 bg-indigo-600 rounded-full"></div>
                                        <h4 className="text-[11px] font-black uppercase tracking-widest text-indigo-600">{groupName}</h4>
                                        <span className="text-[9px] font-black text-slate-400 bg-slate-100 px-3 py-1 rounded-full">{grouped[groupName].length}</span>
                                    </div>
                                    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden divide-y divide-slate-50">
                                        {grouped[groupName].map(res => {
                                            const isExpanded = expandedResId === res.id;
                                            const resAssignments = assignments.filter(a => a.resource_id === res.id);
                                            const totalLoad = resAssignments.reduce((sum, a) => sum + parseFloat(a.percentage || 0), 0);

                                            return (
                                                <div key={res.id}>
                                                    <div
                                                        className={`flex items-center gap-6 px-8 py-4 cursor-pointer hover:bg-slate-50/70 transition-all group ${isExpanded ? 'bg-indigo-50/30' : ''}`}
                                                        onClick={() => setExpandedResId(isExpanded ? null : res.id)}
                                                    >
                                                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white font-black text-base shrink-0 shadow-sm relative">
                                                            {res.name[0]}
                                                            {totalLoad > 100 && (
                                                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-rose-500 rounded-full border-2 border-white animate-pulse"></div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <p className="font-black text-sm uppercase tracking-tight text-slate-800 truncate">{res.name}</p>
                                                                {res.user_id && <UserCheck size={12} className="text-emerald-500" />}
                                                            </div>
                                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate">{res.email || 'Sin correo'} • {res.position}</p>
                                                        </div>
                                                        <div className="hidden md:flex items-center gap-8 shrink-0">
                                                            <div className="text-right">
                                                                <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">{resAssignments.length} Proyecto{resAssignments.length !== 1 ? 's' : ''}</p>
                                                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{parseFloat(res.daily_hours)}h diarios</p>
                                                            </div>
                                                        </div>
                                                        <div className={`p-2 transition-transform duration-200 text-slate-300 ${isExpanded ? 'rotate-180' : ''}`}>
                                                            <ChevronDown size={16} />
                                                        </div>
                                                    </div>
                                                    {isExpanded && (
                                                        <div className="px-8 pb-8 pt-4 bg-gradient-to-b from-indigo-50/30 to-white border-t border-indigo-100 animate-in fade-in slide-in-from-top-2 duration-200">
                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                                                <div className="space-y-4">
                                                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Capacidad y Costo</p>
                                                                    <div className="flex gap-8">
                                                                        <div>
                                                                            <p className="text-[8px] font-bold text-slate-400 uppercase mb-1">Dedicación</p>
                                                                            <p className="text-xl font-black text-slate-800 tracking-tighter">{parseFloat(res.daily_hours)}h / día</p>
                                                                        </div>
                                                                        <div>
                                                                            <p className="text-[8px] font-bold text-slate-400 uppercase mb-1">Costo Hora</p>
                                                                            <p className="text-xl font-black text-slate-800 tracking-tighter">{res.currency || 'ARS'} {parseFloat(res.hourly_rate).toLocaleString()}</p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center justify-end md:col-span-1 lg:col-span-2 gap-3">
                                                                    <button onClick={(e) => { e.stopPropagation(); setModalData(res); setActiveModal('MANAGE_INACTIVITY'); }} className="px-4 py-2.5 bg-rose-50 text-rose-600 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-rose-100 transition-all border border-rose-100 flex items-center gap-2">
                                                                        <CalendarOff size={14} /> Inactividad
                                                                    </button>
                                                                    <button onClick={(e) => { e.stopPropagation(); onAssign(res); }} className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-slate-900 transition-all shadow-lg shadow-indigo-100 flex items-center gap-2">
                                                                        <Plus size={14} /> Asignar
                                                                    </button>
                                                                    <div className="h-8 w-px bg-slate-200 mx-2"></div>
                                                                    <button onClick={(e) => { e.stopPropagation(); onEdit(res); }} className="p-2.5 bg-slate-50 rounded-xl hover:bg-indigo-100 hover:text-indigo-600 transition-all text-slate-300">
                                                                        <Edit3 size={15} />
                                                                    </button>
                                                                    <button onClick={(e) => { e.stopPropagation(); if (confirm('¿Eliminar recurso?')) onDelete(res.id); }} className="p-2.5 bg-slate-50 rounded-xl hover:bg-rose-100 hover:text-rose-600 transition-all text-slate-300">
                                                                        <Trash2 size={15} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                })()
            ) : (
                <ResourceControlDashboard
                    resources={resources}
                    assignments={assignments.filter(a => resources.some(r => r.id === a.resource_id && filteredResources.includes(r)))}
                    projects={projects}
                    resourceInactivity={resourceInactivity}
                    onEditAssignment={onEditAssignment}
                    onDeleteAssignment={onDeleteAssignment}
                    resourceHoursLog={resourceHoursLog}
                    currentUser={currentUser}
                    globalFxRate={globalFxRate}
                />
            )}

        </div>
    );
}

function ResourceReportModal({ onClose, projects, resources, companyId }) {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [projectId, setProjectId] = useState('');
    const [resourceId, setResourceId] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleExport = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                action: 'get_resource_report',
                company_id: companyId,
                start_date: startDate,
                end_date: endDate,
                project_id: projectId,
                resource_id: resourceId
            });

            const token = localStorage.getItem('em_token');
            const headers = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const res = await fetch(`${API_URL}?${params.toString()}`, {
                headers: headers
            });
            const data = await res.json();

            if (data.status === 'success') {
                if (typeof XLSX === 'undefined') {
                    alert("Error: Librería XLSX no cargada.");
                    return;
                }
                const wb = XLSX.utils.book_new();

                // Sheet 1: Logs Detallados
                if (data.logs && data.logs.length > 0) {
                    const wsLogs = XLSX.utils.json_to_sheet(data.logs.map(l => ({
                        Fecha: l.date,
                        Recurso: l.resource_name,
                        Cargo: l.position,
                        Proyecto: l.project_name,
                        Horas: parseFloat(l.hours),
                        Detalle: l.details
                    })));
                    XLSX.utils.book_append_sheet(wb, wsLogs, "Registros Diarios");
                }

                // Sheet 2: Por Recurso
                if (data.by_resource && data.by_resource.length > 0) {
                    const wsRes = XLSX.utils.json_to_sheet(data.by_resource.map(r => ({
                        ID: r.id,
                        Nombre: r.name,
                        Cargo: r.position,
                        'Total Horas': parseFloat(r.total_hours),
                        'Dias Trabajados': parseInt(r.days_worked)
                    })));
                    XLSX.utils.book_append_sheet(wb, wsRes, "Resumen por Recurso");
                }

                // Sheet 3: Por Proyecto
                if (data.by_project && data.by_project.length > 0) {
                    const wsProj = XLSX.utils.json_to_sheet(data.by_project.map(p => ({
                        ID: p.id,
                        Proyecto: p.name,
                        'Total Horas': parseFloat(p.total_hours),
                        'Recursos Involucrados': parseInt(p.resource_count)
                    })));
                    XLSX.utils.book_append_sheet(wb, wsProj, "Resumen por Proyecto");
                }

                XLSX.writeFile(wb, `Reporte_Recursos_${new Date().toISOString().split('T')[0]}.xlsx`);
                onClose();
            } else {
                alert("Error al obtener datos: " + data.message);
            }
        } catch (error) {
            console.error(error);
            alert("Error de conexión al exportar.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal title="Reporte de Recursos y Horas" onClose={onClose}>
            <form onSubmit={handleExport} className="space-y-6">
                <div className="bg-slate-50 p-6 rounded-3xl space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Desde</label>
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="input-base" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Hasta</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="input-base" />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Filtrar por Proyecto (Opcional)</label>
                        <select value={projectId} onChange={e => setProjectId(e.target.value)} className="input-base">
                            <option value="">Todos los Proyectos</option>
                            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>

                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Filtrar por Recurso (Opcional)</label>
                        <select value={resourceId} onChange={e => setResourceId(e.target.value)} className="input-base">
                            <option value="">Todos los Recursos</option>
                            {resources.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                    <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl text-slate-500 font-bold hover:bg-slate-100 text-xs uppercase tracking-widest">Cancelar</button>
                    <button type="submit" disabled={isLoading} className="btn-primary py-3 px-8 text-xs uppercase font-black flex items-center gap-2">
                        {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        Descargar Excel
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function ResourceControlDashboard({ resources, assignments, projects, resourceInactivity, onEditAssignment, onDeleteAssignment, resourceHoursLog = [], currentUser, globalFxRate }) {
    const [showReportModal, setShowReportModal] = useState(false);
    const [groupBy, setGroupBy] = useState('RESOURCE'); // RESOURCE, PROJECT, MATRIX_RESOURCE, MATRIX_PROJECT, GRID
    const [filterYear, setFilterYear] = useState(null);  // null = all years
    const [filterMonth, setFilterMonth] = useState(null); // null = all months
    const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
    const [expandedProjects, setExpandedProjects] = useState({});
    const [linkPopover, setLinkPopover] = useState(null); // { resId, projId, fromDate, toDate }



    const myCompanyResources = useMemo(() => {
        if (!currentUser) return resources;
        return resources.filter(r => r.company_id === (currentUser.company_id || currentUser.companyId));
    }, [resources, currentUser]);

    // Filter assignments by selected year/month (assignments that overlap the period)
    const filteredAssignments = useMemo(() => {
        if (!filterYear && !filterMonth) return assignments;
        return assignments.filter(a => {
            const start = new Date(a.start_date);
            const end = new Date(a.end_date);
            if (filterYear && !filterMonth) {
                // Any assignment that overlaps the year
                return start.getFullYear() <= filterYear && end.getFullYear() >= filterYear;
            }
            if (filterYear && filterMonth) {
                // Any assignment that overlaps the specific month
                const periodStart = new Date(filterYear, filterMonth - 1, 1);
                const periodEnd = new Date(filterYear, filterMonth, 0);
                return start <= periodEnd && end >= periodStart;
            }
            return true;
        });
    }, [assignments, filterYear, filterMonth]);

    // Build year options from assignments
    const availableYears = useMemo(() => {
        const years = new Set();
        assignments.forEach(a => {
            if (a.start_date) years.add(new Date(a.start_date).getFullYear());
            if (a.end_date) years.add(new Date(a.end_date).getFullYear());
        });
        return Array.from(years).sort((a, b) => b - a);
    }, [assignments]);

    const myCompanyAssignments = useMemo(() => {
        return filteredAssignments.filter(a => myCompanyResources.some(r => r.id === a.resource_id));
    }, [filteredAssignments, myCompanyResources]);

    const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

    // Calcular presupuesto de mano de obra por proyecto basado en horas asignadas
    const projectVantages = useMemo(() => {
        const vand = {};
        filteredAssignments.forEach(asig => {
            const res = resources.find(r => r.id === asig.resource_id);
            if (!res) return;

            const proj = projects.find(p => p.id === asig.event_id);
            if (!proj) return;

            const start = new Date(asig.start_date);
            const end = new Date(asig.end_date);
            const wDays = getWorkDays(start, end);
            const hours = wDays * parseFloat(res.daily_hours) * (parseFloat(asig.percentage) / 100);
            const cost = hours * parseFloat(res.hourly_rate);

            if (!vand[asig.event_id]) vand[asig.event_id] = { name: proj.name, costs: {}, totalHours: 0, resourcesCount: 0, sync: proj.sync_mdo_resources == 1 };

            const currency = res.currency || 'ARS';
            if (!vand[asig.event_id].costs[currency]) vand[asig.event_id].costs[currency] = 0;

            vand[asig.event_id].costs[currency] += cost;
            vand[asig.event_id].totalHours += hours;
            vand[asig.event_id].resourcesCount++;
        });
        return vand;
    }, [filteredAssignments, resources, projects]);

    return (
        <div className="space-y-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                {/* Alertas de Sobre-asignación */}
                <div className="col-span-1 lg:col-span-2 space-y-6">
                    <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm space-y-6">
                        <div onClick={() => setIsSummaryExpanded(!isSummaryExpanded)} className="flex justify-between items-center cursor-pointer group">
                            <h4 className="text-xl font-black uppercase tracking-tighter text-slate-800">Presupuesto MDO Estimado por Proyecto</h4>
                            <div className={`w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-all ${isSummaryExpanded ? 'rotate-180' : ''}`}>
                                <ChevronDown size={20} />
                            </div>
                        </div>
                        {isSummaryExpanded && (
                            <div className="overflow-x-auto animate-in fade-in duration-300">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                                            <th className="py-4"></th>
                                            <th className="py-4">Proyecto</th>
                                            <th className="py-4 text-center">Recursos</th>
                                            <th className="py-4 text-center">Total Horas</th>
                                            <th className="py-4 text-right">MDO Proyectada</th>
                                            <th className="py-4 text-center">Estado Sinc.</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {Object.entries(projectVantages).map(([pid, data]) => {
                                            const isExpanded = expandedProjects[pid];
                                            const projAsigs = filteredAssignments.filter(a => a.event_id === pid);
                                            return (
                                                <React.Fragment key={pid}>
                                                    <tr className="group hover:bg-slate-50 transition-all cursor-pointer" onClick={() => setExpandedProjects(prev => ({ ...prev, [pid]: !prev[pid] }))}>
                                                        <td className="py-5 pl-2">
                                                            <div className={`w-6 h-6 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                                                                <ChevronDown size={14} />
                                                            </div>
                                                        </td>
                                                        <td className="py-5 font-black text-xs text-slate-700 uppercase">{data.name}</td>
                                                        <td className="py-5 text-center font-bold text-xs text-slate-500">{data.resourcesCount}</td>
                                                        <td className="py-5 text-center font-bold text-xs text-slate-500">{data.totalHours.toFixed(0)}h</td>
                                                        <td className="py-5 text-right font-black text-xs text-indigo-600">
                                                            {Object.entries(data.costs).map(([curr, total]) => (
                                                                <div key={curr}>{curr} {total.toLocaleString()}</div>
                                                            ))}
                                                        </td>
                                                        <td className="py-5 text-center">
                                                            {data.sync ? (
                                                                <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[8px] font-black uppercase">Sincronizado</span>
                                                            ) : (
                                                                <span className="px-3 py-1 bg-slate-100 text-slate-400 rounded-full text-[8px] font-black uppercase">Solo lectura</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {isExpanded && projAsigs.map(asig => {
                                                        const res = resources.find(r => r.id === asig.resource_id);
                                                        if (!res) return null;
                                                        const wDays = getWorkDays(new Date(asig.start_date), new Date(asig.end_date));
                                                        const hours = wDays * parseFloat(res.daily_hours) * (parseFloat(asig.percentage) / 100);
                                                        const cost = hours * parseFloat(res.hourly_rate);
                                                        return (
                                                            <tr key={asig.id} className="bg-slate-50/50">
                                                                <td></td>
                                                                <td className="py-3 pl-6">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="w-6 h-6 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 text-[8px] font-black">{res.name[0]}</div>
                                                                        <div>
                                                                            <p className="text-[10px] font-black text-slate-600 uppercase">{res.name}</p>
                                                                            <p className="text-[8px] text-slate-400">{res.position}</p>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 text-center text-[9px] font-bold text-slate-400">{asig.percentage}%</td>
                                                                <td className="py-3 text-center text-[9px] font-bold text-slate-400">{hours.toFixed(1)}h</td>
                                                                <td className="py-3 text-right text-[9px] font-bold text-slate-500">{res.currency || 'ARS'} {Math.round(cost).toLocaleString()}</td>
                                                                <td className="py-3 text-center text-[8px] text-slate-300">{asig.start_date.split('-').reverse().join('/')} → {asig.end_date.split('-').reverse().join('/')}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            );
                                        })}
                                        {Object.keys(projectVantages).length === 0 && (
                                            <tr><td colSpan="6" className="py-10 text-center text-[10px] uppercase font-bold text-slate-300">No hay asignaciones cargadas</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-rose-50 p-8 rounded-[2.5rem] border border-rose-100">
                        <h4 className="flex items-center gap-2 text-rose-600 font-black uppercase text-xs tracking-widest mb-4">
                            <AlertTriangle size={16} /> Conflictos de Carga
                        </h4>
                        <div className="space-y-3">
                            {resources.map(res => {
                                const total = getResourceMaxLoad(res.id, assignments);
                                if (total <= 100) return null;
                                return (
                                    <div key={res.id} className="bg-white p-4 rounded-2xl flex justify-between items-center shadow-sm">
                                        <div>
                                            <p className="text-[11px] font-black uppercase text-slate-800 leading-tight">{res.name}</p>
                                            <p className="text-[8px] font-bold text-rose-500 uppercase tracking-widest mt-1">Exceso: {total - 100}%</p>
                                        </div>
                                        <span className="text-sm font-black text-rose-600">{total}%</span>
                                    </div>
                                );
                            })}
                            {!resources.some(res => getResourceMaxLoad(res.id, assignments) > 100) && (
                                <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest italic">Carga de trabajo balanceada</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Detalle de Asignaciones con Control de Agrupación */}
            <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm space-y-8">
                <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-6">
                    <div>
                        <h4 className="text-xl font-black uppercase tracking-tighter text-slate-800">Grilla de Asignaciones y Costos</h4>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Desglose detallado de recursos y su impacto presupuestario</p>
                    </div>
                    <div className="flex flex-col gap-3 items-end">
                        {/* Year/Month Filters */}
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                            <select
                                value={filterYear || ''}
                                onChange={e => { setFilterYear(e.target.value ? parseInt(e.target.value) : null); setFilterMonth(null); }}
                                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-black uppercase text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500/20"
                            >
                                <option value="">Todos los años</option>
                                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            {filterYear && (
                                <select
                                    value={filterMonth || ''}
                                    onChange={e => setFilterMonth(e.target.value ? parseInt(e.target.value) : null)}
                                    className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-black uppercase text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500/20"
                                >
                                    <option value="">Todos los meses</option>
                                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                                </select>
                            )}
                            {(filterYear || filterMonth) && (
                                <button onClick={() => { setFilterYear(null); setFilterMonth(null); }} className="px-3 py-2 bg-rose-50 text-rose-500 rounded-xl text-[10px] font-black uppercase hover:bg-rose-100 transition-all">
                                    Limpiar
                                </button>
                            )}
                        </div>
                        {/* View Mode Buttons */}
                        <div className="flex bg-slate-50 p-1.5 rounded-2xl border border-slate-100 flex-wrap gap-2">
                            <button onClick={() => setGroupBy('RESOURCE')} className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${groupBy === 'RESOURCE' ? 'bg-white text-indigo-600 shadow-sm border border-indigo-50' : 'text-slate-400 hover:text-slate-600'}`}>Listado Recurso</button>
                            <button onClick={() => setGroupBy('PROJECT')} className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${groupBy === 'PROJECT' ? 'bg-white text-indigo-600 shadow-sm border border-indigo-50' : 'text-slate-400 hover:text-slate-600'}`}>Listado Proyecto</button>
                            <button onClick={() => setGroupBy('MATRIX_RESOURCE')} className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${groupBy === 'MATRIX_RESOURCE' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}>Matriz Recurso</button>
                            <button onClick={() => setGroupBy('MATRIX_PROJECT')} className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${groupBy === 'MATRIX_PROJECT' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}>Matriz Proyecto</button>
                            <button onClick={() => setGroupBy('GRID')} className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${groupBy === 'GRID' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}>Grilla Total</button>
                            <button onClick={() => setShowReportModal(true)} className="px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all bg-emerald-600 text-white shadow-lg flex items-center gap-2 hover:bg-emerald-700 ml-2">
                                <Download size={12} /> Exportar Reporte
                            </button>
                        </div>
                    </div>
                </div>

                {groupBy === 'GRID' ? (
                    <ResourceAssignmentsTable
                        assignments={myCompanyAssignments}
                        resources={resources}
                        projects={projects}
                        onEdit={onEditAssignment}
                        onDelete={onDeleteAssignment}
                        onSaveLog={(log) => handleAction('save_resource_hours_log', log)}
                        onDeleteLog={(id) => handleAction('delete_resource_hours_log', { id })}
                        canEdit={true}
                        allAssignments={assignments}
                        resourceHoursLog={resourceHoursLog}
                        globalFxRate={globalFxRate}
                    />
                ) : groupBy === 'MATRIX_RESOURCE' || groupBy === 'MATRIX_PROJECT' ? (
                    <ResourceUsageMatrix
                        resources={myCompanyResources}
                        assignments={myCompanyAssignments}
                        projects={projects}
                        resourceInactivity={resourceInactivity}
                        groupBy={groupBy === 'MATRIX_RESOURCE' ? 'RESOURCE' : 'PROJECT'}
                    />
                ) : groupBy === 'RESOURCE' ? (
                    <div className="space-y-6">
                        {myCompanyResources.map(res => {
                            const resAsigs = myCompanyAssignments.filter(a => a.resource_id === res.id);
                            if (resAsigs.length === 0) return null;

                            return (
                                <div key={res.id} className="p-8 bg-slate-50/50 rounded-[2.5rem] border border-slate-100">
                                    <div className="flex justify-between items-center mb-6">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center text-white font-black">{res.name[0]}</div>
                                            <div>
                                                <h5 className="text-sm font-black uppercase text-slate-800 tracking-tight">{res.name}</h5>
                                                <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">{res.position}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Per-project assignments with individual link buttons */}
                                    {(() => {
                                        const projGroups = {};
                                        resAsigs.forEach(a => { if (!projGroups[a.event_id]) projGroups[a.event_id] = []; projGroups[a.event_id].push(a); });
                                        return Object.entries(projGroups).map(([projId, projAsigs]) => {
                                            const proj = projects.find(p => p.id === projId);
                                            if (!proj) return null;
                                            const popoverKey = `${res.id}_${projId}`;
                                            const isPopoverOpen = linkPopover?.key === popoverKey;
                                            return (
                                                <div key={projId} className="mb-4 last:mb-0">
                                                    <div className="flex items-center justify-between mb-2 px-2">
                                                        <p className="text-[10px] font-black uppercase text-slate-500 tracking-wider">{proj.name}</p>
                                                        <div className="relative">
                                                            <button onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isPopoverOpen) { setLinkPopover(null); return; }
                                                                // Default dates: first assignment start to last assignment end for this project
                                                                const starts = projAsigs.map(a => a.start_date).sort();
                                                                const ends = projAsigs.map(a => a.end_date).sort();
                                                                setLinkPopover({ key: popoverKey, resId: res.id, projId, fromDate: starts[0] || '', toDate: ends[ends.length - 1] || '' });
                                                            }} className="p-2 bg-indigo-50 rounded-lg hover:bg-indigo-100 text-indigo-600 transition-all" title="Generar Link de Imputación por Proyecto">
                                                                <LinkIcon size={14} />
                                                            </button>
                                                            {isPopoverOpen && (
                                                                <div className="absolute right-0 top-10 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 w-72 animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                                                                    <p className="text-[9px] font-black uppercase text-slate-500 tracking-widest mb-3">Rango de Fechas para Imputación</p>
                                                                    <div className="flex gap-2 mb-3">
                                                                        <div className="flex-1">
                                                                            <label className="text-[8px] font-bold text-slate-400 uppercase">Desde</label>
                                                                            <input type="date" value={linkPopover.fromDate} onChange={e => setLinkPopover({ ...linkPopover, fromDate: e.target.value })} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-bold text-slate-700 outline-none focus:border-indigo-400" />
                                                                        </div>
                                                                        <div className="flex-1">
                                                                            <label className="text-[8px] font-bold text-slate-400 uppercase">Hasta</label>
                                                                            <input type="date" value={linkPopover.toDate} onChange={e => setLinkPopover({ ...linkPopover, toDate: e.target.value })} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-bold text-slate-700 outline-none focus:border-indigo-400" />
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex gap-2">
                                                                        <button onClick={() => setLinkPopover(null)} className="flex-1 px-3 py-2 bg-slate-100 text-slate-500 rounded-xl text-[9px] font-black uppercase hover:bg-slate-200 transition-all">Cancelar</button>
                                                                        <button onClick={() => {
                                                                            fetch(API_URL + '?action=get_resource_token&resource_id=' + res.id, { headers: { 'Authorization': `Bearer ${localStorage.getItem('em_token') || ''}` } })
                                                                                .then(r => r.json())
                                                                                .then(d => {
                                                                                    if (d.status === 'success') {
                                                                                        let link = `${window.location.protocol}//${window.location.host}${window.location.pathname.replace('index.html', '')}hours.html?t=${d.token}`;
                                                                                        link += `&project_id=${projId}`;
                                                                                        if (linkPopover.fromDate) link += `&from=${linkPopover.fromDate}`;
                                                                                        if (linkPopover.toDate) link += `&to=${linkPopover.toDate}`;
                                                                                        navigator.clipboard.writeText(link);
                                                                                        alert(`Enlace (24h) copiado para ${proj.name}:\n${link}`);
                                                                                        setLinkPopover(null);
                                                                                    } else { alert('Error al generar link'); }
                                                                                }).catch(() => alert('Error de red'));
                                                                        }} className="flex-[2] px-3 py-2 bg-indigo-600 text-white rounded-xl text-[9px] font-black uppercase hover:bg-indigo-700 transition-all flex items-center justify-center gap-2">
                                                                            <LinkIcon size={12} /> Copiar Enlace
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <ResourceAssignmentsTable
                                                        assignments={projAsigs}
                                                        resources={resources}
                                                        projects={projects}
                                                        onEdit={onEditAssignment}
                                                        onDelete={onDeleteAssignment}
                                                        onSaveLog={(log) => handleAction('save_resource_hours_log', log)}
                                                        onDeleteLog={(id) => handleAction('delete_resource_hours_log', { id })}
                                                        showResourceColumn={false}
                                                        showProjectColumn={false}
                                                        canEdit={true}
                                                        allAssignments={assignments}
                                                        resourceHoursLog={resourceHoursLog}
                                                        globalFxRate={globalFxRate}
                                                    />
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="space-y-6">
                        {projects.map(proj => {
                            const projAsigs = myCompanyAssignments.filter(a => a.event_id === proj.id);
                            if (projAsigs.length === 0) return null;

                            return (
                                <div key={proj.id} className="p-8 bg-slate-50/50 rounded-[2.5rem] border border-slate-100">
                                    <div className="flex justify-between items-center mb-6">
                                        <div>
                                            <h5 className="text-sm font-black uppercase text-slate-800 tracking-tight">{proj.name}</h5>
                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Recursos asignados: {projAsigs.length}</p>
                                        </div>
                                    </div>
                                    <ResourceAssignmentsTable
                                        assignments={projAsigs}
                                        resources={resources}
                                        projects={projects}
                                        onEdit={onEditAssignment}
                                        onDelete={onDeleteAssignment}
                                        onSaveLog={(log) => handleAction('save_resource_hours_log', log)}
                                        onDeleteLog={(id) => handleAction('delete_resource_hours_log', { id })}
                                        showProjectColumn={false}
                                        canEdit={true}
                                        allAssignments={assignments}
                                        resourceHoursLog={resourceHoursLog}
                                        globalFxRate={globalFxRate}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {showReportModal && (
                <ResourceReportModal
                    onClose={() => setShowReportModal(false)}
                    resources={resources}
                    projects={projects}
                    companyId={currentUser?.company_id}
                />
            )}
        </div>
    );
}

function PurchaseOrderModal({ onClose, onSave, onDelete, project, data, purchase_orders, fCur }) {
    const [form, setForm] = useState({
        id: data?.id || null,
        event_id: project.id,
        client_id: project.client_id,
        oc_number: data?.oc_number || '',
        date: data?.date || new Date().toISOString().split('T')[0],
        amount_total: data?.amount_total || 0,
        description: data?.description || '',
        status: data?.status || 'ACTIVE'
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(form);
    };

    return (
        <Modal onClose={onClose} title={data?.id ? "Editar Orden de Compra" : "Nueva Orden de Compra"}>
            <form onSubmit={handleSubmit} className="space-y-8 p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Número de OC</label>
                        <input
                            type="text"
                            value={form.oc_number}
                            onChange={(e) => setForm({ ...form, oc_number: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 font-black uppercase tracking-tight outline-none focus:ring-2 focus:ring-indigo-500/20"
                            placeholder="Ej: OC-2026-001"
                            required
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Monto Total ({project.base_currency || 'ARS'})</label>
                        <div className="relative">
                            <span className="absolute left-6 top-1/2 -translate-y-1/2 text-indigo-400 font-bold">$</span>
                            <input
                                type="number"
                                value={form.amount_total}
                                onChange={(e) => setForm({ ...form, amount_total: parseFloat(e.target.value) || 0 })}
                                className="w-full bg-slate-50 border border-slate-100 rounded-2xl pl-12 pr-6 py-4 font-black outline-none focus:ring-2 focus:ring-indigo-500/20"
                                step="0.01"
                                required
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Fecha de Emisión</label>
                        <input
                            type="date"
                            value={form.date}
                            onChange={(e) => setForm({ ...form, date: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 font-black outline-none focus:ring-2 focus:ring-indigo-500/20"
                            required
                        />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Descripción / Concepto</label>
                        <textarea
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 font-bold outline-none focus:ring-2 focus:ring-indigo-500/20 h-24 resize-none"
                            placeholder="Detalle de la OC..."
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Estado</label>
                        <select
                            value={form.status}
                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 font-black uppercase outline-none focus:ring-2 focus:ring-indigo-500/20"
                        >
                            <option value="ACTIVE">Activa</option>
                            <option value="CLOSED">Cerrada</option>
                            <option value="CANCELLED">Cancelada</option>
                        </select>
                    </div>
                </div>

                <div className="flex gap-4 pt-6">
                    {data?.id && (
                        <button
                            type="button"
                            onClick={() => {
                                if (confirm("¿Seguro que desea eliminar esta Orden de Compra?")) {
                                    onDelete(data.id);
                                }
                            }}
                            className="p-5 bg-rose-50 text-rose-400 rounded-3xl hover:bg-rose-100 transition-all"
                            title="Eliminar OC"
                        >
                            <Trash2 size={20} />
                        </button>
                    )}
                    <button type="button" onClick={onClose} className="flex-1 py-5 rounded-3xl text-[11px] font-black uppercase text-slate-400 hover:text-slate-600 transition-all">Cancelar</button>
                    <button type="submit" className="flex-[2] btn-primary py-5 rounded-3xl text-[11px] font-black uppercase tracking-widest">Guardar OC</button>
                </div>
            </form>
        </Modal>
    );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);

